import type { Locale } from "@/i18n/locales";

function copy<T>(nl: T, en: T, de: T, fr: T, es: T): Record<Locale, T> {
  return { nl, en, de, fr, es };
}

const nl = [
  {
    slug: "west-nile-virus",
    date: "2026-09-11",
    category: "Infectieziekten",
    image: "/images/blog/west-nile-virus-hero.svg",
    author: "Vaxy",
    readTime: "6 min lezen",
    title:
      "Westnijlvirus: wat het is, wat de risico's zijn en wat er de komende jaren gebeurt",
    excerpt:
      "Het westnijlvirus verspreidt zich via muggen. De meeste mensen worden niet ziek, maar een kleine groep kan ernstig ziek worden. Wat betekent dat voor de komende jaren?",
    faq: [
      {
        q: "Wat is het westnijlvirus?",
        a: "Het westnijlvirus is een virus dat vooral bij vogels voorkomt. Muggen kunnen het virus overbrengen op mensen en paarden.",
      },
      {
        q: "Hoe gevaarlijk is het?",
        a: "De meeste mensen merken weinig of niets. Een kleine groep krijgt ernstige klachten, vooral ouderen en mensen met minder weerstand.",
      },
      {
        q: "Kun je het van mens op mens krijgen?",
        a: "Nee, niet via gewoon contact. Muggen geven het virus door. Soms kan overdracht wel via bloed of transplantatie, maar dat is zeldzaam.",
      },
      {
        q: "Wordt het de komende jaren meer?",
        a: "WHO en ECDC zeggen dat warmere zomers en langere muggenseizoenen het virus meer ruimte geven. In Europa kan het daarom langer rondgaan en in meer gebieden opduiken.",
      },
    ],
    content: `## Westnijlvirus

Het westnijlvirus is een virus dat vooral bij vogels voorkomt. Muggen kunnen het virus overbrengen op mensen en paarden. De meeste mensen merken niets of hebben alleen lichte klachten.

### Wat zijn de risico's?

De klachten lijken vaak op griep: koorts, hoofdpijn, spierpijn en moeheid. Een kleine groep wordt ernstig ziek. Dat gebeurt vaker bij ouderen en mensen met minder weerstand. Dan kan het virus de hersenen of zenuwen aantasten.

Er is geen vaccin voor mensen en er is geen medicijn dat het virus zelf wegneemt. In het ziekenhuis kunnen artsen wel helpen met de klachten en de zorg die je nodig hebt.

### Hoe kan het zich de komende jaren ontwikkelen?

WHO en ECDC zien dat warmere zomers en langere muggenseizoenen het virus meer tijd geven. Daardoor kan het in Europa langer rondgaan en in meer gebieden opduiken.

In Nederland houdt het RIVM het virus al in de gaten bij vogels en muggen. Dat betekent dat nieuwe menselijke gevallen mogelijk blijven als het weer en de muggen gunstig zijn.

### Wat kun je zelf doen?

Bescherm je tegen muggen, vooral in de avond en vroege ochtend. Gebruik muggenwering met DEET, draag bedekkende kleding en slaap zo nodig onder een klamboe.

Haal stilstaand water weg rond huis en tuin, want daar kunnen muggen zich voortplanten. Heb je na een muggenbeet hoge koorts, nekstijfheid of verwardheid, neem dan direct contact op met een arts.

### Korte antwoorden

**Kun je het westnijlvirus ook gewoon van een ander persoon krijgen?**
Nee, niet via normaal contact. Muggen geven het virus door. Alleen overdracht via bloed of transplantatie komt soms voor.

**Wie loopt meer risico op ernstige klachten?**
Vooral ouderen en mensen met minder weerstand. De meeste andere mensen worden niet of nauwelijks ziek.

**Is er een vaccin of behandeling?**
Nee, niet voor mensen. Daarom is muggenpreventie zo belangrijk.

**Wat is de beste tip voor de komende jaren?**
Blijf muggenbeten voorkomen. Dat is nu en in de toekomst de beste bescherming.

### Bronnen

- [WHO Europe: West Nile virus, as cases rise across parts of Europe](https://www.who.int/europe/news/item/24-07-2026-west-nile-virus--as-cases-rise-across-parts-of-europe--here-is-what-you-need-to-know)
- [WHO: vector-borne diseases](https://www.who.int/news-room/fact-sheets/detail/vector-borne-diseases)
- [RIVM: westnijlkoorts](https://www.rivm.nl/westnijlkoorts)
- [RIVM: westnijlkoorts, actueel](https://www.rivm.nl/westnijlkoorts/actueel)
- [ECDC: surveillance and updates for West Nile virus infection](https://www.ecdc.europa.eu/en/topics-z/west-nile-virus-infection/surveillance-and-updates-west-nile-virus-infection)

Dit is informatie, geen medisch advies.`,
  },
  {
    slug: "future-of-vaccination",
    date: "2026-08-26",
    category: "Gezondheidstips",
    image: "/images/blog/future-of-vaccination.webp",
    author: "Vaxy",
    readTime: "8 min lezen",
    title: "Toekomstige alternatieven voor de vaccinatieprik",
    excerpt:
      "De meeste vaccins gaan nog met een naald. Er zijn al druppels, een neusvaccin en naaldvrije straalinjectie. Huidpleisters van Micron, Vaxxas en anderen zijn nog onderzoek. WHO, Gavi en RIVM op een rij.",
    faq: [
      {
        q: "Welke alternatieven voor de naald zijn er al?",
        a: "Orale poliovaccins, rotavirusdruppels en oraal choleravaccin. In sommige landen een neusvaccin tegen griep. PharmaJet Tropis is een WHO-geprekwalificeerde naaldvrije straal in de huid, gebruikt bij polio-campagnes. In het Nederlandse RVP blijft de standaard een prik.",
      },
      {
        q: "Wat is een microarray-pleister?",
        a: "Een kleine huidpleister met heel korte puntjes die vaccin in de bovenste huidlaag brengen. De WHO noemt twee kandidaten voor mazelen en rodehond: Micron Biomedical (oplosbaar) en Vaxxas (gecoat, met applicator). Fase 3 en WHO-goedkeuring moeten nog.",
      },
      {
        q: "Kan ik in Nederland al zonder naald gevaccineerd worden?",
        a: "Baby's krijgen rotavirus al als druppel. Verder vaccineert het RIVM in het Rijksvaccinatieprogramma nog met naald en spuit. Huidpleisters zijn geen routineproduct.",
      },
      {
        q: "Waarom zoekt de WHO überhaupt naar alternatieven?",
        a: "Omdat de dekking stagneert. Spuiten, reconstitutie en een koude keten maken vaccineren lastig, vooral waar kinderen nog geen enkele prik hebben gehad. Een eenvoudiger product kan méér mensen bereiken.",
      },
      {
        q: "Is dit medisch advies?",
        a: "Nee. Dit is uitleg op basis van WHO, Gavi, BARDA en RIVM. Welk vaccin je nodig hebt, bepaal je met je huisarts, GGD of reizigersvaccinatiebureau.",
      },
    ],
    content: `## Toekomstige alternatieven voor de vaccinatieprik

De meeste vaccins gaan nog met een naald in de bovenarm. Dat werkt. Het is ook ingewikkeld: spuiten, soms eerst poeder oplossen, een koude keten, en mensen die de prik liever overslaan.

De Wereldgezondheidsorganisatie (WHO) zegt: die manier van toedienen is een van de redenen waarom de vaccinatiegraad is gestagneerd. In 2020 misten 23 miljoen kinderen hun prikken, 3,7 miljoen meer dan in 2019.

Daarom kijken WHO, Gavi, UNICEF, PATH en de Bill & Melinda Gates Foundation naar andere manieren van vaccineren. Hun Vaccine Innovation Prioritisation Strategy (VIPS) koos in mei 2020 drie prioriteiten: microarray-pleisters, hittebestendige vaccins, en barcodes op de verpakking.

Niet alles is toekomstmuziek. Sommige alternatieven voor de naald bestaan al. Andere zitten in trials. Dit is uitleg, geen medisch advies. In het Rijksvaccinatieprogramma blijft de standaard een prik.

### Wat is er al zonder naald?

Al langer:

- orale poliovaccins
- rotavirusdruppels voor baby's, ook in Nederland
- oraal choleravaccin
- in sommige landen een neusvaccin tegen griep

Die laatste prikken het slijmvlies, waar luchtwegvirussen binnenkomen. Onderzoekers werken ook aan neusvaccins tegen andere luchtweginfecties. De meeste coronavaccins in Europa blijven een spuit in de arm.

**PharmaJet Tropis** is iets anders: geen pleister, maar een dunne straal vloeistof die de huid in gaat, zonder naald. De WHO heeft het apparaat geprekwalificeerd voor intradermale toediening. Het is gebruikt bij polio-campagnes, onder meer in Pakistan en Nigeria. Dat is het dichtst bij “nu al zonder naald in het veld”. Het vervangt niet de DTP of BMR in Nederland.

### Huidpleisters: Micron, Vaxxas en anderen

Een microarray patch, of MAP, is een kleine huidpleister met heel korte puntjes. Die zijn te kort voor een gewone injectie. Ze brengen het vaccin in de bovenste huidlaag. Daar zitten veel immuuncellen.

De WHO noemt onder meer deze voordelen: geen reconstitutie en geen spuit, minder pijn, mogelijk beter houdbaar buiten de koelkast, en toediening door minder gespecialiseerd personeel.

Voor mazelen en rodehond noemt de WHO twee klinische kandidaten:

**Micron Biomedical** maakt een oplosbare pleister. Die blijft een paar minuten op de pols. Hun fase 1/2-studie bij volwassenen, peuters en zuigelingen in Gambia is gepubliceerd in *The Lancet*. Veiligheid en immuunrespons leken vergelijkbaar met de gewone prik onder de huid. Bij zuigelingen is dit de verst gevorderde MAP.

**Vaxxas** maakt een High-Density Microarray Patch: duizenden puntjes van ongeveer 0,25 millimeter, met gedroogd vaccin, en een eenmalige applicator op de bovenarm of het bovenbeen. Hun volwassenentrial voor mazelen-rodehond is afgerond. Een kinderfase stond daarna gepland.

Fase 3, registratie en WHO-prekwalificatie moeten nog. Reken er niet op bij je volgende DTP of BMR.

Andere bedrijven koppelen mRNA aan een pleister. Dat is vroeger stadium. De Amerikaanse BARDA Patch Forward Prize noemde naast Vaxxas en Micron onder meer **LTS Lohmann** (Duitsland) met **BioNet**, en **PopVax** (India) met LTS. Doel: griep- of coronavaccin als RNA op een microneedle-pleister. Geen routineproduct.

### Hittebestendige vaccins

De tweede VIPS-prioriteit is vaccins die een beperkte tijd buiten de koelkast mogen, in een controlled temperature chain. Minder ijs, minder uitval, meer bereik. Dat kan samengaan met een pleister of een klassieke spuit.

mRNA is een andere sprong: sneller maken bij een nieuwe variant. Die vaccins gaan nu nog wél met een naald, en vragen vaak extra koude opslag.

### Wat betekent dit voor jouw overzicht?

Of je een prik, een druppel, een straal of later een pleister krijgt: de datum, de ziekte en de naam van het product blijven nodig. Bij reizen, een booster of een nieuwe uitbraak wil een arts weten wat je al hebt.

Met Vaxy bewaar je die gegevens op één plek, voor jezelf en je gezin.

Twijfel je over een prik? Vraag het je huisarts, de GGD of een reizigersvaccinatiebureau.

### Korte antwoorden

**Welke alternatieven voor de naald zijn er al?**
Druppels (polio, rotavirus, cholera), in sommige landen een neusvaccin tegen griep, en naaldvrije straalinjectie van PharmaJet bij sommige polio-campagnes.

**Wat is een microarray-pleister?**
Een kleine huidpleister met korte puntjes. Micron en Vaxxas zijn de twee kandidaten die de WHO noemt voor mazelen en rodehond.

**Kan ik in Nederland al zonder naald?**
Rotavirus al als druppel. Verder prikt het RIVM in het RVP nog met naald en spuit. Huidpleisters zijn onderzoek.

**Waarom wil de WHO dit?**
Omdat eenvoudiger toediening meer mensen kan bereiken, vooral kinderen die nu nul prikken krijgen.

### Bronnen

- [WHO: innovatieve toediening van vaccins, inclusief microarray-pleisters](https://www.who.int/teams/immunization-vaccines-and-biologicals/product-and-delivery-research/delivery-technologies)
- [WHO: Technical Advisory Group over mazelen-rodehond-microarray-pleisters](https://www.who.int/news-room/articles-detail/2023-November-132)
- [WHO: Target Product Profile voor mazelen-rodehond-MAP](https://www.who.int/publications/i/item/9789240000209)
- [The Lancet: Micron mazelen-rodehond-pleister, fase 1/2 in Gambia](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(24)00532-4/fulltext)
- [WHO PQS: PharmaJet Tropis, naaldvrije injector](https://extranet.who.int/prequal/immunization-devices/e008-071)
- [BARDA: Patch Forward Prize, conceptwinnaars](https://www.patchforwardprize.com/)
- [Gavi: Vaccine Innovation Prioritisation Strategy (VIPS)](https://www.gavi.org/our-alliance/market-shaping/vaccine-innovation-prioritisation-strategy)
- [RIVM: Rijksvaccinatieprogramma](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "philanthropy-global-infectious-diseases",
    date: "2026-08-26",
    category: "Gezondheidsnieuws",
    image: "/images/blog/philanthropy-global-infectious-diseases.webp",
    author: "Vaxy",
    readTime: "8 min lezen",
    title:
      "Hoe filantropie denkt over infectieziekten: Gates, Gavi en partners",
    excerpt:
      "De Gates Foundation, Gavi, Wellcome en CEPI zien vaccins als een van de sterkste middelen tegen infectieziekten wereldwijd. De WHO zet de koers. Zo werkt die samenwerking.",
    faq: [
      {
        q: "Wat wil de Gates Foundation met infectieziekten?",
        a: "Kinderen, tieners en volwassenen in lagere-inkomenslanden bereiken met vaccins, en voorkomen dat de volgende generatie sterft aan infectieziekten. De stichting noemt vaccins een van de goedkoopste gezondheidsinstrumenten: 3,5 tot 5 miljoen levens per jaar.",
      },
      {
        q: "Wat is Gavi?",
        a: "Gavi, the Vaccine Alliance, is een publiek-privaat partnerschap. Overheden, WHO, UNICEF, de Wereldbank, de vaccinindustrie en de Gates Foundation werken erin samen. Gavi koopt en verdeelt vaccins voor lagere-inkomenslanden.",
      },
      {
        q: "Bepaalt Bill Gates het Nederlandse vaccinatiebeleid?",
        a: "Nee. In Nederland bepalen het RIVM, de Gezondheidsraad en het ministerie van VWS het beleid. De WHO geeft wereldwijde richtlijnen. Filantropische fondsen financieren onderzoek en inkoop, ze schrijven geen RVP-schema.",
      },
      {
        q: "Welke andere fondsen doen dit werk?",
        a: "Onder meer Wellcome Trust en CEPI (epidemievaccins), Rotary International (polio) en Gavi zelf. Ze werken meestal samen met WHO en overheden, niet in hun plaats.",
      },
      {
        q: "Is dit medisch advies?",
        a: "Nee. Dit is uitleg over wereldwijde strategie. Voor jouw prikken: huisarts, GGD of reizigersvaccinatiebureau.",
      },
    ],
    content: `## Hoe filantropie denkt over infectieziekten: Gates, Gavi en partners

Infectieziekten stoppen niet bij een grens. Filantropische fondsen redeneren zo: als een vaccin in Lagos of Lahore ontbreekt, kan dezelfde ziekte later in Europa opduiken. Vaccins zijn volgens hen het scherpste, goedkoopste instrument.

De Bill & Melinda Gates Foundation is daarin de grootste private speler. Zij werkt niet alleen. De Wereldgezondheidsorganisatie (WHO) zet de koers. Gavi koopt en verdeelt. Wellcome en CEPI betalen onderzoek naar nieuwe uitbraken.

Dit is uitleg over die strategie, geen medisch advies. Nederlandse prikken volgen het Rijksvaccinatieprogramma, niet een fonds in Seattle.

### Hoe denkt de Gates Foundation?

De stichting zegt het kort: vaccins redden 3,5 tot 5 miljoen levens per jaar. Sinds de WHO in 1974 het Expanded Programme on Immunization startte, zijn naar schatting 154 miljoen levens gered, vooral kinderen.

Hun doel voor lagere-inkomenslanden: kinderen, tieners en volwassenen bereiken met de prikken die ze nodig hebben. En een wereld zonder ziekten die je met een vaccin kunt voorkomen.

Drie lijnen komen steeds terug:

1. **Koop en verdeel wat al bestaat.** Gavi is hun grootste begunstigde. Van 2000 tot 2023 vaccineerde Gavi volgens de stichting meer dan 1,1 miljard kinderen en voorkwam het ruim 18,8 miljoen toekomstige sterfgevallen.
2. **Maak nieuwe vaccins betaalbaar.** Denk aan pneumokokken, rotavirus, HPV, cholera, meningitis A.
3. **Versterk de gewone zorg.** Een prikprogramma is volgens hen de ruggengraat van de eerstelijnszorg.

In juni 2025 zegde de stichting 1,6 miljard dollar toe aan Gavi voor vijf jaar. Sinds 2000 ging meer dan 30,6 miljard dollar naar vaccins, waarvan 7,7 miljard naar Gavi.

Ze sluiten aan bij de Immunization Agenda 2030 van de WHO: iedereen, overal, op elke leeftijd. En bij Gavi 6.0 (2026–2030): meer dan 500 miljoen extra kinderen en tieners vaccineren, 8 tot 9 miljoen extra levens redden.

### Waar zien ze het misgaan?

De vooruitgang stokt. In 2023 kregen 14,5 miljoen kinderen geen enkele prik, meer dan het jaar ervoor. Mazelenuitbraken namen toe. De stichting noemt dat een risico voor decennia winst.

Hun antwoord is niet één nieuw vaccin, maar bereik: HPV-introducties, mazelencampagnes, en extra inzet in gebieden met de laagste dekking. Inclusief conflictgebieden.

Polio blijft een speerpunt. Sinds 2007 steunt de stichting het Global Polio Eradication Initiative, samen met WHO, Rotary International, CDC, UNICEF en Gavi. Wilde polio komt nog voor in Pakistan en Afghanistan. Sinds 1988 daalde het aantal gevallen met meer dan 99%.

### Wellcome, CEPI en Rotary

Andere fondsen denken in dezelfde richting, met een andere taak.

**Wellcome Trust** financiert wetenschap en was in 2017 medeoprichter van CEPI.

**CEPI**, de Coalition for Epidemic Preparedness Innovations, bestaat om vaccins klaar te hebben vóór de volgende epidemie. Overheden, Gates en Wellcome richtten het op na ebola. Het doel: bij een onbekende ziekte X sneller een vaccin, in hun visie binnen 100 dagen. CEPI maakt geen prikschema. Dat blijft bij WHO en landen.

**Rotary International** zamelt al decennia geld in voor polio, als partner van het GPEI.

De VIPS-alliantie (Gavi, WHO, Gates Foundation, UNICEF, PATH) laat zien hoe die clubs samen innovatie kiezen, zoals huidpleisters. Filantropie betaalt vaak de eerste stappen. Overheden en WHO beslissen of het product in een programma komt.

### Wat betekent dit voor Nederland?

Niets in jouw RVP-boekje wordt door een fonds bepaald. Het RIVM, de Gezondheidsraad en VWS doen dat.

Wél raakt het je indirect. Wereldwijde inkoop houdt mazelen, polio en cholera kleiner. Reizigersvaccins, pandemievoorbereiding en nieuwe toedieningsvormen komen uit hetzelfde netwerk.

Schrijf per prik de datum en de naam op. Met Vaxy bewaar je dat overzicht, ook als je later naar een land gaat waar Gavi wél de inkoop doet.

Dit artikel is informatie, geen medisch advies.

### Korte antwoorden

**Wat wil de Gates Foundation?**
Vaccins bij mensen in lagere-inkomenslanden krijgen, en infectieziekten bij de volgende generatie voorkomen.

**Wat is Gavi?**
Een alliantie van overheden, WHO, UNICEF, Wereldbank, industrie en de Gates Foundation die vaccins inkoopt voor armere landen.

**Bepaalt filantropie het Nederlandse beleid?**
Nee. RIVM, Gezondheidsraad en VWS doen dat. De WHO geeft wereldwijde richtlijnen.

**Wie werkt hier nog meer aan?**
Onder meer Wellcome, CEPI, Rotary, UNICEF en nationale donoren.

### Bronnen

- [Gates Foundation: Immunization](https://www.gatesfoundation.org/our-work/programs/global-development/immunization)
- [Gates Foundation: toezegging van 1,6 miljard dollar aan Gavi, juni 2025](https://www.gatesfoundation.org/ideas/media-center/press-releases/2025/06/renewed-gavi-commitment-lifesaving-vaccine-access)
- [Gates Foundation: polio](https://www.gatesfoundation.org/our-work/programs/global-development/polio)
- [WHO: Immunization Agenda 2030](https://www.who.int/teams/immunization-vaccines-and-biologicals/strategies/ia2030)
- [WHO: vaccinatiegraad, factsheet](https://www.who.int/news-room/fact-sheets/detail/immunization-coverage)
- [Gavi 6.0, strategie 2026–2030](https://www.gavi.org/our-work/strategy/phase-6-2026-2030)
- [CEPI](https://cepi.net/)
- [RIVM: Rijksvaccinatieprogramma](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "climate-diseases-netherlands",
    date: "2026-08-26",
    category: "Gezondheidstips",
    image: "/images/blog/climate-diseases-netherlands.webp",
    author: "Vaxy",
    readTime: "8 min lezen",
    title: "Nieuwe infectieziekten in Nederland door klimaatverandering",
    excerpt:
      "Dengue is in Europees Nederland nog een importziekte. RIVM, Gezondheidsraad en ECDC zeggen wel: warmer weer, teken en muggen veranderen het risico. Wat komt eraan, en wat is er al?",
    faq: [
      {
        q: "Komt dengue al voor in Nederland?",
        a: "In Europees Nederland alleen bij reizigers. Het RIVM heeft nog geen lokale besmetting vastgesteld. In Caribisch Nederland komt dengue wél voor, met grotere uitbraken in 2023–2024. In Zuid-Europa zijn er al lokale gevallen, onder meer in Frankrijk, Italië en Spanje.",
      },
      {
        q: "Gaat de tijgermug zich hier vestigen?",
        a: "Nog niet. De NVWA bestrijdt introducties. Het RIVM zegt: méér tijgermuggen komen vooral door handel en reizen, niet door klimaat. De Gezondheidsraad verwacht vestiging op sommige plekken binnen 2 tot 5 jaar. Dán maken hogere zomertemperaturen lokale dengue mogelijk na introductie door een reiziger.",
      },
      {
        q: "Welke ziekten zijn hier al veranderd?",
        a: "Tekenencefalitis sinds 2016 in Nederlandse teken, met 8 meldingen van infectie in Nederland in 2024. Westnijlvirus in 2020 bij 8 mensen. Lyme via teken is al lang aanwezig. Warmer water verhoogt het risico op vibrio en legionella.",
      },
      {
        q: "Moet ik me in Nederland laten vaccineren tegen dengue of TBE?",
        a: "Het RIVM zegt: voor tekenencefalitis in Nederland geen vaccinatie nodig. Voor reizen naar landen waar TBE of dengue veel voorkomt: vraag het LCR, je huisarts of een vaccinatiebureau. Er bestaan vaccins tegen dengue en TBE.",
      },
      {
        q: "Is dit medisch advies?",
        a: "Nee. Dit is uitleg op basis van RIVM, Gezondheidsraad, ECDC en WHO. Bij klachten na een muggen- of tekenbeet: huisarts.",
      },
    ],
    content: `## Nieuwe infectieziekten in Nederland door klimaatverandering

Dengue klinkt tropisch. In Europees Nederland is het dat nog: een ziekte die reizigers meenemen. De Gezondheidsraad waarschuwde in mei 2026 toch: door klimaatverandering worden de omstandigheden hier gunstiger voor ziekten via muggen, teken en water.

Het RIVM zegt het voorzichtiger. Klimaat is één factor. Handel, reizen en hoe we wonen doen vaak meer. Een nieuwe mug in Nederland is niet automatisch de schuld van een warmere zomer.

Dit is uitleg, geen medisch advies.

### Dengue: nu import, later misschien lokaal

Dengue verspreidt zich via de gelekoortsmug en de Aziatische tijgermug. De WHO schat wereldwijd honderden miljoenen infecties per jaar. Koorts, spierpijn, soms ernstige bloedingen.

Volgens de LCI-richtlijn van het RIVM is dengue in Europees Nederland een importziekte. Er is tot nu toe geen lokale besmetting vastgesteld. Wel steeg het aantal positieve tests bij terugkerende reizigers: gemiddeld 136 per jaar in 2014–2019, 367 in 2023 en 544 in 2024.

In Caribisch Nederland komt dengue wél voor. Het RIVM noemt tientallen gevallen per jaar en grotere uitbraken in 2023–2024. De Gezondheidsraad geeft die eilanden voorrang, omdat de risico’s daar groter en diverser zijn.

In Zuid-Europa is lokale dengue al gebeurd. Het ECDC houdt gevallen bij in onder meer Frankrijk, Italië, Spanje en Kroatië. In 2026 meldde tot begin augustus alleen Frankrijk een lokale denguebesmetting in Europees vasteland.

Er bestaan denguevaccins. Die zijn in Nederland bedoeld voor reizigers, niet voor de hele bevolking. Vraag het LCR of een reizigersvaccinatiebureau.

### De tijgermug: bestrijding koopt tijd

De Aziatische tijgermug (*Aedes albopictus*) kan dengue, chikungunya en zika overdragen. In het Middellandse Zeegebied zit ze stevig. In Nederland nog niet, dankzij bestrijding door VWS en de NVWA.

Het RIVM is helder: méér vondsten in Nederland komen vooral door handel en reizen, niet door klimaatverandering. Stel dat de mug zich wél vestigt, dan speelt klimaat wél. Bij hogere zomertemperaturen vermenigvuldigen virussen in muggen sneller.

De Gezondheidsraad verwacht vestiging op bepaalde locaties binnen 2 tot 5 jaar. Daarna kunnen lokale dengue-uitbraken volgen als een viremische reiziger het virus meeneemt.

In augustus 2026 publiceerde de NVWA DNA-onderzoek: de mug komt via meerdere introducties binnen, onder meer via gebruikte banden en luchthavens. Op sommige plekken overleefde ze de winter. Bestrijding kan een populatie wél wegkrijgen.

### Wat is er al in Nederland?

Niet alles is toekomstmuziek.

**Tekenencefalitis (TBE).** Sinds 2016 weten we dat Nederlandse teken het virus kunnen dragen, onder meer op de Sallandse en Utrechtse Heuvelrug. Tussen 2016 en juni 2024 waren er 23 patiënten die de infectie in Nederland opliepen. In 2024 8 meldingen. Sinds 1 maart 2025 is TBE met neurologische klachten meldingsplichtig. Ongeveer 1 op 1500 teken in bekende gebieden draagt het virus. Bij Lyme is dat ongeveer 1 op 5. Het RIVM zegt: in Nederland geen TBE-vaccinatie nodig. Wél bij reizen naar landen waar TBE veel voorkomt.

**Westnijlkoorts.** In 2020 liepen 8 mensen in Nederland het virus op, vooral rond Utrecht, via de gewone huissteekmug. Het virus zit in vogels. De Gezondheidsraad noemt warmere zomers gunstig voor overdracht. Het risico voor de meeste mensen blijft klein.

**Lyme.** Al lang de meest voorkomende tekenziekte hier. Meer teken door milder weer verhoogt het contact, zegt ook de Gezondheidsraad.

**Water.** Nederlandse kustwateren zijn geschikter geworden voor *Vibrio*. Warmer oppervlaktewater helpt ook legionella en blauwalg.

Chikungunya en zika volgen hetzelfde muggenverhaal als dengue: nu vooral een reisrisico, later mogelijk lokaal als de tijgermug blijft.

### Wat kun je zelf doen?

- Op reis naar gebieden met dengue of TBE: vraag tijdig of vaccinatie zinvol is.
- In Nederland: teken checken na groen, muggen weren in de avond.
- Koorts na een tropische reis: huisarts, en zeg waar je was.

Schrijf vaccinaties en reisprikken op. Met Vaxy zie je of dengue of TBE al in je overzicht staat vóór je vertrekt.

Dit artikel is informatie, geen medisch advies.

### Korte antwoorden

**Komt dengue hier al voor?**
In Europees Nederland alleen bij reizigers. In Caribisch Nederland wél. Lokale overdracht in Zuid-Europa bestaat al.

**Gaat de tijgermug zich vestigen?**
Nog niet, door bestrijding. De Gezondheidsraad verwacht dat op sommige plekken binnen 2 tot 5 jaar.

**Welke ziekten zijn hier al?**
TBE sinds 2016, westnijl in 2020 bij mensen, Lyme al langer. Warmer water: vibrio en legionella.

**Moet ik me hier vaccineren tegen dengue of TBE?**
Voor wonen in Europees Nederland meestal niet. Voor bepaalde reizen soms wel. Vraag LCR, huisarts of GGD.

### Bronnen

- [RIVM: klimaatverandering en infectieziekten](https://www.rivm.nl/klimaat-en-gezondheid/infectieziekten)
- [RIVM: dengue](https://www.rivm.nl/dengue)
- [LCI-richtlijn dengue](https://lci.rivm.nl/richtlijnen/dengue)
- [RIVM: westnijlkoorts](https://www.rivm.nl/westnijlkoorts)
- [RIVM: tekenencefalitis](https://www.rivm.nl/tekenencefalitis)
- [Gezondheidsraad: klimaatverandering en gezondheid, 21 mei 2026](https://www.gezondheidsraad.nl/actueel/nieuws/2026/05/21/bescherm-de-bevolking-tegen-de-toenemende-gezondheidsrisicos-van-klimaatverandering)
- [ECDC: dengue-risico EU/EER](https://www.ecdc.europa.eu/en/dengue/surveillance-and-updates/risk-assessment)
- [WHO: dengue](https://www.who.int/news-room/fact-sheets/detail/dengue-and-severe-dengue)
- [NVWA: DNA-onderzoek Aziatische tijgermug, 20 augustus 2026](https://www.nvwa.nl/actueel/nieuws/2026/08/20/dna-onderzoek-biedt-nvwa-nieuw-inzicht-in-verspreiding-en-bestrijding-van-de-aziatische-tijgermug)
- [LCR: informatie over vaccinaties](https://lcr.nl/informatie-vaccinaties)`,
  },
  {
    slug: "vaccine-boosters",
    date: "2026-08-22",
    category: "Gezondheidstips",
    image: "/images/blog/vaccine-boosters.webp",
    author: "Vaxy",
    readTime: "8 min lezen",
    title:
      "Welke vaccins hebben een booster? En hoe lang ben je dan beschermd?",
    excerpt:
      "Niet elk vaccin heeft een herhaalprik. RIVM, LCI en LCR zeggen wanneer een booster nodig is, en hoe lang de bescherming daarna duurt.",
    faq: [
      {
        q: "Welke vaccins hebben een booster?",
        a: "Vooral DTP (elke 10 jaar), buiktyfus (elke 3 jaar), tekenencefalitis, Japanse encefalitis en cholera. Griep en corona voor risicogroepen zijn jaarlijkse prikken, geen klassieke booster.",
      },
      {
        q: "Hoe vaak moet ik DTP herhalen?",
        a: "Na een volledige serie elke 10 jaar. Het Rijksvaccinatieprogramma en het LCR zeggen dat je daarna 10 jaar beschermd bent tegen difterie en tetanus. Tegen polio ben je na het kinderschema waarschijnlijk levenslang beschermd tegen verlamming.",
      },
      {
        q: "Moet hepatitis A later opnieuw?",
        a: "Nee, niet als je twee prikken hebt gehad. Het LCR zegt: na de eerste prik 1 jaar bescherming, na de tweede 50 jaar. De LCI zegt dat een extra prik bij gezonde mensen niet nodig is.",
      },
      {
        q: "Is gele koorts levenslang geldig?",
        a: "Meestal wel. Eén prik beschermt in de meeste gevallen levenslang. Het vaccinatiebewijs is sinds 2016 levenslang geldig. Sommige groepen, zoals mensen met hiv, hebben wél een herhaalprik nodig.",
      },
      {
        q: "Is dit medisch advies?",
        a: "Nee. Dit is uitleg op basis van RIVM, LCI en LCR. Vraag bij twijfel je huisarts, GGD of een reizigersvaccinatiebureau.",
      },
    ],
    content: `## Welke vaccins hebben een booster? En hoe lang ben je dan beschermd?

Niet elk vaccin heeft een herhaalprik. Sommige series beschermen na afronding lang, of levenslang. Andere vaccins moet je wél opnieuw halen.

Dit overzicht is voor gezonde mensen in Nederland, op basis van het Rijksvaccinatieprogramma (RVP), de LCI-richtlijnen van het RIVM en het Landelijk Coördinatiecentrum Reizigersadvisering (LCR). Het is informatie, geen medisch advies.

### Wat is een booster?

Een booster is een extra prik ná een volledige basisserie, omdat de bescherming afneemt.

Dat is iets anders dan:

- de tweede of derde prik van een serie, zoals hepatitis A of hepatitis B
- een jaarlijkse prik, zoals de griepprik, omdat het virus verandert
- extra prikken na een beet of een wond, zoals bij rabiës of tetanus

### Deze vaccins hebben wél een booster

**DTP (difterie, tetanus, polio).** Elke 10 jaar na een volledige serie. Nooit eerder gevaccineerd? Dan eerst drie prikken. Het RVP zegt: na de DTP-prik op 14 jaar ben je ongeveer 10 jaar beschermd tegen difterie en tetanus, en waarschijnlijk levenslang tegen verlamming door polio. Het LCR adviseert dezelfde 10-jaarsherhaling voor reizigers.

**Kinkhoest.** Het acellulaire vaccin beschermt volgens de LCI ongeveer 4 tot 6 jaar. Kinderen krijgen een herhaalprik op 5 jaar. Volwassenen hebben geen vast interval. Ben je zwanger, dan raad het RIVM de kinkhoestprik aan vanaf 22 weken, bij elke zwangerschap opnieuw.

**Buiktyfus.** Eén prik beschermt 3 jaar, zegt het LCR. De bescherming is niet volledig. Herhaal elke 3 jaar als je nog risico loopt.

**Japanse encefalitis.** Twee prikken binnen een maand geven 2 jaar bescherming. Vanaf een jaar later kan een derde prik. Daarna 10 jaar, volgens het LCR.

**Tekenencefalitis.** Drie prikken (maand 0, 1 en 6). Daarna een booster na 3 jaar. Vervolgens elke 5 jaar als je jonger bent dan 60, en elke 3 jaar vanaf 60 jaar, volgens de LCI. Het LCR noemt voor reizigers van 50 jaar en ouder soms een extra dosis in de basisserie.

**Cholera.** Gewone reizigers krijgen dit in Nederland meestal niet. Volgens het LCR beschermt één vaccinatie 6 maanden bij kinderen van 2 tot 5 jaar, en 2 jaar bij oudere kinderen en volwassenen.

**Meningokokken ACWY.** Eén prik beschermt minimaal 10 jaar, zegt het LCR. Voor pelgrims naar Mekka is het bewijs 5 jaar geldig.

**Gele koorts.** Meestal geen booster. Eén prik beschermt in de meeste gevallen levenslang. Het bewijs is sinds 1 juli 2016 levenslang geldig. De LCI raadt wél een herhaalprik aan bij vaccinatie tijdens zwangerschap, hiv, of laboratoriumwerk met het virus.

**Rabiës.** Twee prikken vóór de reis geven levenslange basisbescherming, zegt de LCI. Geen standaardbooster voor reizigers. Na een beet, krab of lik zijn altijd extra prikken nodig.

**Griep.** Jaarlijks, voor mensen met extra risico. De LCI zegt: antistoffen blijven ongeveer 24 weken op peil. Het vaccin is na een half jaar uitgewerkt, en het virus verandert.

**COVID-19.** Voor risicogroepen een najaarsronde. Het RIVM houdt bij voorkeur 6 maanden aan na de vorige prik, minimaal 3 maanden.

**Pneumokokken.** Het oudere PPV23-vaccin wordt elke 5 jaar herhaald. Na het nieuwere conjugaatvaccin PCV20 is bij volwassenen vooralsnog geen herhaalprik nodig, zegt de LCI.

### Deze vaccins hebben géén booster na de serie

**Hepatitis A.** Twee prikken, minstens 6 maanden uit elkaar. Na de eerste prik 1 jaar bescherming. Na de tweede 50 jaar, volgens de publieke LCR-pagina. De LCI zegt: bij gezonde mensen is daarna geen extra prik nodig. Modellen wijzen op 50 jaar, mogelijk levenslang.

**Hepatitis B.** Drie prikken. Daarna in principe levenslang, als je lichaam goed heeft gereageerd. Een booster is dan niet nodig. Sommige beroepen doen wél een bloedtest.

**HPV.** Twee prikken met 6 maanden ertussen, in het RVP rond 10 jaar. De LCI ziet na 12 jaar nog goede bescherming. Een latere booster is vooralsnog niet nodig.

**BMR (bof, mazelen, rodehond).** Twee prikken. Daarna waarschijnlijk levenslang tegen mazelen en rodehond. Voor de bof is dat minder duidelijk. Na twee prikken is 99% beschermd, zegt het RVP.

**Gordelroos.** Twee prikken met Shingrix. Het RIVM zegt: de bescherming houdt minstens 6 tot 10 jaar aan. Er is nog geen standaard derde prik.

**Dengue en chikungunya.** Het LCR zegt dat nog onbekend is hoe lang de vaccinatie beschermt.

### Kinderen: boosters in het Rijksvaccinatieprogramma

Kinderen krijgen herhaalprikken al in het schema. Volgens rijksvaccinatieprogramma.nl onder meer:

- 12 maanden: DKTP-Hib-HepB en pneumokokken
- 14 maanden: BMR en MenACWY
- 3 jaar: tweede BMR (sommige geboortejaren later, via inhaal)
- 5 jaar: DKT
- 10 jaar: HPV, twee keer
- 14 jaar: DTP en MenACWY (DTP op 14 jaar vanaf geboortejaar 2016)

Daarna zit DTP niet meer in het RVP. Voor difterie en tetanus blijft elke 10 jaar een herhaalprik verstandig, vooral als je gaat reizen.

### Wat betekent dit voor jouw overzicht?

Schrijf per prik de datum, de naam van het vaccin en of de serie af is. Dan zie je of je een booster nodig hebt, of alleen nog de volgende dosis van dezelfde serie.

Met Vaxy bewaar je die gegevens op één plek. Zo mis je minder snel een DTP na 10 jaar, of de tweede hepatitis A-prik.

Twijfel je? Vraag het je huisarts, de GGD of een reizigersvaccinatiebureau. Mensen met een verminderde weerstand, zwangeren en sommige beroepen hebben vaak een ander schema.

### Korte antwoorden

**Welke vaccins hebben een booster?**
DTP elke 10 jaar, buiktyfus elke 3 jaar, plus tekenencefalitis, Japanse encefalitis en cholera. Griep en corona voor risicogroepen elk jaar.

**Hoe lang werkt DTP?**
10 jaar tegen difterie en tetanus. Polio na het kinderschema waarschijnlijk levenslang tegen verlamming.

**Moet hepatitis A later opnieuw?**
Niet na twee prikken. Na de tweede dosis 50 jaar volgens het LCR.

**Is gele koorts levenslang?**
Meestal wel. Soms is een extra prik nodig, bijvoorbeeld bij hiv.

### Bronnen

- [Rijksvaccinatieprogramma: vaccinatieschema](https://rijksvaccinatieprogramma.nl/vaccinaties/vaccinatieschema)
- [RVP: DTP-vaccinatie](https://rijksvaccinatieprogramma.nl/vaccinaties/dtp)
- [RVP: BMR-vaccinatie](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [LCR: informatie over vaccinaties](https://lcr.nl/informatie-vaccinaties)
- [LCI: tetanus](https://lci.rivm.nl/richtlijnen/tetanus)
- [LCI: hepatitis A-vaccinatie](https://lci.rivm.nl/factsheets/hepatitis-a-vaccinatie)
- [LCI: hepatitis B-vaccinatie](https://lci.rivm.nl/factsheets/hepatitis-b-vaccinatie)
- [LCI: gele koorts](https://lci.rivm.nl/richtlijnen/gele-koorts)
- [LCI: TBE-vaccinatie](https://lci.rivm.nl/factsheets/tbe-vaccinatie)
- [LCI: kinkhoestvaccinatie](https://lci.rivm.nl/factsheets/kinkhoestvaccinatie)
- [LCI: HPV-vaccinatie](https://lci.rivm.nl/factsheets/hpv-vaccinatie)
- [LCI: pneumokokkenvaccinatie](https://lci.rivm.nl/factsheets/pneumokokkenvaccinatie)
- [LCI: influenzavaccinatie](https://lci.rivm.nl/richtlijnen/influenzavaccinatie)
- [LCI: COVID-19-vaccinatie](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)
- [LCI: rabiës](https://lci.rivm.nl/richtlijnen/rabies)
- [RIVM: coronaprik](https://www.rivm.nl/corona/coronaprik)
- [RIVM: gordelroosvaccinatie](https://www.rivm.nl/gordelroos/vaccinatie)
- [RIVM: vaccinaties tijdens de zwangerschap](https://www.rivm.nl/zwangerschap-en-infectieziekten/vaccinaties-tijdens-zwangerschap)`,
  },
  {
    slug: "how-mrna-vaccines-work",
    date: "2026-08-19",
    category: "Gezondheidstips",
    image: "/images/blog/how-mrna-vaccines-work.webp",
    author: "Vaxy",
    readTime: "7 min lezen",
    title: "Wat zijn mRNA-vaccins en hoe werken ze?",
    excerpt:
      "Een mRNA-vaccin is een kort bericht voor je cellen, geen levend virus. WHO, CDC, EMA en RIVM leggen uit hoe het werkt en waarom het je DNA niet verandert.",
    faq: [
      {
        q: "Wat is een mRNA-vaccin?",
        a: "Een mRNA-vaccin is een prik met een kort bericht voor je cellen. Het bericht leert je lichaam een onschadelijk stukje van een virus te herkennen. Er zit geen levend virus in.",
      },
      {
        q: "Hoe werkt een mRNA-vaccin?",
        a: "Na de prik lezen je cellen het mRNA en maken een onschadelijk eiwitstukje. Je afweersysteem leert dat stukje herkennen. Daarna ruimt je lichaam het mRNA snel op.",
      },
      {
        q: "Verandert een mRNA-vaccin je DNA?",
        a: "Nee. CDC en EMA zeggen dat het mRNA niet in de celkern komt, waar je DNA ligt. Het kan je erfelijke code dus niet veranderen.",
      },
      {
        q: "Kan een mRNA-vaccin je ziek maken van corona?",
        a: "Nee. De CDC zegt dat er geen levend coronavirus in zit. Je kunt er geen COVID-19 van krijgen.",
      },
      {
        q: "Hoe lang blijft het mRNA in je lichaam?",
        a: "Kort. Het EMA zegt dat je lichaam het mRNA snel afbreekt, net als je eigen mRNA. In studies daalde het binnen dagen.",
      },
    ],
    content: `## Wat zijn mRNA-vaccins en hoe werken ze?

Een mRNA-vaccin is een prik die je lichaam leert om een ziekte te herkennen. Het bevat geen levend virus. Het bevat een kort bericht: mRNA.

mRNA betekent messenger RNA, of boodschapper-RNA. Je lichaam maakt zelf de hele dag mRNA. Dat bericht zegt tegen een cel welk eiwit hij moet maken.

Bij een mRNA-vaccin is het bericht nagemaakt in een lab. Het gaat over één onschadelijk stukje van het virus. Bij corona is dat vaak het spike-eiwit. Daarna ruimt je lichaam het bericht snel op.

De Wereldgezondheidsorganisatie (WHO) noemt dit een vaccin met een bouwtekening, in plaats van het virus zelf.

### Hoe werkt een mRNA-vaccin, stap voor stap?

Volgens de Amerikaanse CDC werkt het zo:

1. Je krijgt de prik in de bovenarm.
2. Het mRNA zit in kleine vetbolletjes. Die helpen het bericht de cel in.
3. In de cel leest het lichaam het bericht en maakt een onschadelijk stukje eiwit.
4. Daarna breekt de cel het mRNA af. Het verdwijnt als afval.
5. De cel toont het eiwitstukje aan je afweersysteem.
6. Je afweersysteem maakt antistoffen en geheugencellen.
7. Als je later het echte virus tegenkomt, herkent je lichaam het sneller.

De CDC zegt: het duurt meestal een paar weken voordat die bescherming klaar is.

### Verandert een mRNA-vaccin je DNA?

Nee. Dat zeggen de CDC en het Europees Geneesmiddelenbureau (EMA) allebei.

Het mRNA komt niet in de celkern. Daar ligt je DNA. Het bericht blijft buiten de kern, waar je lichaam altijd eiwitten maakt.

mRNA is ook chemisch anders dan DNA. Het kan niet zomaar in je DNA gaan zitten.

Het EMA zegt ook: een mRNA-vaccin is geen gentherapie. Het doel is niet om je genen te herstellen of te veranderen.

Het vaccin kan je niet ziek maken van corona. Er zit geen levend coronavirus in, zegt de CDC.

### Hoe lang blijft het mRNA in je lichaam?

Kort. Het EMA zegt: het lichaam breekt het mRNA snel af, net als je eigen mRNA.

In dierstudies van Comirnaty (Pfizer/BioNTech) daalde het mRNA in 6 tot 9 dagen. Bij Spikevax (Moderna) vonden onderzoekers na 1 tot 3 dagen vooral nog mRNA op de prikplek, in lymfeklieren en in de milt.

Bij de goedkeuring verwachtten toezichthouders geen ophoping in organen.

### Waarom zitten er vetbolletjes in het vaccin?

mRNA is kwetsbaar. Zonder bescherming valt het snel uit elkaar.

Het EMA legt uit: kleine vetdeeltjes, lipid nanoparticles, helpen het mRNA de cel in. De celwand bestaat ook uit vet. De bolletjes smelten daarmee samen.

Dit soort vetdeeltjes in medicijnen is niet nieuw. Het eerste medicijn met deze bolletjes werd in 2018 in de EU goedgekeurd.

### Zijn mRNA-vaccins nieuw?

De coronaprikken waren nieuw voor veel mensen. De techniek niet.

Het EMA zegt: onderzoekers werkten al zo'n twintig jaar met mRNA, voor infectieziekten en voor kanker. De CDC noemt eerdere studies voor griep, zika, rabiës en CMV.

mRNA is relatief klein en eenvoudig te maken. Daarom konden fabrikanten sneller opschalen dan bij klassieke vaccins. De veiligheidstests zijn niet overgeslagen, zegt de CDC.

### Welke mRNA-vaccins bestaan er?

De bekendste in Europa zijn:

- Comirnaty van Pfizer/BioNTech
- Spikevax van Moderna

Die zijn goedgekeurd door het EMA. Het RIVM gebruikt coronavaccins in het najaarsprogramma, vooral voor mensen met meer risico. In 2026 kunnen onder meer mensen van 70 jaar en ouder, en mensen met extra medisch risico, een coronaprik halen.

Er zijn ook andere soorten coronavaccins, zoals eiwitvaccins. Die bevatten al een stukje eiwit. Het RIVM noemt dat een optie als je om medische redenen geen mRNA-vaccin mag of wilt.

De richtlijn van het RIVM zegt het kort: Comirnaty is een mRNA-vaccin. Het bevat geen levend virus. Het mRNA codeert voor het spike-eiwit.

mRNA wordt ook onderzocht voor andere ziekten, en voor sommige kankers. Dat is nog onderzoek. Een COVID-19-mRNA-vaccin is niet hetzelfde als een persoonlijk kankervaccin.

### Wat zijn de bijwerkingen?

De WHO zegt: milde tot matige klachten komen voor. Dat is een teken dat je lichaam bescherming opbouwt.

Vaak:

- pijn of roodheid op de prikplek
- moeheid
- hoofdpijn
- spierpijn
- koude rillingen
- koorts

De meeste klachten gaan binnen een paar dagen over. Niet iedereen krijgt bijwerkingen.

Ernstige bijwerkingen zijn zeldzaam. Het EMA zegt: de veiligheid van de goedgekeurde COVID-19-vaccins is geruststellend, na honderden miljoenen prikken.

Een bekende zeldzame bijwerking van mRNA-vaccins is ontsteking van de hartspier of het hartzakje. Artsen noemen dat myocarditis of pericarditis. Het RIVM noemt dit zeldzaam. Het komt vooral voor bij jonge mannen, kort na de prik. De meeste mensen herstellen. Ga naar een arts bij pijn op de borst, kortademigheid of een snelle hartslag na de prik.

### Beschermen mRNA-vaccins nog?

Ja, vooral tegen ernstige ziekte en overlijden. Geen vaccin werkt 100%.

De WHO zegt: de bescherming is het hoogst in de eerste maanden. Na ongeveer zes maanden neemt die af. Daarom krijgen mensen met meer risico soms een nieuwe prik.

Je kunt na vaccinatie nog corona krijgen. De kans op ernstig ziek worden blijft dan meestal lager.

### Wat betekent dit voor jouw vaccinatieoverzicht?

Noteer welke prik je kreeg, de datum, en de naam van het vaccin, zoals Comirnaty of Spikevax. Dat helpt bij een volgende prik, bij reizen, of als een arts ernaar vraagt.

Met Vaxy bewaar je die gegevens op één plek, voor jezelf en je gezin.

Dit artikel is informatie, geen medisch advies. Twijfel je? Vraag het je huisarts of de GGD.

### Korte antwoorden

**Wat is een mRNA-vaccin?**
Een prik met een kort bericht voor je cellen. Geen levend virus.

**Hoe werkt het?**
Je cellen maken een onschadelijk eiwitstukje. Je afweer leert dat herkennen. Daarna verdwijnt het mRNA.

**Verandert het je DNA?**
Nee. CDC en EMA zeggen dat het mRNA niet bij je DNA in de celkern komt.

**Kun je er corona van krijgen?**
Nee. Er zit geen levend coronavirus in.

**Hoe lang blijft het mRNA in je lichaam?**
Kort. Het EMA zegt dat je lichaam het snel afbreekt.

### Bronnen

- [WHO: hoe vaccins werken](https://www.who.int/news-room/feature-stories/detail/how-do-vaccines-work)
- [WHO: vragen en antwoorden over COVID-19-vaccins, 15 mei 2026](https://www.who.int/news-room/questions-and-answers/item/coronavirus-disease-(covid-19)-vaccines)
- [CDC: COVID-19 Vaccine Basics, hoe mRNA-vaccins werken](https://www.cdc.gov/covid/vaccines/how-they-work.html)
- [EMA: kernfeiten over COVID-19-vaccins, inclusief mRNA](https://www.ema.europa.eu/en/human-regulatory-overview/public-health-threats/coronavirus-disease-covid-19/covid-19-medicines/covid-19-vaccines-key-facts)
- [EMA: Comirnaty (Pfizer/BioNTech)](https://www.ema.europa.eu/en/medicines/human/EPAR/comirnaty)
- [EMA: Spikevax (Moderna)](https://www.ema.europa.eu/en/medicines/human/EPAR/spikevax)
- [RIVM: coronaprik](https://www.rivm.nl/covid-19-vaccinatie)
- [RIVM LCI-richtlijn COVID-19-vaccinatie](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)`,
  },
  {
    slug: "us-green-card-vaccination-records",
    date: "2026-08-19",
    category: "Reisgezondheid",
    image: "/images/blog/us-green-card-vaccination-records.webp",
    author: "Vaxy",
    readTime: "7 min lezen",
    title:
      "Hoe Vaxy helpt bij de vaccinatie-eisen voor een Amerikaanse green card",
    excerpt:
      "Voor een Amerikaanse green card moet je vaccinaties kunnen aantonen. USCIS vraagt je om schriftelijk bewijs mee te nemen naar de keuring. Zo helpt een digitaal overzicht in Vaxy, met bronnen.",
    faq: [
      {
        q: "Is Vaxy een green card?",
        a: "Nee. Een green card is een verblijfsvergunning. Vaxy is een digitaal vaccinatieoverzicht. USCIS vraagt je om schriftelijk bewijs van prikken mee te nemen naar de immigratiekeuring.",
      },
      {
        q: "Hebben toeristen dit nodig om de VS in te komen?",
        a: "Nee. Deze vaccinatiekeuring geldt voor mensen die een immigrantenvisum of aanpassing van status naar permanent resident aanvragen. Voor een gewone vakantie is Formulier I-693 niet nodig.",
      },
      {
        q: "Kan Vaxy Formulier I-693 vervangen?",
        a: "Nee. Alleen een aangewezen civil surgeon in de VS, of een panel physician in het buitenland, mag de vaccinatiebeoordeling op Formulier I-693 afronden.",
      },
      {
        q: "Is een COVID-19-vaccin nog verplicht voor een green card?",
        a: "Nee. Sinds 20 januari 2025 hoef je geen COVID-19-vaccinatie meer te laten zien voor aanpassing van status.",
      },
    ],
    content: `## Hoe Vaxy helpt bij de vaccinatie-eisen voor een Amerikaanse green card

Een groene kaart van de VS is geen vaccinatieboekje. Het is een verblijfsvergunning: lawful permanent residence.

Wie die status aanvraagt, moet wel vaccinaties kunnen aantonen. De Amerikaanse immigratiewet zegt dat je zonder dat bewijs niet toelaatbaar bent. USCIS zegt duidelijk: neem elk schriftelijk vaccinatiebewijs mee naar de civil surgeon.

Dat is waar Vaxy helpt. Vaxy is geen green card en geen officieel USCIS-formulier. Het is je vaccinatieoverzicht, altijd op je telefoon, klaar voor de keuring.

### Voor wie geldt dit

Dit geldt als jij:

- in het buitenland een immigrantenvisum aanvraagt, of
- in de Verenigde Staten je status wilt aanpassen naar permanent resident, vaak via Formulier I-485

Voor een gewone vakantie of kort bezoek is deze keuring niet nodig. De regels hieronder gaan over green-cardaanvragers.

### Waarom bewijs van vaccinaties telt

Volgens artikel 212(a)(1)(A)(ii) van de Immigration and Nationality Act (INA) kan iemand die geen bewijs van de vereiste vaccinaties kan laten zien, worden geweigerd voor toelating of aanpassing van status.

In de VS doet een aangewezen civil surgeon de keuring. In het buitenland doet een panel physician dat, aangewezen door het Amerikaanse ministerie van Buitenlandse Zaken. De resultaten komen op Formulier I-693, Report of Medical Examination and Vaccination Record.

USCIS zegt: je hoeft prikken niet opnieuw te halen als je al bewijs hebt. Neem je administratie mee.

### Welke vaccins zijn verplicht

De immigratiewet noemt onder meer:

- Bof
- Mazelen
- Rodehond
- Polio
- Tetanus en difterie
- Kinkhoest
- Haemophilus influenzae type B (Hib)
- Hepatitis B
- Andere vaccins die ACIP aanbeveelt en die CDC voor immigratie verplicht stelt

CDC vraagt daarnaast, als ze bij jouw leeftijd horen, ook om bescherming tegen:

- Waterpokken (varicella)
- Hepatitis A
- Meningokokken
- Pneumokokken
- Rotavirus, bij jonge baby's
- Seizoensgriep, in het griepseizoen

Je hoeft niet elk vaccin op de lijst. Alleen wat bij jouw leeftijd past. De civil surgeon volgt de Technical Instructions van CDC, inclusief een tabel per leeftijd.

**COVID-19:** Sinds 20 januari 2025 is een COVID-19-vaccin niet meer verplicht voor aanpassing van status.

**Polio:** Sinds 1 mei 2024 moet iedereen van 2 maanden en ouder die niet up-to-date is één dosis IPV krijgen. Dat geldt ook voor volwassenen zonder bewijs van de kinderserie.

**Griep:** Voor de immigratiekeuring is het griepvaccin verplicht van 1 september tot 31 maart, als het vaccin beschikbaar is.

### Wat je meeneemt naar de keuring

USCIS zegt dat je elk schriftelijk vaccinatiebewijs moet meenemen. Heb je labuitslagen die immuniteit aantonen, neem die ook mee.

Mist er iets, dan kan de civil surgeon het vaccin geven. Je mag ontbrekende prikken ook bij je eigen arts halen, en daarna terugkomen met bewijs. Alleen een civil surgeon mag het vaccinatiegedeelte van Formulier I-693 invullen.

Je hoeft meestal maar één dosis van elk verplicht vaccin op het moment van de keuring. Je hoeft een lange serie niet af te maken voordat het formulier mag worden ondertekend.

### Hoe Vaxy helpt

Papieren boekjes raken kwijt. Prikken staan in verschillende landen, talen en dossiers.

Met Vaxy kun je:

- Elke vaccinatie op één plek bewaren, met data
- Gezinsleden toevoegen die ook een green card aanvragen
- Je overzicht op je telefoon openen tijdens de afspraak
- Vooraf zien wat je al hebt, zodat je minder vaak opnieuw geprikt wordt

Laat de civil surgeon je Vaxy-overzicht zien, samen met papieren bewijs dat je nog hebt. De civil surgeon bepaalt wat telt.

### Wat Vaxy niet is

Vaxy is geen green card, geen Formulier I-693, en geen vervanging van de immigratiekeuring. USCIS accepteert geen onvolledige vaccinatietabel. Alleen een aangewezen civil surgeon of panel physician mag het formulier certificeren.

Dit artikel is informatie, geen juridisch of medisch advies. Regels kunnen veranderen. Check USCIS en CDC voor je afspraak.

### Korte antwoorden

**Is Vaxy een green card?**
Nee. Het is een digitaal vaccinatieoverzicht dat je meeneemt naar de keuring.

**Geldt dit voor toeristen?**
Nee. Dit geldt voor mensen die een green card aanvragen.

**Kan Vaxy Formulier I-693 vervangen?**
Nee. Alleen een civil surgeon of panel physician mag dat formulier afronden.

**Is COVID-19 nog verplicht?**
Nee, niet sinds 20 januari 2025.

### Bronnen

- [USCIS, Vaccination Requirements](https://www.uscis.gov/tools/designated-civil-surgeons/vaccination-requirements)
- [CDC, Vaccination Technical Instructions for Civil Surgeons](https://www.cdc.gov/immigrant-refugee-health/hcp/civil-surgeons/vaccination.html)
- [USCIS Policy Manual, Volume 8, Part B, Chapter 9](https://www.uscis.gov/policy-manual/volume-8-part-b-chapter-9)
- [Formulier I-693, Report of Medical Examination and Vaccination Record](https://www.uscis.gov/i-693)
- [42 C.F.R. Section 34.3](https://www.ecfr.gov/current/title-42/chapter-I/subchapter-C/part-34/section-34.3)`,
  },
  {
    slug: "vaccines-do-not-cause-autism",
    date: "2026-08-19",
    category: "Gezondheidsnieuws",
    image: "/images/blog/vaccines-do-not-cause-autism.webp",
    author: "Vaxy",
    readTime: "6 min lezen",
    title: "Vaccins veroorzaken geen autisme: wat de wetenschap zegt",
    excerpt:
      "Vaccins veroorzaken geen autisme. WHO, RIVM en grote studies vinden geen bewijs. Zo ontstond de mythe, en wat ouders nu moeten weten.",
    faq: [
      {
        q: "Veroorzaken vaccins autisme?",
        a: "Nee. De WHO, het RIVM en grote studies in veel landen vinden geen verband. Vaccins veroorzaken geen autisme.",
      },
      {
        q: "Veroorzaakt de BMR-prik autisme?",
        a: "Nee. De BMR-prik beschermt tegen bof, mazelen en rodehond. Grote studies, onder meer in Denemarken met meer dan 650.000 kinderen, vinden geen verband met autisme.",
      },
      {
        q: "Waar komt de mythe vandaan dat vaccins autisme veroorzaken?",
        a: "Uit een artikel uit 1998 van Andrew Wakefield. Er deden maar 12 kinderen mee. Het artikel is later ingetrokken. De data klopten niet, en Wakefield mocht geen arts meer zijn.",
      },
      {
        q: "Is het veiliger om de BMR-prik te splitsen in drie aparte prikken?",
        a: "Nee. Er is geen bewijs dat splitsen veiliger is. Je kind heeft dan meer afspraken nodig en blijft langer onbeschermd tegen mazelen.",
      },
      {
        q: "Wat zeggen de WHO en het RIVM over vaccins en autisme?",
        a: "Beide zeggen hetzelfde: vaccins veroorzaken geen autisme. De WHO bevestigde dit opnieuw in december 2025. Het RIVM zegt het op de pagina’s over de BMR-prik en over twijfels bij vaccinatie.",
      },
    ],
    content: `## Vaccins veroorzaken geen autisme: wat de wetenschap zegt

Vaccins veroorzaken geen autisme. Dat is de duidelijke conclusie van de Wereldgezondheidsorganisatie (WHO), het Rijksvaccinatieprogramma van het RIVM, en grote studies in veel landen.

Vaxy is het daar mee eens. Ouders verdienen heldere informatie, geen angst.

### Wat is er in het nieuws?

Op 11 augustus 2026 ondertekende de Amerikaanse president Donald Trump een besluit over kindervaccins. Hij herhaalde de oude claim dat vaccins autisme veroorzaken. Hij wil ook dat de BMR-prik tegen bof, mazelen en rodehond wordt gesplitst in aparte prikken.

Artsen en gezondheidsorganisaties noemen dit besluit gevaarlijk. Het is geen wetenschap. In de Verenigde Staten bepalen staten zelf welke prikken scholen vragen, niet de president. Het besluit is dus advies, geen wet. Toch kan het ouders onzeker maken.

### Waar komt deze mythe vandaan?

In 1998 publiceerde de Britse arts Andrew Wakefield een artikel. Hij schreef dat de BMR-prik autisme zou veroorzaken. Er deden maar 12 kinderen mee. Later bleek dat de data niet klopten. Wakefield had ook geld gekregen van advocaten die vaccins wilden aanklagen.

Het medische tijdschrift The Lancet heeft het artikel ingetrokken. Dat betekent: het artikel mag niet meer als bewijs worden gebruikt. Wakefield mocht daarna geen arts meer zijn.

Sinds 1998 zijn er heel veel grote, eerlijke studies gedaan. Die vinden geen verband tussen vaccins en autisme.

### Wat zeggen grote studies?

Een bekende studie uit Denemarken uit 2019 volgde 657.461 kinderen. Onderzoekers van het Statens Serum Institut keken of de BMR-prik autisme veroorzaakte. Het antwoord was nee. Ook niet bij kinderen met een hoger risico, bijvoorbeeld omdat een broer of zus autisme heeft.

In 2025 kwam er een nieuwe Deense studie over aluminium in vaccins. Aluminiumzouten zitten in kleine hoeveelheden in sommige vaccins. Ze helpen het lichaam om beter te reageren op de prik. Onderzoekers volgden meer dan 1 miljoen kinderen. Zij vonden geen verband met autisme, en ook niet met 49 andere gezondheidsproblemen.

Het RIVM verzamelt dit soort onderzoek op een eigen pagina. De conclusie is steeds hetzelfde: vaccineren veroorzaakt geen autisme.

### Wat zegt de WHO?

In december 2025 keek een WHO-expertgroep opnieuw naar de wetenschap. Deze groep heet GACVS. Zij bekeken 31 studies uit 2010 tot 2025, uit veel landen.

Hun conclusie: vaccins veroorzaken geen autisme. Ook niet vaccins met thiomersal, een conserveermiddel, of met een klein beetje aluminium.

De WHO zei dit eerder al in 2002, 2004 en 2012. De nieuwe check bevestigt het opnieuw.

De WHO zegt ook: kindervaccins hebben in 50 jaar minstens 154 miljoen levens gered.

### Wat zegt het Rijksvaccinatieprogramma?

Het Nederlandse Rijksvaccinatieprogramma beschermt kinderen tegen ernstige ziekten, zoals mazelen, bof, rodehond, kinkhoest en polio.

Op de RIVM-pagina over de BMR-prik staat het duidelijk: je krijgt geen autisme van de BMR-vaccinatie. Ook geen suikerziekte of een darmziekte.

Kinderen in Nederland krijgen de BMR-prik twee keer: rond 14 maanden en rond 3 jaar. Na de eerste prik is ongeveer 95% van de kinderen beschermd. Na de tweede prik is dat ongeveer 99%.

Het RIVM zegt ook: twijfel is begrijpelijk. Stel je vragen aan het consultatiebureau of de huisarts. Zoek informatie bij betrouwbare bronnen, niet alleen op sociale media.

### Is het veiliger om de BMR-prik te splitsen?

Nee. De WHO, het RIVM en de Amerikaanse CDC zeggen dat er geen bewijs is dat aparte prikken veiliger zijn.

Als je de prikken splitst, heeft je kind meer afspraken nodig. Tussen die afspraken is je kind langer onbeschermd. Mazelen is heel besmettelijk. Een kind kan ziek worden voordat alle prikken klaar zijn.

De BMR-prik is één kleine dosis. Dat is een paar druppels. Het is geen grote hoeveelheid vloeistof in het lichaam.

### Waarom krijgen kinderen nu meer prikken dan vroeger?

Kinderen krijgen nu bescherming tegen meer ziekten. Dat is vooruitgang, geen gevaar. Combinatieprikken betekenen juist minder prikken in de arm, niet meer.

Het schema volgt de leeftijd waarop het afweersysteem het beste reageert, en waarop kinderen het meest risico lopen.

Het overslaan of uitstellen van prikken verlaagt die bescherming. Mazelen komt weer vaker voor als minder kinderen gevaccineerd zijn.

### Wat kun je als ouder doen?

1. Volg het Rijksvaccinatieprogramma, of het schema in jouw land.
2. Stel vragen aan een arts of het consultatiebureau als je twijfelt.
3. Gebruik bronnen zoals de WHO en het RIVM, niet anonieme berichten.
4. Houd de prikken van je kind bij, zodat je niets mist.

Met Vaxy zet je alle vaccinaties van je gezin in één overzicht. Je ziet wat er is gezet, en je krijgt een herinnering voor de volgende prik.

Dit artikel is informatie, geen medisch advies. Heb je zorgen over de gezondheid van je kind? Praat met een arts.

### Korte antwoorden

**Veroorzaken vaccins autisme?**
Nee. WHO, RIVM en grote studies vinden geen verband.

**Veroorzaakt de BMR-prik autisme?**
Nee. Grote studies onder honderdduizenden kinderen vinden geen verband.

**Waar komt de mythe vandaan?**
Uit een artikel uit 1998 dat later is ingetrokken. De data klopten niet.

**Is splitsen van de BMR-prik veiliger?**
Nee. Er is geen bewijs voor. Je kind blijft dan langer onbeschermd.

**Wat zeggen WHO en RIVM?**
Vaccins veroorzaken geen autisme. De WHO bevestigde dit opnieuw in 2025.

### Bronnen

- [WHO: vaccins veroorzaken geen autisme, 11 december 2025](https://www.who.int/news/item/11-12-2025-who-expert-group-s-new-analysis-reaffirms-there-is-no-link-between-vaccines-and-autism)
- [WHO GACVS-verklaring over vaccins en autisme](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [RIVM: onderzoek naar verband tussen vaccineren en autisme](https://rijksvaccinatieprogramma.nl/over-vaccineren/onderzoek-vaccins/onderzoek-verband-vaccineren-autisme)
- [RIVM: vaccineren veroorzaakt geen autisme](https://rijksvaccinatieprogramma.nl/over-vaccineren/twijfels)
- [RIVM: BMR-vaccinatie](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [Hviid et al., BMR-vaccinatie en autisme, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [Statens Serum Institut: geen verband tussen aluminium in vaccins en autisme, 2025](https://en.ssi.dk/news/news/2025/large-danish-study-no-link-between-vaccines-and-autism-or-49-other-health-conditions)
- [The Lancet trekt het artikel van Wakefield in, 2010](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(10)60175-4/fulltext)
- [CDC: Vaccines Do Not Cause Autism](https://www.cdc.gov/vaccine-safety/about/autism.html)
- [BBC: Trump tekent besluit over kindervaccins, 11 augustus 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)`,
  },
  {
    slug: "childhood-vaccines-trump-order",
    date: "2026-08-19",
    category: "Gezondheidsnieuws",
    image: "/images/blog/childhood-vaccines-trump-order.webp",
    author: "Vaxy",
    readTime: "8 min lezen",
    title: "Waarom kindervaccins belangrijk blijven na Trumps besluit",
    excerpt:
      "Trump wil minder kindervaccins en de BMR-prik splitsen. Dit is waarom die prikken bestaan, wat de wetenschap zegt, en wat er kan gebeuren als minder kinderen beschermd zijn.",
    faq: [
      {
        q: "Verandert dit Amerikaanse besluit het vaccinatieschema in Europa?",
        a: "Nee. Het is advies in de VS, geen wet. In Nederland geldt het Rijksvaccinatieprogramma. Staten in de VS bepalen zelf welke prikken scholen vragen.",
      },
      {
        q: "Waarom krijgen kinderen zoveel vaccins?",
        a: "Omdat er nu bescherming is tegen meer ernstige ziekten. Het schema volgt de leeftijd waarop het afweersysteem het beste reageert, en waarop kinderen het meest risico lopen.",
      },
      {
        q: "Is het veiliger om de BMR-prik te splitsen?",
        a: "Nee. Er is geen bewijs dat splitsen helpt. Je kind heeft dan meer afspraken nodig en blijft langer onbeschermd tegen mazelen, bof en rodehond.",
      },
      {
        q: "Wat kan er gebeuren als minder kinderen gevaccineerd zijn?",
        a: "Ziekten zoals mazelen, kinkhoest en polio kunnen terugkomen. Mazelen is heel besmettelijk. Uitbraken groeien al in landen waar de vaccinatiegraad daalt.",
      },
    ],
    content: `## Waarom kindervaccins belangrijk blijven na Trumps besluit

Op 11 augustus 2026 ondertekende de Amerikaanse president Donald Trump een besluit over kindervaccins. Hij wil minder prikken voor alle kinderen, en hij wil de BMR-prik tegen bof, mazelen en rodehond splitsen in aparte shots.

Artsen noemen dit gevaarlijk. Het besluit is advies, geen wet. Toch kan het ouders onzeker maken, en vaccinatiecijfers verder laten dalen. Dit artikel legt uit waarom kindervaccins bestaan, en wat de gevolgen kunnen zijn.

### Wat houdt het besluit in?

Volgens de [BBC](https://www.bbc.com/news/articles/ce3q5vl581wo) wil Trump het aantal ziekten waarvoor alle Amerikaanse kinderen een prik krijgen, verlagen van 18 naar 11. De 11 ziekten op zijn lijst zijn: mazelen, bof, rodehond, difterie, tetanus, kinkhoest, polio, Hib, pneumokokken, HPV en waterpokken.

Andere prikken, zoals hepatitis B en meningokokken, zouden alleen voor kinderen met extra risico blijven. Griep en COVID-19 zouden een keuze worden tussen ouder en arts.

Trump wil ook dat vaccins niet meer samen in één bezoek worden gegeven, en dat de gecombineerde BMR-prik wordt gesplitst.

Schoolprikken blijven een zaak van de staten. Ouders in de VS kunnen dus niet meteen om drie losse BMR-prikken vragen. Die losse vaccins zijn in de VS nu ook niet als apart product goedgekeurd.

### Waarom kindervaccins bestaan

Kindervaccins beschermen tegen ziekten die vroeger veel kinderen ernstig ziek maakten, of lieten overlijden. Mazelen kan longontsteking, hersenontsteking en doofheid geven. Kinkhoest is vooral gevaarlijk voor baby's. Polio kan verlamming geven. Hib kan hersenvliesontsteking geven. Hepatitis B kan later leverkanker veroorzaken.

De WHO zegt dat vaccins in 50 jaar meer dan 150 miljoen levens hebben gered. Alleen al tegen mazelen voorkwamen vaccinaties tussen 2000 en 2024 naar schatting 59 miljoen sterfgevallen.

Kinderen krijgen prikken op een vaste leeftijd, niet willekeurig. Artsen kiezen het moment waarop het afweersysteem het beste reageert, en waarop het kind het meest kwetsbaar is. De American Academy of Pediatrics (AAP) zegt: er is geen medische reden om aanbevolen prikken uit te stellen of over te slaan.

Kinderen krijgen nu bescherming tegen meer ziekten dan vroeger. Dat is vooruitgang. Combinatieprikken betekenen juist minder naalden, niet meer.

### Groepsbescherming is geen extraatje

Sommige kinderen kunnen niet gevaccineerd worden. Bijvoorbeeld baby's die te jong zijn, of kinderen met een zwakke afweer. Zij hangen af van de mensen om hen heen.

Voor mazelen is minstens 95% vaccinatie met twee doses nodig om een uitbraak te stoppen. Daalt dat cijfer, dan vindt het virus snel een weg. Mazelen is vaak de eerste ziekte die terugkomt als de vaccinatiegraad zakt.

In 2023 waren er wereldwijd naar schatting 10,3 miljoen mazelengevallen. WHO, UNICEF en Gavi waarschuwen dat uitbraken van mazelen, meningitis en gele koorts toenemen. In de VS liggen de mazelencijfers volgens recente berichtgeving op het hoogste punt in 35 jaar.

### Wat kan dit besluit veroorzaken?

**Meer gemiste prikken.** Extra afspraken klinken voorzichtig. In de praktijk komen gezinnen minder vaak terug. Een gemiste dosis betekent langer geen bescherming.

**Langer onbeschermd tegen mazelen.** Tussen losse prikken zit tijd. In die tijd kan een kind mazelen, bof of rodehond oplopen. De CDC zegt dat er geen wetenschappelijk bewijs is dat splitsen van de BMR-prik iets oplevert. De gecombineerde prik bestaat sinds 1971 en werkt even goed als drie losse prikken.

**Ziekten die van de "voor iedereen"-lijst vallen.** Hepatitis B bij de geboorte voorkomt een levenslange infectie. Rotavirus voorkomt ernstige diarree bij baby's. Als die prikken niet meer voor alle kinderen worden aangeraden, raken meer kinderen die ziekten.

**Meer verwarring, lagere vaccinatiegraad.** Trump herhaalde de claim dat vaccins autisme veroorzaken. Die claim is ontkracht. Als ouders door twijfel prikken uitstellen, stijgt het risico op uitbraken. Senator Bill Cassidy, zelf arts, noemde het besluit "fout". De voorzitter van de AAP noemde het "gevaarlijk".

**Geen directe wet, wel een signaal.** Staten maken nog steeds de schoolregels. Maar federale adviezen beïnvloeden artsen, verzekeraars en ouders. Minder duidelijke aanbevelingen leiden vaak tot minder vaccinaties.

### Veroorzaken vaccins autisme?

Nee. De WHO bevestigde dit opnieuw in december 2025. Een Deense studie uit 2019 volgde 657.461 kinderen en vond geen verband tussen de BMR-prik en autisme. Meer over die mythe staat in ons artikel [Vaccins veroorzaken geen autisme](/nl/blog/vaccines-do-not-cause-autism).

### Wat betekent dit als je niet in de VS woont?

Het Nederlandse Rijksvaccinatieprogramma verandert hierdoor niet. Hetzelfde geldt voor de schema's in andere Europese landen.

Mazelen stopt niet bij een grens. Reizen naar gebieden met uitbraken, of contact met reizigers, blijft een reden om op schema te blijven. Check je eigen prikken, en die van je kinderen, voor je vertrekt.

### Wat kun je doen?

1. Volg het nationale schema van jouw land, niet een politiek advies uit een ander land.
2. Stel vragen aan een arts of het consultatiebureau als je twijfelt.
3. Stel prikken niet uit "voor de zekerheid". Uitstel is extra risico.
4. Houd bij welke prikken je kind al heeft gehad.

Met Vaxy zet je alle vaccinaties van je gezin in één overzicht. Je ziet wat er is gezet, en je krijgt een herinnering voor de volgende prik.

Dit artikel is informatie, geen medisch advies. Heb je zorgen over de gezondheid van je kind? Praat met een arts.

### Korte antwoorden

**Verandert dit het schema in Europa?**
Nee. Het is Amerikaans advies, geen Europese regel.

**Waarom zoveel kindervaccins?**
Omdat ze beschermen tegen meer ernstige ziekten, op de leeftijd waarop dat het meest nodig is.

**Is de BMR-prik splitsen veiliger?**
Nee. Er is geen bewijs voor. Je kind blijft dan langer onbeschermd.

**Wat als minder kinderen een prik krijgen?**
Mazelen en andere ziekten komen terug. Uitbraken groeien al.

### Bronnen

- [BBC: Trump tekent besluit over kindervaccins, 11 augustus 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)
- [CIDRAP: Trump tekent besluit om BMR-vaccin te splitsen](https://www.cidrap.umn.edu/trump-signs-executive-order-break-measles-mumps-rubella-vaccine)
- [WHO, UNICEF en Gavi: uitbraken van vaccineerbare ziekten nemen toe, 24 april 2025](https://www.who.int/news/item/24-04-2025-increases-in-vaccine-preventable-disease-outbreaks-threaten-years-of-progress--warn-who--unicef--gavi)
- [WHO: mazelen, feitenoverzicht](https://www.who.int/news-room/fact-sheets/detail/measles)
- [WHO: mazelensterfte daalt, gevallen stijgen, 28 november 2025](https://www.who.int/news/item/28-11-2025-measles-deaths-down-88--since-2000--but-cases-surge)
- [WHO GACVS: vaccins veroorzaken geen autisme](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [Hviid et al., BMR-vaccinatie en autisme, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [RIVM: BMR-vaccinatie](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)`,
  },
  {
    slug: "moderna-mrna-melanoma-vaccine",
    date: "2026-08-19",
    category: "Gezondheidsnieuws",
    image: "/images/blog/moderna-mrna-melanoma-vaccine.webp",
    author: "Vaxy",
    readTime: "6 min lezen",
    title: "mRNA-kankervaccin van Moderna slaagt in grote studie bij melanoom",
    excerpt:
      "Moderna en Merck zeggen dat een persoonlijk mRNA-vaccin plus Keytruda helpt om te voorkomen dat melanoom terugkomt na een operatie. Dit is wat de fase-3-studie betekent, in gewone taal.",
    faq: [
      {
        q: "Voorkomt dit mRNA-kankervaccin kanker?",
        a: "Nee. Het is een persoonlijke behandeling voor mensen die al melanoom hadden. Artsen geven het na een operatie, om de kans kleiner te maken dat de kanker terugkomt.",
      },
      {
        q: "Is het Moderna-vaccin tegen melanoom hetzelfde als het COVID-19-vaccin?",
        a: "Nee. Beide gebruiken mRNA, maar dit kankervaccin wordt gemaakt voor de tumor van één persoon. Een COVID-19-vaccin is hetzelfde voor veel mensen en richt zich op een virus.",
      },
      {
        q: "Kun je het mRNA-vaccin tegen melanoom nu al krijgen?",
        a: "Nog niet. Het is niet goedgekeurd voor algemeen gebruik. Moderna en Merck willen de komende maanden goedkeuring vragen aan gezondheidsautoriteiten.",
      },
      {
        q: "Werkt het vaccin beter dan Keytruda alleen?",
        a: "Ja, in deze fase-3-studie. Mensen die het vaccin plus Keytruda kregen, bleven langer kankervrij, en de kanker spreidde minder vaak uit. De volledige cijfers volgen op een medisch congres.",
      },
    ],
    content: `## mRNA-kankervaccin van Moderna slaagt in grote studie bij melanoom

Op 19 augustus 2026 deelden Moderna en Merck goed nieuws. Hun persoonlijke mRNA-kankervaccin, samen met het kankermedicijn Keytruda, werkte in een grote studie. Het vaccin hielp mensen met melanoom om na een operatie langer kankervrij te blijven.

Dit is de eerste grote fase-3-studie die laat zien dat een mRNA-kankervaccin kan werken. Het is ook de eerste keer dat dit soort persoonlijk vaccin beter presteerde dan Keytruda alleen, na een operatie voor melanoom.

### Wat is het mRNA-kankervaccin van Moderna?

De behandeling heet intismeran autogene. Je ziet ook de namen V940 of mRNA-4157.

Het is geen vaccin dat gezonde mensen krijgen om kanker te voorkomen. Het is een behandeling voor mensen die al melanoom hadden, nadat artsen de tumor met een operatie hebben weggehaald.

Artsen noemen dit adjuvante behandeling. Dat betekent extra behandeling na de operatie, om de kans kleiner te maken dat de kanker terugkomt.

### Hoe werkt een persoonlijk mRNA-kankervaccin?

Melanoom is een ernstige vorm van huidkanker. Elke tumor heeft een eigen set veranderingen, mutaties genoemd. Zie het als een unieke vingerafdruk.

Het vaccin wordt voor één persoon gemaakt:

1. Artsen nemen een stukje van de tumor.
2. Wetenschappers lezen de vingerafdruk van die tumor.
3. Zij maken een mRNA-vaccin dat het immuunsysteem leert om die kankermerken te herkennen.
4. Na de prik leest het lichaam het mRNA en maakt kleine eiwitstukjes. Het immuunsysteem leert daarna kankercellen met dezelfde merken aan te vallen.

mRNA is een kort bericht dat cellen kunnen lezen. COVID-19-vaccins gebruiken ook mRNA, maar dit kankervaccin is anders. Het wordt gemaakt voor één patiënt en één tumor.

Het vaccin wordt gegeven samen met Keytruda (pembrolizumab). Keytruda is een immuuntherapie. Het helpt het immuunsysteem om kankercellen op te merken en te bestrijden.

### Wat vond de fase-3-studie bij melanoom?

De studie heet INTerpath-001. Een fase-3-studie is een grote, late test. Bedrijven gebruiken dit soort onderzoek vaak voordat ze gezondheidsautoriteiten om goedkeuring vragen.

Bij een geplande tussentijdse check haalde de studie twee hoofddoelen:

- Mensen bleven langer kankervrij. De kanker kwam minder vaak terug.
- De kanker spreidde minder vaak uit naar andere delen van het lichaam.

De bedrijven zeggen dat het verschil echt is, dus geen toeval, en dat het voor patiënten telt. De studie loopt nog. Onderzoekers kijken ook hoe lang mensen leven.

De volledige cijfers volgen op een medisch congres. Moderna en Merck willen ook met toezichthouders praten over goedkeuring.

### Wie deed mee aan de studie?

Aan de studie deden 1.137 mensen mee met een hoog risico op terugkeer van melanoom. Hun kanker was stadium 2B, 2C, 3 of 4. Een operatie had de kanker al weggehaald.

Deelnemers werden willekeurig in twee groepen verdeeld:

- Ongeveer twee op de drie mensen kregen het persoonlijke mRNA-vaccin plus Keytruda
- Ongeveer één op de drie mensen kreeg alleen Keytruda

De behandeling duurde ongeveer een jaar. Mensen konden tot negen doses van het vaccin krijgen.

### Is het mRNA-kankervaccin veilig?

Tot nu toe melden de bedrijven geen nieuwe veiligheidsproblemen. In eerdere studies waren veelvoorkomende bijwerkingen:

- Vermoeidheid
- Pijn op de plek van de prik
- Koude rillingen

Keytruda kan ook immuunbijwerkingen geven. Mensen in een studie worden nauw gevolgd.

### Wat lieten eerdere studies zien?

In een kleinere fase-2-studie verlaagde dezelfde combinatie het risico op terugkeer van kanker of overlijden met 49% na vijf jaar, vergeleken met Keytruda alleen. Het risico dat de kanker ver weg uitzaaide, of dat iemand overleed, daalde met 59%.

De nieuwe fase-3-studie is groter. Dit is het soort onderzoek dat toezichthouders gebruiken als zij beslissen of een behandeling mag worden goedgekeurd.

### Kun je dit vaccin nu al krijgen?

Nog niet. Deze behandeling is niet goedgekeurd voor algemeen gebruik.

Moderna zegt dat de bedrijven binnen enkele maanden goedkeuring willen aanvragen. Er is nog geen exacte datum.

Als het wordt goedgekeurd, is het eerst voor mensen met een hoog risico op terugkeer van melanoom na een operatie, niet voor iedereen.

### Kan dit ook bij andere kankersoorten helpen?

Merck en Moderna testen dit idee ook bij andere kankers, zoals longkanker, blaaskanker en nierkanker.

Die studies lopen nog. Goede resultaten bij melanoom betekenen niet automatisch dat het bij elke kanker werkt.

### Wat betekent dit nieuws voor jou?

Als je geen melanoom hebt, heb je dit vaccin niet nodig. De beste manieren om het risico op melanoom te verlagen blijven eenvoudig:

- Bescherm je huid tegen de zon
- Vermijd zonnebanken
- Controleer je huid en ga naar de dokter als een moedervlek verandert

Als jij of iemand dichtbij jou melanoom heeft gehad, is dit hoopvol nieuws. Bespreek het met een oncoloog. Die kan uitleggen wat de huidige behandeling is, en of een klinische studie een optie kan zijn.

Dit artikel is informatie, geen medisch advies.

### Korte antwoorden

**Voorkomt dit vaccin kanker?**
Nee. Het is een persoonlijke behandeling na een operatie voor melanoom.

**Is het hetzelfde als het COVID-19-vaccin?**
Nee. Beide gebruiken mRNA, maar dit vaccin wordt gemaakt voor de tumor van één persoon.

**Is het goedgekeurd?**
Nog niet. De bedrijven willen binnenkort goedkeuring vragen.

**Werkt het beter dan Keytruda alleen?**
Ja, in deze fase-3-studie. De volledige details volgen op een medisch congres.

### Bronnen

- [Persbericht van Merck, 19 augustus 2026](https://www.merck.com/news/merck-and-moderna-announce-phase-3-interpath-001-trial-of-intismeran-autogene-plus-keytruda-met-endpoints-of-recurrence-free-survival-rfs-and-distant-metastasis-free-survival-dmfs-in-patient/)
- [CNN Health, 19 augustus 2026](https://edition.cnn.com/2026/08/19/health/moderna-merck-mrna-melanoma-vaccine)`,
  },
  {
    slug: "why-vaccination-records-matter",
    date: "2026-06-10",
    category: "Gezondheidstips",
    image: "/images/blog/why-vaccination-records-matter.webp",
    author: "Vaxy",
    readTime: "4 min lezen",
    title: "Waarom je vaccinatiegegevens belangrijker zijn dan ooit",
    excerpt:
      "In een steeds meer verbonden wereld kan directe toegang tot je vaccinatiehistorie het verschil maken tussen een soepele reis en stress aan de grens.",
    content: `## Waarom je vaccinatiegegevens belangrijker zijn dan ooit

In een steeds meer verbonden wereld kan directe toegang tot je vaccinatiehistorie het verschil maken tussen een soepele reis en stress aan de grens.

### Het probleem met papier

Het gele WHO-vaccinatieboekje was decennialang de standaard voor reisgezondheidsdocumentatie. Maar papier raakt kwijt, vervaagt of blijft thuis liggen, precies wanneer je het nodig hebt. Veel reizigers kennen de stress van een douanebeambte die om bewijs van gelekoortsvaccinatie vraagt, terwijl het boekje nog op het aanrecht ligt.

### Wat er mis kan gaan zonder overzicht

- **Toegang geweigerd** aan grenzen waar specifieke vaccinaties verplicht zijn
- **Onnodige herhaalvaccinaties** omdat artsen je historie niet kunnen zien
- **Gemiste boosters** doordat er geen herinneringssysteem is
- **Noodsituaties** waarin zorgverleners je immuunstatus niet kennen

### Het digitale alternatief

Apps zoals Vaxy lossen dit op met een veilig, altijd beschikbaar digitaal overzicht van elke vaccinatie, voor jezelf en je hele gezin. Geen papier meer, geen giswerk.

### Reisregels veranderen

Landen passen hun vaccinatie-eisen regelmatig aan. Regels rond gele koorts, meningokokken en COVID-19 zijn de afgelopen jaren sterk veranderd. Met een actueel digitaal overzicht zie je snel wat je hebt en wat je nog nodig hebt voordat je boekt.

### Begin op tijd

Het beste moment om je vaccinatiegegevens te digitaliseren is voordat je ze nodig hebt. Neem vandaag tien minuten om je vaccinaties in Vaxy te zetten, dan hoef je nooit meer naar een papieren boekje te zoeken.`,
  },
  {
    slug: "travel-vaccinations-guide",
    date: "2026-05-28",
    category: "Reisgezondheid",
    image: "/images/blog/travel-vaccinations-guide.webp",
    author: "Vaxy",
    readTime: "7 min lezen",
    title: "De complete gids voor reisvaccinaties in 2026",
    excerpt:
      "Ga je naar het buitenland? Dit moet je weten over verplichte en aanbevolen vaccins en wanneer je ze het beste haalt.",
    content: `## De complete gids voor reisvaccinaties in 2026

Ga je naar het buitenland? Dit moet je weten over verplichte en aanbevolen vaccins en wanneer je ze het beste haalt.

### Begin 6-8 weken van tevoren

De grootste fout die reizigers maken is wachten tot het laatste moment. Sommige vaccins bestaan uit meerdere doses verspreid over weken, en je immuunsysteem heeft tijd nodig om bescherming op te bouwen. Plan je reisgezondheidsconsult minimaal 6-8 weken voor vertrek.

### Verplicht of aanbevolen

**Verplichte** vaccinaties zijn wettelijk vereist voor toegang tot bepaalde landen. Het bekendste voorbeeld is gele koorts: veel landen in Afrika en Zuid-Amerika vragen bewijs als je uit een risicogebied komt.

**Aanbevolen** vaccinaties zijn niet wettelijk verplicht, maar worden sterk aangeraden op basis van bestemming, activiteiten en gezondheid.

### Veelvoorkomende reisvaccins per regio

**Afrika**
- Gele koorts, verplicht voor veel landen
- Buiktyfus
- Hepatitis A & B
- Malariaprofylaxe, geen vaccin maar vaak essentieel
- Meningokokken, vooral voor Hadj-reizigers

**Zuidoost-Azië**
- Hepatitis A & B
- Buiktyfus
- Japanse encefalitis voor landelijke gebieden
- Rabiës bij lang verblijf of contact met dieren

**Zuid-Amerika**
- Gele koorts
- Buiktyfus
- Hepatitis A & B

**Europa & Noord-Amerika**
- Meestal alleen routinevaccinaties nodig
- Tekenencefalitis voor bosrijke gebieden in Midden- en Oost-Europa

### Vergeet routinevaccins niet

Controleer eerst je basisvaccinaties voordat je naar exotische reisvaccins kijkt:
- BMR
- DTP
- Griep
- COVID-19

### Vaxy gebruiken voor je reisplanning

Met de reisgezondheidsfunctie van Vaxy kies je je bestemming en zie je meteen welke vaccins verplicht of aanbevolen zijn. Vaxy vergelijkt dit met je bestaande overzicht en laat precies zien wat je mist.`,
  },
  {
    slug: "vaccinating-your-children",
    date: "2026-05-14",
    category: "Gezin en gezondheid",
    image: "/images/blog/vaccinatieschema-rvp-2025.jpeg",
    author: "Vaxy",
    readTime: "5 min lezen",
    title: "Het vaccinatieschema van je kinderen bijhouden",
    excerpt:
      "Kinderen krijgen in hun eerste levensjaren veel vaccinaties. Zo houd je overzicht en mis je geen belangrijke dosis.",
    content: `## Het vaccinatieschema van je kinderen bijhouden

Kinderen krijgen in hun eerste levensjaren veel vaccinaties. Zo houd je overzicht en mis je geen belangrijke dosis.

### Het vaccinatieschema voor kinderen

In Nederland en een groot deel van Europa volgen kinderen een nationaal vaccinatieprogramma. In Nederland is dat het Rijksvaccinatieprogramma, met bescherming vanaf de geboorte tot in de tienerjaren. Het schema omvat onder meer:

- DKTP
- Hib
- Hepatitis B
- Pneumokokken
- Meningokokken
- BMR
- HPV voor meisjes en jongens rond 10 jaar

### Waarom een gemiste dosis telt

Vaccins werken het best wanneer ze op de juiste leeftijd worden gegeven. Het schema is zo opgebouwd dat kinderen beschermd zijn voordat ze extra kwetsbaar zijn voor bepaalde ziekten. Een gemiste dosis raakt niet alleen je kind, maar kan ook groepsbescherming verminderen.

### De uitdaging voor ouders

Met zoveel vaccins op verschillende leeftijden raak je makkelijk het overzicht kwijt, zeker met meerdere kinderen. Elk kind heeft een eigen schema en papieren boekjes bij verschillende bezoeken maken het niet eenvoudiger.

### Hoe Vaxy gezinnen helpt

Met Vaxy voeg je alle gezinsleden toe onder één account. Ieder gezinslid heeft een eigen compleet vaccinatieoverzicht en je krijgt herinneringen wanneer komende prikken nodig zijn.

### Tips om op schema te blijven

1. **Registreer vanaf de geboorte**: voeg je pasgeboren baby zo snel mogelijk toe aan Vaxy
2. **Controleer met je huisarts of consultatiebureau**: laat gegevens na elk bezoek bevestigen
3. **Zet herinneringen aan**: gebruik pushmeldingen voor komende doses
4. **Voeg notities toe**: leg reacties of bijzondere omstandigheden vast per vaccinatie`,
  },
  {
    slug: "bof",
    date: "2026-09-12",
    category: "Infectieziekten",
    image: "/images/blog/bof-hero.jpg",
    author: "Vaxy",
    readTime: "5 min lezen",
    title: "Bof herkennen: symptomen, wat je kunt doen en wanneer je moet bellen",
    excerpt:
      "Bof begint vaak met koorts en een pijnlijke dikke wang. Zo herken je het, wat je thuis kunt doen en wanneer je medische hulp moet vragen.",
    faq: [
      {
        q: "Wat is het eerste signaal van bof?",
        a: "Bof begint vaak met koorts, hoofdpijn, spierpijn en een ziek gevoel. Daarna worden meestal een of twee wangen dik en pijnlijk.",
      },
      {
        q: "Hoe lang duurt bof meestal?",
        a: "De klachten duren meestal een tot twee weken. De dikke wang verdwijnt vaak na ongeveer een week.",
      },
      {
        q: "Wat kun je zelf doen bij bof?",
        a: "Rust, voldoende drinken en zo nodig paracetamol helpen meestal. Zure drankjes of eten kunnen extra pijn doen aan de speekselklieren.",
      },
      {
        q: "Wanneer moet ik direct bellen?",
        a: "Bel direct bij sufheid, ernstig ziek zijn, overgeven, erge hoofdpijn, erge buikpijn, plots slechter horen of pijn in de zaadbal of onderbuik.",
      },
    ],
    content: `## Bof herkennen: symptomen, wat je kunt doen en wanneer je moet bellen

Bof is een virusinfectie die vaak begint als een gewone griepachtige ziekte. Juist daarom wordt het soms laat herkend. De bekende dikke wang komt meestal pas wat later, nadat iemand zich al een paar dagen ziek voelt.

### Hoe herken je bof?

De eerste klachten zijn vaak:

- koorts
- hoofdpijn
- spierpijn
- een ziek gevoel

Daarna kunnen een of twee wangen dik en pijnlijk worden. Kauwen en slikken kunnen pijn doen, en soms doet ook de kaak of het oor zeer. Volgens Thuisarts en het RIVM zijn dit de klassieke signalen van bof.

WHO beschrijft mumps als een besmettelijke virusinfectie met koorts, hoofdpijn, moeheid en zwelling van de speekselklieren bij de kaak. Mogelijke complicaties zijn onder meer hersenvliesontsteking, hersenontsteking, zaadbalontsteking en gehoorverlies. De WHO noemt twee doses MMR als de beste bescherming.

### Wat kun je thuis doen?

Er is geen medicijn dat bof sneller laat verdwijnen. Meestal helpt het om:

- veel te drinken
- rust te nemen
- paracetamol te gebruiken tegen pijn of koorts
- zure voeding of drinken te vermijden als dat pijn doet, zoals sinaasappelsap of augurk

De klachten trekken meestal vanzelf weg binnen een tot twee weken.

### Wanneer moet je de huisarts bellen?

Neem contact op met de huisarts of huisartsenpost als iemand met bof:

- erg ziek wordt
- suf wordt
- moet overgeven
- erge hoofdpijn of buikpijn krijgt
- plots slechter gaat horen
- pijn krijgt in een zaadbal of in de onderbuik

Denk je dat je bof hebt? Meld het dan altijd bij de huisarts. De huisarts moet dit ook doorgeven aan de GGD.

### Waarom de BMR-prik belangrijk blijft

De BMR-prik beschermt tegen bof, mazelen en rodehond. Na twee prikken is de bescherming tegen ernstige ziekte veel beter, maar geen enkele prik maakt de kans ooit helemaal nul. Juist daarom blijft herkennen belangrijk.

### Korte antwoorden

**Is bof besmettelijk?**  
Ja, bof is heel besmettelijk. Het verspreidt zich via speeksel en druppeltjes.

**Moet je naar bed blijven?**  
Rust is verstandig, maar de belangrijkste stap is voldoende drinken en letten op alarmsymptomen.

**Gaat bof vanzelf over?**  
Meestal wel, binnen een tot twee weken.

### Bronnen

- [Thuisarts: Ik heb de bof](https://www.thuisarts.nl/bof/ik-heb-bof)
- [RIVM: Bof](https://www.rivm.nl/bof)
- [WHO: Mumps](https://www.who.int/teams/immunization-vaccines-and-biologicals/diseases/mumps)`,
  },
  {
    slug: "rode-hond",
    date: "2026-09-12",
    category: "Infectieziekten",
    image: "/images/blog/rodehond-hero.jpg",
    author: "Vaxy",
    readTime: "5 min lezen",
    title: "Rodehond herkennen: klachten, zwangerschap en wat je moet doen",
    excerpt:
      "Rodehond geeft meestal milde klachten, maar het is belangrijk om het op tijd te herkennen, vooral als iemand zwanger is of zwanger kan zijn.",
    faq: [
      {
        q: "Hoe begint rodehond meestal?",
        a: "Rodehond begint vaak met een lichte verkoudheid, wat koorts, moeheid en opgezette klieren in de hals.",
      },
      {
        q: "Hoe ziet de uitslag van rodehond eruit?",
        a: "De uitslag begint vaak in het gezicht of achter de oren en verspreidt zich daarna over het lichaam. De vlekjes zijn roze-rood.",
      },
      {
        q: "Wat moet je doen bij rodehond?",
        a: "Rust nemen, voldoende drinken en eventueel paracetamol gebruiken. De ziekte gaat meestal vanzelf over.",
      },
      {
        q: "Waarom is rodehond extra belangrijk bij zwangerschap?",
        a: "Rodehond kan ernstig zijn voor een ongeboren baby. Bij contact of verdenking moet je direct de huisarts of verloskundige bellen.",
      },
    ],
    content: `## Rodehond herkennen: klachten, zwangerschap en wat je moet doen

Rodehond is meestal een milde virusinfectie, maar het is wel een ziekte die je serieus moet nemen. Dat geldt vooral als iemand zwanger is of zwanger kan zijn. De eerste klachten lijken vaak op een lichte verkoudheid, waardoor rodehond niet altijd meteen opvalt.

### Hoe herken je rodehond?

De typische klachten zijn:

- lichte koorts
- vermoeidheid
- verkoudheidsklachten
- opgezette klieren in de hals
- een roze-rode huiduitslag

De uitslag begint vaak in het gezicht of achter de oren en verspreidt zich daarna snel over de rest van het lichaam. Volgens Thuisarts en het RIVM is dat het klassieke patroon.

WHO beschrijft rubella als meestal mild of zelfs symptoomloos, met klachten zoals koorts, een uitslag die 1 tot 3 dagen duurt, opgezette lymfeklieren achter het oor en in de nek, en soms gewrichtsklachten. Voor zwangerschap is het extra belangrijk: infectie in het begin van de zwangerschap kan miskraam, doodgeboorte of congenitaal rubellasyndroom veroorzaken. WHO adviseert immuniteit vóór de zwangerschap en geen MMR tijdens zwangerschap.

### Wat kun je zelf doen?

Bij rodehond kun je meestal hetzelfde doen als bij een gewone virusinfectie:

- voldoende drinken
- rust nemen
- paracetamol gebruiken als dat nodig is

De ziekte gaat meestal vanzelf over binnen een paar dagen. Toch is het slim om de huisarts te laten weten dat je rodehond vermoedt, zeker als het om een kind gaat of als er kwetsbare mensen in de buurt zijn.

### Wanneer moet je extra opletten?

Rodehond is vooral belangrijk vanwege het risico in de zwangerschap. Als een zwangere persoon in contact is geweest met rodehond, of denkt dat zij het heeft, moet zij direct contact opnemen met de huisarts of verloskundige.

Ook als iemand zich snel zieker voelt, benauwd wordt of andere ongewone klachten krijgt, is medisch advies verstandig.

### Bescherming via de BMR-prik

In Nederland zijn kinderen beschermd via de BMR-prik. Daarmee verklein je de kans op rodehond, maar ook op bof en mazelen. Die bescherming blijft belangrijk, juist omdat rodehond in sommige situaties grote gevolgen kan hebben.

### Korte antwoorden

**Is rodehond gevaarlijk?**  
Meestal niet voor gezonde kinderen of volwassenen, maar wel voor ongeboren baby’s.

**Kun je rodehond verwarren met een verkoudheid?**  
Ja. De eerste klachten lijken vaak op een lichte verkoudheid.

**Gaat rodehond vanzelf over?**  
Ja, meestal binnen een paar dagen.

### Bronnen

- [Thuisarts: Mijn kind heeft rodehond](https://www.thuisarts.nl/rode-hond/mijn-kind-heeft-rode-hond)
- [RIVM: Rodehond](https://www.rivm.nl/rodehond)
- [WHO: Rubella](https://www.who.int/en/news-room/fact-sheets/detail/rubella)`,
  },
  {
    slug: "influenza",
    date: "2026-09-12",
    category: "Griep",
    image: "/images/blog/vaccine-boosters.webp",
    author: "Vaxy",
    readTime: "6 min lezen",
    title: "Griepprik 2026: waarom influenza weer relevant is in Nederland",
    excerpt:
      "Het griepseizoen komt elk jaar terug. Dit laten de Nederlandse cijfers zien, en dit is waarom de griepprik voor 2026 weer relevant wordt.",
    faq: [
      {
        q: "Wanneer is influenza meestal actief in Nederland?",
        a: "Influenza komt vooral terug in de herfst en winter. RIVM publiceert wekelijks actuele cijfers over griep in Nederland.",
      },
      {
        q: "Hoe lang duurde de griepepidemie in seizoen 2025/2026?",
        a: "De officiële griepepidemie duurde van week 5 tot en met week 10 van 2026. Dat was in totaal zes weken.",
      },
      {
        q: "Voor wie is de griepprik bedoeld?",
        a: "In Nederland krijgen vooral mensen van 60 jaar en ouder en mensen met een medische risicogroep een uitnodiging voor de griepprik.",
      },
      {
        q: "Beschermt de griepprik ook tegen verkoudheid?",
        a: "Nee. De griepprik beschermt tegen griep, niet tegen gewone verkoudheid of andere luchtweginfecties.",
      },
    ],
    content: `## Griepprik 2026: waarom influenza weer relevant is in Nederland

Influenza, of griep, komt ieder jaar terug. Meestal begint het griepseizoen in de herfst en loopt het door in de winter. Juist daarom krijgt de griepprik elk jaar opnieuw aandacht.

### Wat lieten de Nederlandse cijfers zien?

Volgens RIVM duurde de officiële griepepidemie in seizoen 2025/2026 van **week 5 tot en met week 10 van 2026**. Dat is in totaal **zes weken**. Het RIVM publiceert daarnaast wekelijks actuele cijfers over griep in Nederland.

Dat is belangrijk, omdat de situatie per jaar kan verschillen. Soms is een seizoen kort, soms langer. De timing van de griepprik blijft daarom relevant, ook als het een tijd rustig lijkt.

### Waarom is de griepprik weer actueel?

De griepprik helpt vooral om ernstige ziekte te voorkomen bij mensen die extra risico lopen. In Nederland is de prik bedoeld voor onder andere:

- mensen van 60 jaar en ouder
- mensen met een chronische aandoening of andere medische risicofactor

De prik beschermt niet tegen een gewone verkoudheid, maar wel tegen griep en tegen complicaties die daarbij kunnen horen.

### Wat kun je doen als het griepseizoen begint?

Het handigste is om op tijd te plannen:

1. Check in het najaar of je een uitnodiging krijgt.
2. Zet de prik in je agenda.
3. Bewaar je vaccinatieoverzicht, zodat je weet wat je al hebt gehad.

Voor veel mensen is dat precies het moment waarop een app zoals Vaxy nuttig wordt: je ziet direct wat je hebt, wat je mist en wanneer je aan de beurt bent.

### Korte antwoorden

**Is influenza hetzelfde als een verkoudheid?**  
Nee. Griep is meestal heftiger dan een verkoudheid en geeft vaker plots hoge koorts en echt ziek gevoel.

**Hoe lang duurde de griepepidemie in 2025/2026?**  
Zes weken, van week 5 tot en met week 10 van 2026.

**Wanneer komt de griepprik?**  
Meestal in het najaar, voor het griepseizoen goed op gang komt.

### Bronnen

- [RIVM: Actualiteiten over griep](https://www.rivm.nl/griep-griepprik/actueel)
- [RIVM: Terugblik surveillance 2025/2026](https://www.rivm.nl/griep-griepprik/griepsurveillance/terugblik-2025-2026)
- [Nivel: Griep centraal](https://www.nivel.nl/nl/zorg-en-ziekte-in-cijfers/griep-centraal)`,
  },
  {
    slug: "waterpokken-vaccinatie",
    date: "2026-09-12",
    category: "Vaccinaties",
    image: "/images/blog/vaccinating-your-children.webp",
    author: "Vaxy",
    readTime: "6 min lezen",
    title: "Waterpokkenvaccinatie: vanaf welke leeftijd kan het en wordt het vergoed?",
    excerpt:
      "In Europees Nederland zit waterpokkenvaccinatie niet in het Rijksvaccinatieprogramma. Wel kun je je laten vaccineren vanaf jonge leeftijd, maar meestal betaal je dit zelf.",
    faq: [
      {
        q: "Vanaf welke leeftijd kan je waterpokkenvaccinatie krijgen?",
        a: "Volgens de richtlijnen kan vaccinatie al vanaf ongeveer 11 of 12 maanden. Voor optimale bescherming zijn meestal twee doses nodig.",
      },
      {
        q: "Wordt waterpokkenvaccinatie standaard vergoed?",
        a: "Nee, in Europees Nederland meestal niet. Alleen in bepaalde medische situaties of via een aanvullende verzekering kan vergoeding mogelijk zijn.",
      },
      {
        q: "Zit waterpokkenvaccinatie in het Rijksvaccinatieprogramma?",
        a: "Niet in Europees Nederland. Op Bonaire, Saba en Sint-Eustatius zijn de regels anders.",
      },
      {
        q: "Wanneer is vaccinatie vooral verstandig?",
        a: "Voor mensen die waterpokken nog niet hebben gehad en voor wie een infectie meer risico geeft, bijvoorbeeld oudere kinderen of volwassenen.",
      },
    ],
    content: `## Waterpokkenvaccinatie: vanaf welke leeftijd kan het en wordt het vergoed?

Waterpokken lijkt vaak een onschuldige kinderziekte, maar dat is niet altijd zo. Vooral oudere kinderen, volwassenen en sommige medische risicogroepen kunnen er zieker van worden. Daarom is het goed om te weten dat waterpokkenvaccinatie in Nederland wel mogelijk is.

WHO noemt varicella een zeer besmettelijke infectie. De ziekte begint vaak met koorts of malaise en een jeukende uitslag die overgaat van plekjes naar blaasjes en daarna korstjes. Volwassenen, zwangeren, zuigelingen en mensen met een verminderde afweer lopen meer risico op een ernstig beloop. Een besmet persoon kan al 1 tot 2 dagen vóór de uitslag besmettelijk zijn. WHO beschrijft vaccinatie met een levend verzwakt varicellavaccin als effectieve preventie, meestal in een schema van twee doses.

### Vanaf welke leeftijd kan vaccinatie?

Volgens de RIVM-factsheet en het advies van de Gezondheidsraad kan waterpokkenvaccinatie in Europees Nederland al vanaf **11 of 12 maanden** worden gegeven. Voor een goede bescherming zijn meestal **twee doses** nodig.

Bij oudere kinderen en volwassenen kan het interval tussen de prikken anders zijn. Bij kinderen vanaf 13 jaar is het gebruikelijke schema doorgaans strakker dan bij jonge kinderen.

### Wordt het vergoed?

In Europees Nederland zit waterpokkenvaccinatie **niet standaard in het Rijksvaccinatieprogramma**. Dat betekent dat je het meestal zelf betaalt.

Een vergoeding kan soms wel mogelijk zijn bij een medische indicatie. Dan gaat het niet om gewone preventie, maar om een individuele medische situatie. Vraag in dat geval altijd vooraf na bij je huisarts en zorgverzekeraar wat wel en niet wordt vergoed.

### Voor wie kan het extra interessant zijn?

Vaccinatie kan vooral relevant zijn als:

- je nog nooit waterpokken hebt gehad
- je als oudere kind of volwassene nog vatbaar bent
- je een medische reden hebt om een infectie zoveel mogelijk te voorkomen

Voor sommige mensen is waterpokken op latere leeftijd namelijk lastiger en zwaarder dan bij jonge kinderen.

### Korte antwoorden

**Is waterpokkenvaccinatie in Nederland standaard gratis?**  
Nee, meestal niet. Alleen in specifieke medische situaties kan vergoeding mogelijk zijn.

**Hoeveel prikken zijn nodig?**  
Meestal twee.

**Is dit hetzelfde als het Rijksvaccinatieprogramma?**  
Nee, niet in Europees Nederland.

### Bronnen

- [RIVM: Waterpokkenvaccinatie](https://www.rivm.nl/waterpokken/waterpokkenvaccinatie)
- [LCI-richtlijn: Waterpokkenvaccinatie](https://lci.rivm.nl/factsheets/waterpokkenvaccinatie)
- [Zorginstituut Nederland: Vaccinaties](https://www.zorginstituutnederland.nl/verzekerde-zorg/v/vaccinaties)
- [WHO: Chickenpox](https://www.who.int/news-room/questions-and-answers/item/chickenpox)
- [WHO: Varicella](https://www.who.int/teams/immunization-vaccines-and-biologicals/diseases/varicella)`,
  },
];

export type BlogPost = (typeof nl)[number];

const en: BlogPost[] = [
  {
    slug: "west-nile-virus",
    date: "2026-09-11",
    category: "Infectious diseases",
    image: "/images/blog/west-nile-virus-hero.svg",
    author: "Vaxy",
    readTime: "6 min read",
    title:
      "West Nile virus: what it is, what the risks are, and what happens next",
    excerpt:
      "West Nile virus spreads through mosquitoes. Most people do not get sick, but a small group can become seriously ill. What could happen in the coming years?",
    faq: [
      {
        q: "What is West Nile virus?",
        a: "West Nile virus is a virus that mainly lives in birds. Mosquitoes can pass it to people and horses.",
      },
      {
        q: "How dangerous is it?",
        a: "Most people notice nothing or only mild symptoms. A small group gets severe illness, especially older adults and people with weaker immunity.",
      },
      {
        q: "Can you catch it from another person?",
        a: "No, not through normal contact. Mosquitoes spread it. Rarely, it can spread through blood or organ transplant.",
      },
      {
        q: "Will it become more common in the next years?",
        a: "WHO and ECDC say warmer summers and longer mosquito seasons give the virus more time. In Europe it may last longer each year and appear in more areas.",
      },
    ],
    content: `## West Nile virus

West Nile virus is a virus that mainly lives in birds. Mosquitoes can pass it to people and horses. Most people notice nothing or only mild symptoms.

### What are the risks?

The symptoms often look like flu: fever, headache, muscle pain and tiredness. A small group becomes seriously ill. That is more common in older adults and people with weaker immunity. Then the virus can affect the brain or nerves.

There is no vaccine for people and no medicine that removes the virus itself. In hospital, doctors can still help with the symptoms and the care you need.

### What may happen in the coming years?

WHO and ECDC say warmer summers and longer mosquito seasons give the virus more time. That means it can stay active longer in Europe and show up in more areas.

In the Netherlands, RIVM already keeps watch on the virus in birds and mosquitoes. That means new human cases can still happen when the weather and mosquitoes are right.

### What can you do?

Protect yourself from mosquito bites, especially in the evening and early morning. Use repellent with DEET, wear covering clothes and sleep under a mosquito net if needed.

Remove standing water around your home and garden, because mosquitoes can breed there. If you get a high fever, stiff neck or confusion after a mosquito bite, contact a doctor right away.

### Short answers

**Can you catch West Nile virus from another person?**
No, not through normal contact. Mosquitoes spread it. Rarely, blood or transplant spread can happen.

**Who has a higher risk of severe illness?**
Older adults and people with weaker immunity. Most other people get no symptoms or only a mild illness.

**Is there a vaccine or treatment?**
No, not for people. That is why mosquito prevention matters so much.

**What is the best advice for the next years?**
Keep preventing mosquito bites. That is the best protection now and later.

### Sources

- [WHO Europe: West Nile virus, as cases rise across parts of Europe](https://www.who.int/europe/news/item/24-07-2026-west-nile-virus--as-cases-rise-across-parts-of-europe--here-is-what-you-need-to-know)
- [WHO: vector-borne diseases](https://www.who.int/news-room/fact-sheets/detail/vector-borne-diseases)
- [RIVM: West Nile fever](https://www.rivm.nl/en/west-nile-fever)
- [RIVM: West Nile fever, current updates](https://www.rivm.nl/westnijlkoorts/actueel)
- [ECDC: surveillance and updates for West Nile virus infection](https://www.ecdc.europa.eu/en/topics-z/west-nile-virus-infection/surveillance-and-updates-west-nile-virus-infection)

This is information, not medical advice.`,
  },
  {
    slug: "future-of-vaccination",
    date: "2026-08-26",
    category: "Health Tips",
    image: "/images/blog/future-of-vaccination.webp",
    author: "Vaxy",
    readTime: "8 min read",
    title: "Future alternatives to the vaccine needle",
    excerpt:
      "Most vaccines still use a needle. Drops, a nasal flu vaccine and needle-free jet injection already exist. Skin patches from Micron, Vaxxas and others are still research. WHO, Gavi and RIVM, in plain terms.",
    faq: [
      {
        q: "Which alternatives to the needle already exist?",
        a: "Oral polio vaccines, rotavirus drops and oral cholera vaccine. In some countries, a nasal flu vaccine. PharmaJet Tropis is a WHO-prequalified needle-free jet into the skin, used in polio campaigns. The Dutch national programme still uses a needle as standard.",
      },
      {
        q: "What is a microarray patch?",
        a: "A small skin patch with very short tips that deliver vaccine into the upper skin. WHO names two measles-rubella candidates: Micron Biomedical (dissolvable) and Vaxxas (coated, with an applicator). Phase 3 and WHO approval are still ahead.",
      },
      {
        q: "Can I already be vaccinated without a needle in the Netherlands?",
        a: "Infants already get rotavirus as drops. Otherwise RIVM still uses needle and syringe in the national programme. Skin patches are not a routine product.",
      },
      {
        q: "Why does WHO look for alternatives at all?",
        a: "Coverage has stalled. Syringes, reconstitution and the cold chain make vaccination hard, especially where children have had no doses at all. A simpler product can reach more people.",
      },
      {
        q: "Is this medical advice?",
        a: "No. This is an explainer based on WHO, Gavi, BARDA and RIVM. Which vaccine you need is a question for your GP, municipal health service or a travel clinic.",
      },
    ],
    content: `## Future alternatives to the vaccine needle

Most vaccines still go into the upper arm with a needle. That works. It is also fiddly: syringes, sometimes mixing powder first, a cold chain, and people who would rather skip the jab.

The World Health Organization (WHO) says delivery is one reason immunisation coverage has plateaued. In 2020, 23 million children missed their shots, 3.7 million more than in 2019.

That is why WHO, Gavi, UNICEF, PATH and the Bill & Melinda Gates Foundation are looking at other ways to vaccinate. Their Vaccine Innovation Prioritisation Strategy (VIPS) picked three priorities in May 2020: microarray patches, heat-stable vaccines, and barcodes on primary packaging.

Not everything is future tense. Some alternatives to the needle already exist. Others are in trials. This is information, not medical advice. In the Dutch national programme, the standard remains a needle.

### What already works without a needle?

For years:

- oral polio vaccines
- rotavirus drops for infants, including in the Netherlands
- oral cholera vaccine
- in some countries, a nasal flu vaccine

Nasal vaccines target the mucosa, where respiratory viruses enter. Researchers are working on nasal vaccines for other respiratory infections. Most COVID-19 vaccines in Europe remain an arm injection.

**PharmaJet Tropis** is different: not a patch, but a thin stream of liquid into the skin, without a needle. WHO has prequalified the device for intradermal delivery. It has been used in polio campaigns, including in Pakistan and Nigeria. That is the closest thing to “already needle-free in the field”. It does not replace DTP or MMR in the Netherlands.

### Skin patches: Micron, Vaxxas and others

A microarray patch, or MAP, is a small skin patch with very short tips. They are too short for a normal injection. They put the vaccine in the upper skin, where many immune cells sit.

WHO lists benefits such as: no reconstitution and no syringe, less pain, potentially better stability outside the fridge, and delivery by less specialised staff.

For measles and rubella, WHO names two clinical candidates:

**Micron Biomedical** makes a dissolvable patch. It stays on the wrist for a few minutes. Their phase 1/2 study in adults, toddlers and infants in The Gambia was published in *The Lancet*. Safety and immune response looked similar to a standard subcutaneous shot. In infants, this is the furthest-along MAP.

**Vaxxas** makes a High-Density Microarray Patch: thousands of tips about 0.25 millimetres, with dried vaccine, and a single-use applicator on the upper arm or thigh. Their adult measles-rubella trial is complete. A paediatric phase was planned next.

Phase 3, licensing and WHO prequalification are still ahead. Do not expect one at your next DTP or MMR visit.

Other companies put mRNA on a patch. That is earlier stage. The US BARDA Patch Forward Prize named, besides Vaxxas and Micron, **LTS Lohmann** (Germany) with **BioNet**, and **PopVax** (India) with LTS. The aim: a flu or COVID RNA vaccine on a microneedle patch. Not a routine product.

### Heat-stable vaccines

The second VIPS priority is vaccines that can sit outside the fridge for a limited time, in a controlled temperature chain. Less ice, less wastage, more reach. That can go with a patch or a classic syringe.

mRNA is a different leap: faster manufacturing when a new variant appears. Those vaccines still use a needle, and often need extra-cold storage.

### What does this mean for your record?

Whether you get a jab, drops, a jet or later a patch: the date, the disease and the product name still matter. For travel, a booster or a new outbreak, a clinician needs to know what you already had.

Vaxy keeps that in one place, for you and your family.

Unsure about a vaccine? Ask your GP, the municipal health service (GGD) or a travel clinic.

### Short answers

**Which alternatives to the needle already exist?**
Drops (polio, rotavirus, cholera), in some countries a nasal flu vaccine, and PharmaJet needle-free jet injection in some polio campaigns.

**What is a microarray patch?**
A small skin patch with short tips. Micron and Vaxxas are the two candidates WHO names for measles and rubella.

**Can I go needle-free in the Netherlands?**
Rotavirus already as drops. Otherwise RIVM still uses needle and syringe in the national programme. Skin patches are research.

**Why does WHO want this?**
Simpler delivery can reach more people, especially children who currently get zero doses.

### Sources

- [WHO: innovative vaccine delivery, including microarray patches](https://www.who.int/teams/immunization-vaccines-and-biologicals/product-and-delivery-research/delivery-technologies)
- [WHO: Technical Advisory Group on measles-rubella microarray patches](https://www.who.int/news-room/articles-detail/2023-November-132)
- [WHO: Target Product Profile for measles-rubella MAP](https://www.who.int/publications/i/item/9789240000209)
- [The Lancet: Micron measles-rubella patch, phase 1/2 in The Gambia](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(24)00532-4/fulltext)
- [WHO PQS: PharmaJet Tropis needle-free injector](https://extranet.who.int/prequal/immunization-devices/e008-071)
- [BARDA: Patch Forward Prize, concept-stage winners](https://www.patchforwardprize.com/)
- [Gavi: Vaccine Innovation Prioritisation Strategy (VIPS)](https://www.gavi.org/our-alliance/market-shaping/vaccine-innovation-prioritisation-strategy)
- [RIVM: Dutch national immunisation programme](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "philanthropy-global-infectious-diseases",
    date: "2026-08-26",
    category: "Health News",
    image: "/images/blog/philanthropy-global-infectious-diseases.webp",
    author: "Vaxy",
    readTime: "8 min read",
    title:
      "How philanthropy thinks about infectious disease: Gates, Gavi and partners",
    excerpt:
      "The Gates Foundation, Gavi, Wellcome and CEPI see vaccines as one of the strongest tools against infectious disease worldwide. WHO sets the course. This is how that partnership works.",
    faq: [
      {
        q: "What does the Gates Foundation want on infectious disease?",
        a: "To reach children, adolescents and adults in lower-income countries with the vaccines they need, and to prevent the next generation dying of infectious disease. The foundation calls vaccines one of the most cost-effective health tools: 3.5 to 5 million lives a year.",
      },
      {
        q: "What is Gavi?",
        a: "Gavi, the Vaccine Alliance, is a public-private partnership. Governments, WHO, UNICEF, the World Bank, vaccine manufacturers and the Gates Foundation work in it. Gavi buys and delivers vaccines for lower-income countries.",
      },
      {
        q: "Does Bill Gates set Dutch vaccination policy?",
        a: "No. In the Netherlands, RIVM, the Health Council and the Ministry of Health set policy. WHO issues global guidance. Philanthropic funds pay for research and procurement. They do not write the national schedule.",
      },
      {
        q: "Which other foundations do this work?",
        a: "Wellcome Trust and CEPI (outbreak vaccines), Rotary International (polio) and Gavi itself, among others. They usually work with WHO and governments, not instead of them.",
      },
      {
        q: "Is this medical advice?",
        a: "No. This explains a global strategy. For your own shots: GP, municipal health service or a travel clinic.",
      },
    ],
    content: `## How philanthropy thinks about infectious disease: Gates, Gavi and partners

Infectious diseases do not stop at a border. Philanthropic funds reason like this: if a vaccine is missing in Lagos or Lahore, the same disease can show up in Europe later. Vaccines, they argue, are the sharpest, cheapest tool.

The Bill & Melinda Gates Foundation is the largest private player. It does not work alone. The World Health Organization (WHO) sets the course. Gavi buys and delivers. Wellcome and CEPI fund research into new outbreaks.

This explains that strategy. It is not medical advice. Dutch vaccinations follow the national programme, not a foundation in Seattle.

### How does the Gates Foundation think?

The foundation puts it briefly: vaccines save 3.5 to 5 million lives a year. Since WHO launched the Expanded Programme on Immunization in 1974, an estimated 154 million lives have been saved, mostly children.

Its goal in lower-income countries: reach children, adolescents and adults with the shots they need. And a world without diseases you can prevent with a vaccine.

Three lines keep coming back:

1. **Buy and deliver what already exists.** Gavi is their largest grantee. From 2000 to 2023, Gavi immunised more than 1.1 billion children and prevented more than 18.8 million future deaths, according to the foundation.
2. **Make new vaccines affordable.** Think pneumococcal, rotavirus, HPV, cholera, meningitis A.
3. **Strengthen ordinary care.** An immunisation programme, they say, is the backbone of primary health care.

In June 2025 the foundation pledged US$1.6 billion to Gavi over five years. Since 2000 it has committed more than US$30.6 billion to vaccines, including US$7.7 billion to Gavi.

That lines up with WHO's Immunization Agenda 2030: everyone, everywhere, at every age. And with Gavi 6.0 (2026–2030): vaccinate more than 500 million extra children and adolescents, save 8 to 9 million extra lives.

### Where do they see it going wrong?

Progress has stalled. In 2023, 14.5 million children received not a single vaccine, more than the year before. Measles outbreaks rose. The foundation calls that a risk to decades of gains.

Their answer is not one new shot. It is reach: HPV introductions, measles campaigns, and extra effort in the lowest-coverage areas, including conflict zones.

Polio remains a flagship. Since 2007 the foundation has backed the Global Polio Eradication Initiative, with WHO, Rotary International, CDC, UNICEF and Gavi. Wild polio still occurs in Pakistan and Afghanistan. Cases have fallen by more than 99% since 1988.

### Wellcome, CEPI and Rotary

Other funds think in the same direction, with a different job.

**Wellcome Trust** funds science and co-founded CEPI in 2017.

**CEPI**, the Coalition for Epidemic Preparedness Innovations, exists to have vaccines ready before the next epidemic. Governments, Gates and Wellcome set it up after Ebola. The aim: a faster vaccine for an unknown disease X, in their vision within 100 days. CEPI does not write immunisation schedules. That stays with WHO and countries.

**Rotary International** has raised money for polio for decades, as a GPEI partner.

The VIPS alliance (Gavi, WHO, Gates Foundation, UNICEF, PATH) shows how these groups pick innovations together, such as skin patches. Philanthropy often pays for the first steps. Governments and WHO decide whether a product enters a programme.

### What does this mean for the Netherlands?

Nothing in your vaccination booklet is set by a foundation. RIVM, the Health Council and the Ministry of Health do that.

It still touches you indirectly. Global procurement keeps measles, polio and cholera smaller. Travel vaccines, pandemic preparedness and new delivery methods come from the same network.

Write down the date and name of each shot. Vaxy keeps that record, including if you later travel to a country where Gavi does the buying.

This article is information, not medical advice.

### Short answers

**What does the Gates Foundation want?**
Get vaccines to people in lower-income countries, and prevent infectious disease in the next generation.

**What is Gavi?**
An alliance of governments, WHO, UNICEF, the World Bank, industry and the Gates Foundation that buys vaccines for poorer countries.

**Does philanthropy set Dutch policy?**
No. RIVM, the Health Council and the Ministry of Health do. WHO issues global guidance.

**Who else works on this?**
Wellcome, CEPI, Rotary, UNICEF and national donors, among others.

### Sources

- [Gates Foundation: Immunization](https://www.gatesfoundation.org/our-work/programs/global-development/immunization)
- [Gates Foundation: US$1.6 billion pledge to Gavi, June 2025](https://www.gatesfoundation.org/ideas/media-center/press-releases/2025/06/renewed-gavi-commitment-lifesaving-vaccine-access)
- [Gates Foundation: polio](https://www.gatesfoundation.org/our-work/programs/global-development/polio)
- [WHO: Immunization Agenda 2030](https://www.who.int/teams/immunization-vaccines-and-biologicals/strategies/ia2030)
- [WHO: immunisation coverage fact sheet](https://www.who.int/news-room/fact-sheets/detail/immunization-coverage)
- [Gavi 6.0, 2026–2030 strategy](https://www.gavi.org/our-work/strategy/phase-6-2026-2030)
- [CEPI](https://cepi.net/)
- [RIVM: Dutch national immunisation programme](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "climate-diseases-netherlands",
    date: "2026-08-26",
    category: "Health Tips",
    image: "/images/blog/climate-diseases-netherlands.webp",
    author: "Vaxy",
    readTime: "8 min read",
    title: "New infectious diseases in the Netherlands as the climate changes",
    excerpt:
      "Dengue is still an imported disease in European Netherlands. RIVM, the Health Council and ECDC say warmer weather, ticks and mosquitoes are changing the risk. What is coming, and what is already here?",
    faq: [
      {
        q: "Is dengue already in the Netherlands?",
        a: "In European Netherlands only in travellers. RIVM has not confirmed local infection. In the Caribbean Netherlands dengue does occur, with larger outbreaks in 2023–2024. Southern Europe already has local cases, including France, Italy and Spain.",
      },
      {
        q: "Will the tiger mosquito settle here?",
        a: "Not yet. NVWA controls introductions. RIVM says more tiger mosquitoes arrive mainly through trade and travel, not climate. The Health Council expects establishment at some sites within 2 to 5 years. Then higher summer temperatures could allow local dengue after a traveller introduces the virus.",
      },
      {
        q: "Which diseases have already changed here?",
        a: "Tick-borne encephalitis in Dutch ticks since 2016, with 8 reports of infection acquired in the Netherlands in 2024. West Nile virus in 8 people in 2020. Lyme via ticks has been here for a long time. Warmer water raises the risk of Vibrio and Legionella.",
      },
      {
        q: "Should I get a dengue or TBE vaccine in the Netherlands?",
        a: "RIVM says no TBE vaccine is needed for living in the Netherlands. For travel to countries where TBE or dengue is common: ask LCR, your GP or a travel clinic. Vaccines exist for both.",
      },
      {
        q: "Is this medical advice?",
        a: "No. This is an explainer based on RIVM, the Health Council, ECDC and WHO. After a mosquito or tick bite with symptoms: see your GP.",
      },
    ],
    content: `## New infectious diseases in the Netherlands as the climate changes

Dengue sounds tropical. In European Netherlands it still is: a disease travellers bring home. In May 2026 the Health Council still warned: climate change is making conditions here more favourable for diseases spread by mosquitoes, ticks and water.

RIVM is more cautious. Climate is one factor. Trade, travel and how we live often matter more. A new mosquito in the Netherlands is not automatically the fault of a warmer summer.

This is information, not medical advice.

### Dengue: imported now, possibly local later

Dengue spreads through the yellow fever mosquito and the Asian tiger mosquito. WHO estimates hundreds of millions of infections a year worldwide. Fever, muscle pain, sometimes severe bleeding.

According to RIVM's LCI guideline, dengue in European Netherlands is an imported disease. No local infection has been confirmed so far. Positive tests in returning travellers did rise: on average 136 a year in 2014–2019, 367 in 2023 and 544 in 2024.

In the Caribbean Netherlands dengue does occur. RIVM reports several dozen cases a year and larger outbreaks in 2023–2024. The Health Council gives those islands priority, because the risks there are greater and more diverse.

Southern Europe already has local dengue. ECDC tracks cases in France, Italy, Spain and Croatia, among others. In 2026, through early August, only France had reported a locally acquired dengue case on the European mainland.

Dengue vaccines exist. In the Netherlands they are for travellers, not for the whole population. Ask LCR or a travel clinic.

### The tiger mosquito: control buys time

The Asian tiger mosquito (*Aedes albopictus*) can transmit dengue, chikungunya and Zika. It is well established around the Mediterranean. Not yet in the Netherlands, thanks to control by the Ministry of Health and NVWA.

RIVM is clear: more findings in the Netherlands come mainly from trade and travel, not from climate change. If the mosquito does become established, climate does matter. At higher summer temperatures, viruses multiply faster in mosquitoes.

The Health Council expects establishment at some locations within 2 to 5 years. After that, local dengue outbreaks can follow if a viraemic traveller brings the virus in.

In August 2026 NVWA published DNA research: the mosquito arrives through several independent introductions, including used tyres and airports. At some sites it survived the winter. Control can still wipe out a population.

### What is already here?

Not everything is future tense.

**Tick-borne encephalitis (TBE).** Since 2016 we know Dutch ticks can carry the virus, including on the Sallandse and Utrechtse Heuvelrug. Between 2016 and June 2024, 23 patients acquired the infection in the Netherlands. In 2024 there were 8 reports. Since 1 March 2025, TBE with neurological symptoms is notifiable. About 1 in 1,500 ticks in known areas carries the virus. For Lyme that is about 1 in 5. RIVM says: no TBE vaccine is needed for living in the Netherlands. Yes for travel to countries where TBE is common.

**West Nile fever.** In 2020, 8 people in the Netherlands acquired the virus, mainly around Utrecht, via the common house mosquito. The virus sits in birds. The Health Council says warmer summers favour transmission. The risk for most people stays small.

**Lyme.** Still the most common tick-borne disease here. More ticks in milder weather increase contact, the Health Council notes.

**Water.** Dutch coastal waters have become more suitable for *Vibrio*. Warmer surface water also helps Legionella and blue-green algae.

Chikungunya and Zika follow the same mosquito story as dengue: mainly a travel risk now, possibly local later if the tiger mosquito stays.

### What can you do?

- Travelling to areas with dengue or TBE: ask in time whether vaccination makes sense.
- In the Netherlands: check for ticks after time in green space, reduce evening mosquito bites, see a doctor if you get fever after a tropical trip — and say where you were.

Write down vaccinations and travel shots. With Vaxy you can see whether dengue or TBE is already in your record before you leave.

This article is information, not medical advice.

### Short answers

**Is dengue already here?**
In European Netherlands only in travellers. Yes in the Caribbean Netherlands. Local transmission already exists in southern Europe.

**Will the tiger mosquito settle?**
Not yet, because of control. The Health Council expects that at some sites within 2 to 5 years.

**Which diseases are already here?**
TBE since 2016, West Nile in people in 2020, Lyme for longer. Warmer water: Vibrio and Legionella.

**Should I get a dengue or TBE vaccine here?**
Usually not for living in European Netherlands. Sometimes yes for certain trips. Ask LCR, your GP or GGD.

### Sources

- [RIVM: climate change and infectious diseases](https://www.rivm.nl/klimaat-en-gezondheid/infectieziekten)
- [RIVM: dengue](https://www.rivm.nl/dengue)
- [LCI guideline on dengue](https://lci.rivm.nl/richtlijnen/dengue)
- [RIVM: West Nile fever](https://www.rivm.nl/westnijlkoorts)
- [RIVM: tick-borne encephalitis](https://www.rivm.nl/tekenencefalitis)
- [Health Council of the Netherlands: climate change and health, 21 May 2026](https://www.gezondheidsraad.nl/actueel/nieuws/2026/05/21/bescherm-de-bevolking-tegen-de-toenemende-gezondheidsrisicos-van-klimaatverandering)
- [ECDC: dengue risk, EU/EEA](https://www.ecdc.europa.eu/en/dengue/surveillance-and-updates/risk-assessment)
- [WHO: dengue](https://www.who.int/news-room/fact-sheets/detail/dengue-and-severe-dengue)
- [NVWA: DNA study of the Asian tiger mosquito, 20 August 2026](https://www.nvwa.nl/actueel/nieuws/2026/08/20/dna-onderzoek-biedt-nvwa-nieuw-inzicht-in-verspreiding-en-bestrijding-van-de-aziatische-tijgermug)
- [LCR: vaccination information](https://lcr.nl/informatie-vaccinaties)`,
  },
  {
    slug: "vaccine-boosters",
    date: "2026-08-22",
    category: "Health Tips",
    image: "/images/blog/vaccine-boosters.webp",
    author: "Vaxy",
    readTime: "8 min read",
    title: "Which vaccines need a booster, and how long does protection last?",
    excerpt:
      "Not every vaccine needs a repeat dose. RIVM, LCI and LCR explain when a booster is due, and how long you stay protected afterwards.",
    faq: [
      {
        q: "Which vaccines need a booster?",
        a: "Mainly DTP (every 10 years), typhoid (every 3 years), tick-borne encephalitis, Japanese encephalitis and cholera. Flu and COVID-19 for risk groups are yearly shots, not classic boosters.",
      },
      {
        q: "How often should I repeat DTP?",
        a: "Every 10 years after a complete series. The Dutch programme and LCR say you are then protected for 10 years against diphtheria and tetanus. After the childhood schedule you are probably protected for life against paralysis from polio.",
      },
      {
        q: "Do I need another hepatitis A shot later?",
        a: "Not if you have had two doses. LCR says: 1 year after the first dose, 50 years after the second. LCI says healthy people do not need a further dose.",
      },
      {
        q: "Is yellow fever valid for life?",
        a: "Usually yes. One dose protects for life in most people. The certificate has been valid for life since 2016. Some groups, such as people with HIV, still need a repeat dose.",
      },
      {
        q: "Is this medical advice?",
        a: "No. This is an explanation based on RIVM, LCI and LCR. If you are unsure, ask your GP, the public health service or a travel clinic.",
      },
    ],
    content: `## Which vaccines need a booster, and how long does protection last?

Not every vaccine needs a repeat dose. Some series last a long time, or for life, once they are complete. Others do need another shot later.

This overview is for healthy people in the Netherlands, based on the national immunisation programme (RVP), RIVM LCI guidelines and the Dutch travel health centre (LCR). It is information, not medical advice.

### What is a booster?

A booster is an extra dose after a complete primary series, because protection wanes.

That is different from:

- the second or third dose of a series, such as hepatitis A or hepatitis B
- a yearly shot, such as the flu vaccine, because the virus changes
- extra doses after a bite or a wound, such as rabies or tetanus

### Vaccines that do need a booster

**DTP (diphtheria, tetanus, polio).** Every 10 years after a complete series. Never vaccinated? Start with three doses. RVP says: after the DTP dose at age 14 you are protected for about 10 years against diphtheria and tetanus, and probably for life against paralysis from polio. LCR gives the same 10-year interval for travellers.

**Pertussis (whooping cough).** The acellular vaccine protects for about 4 to 6 years, according to LCI. Children get a booster at age 5. Adults have no fixed interval. If you are pregnant, RIVM advises a pertussis vaccine from week 22, in every pregnancy.

**Typhoid.** One dose protects for 3 years, says LCR. Protection is not complete. Repeat every 3 years if you are still at risk.

**Japanese encephalitis.** Two doses within a month give 2 years of protection. From one year later you can have a third dose. Then 10 years, according to LCR.

**Tick-borne encephalitis.** Three doses (months 0, 1 and 6). Then a booster after 3 years. After that every 5 years under 60, and every 3 years from age 60, according to LCI. For travellers aged 50 and over, LCR sometimes uses an extra primary dose.

**Cholera.** Ordinary travellers in the Netherlands usually do not get this. LCR says one vaccination protects for 6 months in children aged 2 to 5, and 2 years in older children and adults.

**Meningococcal ACWY.** One dose protects for at least 10 years, says LCR. For pilgrims to Mecca the certificate is valid for 5 years.

**Yellow fever.** Usually no booster. One dose protects for life in most people. The certificate has been valid for life since 1 July 2016. LCI does advise a repeat dose after vaccination in pregnancy, HIV, or laboratory work with the virus.

**Rabies.** Two doses before travel give lifelong basic protection, says LCI. No routine booster for travellers. After a bite, scratch or lick you always need extra doses.

**Flu.** Yearly, for people at extra risk. LCI says antibodies stay up for about 24 weeks. The vaccine wanes after about six months, and the virus changes.

**COVID-19.** An autumn round for risk groups. RIVM prefers 6 months after the previous dose, with a minimum of 3 months.

**Pneumococcal.** The older PPV23 vaccine is repeated every 5 years. After the newer conjugate vaccine PCV20, adults do not currently need a repeat dose, says LCI.

### Vaccines that do not need a booster after the series

**Hepatitis A.** Two doses, at least 6 months apart. After the first dose: 1 year. After the second: 50 years, according to the public LCR page. LCI says healthy people do not need another dose. Models point to 50 years, possibly lifelong.

**Hepatitis B.** Three doses. Then lifelong in principle, if your body responded well. A booster is then not needed. Some jobs still use a blood test.

**HPV.** Two doses 6 months apart, in the RVP around age 10. LCI still sees good protection after 12 years. A later booster is not currently indicated.

**MMR (measles, mumps, rubella).** Two doses. Then probably lifelong against measles and rubella. For mumps this is less clear. After two doses, 99% are protected, says RVP.

**Shingles.** Two doses of Shingrix. RIVM says protection lasts at least 6 to 10 years. There is no standard third dose yet.

**Dengue and chikungunya.** LCR says it is still unknown how long vaccination protects.

### Children: boosters in the national programme

Children already get repeat doses in the schedule. According to rijksvaccinatieprogramma.nl these include:

- 12 months: DKTP-Hib-HepB and pneumococcal
- 14 months: MMR and MenACWY
- 3 years: second MMR (some birth years later, via catch-up)
- 5 years: DKT
- 10 years: HPV, twice
- 14 years: DTP and MenACWY (DTP at 14 from birth year 2016)

After that, DTP is no longer in the childhood programme. For diphtheria and tetanus, a booster every 10 years remains wise, especially if you travel.

### What this means for your records

Note the date, the vaccine name, and whether the series is complete. Then you can see if you need a booster, or only the next dose of the same series.

Vaxy keeps that on one phone. You are less likely to miss DTP after 10 years, or the second hepatitis A dose.

If you are unsure, ask your GP, the public health service or a travel clinic. People with a weaker immune system, pregnant people and some jobs often have a different schedule.

### Short answers

**Which vaccines need a booster?**
DTP every 10 years, typhoid every 3 years, plus tick-borne encephalitis, Japanese encephalitis and cholera. Flu and COVID-19 for risk groups every year.

**How long does DTP last?**
10 years against diphtheria and tetanus. Polio after the childhood schedule is probably lifelong against paralysis.

**Do I need hepatitis A again later?**
Not after two doses. After the second dose, 50 years according to LCR.

**Is yellow fever lifelong?**
Usually yes. Sometimes an extra dose is needed, for example with HIV.

### Sources

- [Dutch immunisation programme: schedule](https://rijksvaccinatieprogramma.nl/vaccinaties/vaccinatieschema)
- [RVP: DTP vaccine](https://rijksvaccinatieprogramma.nl/vaccinaties/dtp)
- [RVP: MMR vaccine](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [LCR: information on vaccinations](https://lcr.nl/informatie-vaccinaties)
- [LCI: tetanus](https://lci.rivm.nl/richtlijnen/tetanus)
- [LCI: hepatitis A vaccination](https://lci.rivm.nl/factsheets/hepatitis-a-vaccinatie)
- [LCI: hepatitis B vaccination](https://lci.rivm.nl/factsheets/hepatitis-b-vaccinatie)
- [LCI: yellow fever](https://lci.rivm.nl/richtlijnen/gele-koorts)
- [LCI: TBE vaccination](https://lci.rivm.nl/factsheets/tbe-vaccinatie)
- [LCI: pertussis vaccination](https://lci.rivm.nl/factsheets/kinkhoestvaccinatie)
- [LCI: HPV vaccination](https://lci.rivm.nl/factsheets/hpv-vaccinatie)
- [LCI: pneumococcal vaccination](https://lci.rivm.nl/factsheets/pneumokokkenvaccinatie)
- [LCI: influenza vaccination](https://lci.rivm.nl/richtlijnen/influenzavaccinatie)
- [LCI: COVID-19 vaccination](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)
- [LCI: rabies](https://lci.rivm.nl/richtlijnen/rabies)
- [RIVM: COVID vaccination](https://www.rivm.nl/corona/coronaprik)
- [RIVM: shingles vaccination](https://www.rivm.nl/gordelroos/vaccinatie)
- [RIVM: vaccinations in pregnancy](https://www.rivm.nl/zwangerschap-en-infectieziekten/vaccinaties-tijdens-zwangerschap)`,
  },
  {
    slug: "how-mrna-vaccines-work",
    date: "2026-08-19",
    category: "Health Tips",
    image: "/images/blog/how-mrna-vaccines-work.webp",
    author: "Vaxy",
    readTime: "7 min read",
    title: "What are mRNA vaccines and how do they work?",
    excerpt:
      "An mRNA vaccine is a short message for your cells, not a live virus. WHO, CDC, EMA and RIVM explain how it works and why it does not change your DNA.",
    faq: [
      {
        q: "What is an mRNA vaccine?",
        a: "An mRNA vaccine is a shot with a short message for your cells. That message teaches your body to recognise a harmless piece of a virus. There is no live virus in it.",
      },
      {
        q: "How does an mRNA vaccine work?",
        a: "After the shot, your cells read the mRNA and make a harmless protein piece. Your immune system learns to recognise that piece. Then your body quickly breaks the mRNA down.",
      },
      {
        q: "Does an mRNA vaccine change your DNA?",
        a: "No. The CDC and EMA say the mRNA does not enter the cell nucleus, where your DNA is kept. It cannot change your genetic code.",
      },
      {
        q: "Can an mRNA vaccine give you COVID-19?",
        a: "No. The CDC says there is no live coronavirus in the vaccine. You cannot get COVID-19 from it.",
      },
      {
        q: "How long does the mRNA stay in your body?",
        a: "Not long. The EMA says your body breaks the mRNA down quickly, just like your own mRNA. In studies it fell within days.",
      },
    ],
    content: `## What are mRNA vaccines and how do they work?

An mRNA vaccine is a shot that teaches your body to recognise a disease. It does not contain a live virus. It contains a short message: mRNA.

mRNA means messenger RNA. Your body makes mRNA all day. That message tells a cell which protein to make.

In an mRNA vaccine, the message is made in a lab. It is about one harmless piece of the virus. For COVID-19, that is often the spike protein. Then your body clears the message quickly.

The World Health Organization (WHO) calls this a vaccine with a blueprint, instead of the virus itself.

### How does an mRNA vaccine work, step by step?

According to the US CDC, it works like this:

1. You get the shot in the upper arm.
2. The mRNA sits in tiny fat bubbles. Those help the message enter the cell.
3. Inside the cell, the body reads the message and makes a harmless protein piece.
4. Then the cell breaks the mRNA down. It leaves the body as waste.
5. The cell shows the protein piece to your immune system.
6. Your immune system makes antibodies and memory cells.
7. If you later meet the real virus, your body recognises it faster.

The CDC says it usually takes a few weeks for that protection to be ready.

### Does an mRNA vaccine change your DNA?

No. The CDC and the European Medicines Agency (EMA) both say this.

The mRNA does not enter the cell nucleus. That is where your DNA is. The message stays outside the nucleus, where your body always makes proteins.

mRNA is also chemically different from DNA. It cannot simply sit inside your DNA.

The EMA also says an mRNA vaccine is not gene therapy. The aim is not to repair or change your genes.

The vaccine cannot give you COVID-19. There is no live coronavirus in it, says the CDC.

### How long does the mRNA stay in your body?

Not long. The EMA says the body breaks the mRNA down quickly, just like your own mRNA.

In animal studies of Comirnaty (Pfizer/BioNTech), mRNA levels fell over 6 to 9 days. For Spikevax (Moderna), researchers found after 1 to 3 days that remaining mRNA was mainly at the injection site, in lymph nodes and in the spleen.

At approval, regulators did not expect a build-up in organs.

### Why are there fat bubbles in the vaccine?

mRNA is fragile. Without protection it falls apart quickly.

The EMA explains: tiny fat particles, lipid nanoparticles, help the mRNA enter the cell. The cell wall is also made of fat. The bubbles fuse with it.

This kind of fat particle in medicines is not new. The first medicine with these bubbles was authorised in the EU in 2018.

### Are mRNA vaccines new?

COVID-19 shots were new for many people. The technology was not.

The EMA says researchers had already worked with mRNA for about twenty years, for infectious diseases and for cancer. The CDC mentions earlier studies for flu, Zika, rabies and CMV.

mRNA is relatively small and simple to make. That is why manufacturers could scale up faster than with classic vaccines. Safety tests were not skipped, says the CDC.

### Which mRNA vaccines exist?

The best known in Europe are:

- Comirnaty from Pfizer/BioNTech
- Spikevax from Moderna

They are authorised by the EMA. The Dutch institute RIVM uses COVID-19 vaccines in its autumn programme, mainly for people at higher risk. In 2026, people aged 70 and over, and people with extra medical risk, can get a COVID-19 shot.

There are also other types of COVID-19 vaccines, such as protein vaccines. Those already contain a protein piece. RIVM says that can be an option if you cannot or do not want an mRNA vaccine for medical reasons.

The RIVM guideline puts it simply: Comirnaty is an mRNA vaccine. It contains no live virus. The mRNA codes for the spike protein.

mRNA is also being studied for other diseases, and for some cancers. That is still research. A COVID-19 mRNA vaccine is not the same as a personal cancer vaccine.

### What are the side effects?

The WHO says mild to moderate symptoms can happen. That is a sign your body is building protection.

Common ones:

- pain or redness at the injection site
- tiredness
- headache
- muscle ache
- chills
- fever

Most symptoms go away within a few days. Not everyone gets side effects.

Serious side effects are rare. The EMA says the safety of authorised COVID-19 vaccines is reassuring, after hundreds of millions of doses.

A known rare side effect of mRNA vaccines is inflammation of the heart muscle or the lining around the heart. Doctors call this myocarditis or pericarditis. RIVM calls this rare. It happens mostly in young men, shortly after the shot. Most people recover. See a doctor if you get chest pain, shortness of breath or a fast heartbeat after the shot.

### Do mRNA vaccines still protect?

Yes, especially against severe disease and death. No vaccine works 100%.

The WHO says protection is highest in the first months. After about six months it starts to fall. That is why people at higher risk sometimes get another shot.

You can still get COVID-19 after vaccination. The chance of becoming very ill is then usually lower.

### What does this mean for your vaccination record?

Write down which shot you had, the date, and the vaccine name, such as Comirnaty or Spikevax. That helps for a later dose, for travel, or if a doctor asks.

With Vaxy you keep those details in one place, for yourself and your family.

This article is information, not medical advice. If you are unsure, ask your doctor or local public health service.

### Short answers

**What is an mRNA vaccine?**
A shot with a short message for your cells. No live virus.

**How does it work?**
Your cells make a harmless protein piece. Your immune system learns it. Then the mRNA disappears.

**Does it change your DNA?**
No. The CDC and EMA say the mRNA does not reach your DNA in the cell nucleus.

**Can you get COVID-19 from it?**
No. There is no live coronavirus in it.

**How long does the mRNA stay in your body?**
Not long. The EMA says your body breaks it down quickly.

### Sources

- [WHO: how vaccines work](https://www.who.int/news-room/feature-stories/detail/how-do-vaccines-work)
- [WHO: COVID-19 vaccines Q&A, 15 May 2026](https://www.who.int/news-room/questions-and-answers/item/coronavirus-disease-(covid-19)-vaccines)
- [CDC: COVID-19 Vaccine Basics, how mRNA vaccines work](https://www.cdc.gov/covid/vaccines/how-they-work.html)
- [EMA: COVID-19 vaccines key facts, including mRNA](https://www.ema.europa.eu/en/human-regulatory-overview/public-health-threats/coronavirus-disease-covid-19/covid-19-medicines/covid-19-vaccines-key-facts)
- [EMA: Comirnaty (Pfizer/BioNTech)](https://www.ema.europa.eu/en/medicines/human/EPAR/comirnaty)
- [EMA: Spikevax (Moderna)](https://www.ema.europa.eu/en/medicines/human/EPAR/spikevax)
- [RIVM: COVID-19 vaccination](https://www.rivm.nl/covid-19-vaccinatie)
- [RIVM LCI guideline on COVID-19 vaccination](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)`,
  },
  {
    slug: "us-green-card-vaccination-records",
    date: "2026-08-19",
    category: "Travel Health",
    image: "/images/blog/us-green-card-vaccination-records.webp",
    author: "Vaxy",
    readTime: "7 min read",
    title: "How Vaxy helps with US green card vaccination requirements",
    excerpt:
      "A US green card medical exam includes a vaccination check. USCIS says to bring written proof of your shots. Here is how a digital record in Vaxy can help, with sources.",
    faq: [
      {
        q: "Is Vaxy a green card?",
        a: "No. A green card is a residence permit. Vaxy is a digital vaccination record. USCIS asks you to bring written proof of your shots to the immigration medical exam.",
      },
      {
        q: "Do tourists need this to enter the United States?",
        a: "No. This vaccination check is for people applying for an immigrant visa or adjustment of status to permanent resident. Form I-693 is not needed for an ordinary holiday.",
      },
      {
        q: "Can Vaxy replace Form I-693?",
        a: "No. Only a designated civil surgeon in the United States, or a panel physician abroad, can complete the vaccination assessment on Form I-693.",
      },
      {
        q: "Is a COVID-19 vaccine still required for a green card?",
        a: "No. As of 20 January 2025, you no longer need to show a COVID-19 vaccination for adjustment of status.",
      },
    ],
    content: `## How Vaxy helps with US green card vaccination requirements

A US green card is not a vaccination booklet. It is a residence permit: lawful permanent residence.

If you apply for that status, you must be able to show certain vaccinations. US immigration law says you can be found inadmissible without that proof. USCIS is clear: take any written vaccination documentation to the civil surgeon.

That is where Vaxy helps. Vaxy is not a green card and not an official USCIS form. It is your vaccination record, always on your phone, ready for the exam.

### Who this applies to

This applies if you:

- apply for an immigrant visa from abroad, or
- apply to adjust status to permanent resident while in the United States, often with Form I-485

An ordinary holiday or short visit does not need this exam. The rules below are for green card applicants.

### Why proof of vaccinations matters

Under Immigration and Nationality Act (INA) section 212(a)(1)(A)(ii), a person who cannot show proof of the required vaccinations can be refused admission or adjustment of status.

In the United States, a designated civil surgeon does the exam. Abroad, a panel physician designated by the US Department of State does it. The results go on Form I-693, Report of Medical Examination and Vaccination Record.

USCIS says you do not have to get shots again if you already have proof. Take your records with you.

### Which vaccines are required

Immigration law lists, among others:

- Mumps
- Measles
- Rubella
- Polio
- Tetanus and diphtheria
- Pertussis (whooping cough)
- Haemophilus influenzae type B (Hib)
- Hepatitis B
- Other vaccines recommended by ACIP and required by CDC for immigration

CDC also requires, when they match your age, protection against:

- Varicella (chickenpox)
- Hepatitis A
- Meningococcal disease
- Pneumococcal disease
- Rotavirus, for young infants
- Seasonal influenza, during flu season

You do not need every vaccine on the list. Only the ones that are age-appropriate for you. The civil surgeon follows CDC's Technical Instructions, including a table by age.

**COVID-19:** As of 20 January 2025, a COVID-19 vaccine is no longer required for adjustment of status.

**Polio:** From 1 May 2024, anyone 2 months and older who is not up to date should get one dose of IPV. That includes adults without proof of the childhood series.

**Flu:** For the immigration exam, the flu vaccine is required from 1 September to 31 March, if it is available.

### What to bring to the exam

USCIS says it is important to take any written vaccination documentation. If a doctor has tested you for immunity, bring that proof too.

If something is missing, the civil surgeon can give the vaccine. You can also get missing shots from your own doctor, then return with proof. Only a civil surgeon can complete the vaccination part of Form I-693.

You usually only need one dose of each required vaccine at the time of the exam. You do not have to finish a long series before the form can be signed.

### How Vaxy helps

Paper booklets get lost. Shots sit in different countries, languages and files.

With Vaxy you can:

- Keep every vaccination in one place, with dates
- Add family members who also apply for a green card
- Open your record on your phone at the appointment
- See what you already have before you book, so you repeat fewer shots

Show the civil surgeon your Vaxy record, together with any paper proof you still have. The civil surgeon decides what counts.

### What Vaxy is not

Vaxy is not a green card, not Form I-693, and not a substitute for the immigration medical examination. USCIS will not accept an incomplete vaccination chart. Only a designated civil surgeon or panel physician can certify the form.

This article is information, not legal or medical advice. Rules can change. Check USCIS and CDC before your appointment.

### Quick answers

**Is Vaxy a green card?**
No. It is a digital vaccination record you take to the exam.

**Does this apply to tourists?**
No. This applies to people applying for a green card.

**Can Vaxy replace Form I-693?**
No. Only a civil surgeon or panel physician can complete that form.

**Is COVID-19 still required?**
No, not since 20 January 2025.

### Sources

- [USCIS, Vaccination Requirements](https://www.uscis.gov/tools/designated-civil-surgeons/vaccination-requirements)
- [CDC, Vaccination Technical Instructions for Civil Surgeons](https://www.cdc.gov/immigrant-refugee-health/hcp/civil-surgeons/vaccination.html)
- [USCIS Policy Manual, Volume 8, Part B, Chapter 9](https://www.uscis.gov/policy-manual/volume-8-part-b-chapter-9)
- [Form I-693, Report of Medical Examination and Vaccination Record](https://www.uscis.gov/i-693)
- [42 C.F.R. Section 34.3](https://www.ecfr.gov/current/title-42/chapter-I/subchapter-C/part-34/section-34.3)`,
  },
  {
    slug: "vaccines-do-not-cause-autism",
    date: "2026-08-19",
    category: "Health News",
    image: "/images/blog/vaccines-do-not-cause-autism.webp",
    author: "Vaxy",
    readTime: "6 min read",
    title: "Do vaccines cause autism? What the science says",
    excerpt:
      "Vaccines do not cause autism. WHO, RIVM and large studies find no evidence. Here is where the myth came from, and what parents need to know.",
    faq: [
      {
        q: "Do vaccines cause autism?",
        a: "No. The WHO, the Dutch RIVM and large studies in many countries find no link. Vaccines do not cause autism.",
      },
      {
        q: "Does the MMR vaccine cause autism?",
        a: "No. The MMR vaccine protects against measles, mumps and rubella. Large studies, including one in Denmark with more than 650,000 children, find no link with autism.",
      },
      {
        q: "Where did the claim that vaccines cause autism come from?",
        a: "From a 1998 paper by Andrew Wakefield. Only 12 children took part. The paper was later taken back. The data were not true, and Wakefield was no longer allowed to work as a doctor.",
      },
      {
        q: "Is it safer to split the MMR vaccine into three separate shots?",
        a: "No. There is no evidence that splitting is safer. Your child then needs more visits and stays unprotected against measles for longer.",
      },
      {
        q: "What do the WHO and RIVM say about vaccines and autism?",
        a: "Both say the same thing: vaccines do not cause autism. The WHO confirmed this again in December 2025. The RIVM says it on its MMR page and on its page about doubts about vaccination.",
      },
    ],
    content: `## Do vaccines cause autism? What the science says

Vaccines do not cause autism. That is the clear conclusion of the World Health Organization (WHO), the Dutch National Immunisation Programme at RIVM, and large studies in many countries.

Vaxy agrees with this. Parents deserve clear information, not fear.

### What is in the news?

On 11 August 2026, US President Donald Trump signed an order about childhood vaccines. He repeated the old claim that vaccines cause autism. He also wants the MMR shot against measles, mumps and rubella to be split into separate shots.

Doctors and health groups say this order is dangerous. It is not science. In the United States, states decide which shots schools ask for, not the president. So the order is advice, not a law. It can still make parents feel unsure.

### Where did this myth come from?

In 1998, the British doctor Andrew Wakefield published a paper. He wrote that the MMR shot could cause autism. Only 12 children took part. Later it became clear that the data were not true. Wakefield had also received money from lawyers who wanted to sue over vaccines.

The medical journal The Lancet took the paper back. That means: the paper must not be used as evidence. After that, Wakefield was no longer allowed to work as a doctor.

Since 1998, many large and careful studies have been done. They find no link between vaccines and autism.

### What do large studies say?

A well-known study from Denmark in 2019 followed 657,461 children. Researchers at Statens Serum Institut asked if the MMR shot caused autism. The answer was no. This was also true for children with a higher risk, for example because a brother or sister has autism.

In 2025 a new Danish study looked at aluminium in vaccines. Small amounts of aluminium salts are in some vaccines. They help the body respond better to the shot. Researchers followed more than 1 million children. They found no link with autism, and no link with 49 other health problems.

RIVM collects this kind of research on its own page. The conclusion is always the same: vaccination does not cause autism.

### What does the WHO say?

In December 2025, a WHO expert group looked at the science again. The group is called GACVS. They reviewed 31 studies from 2010 to 2025, from many countries.

Their conclusion: vaccines do not cause autism. This includes vaccines with thiomersal, a preservative, or with a small amount of aluminium.

The WHO said this before, in 2002, 2004 and 2012. The new check says the same thing again.

The WHO also says: childhood vaccines have saved at least 154 million lives in 50 years.

### What does the Dutch immunisation programme say?

The Dutch National Immunisation Programme protects children against serious diseases, such as measles, mumps, rubella, whooping cough and polio.

The RIVM page about the MMR shot is clear: you do not get autism from the MMR vaccine. You also do not get diabetes or bowel disease from it.

In the Netherlands, children get the MMR shot twice: around 14 months and around 3 years. After the first shot, about 95% of children are protected. After the second shot, about 99% are protected.

RIVM also says: doubt is understandable. Ask your child health clinic or your doctor. Use trusted sources, not only social media.

### Is it safer to split the MMR shot?

No. The WHO, RIVM and the US CDC say there is no evidence that separate shots are safer.

If you split the shots, your child needs more visits. Between those visits, your child is unprotected for longer. Measles spreads very easily. A child can get ill before all shots are done.

The MMR shot is one small dose. It is a few drops. It is not a large amount of liquid in the body.

### Why do children get more shots now than in the past?

Children now get protection against more diseases. That is progress, not danger. Combination shots mean fewer jabs in the arm, not more.

The schedule follows the age when the immune system responds best, and when children face the highest risk.

Skipping or delaying shots lowers that protection. Measles returns more often when fewer children are vaccinated.

### What can parents do?

1. Follow the national immunisation programme in your country.
2. Ask a doctor or child health clinic if you have doubts.
3. Use sources such as the WHO and RIVM, not anonymous posts.
4. Keep a record of your child's shots, so you miss none.

With Vaxy you keep all family vaccinations in one place. You can see what has been given, and you get a reminder for the next shot.

This article is information, not medical advice. If you worry about your child's health, talk to a doctor.

### Quick answers

**Do vaccines cause autism?**
No. WHO, RIVM and large studies find no link.

**Does the MMR shot cause autism?**
No. Large studies of hundreds of thousands of children find no link.

**Where did the myth come from?**
From a 1998 paper that was later taken back. The data were not true.

**Is splitting the MMR shot safer?**
No. There is no evidence for this. Your child then stays unprotected for longer.

**What do WHO and RIVM say?**
Vaccines do not cause autism. The WHO confirmed this again in 2025.

### Sources

- [WHO: vaccines do not cause autism, 11 December 2025](https://www.who.int/news/item/11-12-2025-who-expert-group-s-new-analysis-reaffirms-there-is-no-link-between-vaccines-and-autism)
- [WHO GACVS statement on vaccines and autism](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [RIVM: research on vaccination and autism](https://rijksvaccinatieprogramma.nl/over-vaccineren/onderzoek-vaccins/onderzoek-verband-vaccineren-autisme)
- [RIVM: vaccination does not cause autism](https://rijksvaccinatieprogramma.nl/over-vaccineren/twijfels)
- [RIVM: MMR vaccination](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [Hviid et al., MMR vaccination and autism, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [Statens Serum Institut: no link between aluminium in vaccines and autism, 2025](https://en.ssi.dk/news/news/2025/large-danish-study-no-link-between-vaccines-and-autism-or-49-other-health-conditions)
- [The Lancet retracts the Wakefield paper, 2010](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(10)60175-4/fulltext)
- [CDC: Vaccines Do Not Cause Autism](https://www.cdc.gov/vaccine-safety/about/autism.html)
- [BBC: Trump signs order on childhood vaccines, 11 August 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)`,
  },
  {
    slug: "childhood-vaccines-trump-order",
    date: "2026-08-19",
    category: "Health News",
    image: "/images/blog/childhood-vaccines-trump-order.webp",
    author: "Vaxy",
    readTime: "8 min read",
    title: "Why childhood vaccines still matter after Trump's new order",
    excerpt:
      "Trump wants fewer childhood vaccines and to split the MMR shot. Here is why those vaccines exist, what the science says, and what can happen if fewer children are protected.",
    faq: [
      {
        q: "Does this US order change childhood vaccines in Europe?",
        a: "No. It is guidance in the United States, not a law. School shot rules are set by US states. National programmes in Europe, including the Dutch Rijksvaccinatieprogramma, do not change because of this order.",
      },
      {
        q: "Why do children get so many vaccines?",
        a: "Because children can now be protected against more serious diseases. The schedule follows the age when the immune system responds best, and when children are most at risk.",
      },
      {
        q: "Is it safer to split the MMR vaccine into three shots?",
        a: "No. There is no evidence that splitting it is safer. Your child would need more appointments and would stay unprotected against measles, mumps and rubella for longer.",
      },
      {
        q: "What can happen if fewer children are vaccinated?",
        a: "Diseases such as measles, whooping cough and polio can return. Measles spreads very easily. Outbreaks are already growing in places where vaccination rates have fallen.",
      },
    ],
    content: `## Why childhood vaccines still matter after Trump's new order

On 11 August 2026, US President Donald Trump signed an order on childhood vaccines. He wants fewer shots for all children, and he wants the combined measles, mumps and rubella (MMR) vaccine split into separate shots.

Doctors have called this dangerous. The order is guidance, not a law. Still, it can make parents unsure, and it can push vaccination rates down. This article explains why childhood vaccines exist, and what could happen next.

### What does the order say?

According to the [BBC](https://www.bbc.com/news/articles/ce3q5vl581wo), Trump wants the number of diseases with a shot for all US children to fall from 18 to 11. The 11 diseases on his list are measles, mumps, rubella, diphtheria, tetanus, pertussis (whooping cough), polio, Hib, pneumococcal disease, HPV and chickenpox.

Other vaccines, such as hepatitis B and meningococcal disease, would stay only for children at higher risk. Flu and COVID-19 shots would become a choice between parent and doctor.

Trump also wants vaccines given at separate visits, and the combined MMR shot split.

School vaccine rules stay with the states. Parents in the US cannot simply ask for three separate MMR shots today. Those separate products are not currently licensed in the United States.

### Why childhood vaccines exist

Childhood vaccines protect against diseases that used to make many children very ill, or kill them. Measles can cause pneumonia, brain inflammation and deafness. Whooping cough is especially dangerous for babies. Polio can cause paralysis. Hib can cause meningitis. Hepatitis B can later cause liver cancer.

The WHO says vaccines have saved more than 150 million lives over 50 years. Measles vaccination alone prevented an estimated 59 million deaths between 2000 and 2024.

Children get shots at set ages, not at random. Doctors choose the moment when the immune system responds best, and when the child is most at risk. The American Academy of Pediatrics (AAP) says there is no medical reason to delay or skip recommended immunisations.

Children are now protected against more diseases than in the past. That is progress. Combination vaccines mean fewer needles, not more.

### Community protection is not optional

Some children cannot be vaccinated. Babies may be too young. Other children have a weak immune system. They depend on the people around them.

For measles, at least 95% of children need two doses to stop an outbreak. If that number falls, the virus finds a path quickly. Measles is often the first disease to return when vaccination rates drop.

In 2023 there were an estimated 10.3 million measles cases worldwide. WHO, UNICEF and Gavi warn that outbreaks of measles, meningitis and yellow fever are rising. In the US, measles cases have reached a 35-year high, according to recent reporting.

### What could this order lead to?

**More missed doses.** Extra appointments sound careful. In real life, families come back less often. A missed dose means a longer stretch with no protection.

**Longer time without measles protection.** Separate shots leave gaps. In those gaps a child can catch measles, mumps or rubella. The CDC says there is no published scientific evidence that splitting the combined MMR vaccine helps. The combined shot has been used since 1971 and works as well as three separate shots.

**Diseases dropped from the "for every child" list.** A hepatitis B shot at birth helps prevent a lifelong infection. Rotavirus vaccines prevent severe diarrhoea in babies. If those shots are no longer advised for all children, more children will get those diseases.

**More confusion, lower uptake.** Trump repeated the claim that vaccines cause autism. That claim has been disproved. If parents delay shots because they are unsure, outbreaks become more likely. Senator Bill Cassidy, a physician, called the order "wrong". The president of the AAP called it "dangerous".

**Not a law, but a signal.** States still set school rules. Federal advice still shapes doctors, insurers and parents. Weaker recommendations often mean fewer vaccinations.

### Do vaccines cause autism?

No. The WHO confirmed this again in December 2025. A 2019 Danish study followed 657,461 children and found no link between the MMR vaccine and autism. We explain that myth in [Vaccines do not cause autism](/en/blog/vaccines-do-not-cause-autism).

### What this means if you live outside the US

This order does not change national vaccine programmes in Europe, including the Dutch Rijksvaccinatieprogramma.

Measles does not stop at a border. Travel to places with outbreaks, or contact with travellers, is still a reason to stay on schedule. Check your own shots, and your children's, before you go.

### What you can do

1. Follow the national schedule in your country, not a political order from another country.
2. Ask a doctor if you have questions.
3. Do not delay shots "to be safe". Delay is extra risk.
4. Keep a record of the shots your child has already had.

With Vaxy you can keep every family vaccination in one place. You can see what has been given, and get a reminder for the next shot.

This article is information, not medical advice. If you are worried about your child's health, talk to a doctor.

### Quick answers

**Does this change vaccines in Europe?**
No. It is US guidance, not a European rule.

**Why so many childhood vaccines?**
Because they protect against more serious diseases, at the ages when that protection is needed most.

**Is splitting MMR safer?**
No. There is no evidence for that. Your child would stay unprotected for longer.

**What if fewer children get vaccinated?**
Measles and other diseases come back. Outbreaks are already growing.

### Sources

- [BBC: Trump signs order on childhood vaccines, 11 August 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)
- [CIDRAP: Trump signs order to break up the MMR vaccine](https://www.cidrap.umn.edu/trump-signs-executive-order-break-measles-mumps-rubella-vaccine)
- [WHO, UNICEF and Gavi: vaccine-preventable outbreaks are rising, 24 April 2025](https://www.who.int/news/item/24-04-2025-increases-in-vaccine-preventable-disease-outbreaks-threaten-years-of-progress--warn-who--unicef--gavi)
- [WHO: measles fact sheet](https://www.who.int/news-room/fact-sheets/detail/measles)
- [WHO: measles deaths down, cases surge, 28 November 2025](https://www.who.int/news/item/28-11-2025-measles-deaths-down-88--since-2000--but-cases-surge)
- [WHO GACVS: vaccines do not cause autism](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [Hviid et al., MMR vaccination and autism, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [RIVM: MMR vaccination](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)`,
  },
  {
    slug: "moderna-mrna-melanoma-vaccine",
    date: "2026-08-19",
    category: "Health News",
    image: "/images/blog/moderna-mrna-melanoma-vaccine.webp",
    author: "Vaxy",
    readTime: "6 min read",
    title: "Moderna mRNA cancer vaccine succeeds in large melanoma trial",
    excerpt:
      "Moderna and Merck say a personal mRNA vaccine plus Keytruda helped stop melanoma coming back after surgery. Here is what the Phase 3 study means, in plain language.",
    faq: [
      {
        q: "Is this mRNA cancer vaccine a shot that prevents cancer?",
        a: "No. It is a personal treatment for people who already had melanoma. Doctors give it after surgery to lower the chance that the cancer comes back.",
      },
      {
        q: "Is the Moderna melanoma vaccine the same as the COVID-19 vaccine?",
        a: "No. Both use mRNA, but this cancer vaccine is made for one person's tumour. A COVID-19 vaccine is the same for many people and targets a virus.",
      },
      {
        q: "Can you get the Moderna mRNA melanoma vaccine now?",
        a: "Not yet. It is not approved for general use. Moderna and Merck plan to ask health authorities for approval in the coming months.",
      },
      {
        q: "Did the vaccine work better than Keytruda alone?",
        a: "Yes, in this Phase 3 study. People who got the vaccine plus Keytruda stayed free of cancer for longer, and cancer was less likely to spread. Full numbers will be shared at a medical meeting.",
      },
    ],
    content: `## Moderna mRNA cancer vaccine succeeds in large melanoma trial

On 19 August 2026, Moderna and Merck shared good news. Their personal mRNA cancer vaccine, used together with the cancer drug Keytruda, worked in a large study. The vaccine helped people with melanoma stay free of cancer for longer after surgery.

This is the first large Phase 3 study to show that an mRNA cancer vaccine can work. It is also the first time this kind of personal vaccine did better than Keytruda alone after melanoma surgery.

### What is the Moderna mRNA cancer vaccine?

The treatment is called intismeran autogene. You may also see the names V940 or mRNA-4157.

It is not a vaccine that healthy people get to prevent cancer. It is a treatment for people who already had melanoma, after doctors removed the tumour with surgery.

Doctors call this adjuvant treatment. That means extra treatment after surgery, to lower the chance that cancer comes back.

### How does a personal mRNA cancer vaccine work?

Melanoma is a serious form of skin cancer. Each tumour has its own set of changes, called mutations. You can think of this as a unique fingerprint.

The vaccine is made for one person:

1. Doctors take a sample of the tumour.
2. Scientists read the fingerprint of that tumour.
3. They make an mRNA vaccine that teaches the immune system to find those cancer marks.
4. After the shot, the body reads the mRNA and makes small protein pieces. The immune system then learns to attack cancer cells with the same marks.

mRNA is a short message that cells can read. COVID-19 vaccines also use mRNA, but this cancer vaccine is different. It is made for one patient and one tumour.

The vaccine is given together with Keytruda (pembrolizumab). Keytruda is an immunotherapy drug. It helps the immune system notice and fight cancer cells.

### What did the Phase 3 melanoma trial find?

The study is called INTerpath-001. A Phase 3 trial is a large, late study. Companies often use this kind of study before they ask health authorities to approve a treatment.

At a planned check of the data, the study met two main goals:

- People stayed free of cancer for longer. Cancer was less likely to come back.
- Cancer was less likely to spread to other parts of the body.

The companies say the difference is real, not chance, and that it matters for patients. The study is still running. Researchers will also look at how long people live.

Full numbers will be shared at a medical meeting. Moderna and Merck also plan to talk with regulators about approval.

### Who took part in the study?

The trial included 1,137 people with high-risk melanoma. Their cancer was stage 2B, 2C, 3 or 4. Surgery had already removed the cancer.

People were split into two groups at random:

- About two out of three people got the personal mRNA vaccine plus Keytruda
- About one out of three people got Keytruda alone

Treatment lasted about one year. People could get up to nine doses of the vaccine.

### Is the mRNA cancer vaccine safe?

So far, the companies report no new safety problems. Earlier studies found common side effects such as:

- Feeling tired
- Pain where the shot was given
- Chills

Keytruda can also cause immune side effects. People in a trial are watched closely.

### What did earlier studies show?

In a smaller Phase 2 study, the same combination lowered the risk of cancer coming back or death by 49% after five years, compared with Keytruda alone. It also lowered the risk of cancer spreading far away, or death, by 59%.

The new Phase 3 study is larger. This is the kind of study that regulators use when they decide if a treatment can be approved.

### Can you get this vaccine now?

Not yet. This treatment is not approved for general use.

Moderna says the companies want to ask for approval within months. There is no exact date yet.

If it is approved, it would first be for people with high-risk melanoma after surgery, not for everyone.

### Could this help other cancers?

Merck and Moderna are also testing this idea in other cancers, including lung cancer, bladder cancer and kidney cancer.

Those studies are still ongoing. Good results in melanoma do not automatically mean it will work for every cancer.

### What this news means for you

If you do not have melanoma, you do not need this vaccine. The best ways to lower melanoma risk are still simple:

- Protect your skin from the sun
- Avoid tanning beds
- Check your skin and see a doctor if a mole changes

If you or someone close to you has had melanoma, this news is hopeful. Talk with a cancer doctor. They can explain current treatment and whether a clinical trial might be an option.

This article is information, not medical advice.

### Quick answers

**Is this a vaccine that prevents cancer?**
No. It is a personal treatment after surgery for melanoma.

**Is it the same as the COVID-19 vaccine?**
No. Both use mRNA, but this vaccine is made for one person's tumour.

**Is it approved?**
Not yet. The companies plan to ask health authorities soon.

**Did it work better than Keytruda alone?**
Yes, in this Phase 3 study. Full details will be presented at a medical meeting.

### Sources

- [Merck news release, 19 August 2026](https://www.merck.com/news/merck-and-moderna-announce-phase-3-interpath-001-trial-of-intismeran-autogene-plus-keytruda-met-endpoints-of-recurrence-free-survival-rfs-and-distant-metastasis-free-survival-dmfs-in-patient/)
- [CNN Health, 19 August 2026](https://edition.cnn.com/2026/08/19/health/moderna-merck-mrna-melanoma-vaccine)`,
  },
  {
    slug: "why-vaccination-records-matter",
    date: "2026-06-10",
    category: "Health Tips",
    image: "/images/blog/why-vaccination-records-matter.webp",
    author: "Vaxy",
    readTime: "4 min read",
    title: "Why Your Vaccination Records Matter More Than Ever",
    excerpt:
      "In an increasingly connected world, having instant access to your vaccination history can be the difference between a smooth trip and a stressful border crossing.",
    content: `## Why Your Vaccination Records Matter More Than Ever

In an increasingly connected world, having instant access to your vaccination history can be the difference between a smooth trip and a stressful border crossing.

### The Problem with Paper

For decades, the yellow WHO vaccination booklet has been the gold standard for travel health documentation. But paper gets lost, fades, or gets left at home, right when you need it most. Many travelers have experienced the anxiety of being asked for proof of a yellow fever vaccination at customs, only to realize their booklet is sitting on their kitchen counter.

### What Can Go Wrong Without Records

- **Denied entry** at borders requiring proof of specific vaccinations
- **Repeated vaccinations** because doctors have no access to your history
- **Missed boosters** because there's no reminder system in place
- **Emergency situations** where medical staff don't know your immunization status

### The Digital Alternative

Apps like Vaxy solve this by giving you a secure, always-available digital record of every vaccination, for yourself and your entire family. No more paper, no more guessing.

### Travel Health Requirements Are Changing

Countries update their vaccination entry requirements frequently. Yellow fever, meningitis and COVID-19 requirements have all shifted dramatically in recent years. Having an up-to-date digital record means you can quickly check what you have and what you need before booking your next trip.

### Getting Started

The best time to digitize your vaccination records is before you need them. Take 10 minutes today to log your vaccinations in Vaxy, and you'll never scramble for a paper booklet again.`,
  },
  {
    slug: "travel-vaccinations-guide",
    date: "2026-05-28",
    category: "Travel Health",
    image: "/images/blog/travel-vaccinations-guide.webp",
    author: "Vaxy",
    readTime: "7 min read",
    title: "The Complete Guide to Travel Vaccinations in 2026",
    excerpt:
      "Planning a trip abroad? Here's everything you need to know about which vaccines are required, recommended, and how far in advance to get them.",
    content: `## The Complete Guide to Travel Vaccinations in 2026

Planning a trip abroad? Here's everything you need to know about which vaccines are required, recommended, and how far in advance to get them.

### Start Planning 6-8 Weeks Ahead

The biggest mistake travelers make is waiting until the last minute. Some vaccines require multiple doses spread over weeks, and your immune system needs time to build protection. Book a travel health consultation at least 6-8 weeks before departure.

### Required vs. Recommended

**Required** vaccinations are legally mandated for entry into certain countries. The most common example is yellow fever: many countries in Africa and South America require proof of vaccination if you're traveling from an endemic region.

**Recommended** vaccinations are not legally required but are strongly advised based on your destination, activities and health status.

### Common Travel Vaccines by Region

**Africa**
- Yellow Fever, required for many countries
- Typhoid
- Hepatitis A & B
- Malaria prophylaxis, not a vaccine but essential
- Meningitis, especially for Hajj pilgrims

**Southeast Asia**
- Hepatitis A & B
- Typhoid
- Japanese Encephalitis for rural areas
- Rabies for long stays or animal exposure

**South America**
- Yellow Fever
- Typhoid
- Hepatitis A & B

**Europe & North America**
- Usually only routine vaccinations needed
- Tick-borne encephalitis for forested areas of Central and Eastern Europe

### Routine Vaccinations: Don't Forget These

Before worrying about exotic travel vaccines, make sure your routine vaccinations are up to date:
- MMR
- Tetanus, Diphtheria and Pertussis
- Influenza
- COVID-19

### Using Vaxy for Travel Planning

Vaxy's travel health feature lets you select your destination and instantly see which vaccines are required or recommended. It checks your existing records and highlights exactly what you're missing, saving you time and ensuring you're properly protected.`,
  },
  {
    slug: "vaccinating-your-children",
    date: "2026-05-14",
    category: "Family Health",
    image: "/images/blog/vaccinating-your-children.webp",
    author: "Vaxy",
    readTime: "5 min read",
    title: "Keeping Track of Your Children's Vaccination Schedule",
    excerpt:
      "Children receive dozens of vaccines in their first years of life. Here's how to stay organized and never miss a critical dose.",
    content: `## Keeping Track of Your Children's Vaccination Schedule

Children receive dozens of vaccines in their first years of life. Here's how to stay organized and never miss a critical dose.

### The Childhood Vaccination Schedule

In the Netherlands and most of Europe, children follow a national vaccination program, the Rijksvaccinatieprogramma, that covers vaccinations from birth through adolescence. The schedule includes protection against:

- DKTP
- Hib
- Hepatitis B
- Pneumococcal disease
- Meningococcal disease
- MMR
- HPV for girls and boys around age 10

### Why Missing a Dose Matters

Vaccines work best when given at the right age. The schedule is designed so that children are protected before they're most vulnerable to specific diseases. Missing a dose doesn't just affect your child, it can reduce community immunity and put vulnerable people at risk.

### The Challenge for Parents

With so many vaccines at different ages, it's easy to lose track, especially when you have multiple children. Each child has their own schedule, and keeping it all organized in paper booklets across different doctors' visits is genuinely difficult.

### How Vaxy Helps Families

With Vaxy, you can add all your family members under one account. Each family member has their own complete vaccination record, and you get reminders when upcoming vaccines are due.

### Tips for Staying on Schedule

1. **Register at birth**: add your newborn to Vaxy as soon as possible
2. **Sync with your GP**: ask your doctor to confirm records after each visit
3. **Enable reminders**: turn on push notifications for upcoming doses
4. **Keep notes**: add any reactions or special circumstances to each record`,
  },
];

const de: BlogPost[] = [
  {
    slug: "west-nile-virus",
    date: "2026-09-11",
    category: "Infektionskrankheiten",
    image: "/images/blog/west-nile-virus-hero.svg",
    author: "Vaxy",
    readTime: "6 Min. Lesezeit",
    title:
      "West-Nil-Virus: was es ist, welche Risiken es gibt und was als Nächstes passieren kann",
    excerpt:
      "Das West-Nil-Virus wird durch Mücken übertragen. Die meisten Menschen werden nicht krank, aber eine kleine Gruppe kann schwer erkranken. Was kann in den kommenden Jahren passieren?",
    faq: [
      {
        q: "Was ist das West-Nil-Virus?",
        a: "Das West-Nil-Virus ist ein Virus, das vor allem bei Vögeln vorkommt. Mücken können es auf Menschen und Pferde übertragen.",
      },
      {
        q: "Wie gefährlich ist es?",
        a: "Die meisten Menschen merken nichts oder nur leichte Beschwerden. Eine kleine Gruppe wird schwer krank, vor allem ältere Menschen und Menschen mit schwächerer Abwehr.",
      },
      {
        q: "Kann man es von Mensch zu Mensch bekommen?",
        a: "Nein, nicht durch normalen Kontakt. Mücken verbreiten es. Selten kann es über Blut oder eine Transplantation weitergegeben werden.",
      },
      {
        q: "Wird es in den nächsten Jahren häufiger?",
        a: "WHO und ECDC sagen, dass wärmere Sommer und längere Mückenzeiten dem Virus mehr Zeit geben. In Europa kann es dadurch länger aktiv sein und in mehr Gebieten vorkommen.",
      },
    ],
    content: `## West-Nil-Virus

Das West-Nil-Virus kommt vor allem bei Vögeln vor. Mücken können es auf Menschen und Pferde übertragen. Die meisten Menschen merken nichts oder haben nur leichte Beschwerden.

### Welche Risiken gibt es?

Die Beschwerden sehen oft wie Grippe aus: Fieber, Kopfschmerzen, Muskelschmerzen und Müdigkeit. Eine kleine Gruppe wird schwer krank. Das passiert häufiger bei älteren Menschen und bei Menschen mit schwächerer Abwehr. Dann kann das Virus Gehirn oder Nerven angreifen.

Es gibt keinen Impfstoff für Menschen und kein Mittel, das das Virus selbst entfernt. Im Krankenhaus kann man aber die Beschwerden und die nötige Pflege behandeln.

### Wie kann es sich in den kommenden Jahren entwickeln?

WHO und ECDC sagen, dass wärmere Sommer und längere Mückenzeiten dem Virus mehr Zeit geben. Deshalb kann es in Europa länger aktiv bleiben und in mehr Gebieten auftreten.

In den Niederlanden beobachtet das RIVM das Virus bereits bei Vögeln und Mücken. Neue menschliche Fälle sind daher möglich, wenn Wetter und Mücken passen.

### Was kann man selbst tun?

Schutze dich vor Mücken, vor allem am Abend und am frühen Morgen. Verwende Mückenschutz mit DEET, trage bedeckende Kleidung und schlafe bei Bedarf unter einem Moskitonetz.

Entferne stehendes Wasser rund um Haus und Garten, denn dort können sich Mücken vermehren. Wenn du nach einem Mückenstich hohes Fieber, Nackensteifigkeit oder Verwirrtheit bekommst, wende dich sofort an eine Ärztin oder einen Arzt.

### Kurze Antworten

**Kann man das West-Nil-Virus von Mensch zu Mensch bekommen?**
Nein, nicht durch normalen Kontakt. Mücken verbreiten es. Selten kann es über Blut oder eine Transplantation weitergegeben werden.

**Wer hat ein hohes Risiko für einen schweren Verlauf?**
Vor allem ältere Menschen und Menschen mit schwächerer Abwehr. Die meisten anderen werden gar nicht oder nur leicht krank.

**Gibt es einen Impfstoff oder eine Behandlung?**
Nein, nicht für Menschen. Darum ist Mückenschutz so wichtig.

**Was ist der beste Rat für die nächsten Jahre?**
Mückenstiche weiter verhindern. Das ist jetzt und später der beste Schutz.

### Quellen

- [WHO Europe: West Nile virus, as cases rise across parts of Europe](https://www.who.int/europe/news/item/24-07-2026-west-nile-virus--as-cases-rise-across-parts-of-europe--here-is-what-you-need-to-know)
- [WHO: vector-borne diseases](https://www.who.int/news-room/fact-sheets/detail/vector-borne-diseases)
- [RIVM: West-Nil-Fieber](https://www.rivm.nl/en/west-nile-fever)
- [RIVM: Westnijlkoorts, actueel](https://www.rivm.nl/westnijlkoorts/actueel)
- [ECDC: surveillance and updates for West Nile virus infection](https://www.ecdc.europa.eu/en/topics-z/west-nile-virus-infection/surveillance-and-updates-west-nile-virus-infection)

Dies ist Information, keine medizinische Beratung.`,
  },
  {
    slug: "future-of-vaccination",
    date: "2026-08-26",
    category: "Gesundheitstipps",
    image: "/images/blog/future-of-vaccination.webp",
    author: "Vaxy",
    readTime: "8 Min. Lesezeit",
    title: "Zukünftige Alternativen zur Impfspritze",
    excerpt:
      "Die meisten Impfstoffe kommen noch mit einer Nadel. Tropfen, ein Nasenimpfstoff und nadelfreie Strahlinjektion gibt es schon. Hautpflaster von Micron, Vaxxas und anderen sind noch Forschung. WHO, Gavi und RIVM im Überblick.",
    faq: [
      {
        q: "Welche Alternativen zur Nadel gibt es schon?",
        a: "Orale Polioimpfstoffe, Rotavirus-Tropfen und oraler Cholera-Impfstoff. In manchen Ländern ein Nasenimpfstoff gegen Grippe. PharmaJet Tropis ist ein von der WHO präqualifizierter nadelfreier Strahl in die Haut, genutzt bei Polio-Kampagnen. Im niederländischen Impfprogramm bleibt die Spritze Standard.",
      },
      {
        q: "Was ist ein Mikroarray-Pflaster?",
        a: "Ein kleines Hautpflaster mit sehr kurzen Spitzen, die Impfstoff in die obere Haut bringen. Die WHO nennt zwei Kandidaten für Masern und Röteln: Micron Biomedical (auflösend) und Vaxxas (beschichtet, mit Applikator). Phase 3 und WHO-Zulassung stehen noch aus.",
      },
      {
        q: "Kann ich in den Niederlanden schon ohne Nadel geimpft werden?",
        a: "Säuglinge bekommen Rotavirus schon als Tropfen. Sonst impft das RIVM im nationalen Programm weiter mit Nadel und Spritze. Hautpflaster sind kein Routineprodukt.",
      },
      {
        q: "Warum sucht die WHO überhaupt Alternativen?",
        a: "Weil die Impfquote stagniert. Spritzen, Auflösen und die Kühlkette machen Impfen schwer, vor allem wo Kinder noch keine einzige Dosis bekommen haben. Ein einfacheres Produkt kann mehr Menschen erreichen.",
      },
      {
        q: "Ist das ein medizinischer Rat?",
        a: "Nein. Das ist eine Erklärung auf Basis von WHO, Gavi, BARDA und RIVM. Welche Impfung Sie brauchen, klären Sie mit Hausarzt, GGD oder einer Reisemedizin.",
      },
    ],
    content: `## Zukünftige Alternativen zur Impfspritze

Die meisten Impfstoffe kommen noch mit einer Nadel in den Oberarm. Das funktioniert. Es ist auch umständlich: Spritzen, manchmal erst Pulver auflösen, eine Kühlkette, und Menschen, die die Spritze lieber auslassen.

Die Weltgesundheitsorganisation (WHO) sagt: diese Art der Gabe ist ein Grund, warum die Impfquote stagniert. 2020 verpassten 23 Millionen Kinder ihre Impfungen, 3,7 Millionen mehr als 2019.

Darum schauen WHO, Gavi, UNICEF, PATH und die Bill & Melinda Gates Foundation auf andere Wege. Ihre Vaccine Innovation Prioritisation Strategy (VIPS) wählte im Mai 2020 drei Prioritäten: Mikroarray-Pflaster, hitzestabile Impfstoffe und Barcodes auf der Primärverpackung.

Nicht alles ist Zukunft. Einige Alternativen zur Nadel gibt es schon. Andere stecken in Studien. Das ist Information, kein medizinischer Rat. Im niederländischen Impfprogramm bleibt die Spritze Standard.

### Was gibt es schon ohne Nadel?

Schon länger:

- orale Polioimpfstoffe
- Rotavirus-Tropfen für Säuglinge, auch in den Niederlanden
- oraler Cholera-Impfstoff
- in manchen Ländern ein Nasenimpfstoff gegen Grippe

Nasenimpfstoffe treffen die Schleimhaut, wo Atemwegsviren eintreten. Forscher arbeiten auch an Nasenimpfstoffen gegen andere Atemwegsinfektionen. Die meisten Corona-Impfstoffe in Europa bleiben eine Spritze in den Arm.

**PharmaJet Tropis** ist etwas anderes: kein Pflaster, sondern ein dünner Flüssigkeitsstrahl in die Haut, ohne Nadel. Die WHO hat das Gerät für intradermale Gabe präqualifiziert. Es wurde bei Polio-Kampagnen genutzt, unter anderem in Pakistan und Nigeria. Das ist am nächsten an „schon nadelfrei im Feld“. Es ersetzt nicht DTP oder MMR in den Niederlanden.

### Hautpflaster: Micron, Vaxxas und andere

Ein Microarray Patch, oder MAP, ist ein kleines Hautpflaster mit sehr kurzen Spitzen. Sie sind zu kurz für eine normale Injektion. Sie bringen den Impfstoff in die obere Haut, wo viele Immunzellen sitzen.

Die WHO nennt unter anderem diese Vorteile: kein Auflösen und keine Spritze, weniger Schmerz, möglicherweise bessere Haltbarkeit außerhalb des Kühlschranks, Gabe durch weniger spezialisiertes Personal.

Für Masern und Röteln nennt die WHO zwei klinische Kandidaten:

**Micron Biomedical** macht ein auflösendes Pflaster. Es bleibt ein paar Minuten am Handgelenk. Ihre Phase-1/2-Studie bei Erwachsenen, Kleinkindern und Säuglingen in Gambia ist in *The Lancet* erschienen. Sicherheit und Immunantwort wirkten vergleichbar mit der normalen Spritze unter die Haut. Bei Säuglingen ist das das am weitesten fortgeschrittene MAP.

**Vaxxas** macht ein High-Density Microarray Patch: Tausende Spitzen von etwa 0,25 Millimeter, mit getrocknetem Impfstoff, und ein Einweg-Applikator am Oberarm oder Oberschenkel. Ihre Erwachsenenstudie zu Masern-Röteln ist abgeschlossen. Eine Kinderphase war danach geplant.

Phase 3, Zulassung und WHO-Präqualifizierung stehen noch aus. Rechnen Sie nicht damit bei der nächsten DTP- oder MMR-Impfung.

Andere Unternehmen koppeln mRNA an ein Pflaster. Das ist früher. Der US-BARDA Patch Forward Prize nannte neben Vaxxas und Micron unter anderem **LTS Lohmann** (Deutschland) mit **BioNet** und **PopVax** (Indien) mit LTS. Ziel: Grippe- oder Corona-RNA auf einem Microneedle-Pflaster. Kein Routineprodukt.

### Hitzestabile Impfstoffe

Die zweite VIPS-Priorität sind Impfstoffe, die begrenzte Zeit außerhalb des Kühlschranks dürfen, in einer controlled temperature chain. Weniger Eis, weniger Ausfall, mehr Reichweite. Das kann mit einem Pflaster oder einer klassischen Spritze einhergehen.

mRNA ist ein anderer Sprung: schneller herstellen bei einer neuen Variante. Diese Impfstoffe gehen weiter mit der Nadel und brauchen oft extra kalte Lagerung.

### Was bedeutet das für Ihre Übersicht?

Ob Spritze, Tropfen, Strahl oder später ein Pflaster: Datum, Krankheit und Produktname bleiben nötig. Bei Reisen, einem Booster oder einem neuen Ausbruch will ein Arzt wissen, was Sie schon haben.

Mit Vaxy bewahren Sie das an einem Ort, für sich und Ihre Familie.

Unsicher bei einer Impfung? Fragen Sie Hausarzt, GGD oder eine Reisemedizin.

### Kurze Antworten

**Welche Alternativen zur Nadel gibt es schon?**
Tropfen (Polio, Rotavirus, Cholera), in manchen Ländern ein Nasenimpfstoff gegen Grippe, und nadelfreie Strahlinjektion von PharmaJet bei manchen Polio-Kampagnen.

**Was ist ein Mikroarray-Pflaster?**
Ein kleines Hautpflaster mit kurzen Spitzen. Micron und Vaxxas sind die zwei Kandidaten, die die WHO für Masern und Röteln nennt.

**Kann ich in den Niederlanden schon ohne Nadel?**
Rotavirus schon als Tropfen. Sonst sticht das RIVM im nationalen Programm weiter mit der Nadel. Hautpflaster sind Forschung.

**Warum will die WHO das?**
Einfachere Gabe kann mehr Menschen erreichen, vor allem Kinder, die heute null Dosen bekommen.

### Quellen

- [WHO: innovative Impfstoffgabe, einschließlich Mikroarray-Pflaster](https://www.who.int/teams/immunization-vaccines-and-biologicals/product-and-delivery-research/delivery-technologies)
- [WHO: Technical Advisory Group zu Masern-Röteln-Mikroarray-Pflastern](https://www.who.int/news-room/articles-detail/2023-November-132)
- [WHO: Target Product Profile für Masern-Röteln-MAP](https://www.who.int/publications/i/item/9789240000209)
- [The Lancet: Micron-Masern-Röteln-Pflaster, Phase 1/2 in Gambia](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(24)00532-4/fulltext)
- [WHO PQS: PharmaJet Tropis, nadelfreier Injektor](https://extranet.who.int/prequal/immunization-devices/e008-071)
- [BARDA: Patch Forward Prize, Konzeptgewinner](https://www.patchforwardprize.com/)
- [Gavi: Vaccine Innovation Prioritisation Strategy (VIPS)](https://www.gavi.org/our-alliance/market-shaping/vaccine-innovation-prioritisation-strategy)
- [RIVM: niederländisches Impfprogramm](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "philanthropy-global-infectious-diseases",
    date: "2026-08-26",
    category: "Gesundheitsnews",
    image: "/images/blog/philanthropy-global-infectious-diseases.webp",
    author: "Vaxy",
    readTime: "8 Min. Lesezeit",
    title:
      "Wie Philanthropie über Infektionskrankheiten denkt: Gates, Gavi und Partner",
    excerpt:
      "Die Gates Foundation, Gavi, Wellcome und CEPI sehen Impfstoffe als eines der stärksten Mittel gegen Infektionskrankheiten weltweit. Die WHO gibt den Kurs vor. So funktioniert diese Zusammenarbeit.",
    faq: [
      {
        q: "Was will die Gates Foundation bei Infektionskrankheiten?",
        a: "Kinder, Jugendliche und Erwachsene in Ländern mit niedrigem Einkommen mit Impfstoffen erreichen und verhindern, dass die nächste Generation an Infektionskrankheiten stirbt. Die Stiftung nennt Impfstoffe eines der günstigsten Gesundheitsinstrumente: 3,5 bis 5 Millionen Leben pro Jahr.",
      },
      {
        q: "Was ist Gavi?",
        a: "Gavi, the Vaccine Alliance, ist eine öffentlich-private Partnerschaft. Regierungen, WHO, UNICEF, Weltbank, Impfstoffindustrie und die Gates Foundation arbeiten darin. Gavi kauft und verteilt Impfstoffe für Länder mit niedrigem Einkommen.",
      },
      {
        q: "Bestimmt Bill Gates die niederländische Impfpolitik?",
        a: "Nein. In den Niederlanden legen RIVM, Gesundheitsrat und das Ministerium für Gesundheit die Politik fest. Die WHO gibt weltweite Leitlinien. Stiftungen finanzieren Forschung und Einkauf. Sie schreiben keinen Impfkalender.",
      },
      {
        q: "Welche anderen Stiftungen tun das?",
        a: "Unter anderem Wellcome Trust und CEPI (Ausbruchsimpfstoffe), Rotary International (Polio) und Gavi selbst. Sie arbeiten meist mit WHO und Regierungen, nicht an deren Stelle.",
      },
      {
        q: "Ist das ein medizinischer Rat?",
        a: "Nein. Das erklärt eine globale Strategie. Für Ihre Impfungen: Hausarzt, GGD oder Reisemedizin.",
      },
    ],
    content: `## Wie Philanthropie über Infektionskrankheiten denkt: Gates, Gavi und Partner

Infektionskrankheiten stoppen nicht an einer Grenze. Stiftungen argumentieren so: Fehlt ein Impfstoff in Lagos oder Lahore, kann dieselbe Krankheit später in Europa auftauchen. Impfstoffe sind für sie das schärfste, günstigste Werkzeug.

Die Bill & Melinda Gates Foundation ist der größte private Akteur. Sie arbeitet nicht allein. Die Weltgesundheitsorganisation (WHO) gibt den Kurs vor. Gavi kauft und verteilt. Wellcome und CEPI finanzieren Forschung zu neuen Ausbrüchen.

Das erklärt diese Strategie, kein medizinischer Rat. Niederländische Impfungen folgen dem staatlichen Programm, nicht einer Stiftung in Seattle.

### Wie denkt die Gates Foundation?

Die Stiftung sagt es kurz: Impfstoffe retten 3,5 bis 5 Millionen Leben pro Jahr. Seit die WHO 1974 das Expanded Programme on Immunization startete, wurden schätzungsweise 154 Millionen Leben gerettet, vor allem Kinder.

Ihr Ziel in Ländern mit niedrigem Einkommen: Kinder, Jugendliche und Erwachsene mit den Impfungen erreichen, die sie brauchen. Und eine Welt ohne Krankheiten, die ein Impfstoff verhindern kann.

Drei Linien kehren immer wieder:

1. **Kaufen und verteilen, was es schon gibt.** Gavi ist ihr größter Förderempfänger. Von 2000 bis 2023 impfte Gavi laut Stiftung mehr als 1,1 Milliarden Kinder und verhinderte über 18,8 Millionen zukünftige Todesfälle.
2. **Neue Impfstoffe bezahlbar machen.** Etwa Pneumokokken, Rotavirus, HPV, Cholera, Meningitis A.
3. **Die normale Versorgung stärken.** Ein Impfprogramm ist für sie das Rückgrat der Primärversorgung.

Im Juni 2025 sagte die Stiftung Gavi 1,6 Milliarden Dollar über fünf Jahre zu. Seit 2000 flossen mehr als 30,6 Milliarden Dollar in Impfstoffe, davon 7,7 Milliarden an Gavi.

Das passt zur Immunization Agenda 2030 der WHO: jeder, überall, in jedem Alter. Und zu Gavi 6.0 (2026–2030): mehr als 500 Millionen zusätzliche Kinder und Jugendliche impfen, 8 bis 9 Millionen zusätzliche Leben retten.

### Wo sehen sie das Problem?

Der Fortschritt stockt. 2023 erhielten 14,5 Millionen Kinder keine einzige Impfung, mehr als im Jahr davor. Masernausbrüche stiegen. Die Stiftung nennt das ein Risiko für Jahrzehnte an Gewinn.

Ihre Antwort ist nicht ein neuer Impfstoff, sondern Reichweite: HPV-Einführungen, Masernkampagnen und extra Einsatz in Gebieten mit der niedrigsten Abdeckung, auch in Konfliktzonen.

Polio bleibt ein Schwerpunkt. Seit 2007 unterstützt die Stiftung die Global Polio Eradication Initiative, mit WHO, Rotary International, CDC, UNICEF und Gavi. Wildes Poliovirus gibt es noch in Pakistan und Afghanistan. Seit 1988 sanken die Fälle um mehr als 99 %.

### Wellcome, CEPI und Rotary

Andere Fonds denken in dieselbe Richtung, mit einer anderen Aufgabe.

**Wellcome Trust** finanziert Wissenschaft und war 2017 Mitgründer von CEPI.

**CEPI**, die Coalition for Epidemic Preparedness Innovations, soll Impfstoffe vor der nächsten Epidemie bereithalten. Regierungen, Gates und Wellcome gründeten sie nach Ebola. Das Ziel: bei einer unbekannten Krankheit X schneller ein Impfstoff, in ihrer Vision innerhalb von 100 Tagen. CEPI schreibt keine Impfpläne. Das bleibt bei WHO und Ländern.

**Rotary International** sammelt seit Jahrzehnten Geld für Polio, als Partner der GPEI.

Die VIPS-Allianz (Gavi, WHO, Gates Foundation, UNICEF, PATH) zeigt, wie diese Clubs Innovationen gemeinsam wählen, etwa Hautpflaster. Philanthropie zahlt oft die ersten Schritte. Regierungen und WHO entscheiden, ob ein Produkt ins Programm kommt.

### Was bedeutet das für die Niederlande?

Nichts in Ihrem Impfausweis bestimmt eine Stiftung. Das tun RIVM, Gesundheitsrat und das Gesundheitsministerium.

Indirekt betrifft es Sie trotzdem. Weltweiter Einkauf hält Masern, Polio und Cholera kleiner. Reiseimpfungen, Pandemievorsorge und neue Darreichungsformen kommen aus demselben Netz.

Notieren Sie Datum und Name jeder Impfung. Mit Vaxy bewahren Sie diese Übersicht, auch wenn Sie später in ein Land reisen, in dem Gavi den Einkauf macht.

Dieser Artikel ist Information, kein medizinischer Rat.

### Kurze Antworten

**Was will die Gates Foundation?**
Impfstoffe zu Menschen in Ländern mit niedrigem Einkommen bringen und Infektionskrankheiten in der nächsten Generation verhindern.

**Was ist Gavi?**
Eine Allianz aus Regierungen, WHO, UNICEF, Weltbank, Industrie und der Gates Foundation, die Impfstoffe für ärmere Länder kauft.

**Bestimmt Philanthropie die niederländische Politik?**
Nein. RIVM, Gesundheitsrat und Ministerium tun das. Die WHO gibt weltweite Leitlinien.

**Wer arbeitet noch daran?**
Unter anderem Wellcome, CEPI, Rotary, UNICEF und nationale Geber.

### Quellen

- [Gates Foundation: Immunization](https://www.gatesfoundation.org/our-work/programs/global-development/immunization)
- [Gates Foundation: Zusage von 1,6 Milliarden Dollar an Gavi, Juni 2025](https://www.gatesfoundation.org/ideas/media-center/press-releases/2025/06/renewed-gavi-commitment-lifesaving-vaccine-access)
- [Gates Foundation: Polio](https://www.gatesfoundation.org/our-work/programs/global-development/polio)
- [WHO: Immunization Agenda 2030](https://www.who.int/teams/immunization-vaccines-and-biologicals/strategies/ia2030)
- [WHO: Impfquote, Factsheet](https://www.who.int/news-room/fact-sheets/detail/immunization-coverage)
- [Gavi 6.0, Strategie 2026–2030](https://www.gavi.org/our-work/strategy/phase-6-2026-2030)
- [CEPI](https://cepi.net/)
- [RIVM: niederländisches Impfprogramm](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "climate-diseases-netherlands",
    date: "2026-08-26",
    category: "Gesundheitstipps",
    image: "/images/blog/climate-diseases-netherlands.webp",
    author: "Vaxy",
    readTime: "8 Min. Lesezeit",
    title:
      "Neue Infektionskrankheiten in den Niederlanden durch den Klimawandel",
    excerpt:
      "Dengue ist im europäischen Teil der Niederlande noch eine Importkrankheit. RIVM, Gesundheitsrat und ECDC sagen: wärmeres Wetter, Zecken und Mücken verändern das Risiko. Was kommt, was ist schon da?",
    faq: [
      {
        q: "Gibt es Dengue schon in den Niederlanden?",
        a: "Im europäischen Teil nur bei Reisenden. Das RIVM hat keine lokale Infektion bestätigt. In der Karibik der Niederlande kommt Dengue vor, mit größeren Ausbrüchen 2023–2024. In Südeuropa gibt es bereits lokale Fälle, unter anderem in Frankreich, Italien und Spanien.",
      },
      {
        q: "Wird sich die Tigermücke hier ansiedeln?",
        a: "Noch nicht. Die NVWA bekämpft Einschleppungen. Das RIVM sagt: mehr Tigermücken kommen vor allem durch Handel und Reisen, nicht durch das Klima. Der Gesundheitsrat erwartet Ansiedlung an manchen Orten innerhalb von 2 bis 5 Jahren. Dann können höhere Sommertemperaturen lokale Dengue nach Einschleppung durch einen Reisenden ermöglichen.",
      },
      {
        q: "Welche Krankheiten haben sich hier schon verändert?",
        a: "Frühsommer-Meningoenzephalitis seit 2016 in niederländischen Zecken, 8 Meldungen einer in den Niederlanden erworbenen Infektion 2024. West-Nil-Virus 2020 bei 8 Menschen. Lyme durch Zecken gibt es lange. Wärmeres Wasser erhöht das Risiko für Vibrionen und Legionellen.",
      },
      {
        q: "Soll ich mich in den Niederlanden gegen Dengue oder FSME impfen lassen?",
        a: "Das RIVM sagt: für FSME in den Niederlanden keine Impfung nötig. Für Reisen in Länder, in denen FSME oder Dengue häufig ist: LCR, Hausarzt oder Reisemedizin fragen. Es gibt Impfstoffe gegen beide.",
      },
      {
        q: "Ist das ein medizinischer Rat?",
        a: "Nein. Das ist eine Erklärung auf Basis von RIVM, Gesundheitsrat, ECDC und WHO. Bei Beschwerden nach Mücken- oder Zeckenstich: Hausarzt.",
      },
    ],
    content: `## Neue Infektionskrankheiten in den Niederlanden durch den Klimawandel

Dengue klingt tropisch. Im europäischen Teil der Niederlande ist sie das noch: eine Krankheit, die Reisende mitbringen. Der Gesundheitsrat warnte im Mai 2026 trotzdem: durch den Klimawandel werden die Bedingungen hier günstiger für Krankheiten über Mücken, Zecken und Wasser.

Das RIVM ist vorsichtiger. Klima ist ein Faktor. Handel, Reisen und wie wir wohnen tun oft mehr. Eine neue Mücke in den Niederlanden ist nicht automatisch Schuld eines wärmeren Sommers.

Das ist Information, kein medizinischer Rat.

### Dengue: jetzt Import, später vielleicht lokal

Dengue verbreitet sich über die Gelbfiebermücke und die Asiatische Tigermücke. Die WHO schätzt weltweit Hunderte Millionen Infektionen pro Jahr. Fieber, Muskelschmerz, manchmal schwere Blutungen.

Laut LCI-Leitlinie des RIVM ist Dengue im europäischen Teil eine Importkrankheit. Bisher wurde keine lokale Infektion bestätigt. Positive Tests bei Rückkehrern stiegen: im Schnitt 136 pro Jahr 2014–2019, 367 im Jahr 2023 und 544 im Jahr 2024.

In der Karibik der Niederlande kommt Dengue vor. Das RIVM nennt Dutzende Fälle pro Jahr und größere Ausbrüche 2023–2024. Der Gesundheitsrat gibt diesen Inseln Vorrang, weil die Risiken dort größer und vielfältiger sind.

In Südeuropa gab es bereits lokale Dengue. Das ECDC erfasst Fälle unter anderem in Frankreich, Italien, Spanien und Kroatien. 2026 meldete bis Anfang August nur Frankreich eine lokal erworbene Dengue auf dem europäischen Festland.

Es gibt Dengue-Impfstoffe. In den Niederlanden sind sie für Reisende gedacht, nicht für die ganze Bevölkerung. Fragen Sie LCR oder eine Reisemedizin.

### Die Tigermücke: Bekämpfung kauft Zeit

Die Asiatische Tigermücke (*Aedes albopictus*) kann Dengue, Chikungunya und Zika übertragen. Im Mittelmeerraum sitzt sie fest. In den Niederlanden noch nicht, dank Bekämpfung durch VWS und NVWA.

Das RIVM ist klar: mehr Funde in den Niederlanden kommen vor allem durch Handel und Reisen, nicht durch den Klimawandel. Sollte sich die Mücke ansiedeln, spielt das Klima dann doch. Bei höheren Sommertemperaturen vermehren sich Viren in Mücken schneller.

Der Gesundheitsrat erwartet Ansiedlung an bestimmten Orten innerhalb von 2 bis 5 Jahren. Danach können lokale Dengue-Ausbrüche folgen, wenn ein virämischer Reisender das Virus mitbringt.

Im August 2026 veröffentlichte die NVWA eine DNA-Studie: die Mücke kommt über mehrere Einschleppungen, unter anderem über Altreifen und Flughäfen. An manchen Orten überlebte sie den Winter. Bekämpfung kann eine Population trotzdem beseitigen.

### Was ist schon da?

Nicht alles ist Zukunft.

**Frühsommer-Meningoenzephalitis (FSME/TBE).** Seit 2016 wissen wir, dass niederländische Zecken das Virus tragen können, unter anderem auf der Sallandse und Utrechtse Heuvelrug. Zwischen 2016 und Juni 2024 erwarben 23 Patienten die Infektion in den Niederlanden. 2024 gab es 8 Meldungen. Seit 1. März 2025 ist FSME mit neurologischen Beschwerden meldepflichtig. Etwa 1 von 1500 Zecken in bekannten Gebieten trägt das Virus. Bei Lyme etwa 1 von 5. Das RIVM sagt: in den Niederlanden keine FSME-Impfung nötig. Wohl bei Reisen in Länder, in denen FSME häufig ist.

**West-Nil-Fieber.** 2020 erwarben 8 Menschen in den Niederlanden das Virus, vor allem um Utrecht, über die gewöhnliche Hausmücke. Das Virus sitzt in Vögeln. Der Gesundheitsrat nennt wärmere Sommer günstig für die Übertragung. Das Risiko für die meisten Menschen bleibt klein.

**Lyme.** Schon lange die häufigste Zeckenkrankheit hier. Mehr Zecken bei milderem Wetter erhöhen den Kontakt, sagt auch der Gesundheitsrat.

**Wasser.** Niederländische Küstengewässer sind geeigneter für *Vibrio* geworden. Wärmeres Oberflächenwasser hilft auch Legionellen und Blaualgen.

Chikungunya und Zika folgen derselben Mückengeschichte wie Dengue: jetzt vor allem ein Reiserisiko, später möglicherweise lokal, wenn die Tigermücke bleibt.

### Was können Sie tun?

- Reise in Gebiete mit Dengue oder FSME: rechtzeitig fragen, ob eine Impfung sinnvoll ist.
- In den Niederlanden: nach Grünflächen auf Zecken prüfen, Abendmücken meiden. Fieber nach einer Tropenreise: Hausarzt, und sagen, wo Sie waren.

Notieren Sie Impfungen und Reisespritzen. Mit Vaxy sehen Sie vor der Abreise, ob Dengue oder FSME schon in Ihrer Übersicht steht.

Dieser Artikel ist Information, kein medizinischer Rat.

### Kurze Antworten

**Gibt es Dengue hier schon?**
Im europäischen Teil nur bei Reisenden. In der Karibik der Niederlande ja. Lokale Übertragung in Südeuropa gibt es schon.

**Siedelt sich die Tigermücke an?**
Noch nicht, wegen der Bekämpfung. Der Gesundheitsrat erwartet das an manchen Orten innerhalb von 2 bis 5 Jahren.

**Welche Krankheiten sind schon da?**
FSME seit 2016, West-Nil 2020 bei Menschen, Lyme länger. Wärmeres Wasser: Vibrionen und Legionellen.

**Soll ich mich hier gegen Dengue oder FSME impfen lassen?**
Für das Leben im europäischen Teil meist nicht. Für bestimmte Reisen manchmal schon. Fragen Sie LCR, Hausarzt oder GGD.

### Quellen

- [RIVM: Klimawandel und Infektionskrankheiten](https://www.rivm.nl/klimaat-en-gezondheid/infectieziekten)
- [RIVM: Dengue](https://www.rivm.nl/dengue)
- [LCI-Leitlinie Dengue](https://lci.rivm.nl/richtlijnen/dengue)
- [RIVM: West-Nil-Fieber](https://www.rivm.nl/westnijlkoorts)
- [RIVM: FSME](https://www.rivm.nl/tekenencefalitis)
- [Gesundheitsrat: Klimawandel und Gesundheit, 21. Mai 2026](https://www.gezondheidsraad.nl/actueel/nieuws/2026/05/21/bescherm-de-bevolking-tegen-de-toenemende-gezondheidsrisicos-van-klimaatverandering)
- [ECDC: Dengue-Risiko EU/EWR](https://www.ecdc.europa.eu/en/dengue/surveillance-and-updates/risk-assessment)
- [WHO: Dengue](https://www.who.int/news-room/fact-sheets/detail/dengue-and-severe-dengue)
- [NVWA: DNA-Studie Asiatische Tigermücke, 20. August 2026](https://www.nvwa.nl/actueel/nieuws/2026/08/20/dna-onderzoek-biedt-nvwa-nieuw-inzicht-in-verspreiding-en-bestrijding-van-de-aziatische-tijgermug)
- [LCR: Informationen zu Impfungen](https://lcr.nl/informatie-vaccinaties)`,
  },
  {
    slug: "vaccine-boosters",
    date: "2026-08-22",
    category: "Gesundheitstipps",
    image: "/images/blog/vaccine-boosters.webp",
    author: "Vaxy",
    readTime: "8 Min. Lesezeit",
    title:
      "Welche Impfstoffe brauchen einen Booster – und wie lange schützt er?",
    excerpt:
      "Nicht jeder Impfstoff braucht eine Wiederholung. RIVM, LCI und LCR sagen, wann ein Booster fällig ist und wie lange der Schutz danach hält.",
    faq: [
      {
        q: "Welche Impfstoffe brauchen einen Booster?",
        a: "Vor allem DTP (alle 10 Jahre), Typhus (alle 3 Jahre), FSME, Japanische Enzephalitis und Cholera. Grippe und Corona für Risikogruppen sind jährliche Impfungen, keine klassischen Booster.",
      },
      {
        q: "Wie oft sollte ich DTP wiederholen?",
        a: "Nach einer vollständigen Serie alle 10 Jahre. Das niederländische Programm und das LCR sagen: danach 10 Jahre Schutz gegen Diphtherie und Tetanus. Nach dem Kinderimpfplan bist du gegen Polio-Lähmung wahrscheinlich lebenslang geschützt.",
      },
      {
        q: "Brauche ich Hepatitis A später noch einmal?",
        a: "Nein, wenn du zwei Dosen hattest. Das LCR sagt: 1 Jahr nach der ersten Dosis, 50 Jahre nach der zweiten. Die LCI sagt, gesunde Menschen brauchen danach keine weitere Dosis.",
      },
      {
        q: "Gilt Gelbfieber lebenslang?",
        a: "Meistens ja. Eine Dosis schützt bei den meisten Menschen lebenslang. Das Zertifikat gilt seit 2016 lebenslang. Manche Gruppen, etwa Menschen mit HIV, brauchen trotzdem eine Wiederholung.",
      },
      {
        q: "Ist das ein medizinischer Rat?",
        a: "Nein. Das ist eine Erklärung auf Basis von RIVM, LCI und LCR. Bei Unsicherheit frag deine Ärztin, den öffentlichen Gesundheitsdienst oder eine Reiseimpfstelle.",
      },
    ],
    content: `## Welche Impfstoffe brauchen einen Booster – und wie lange schützt er?

Nicht jeder Impfstoff braucht eine Wiederholung. Manche Serien schützen nach dem Abschluss lange oder lebenslang. Andere Impfungen musst du später erneut holen.

Dieser Überblick gilt für gesunde Menschen in den Niederlanden, auf Basis des staatlichen Impfprogramms (RVP), der LCI-Leitlinien des RIVM und des niederländischen Reiseimpfzentrums LCR. Es ist Information, kein medizinischer Rat.

### Was ist ein Booster?

Ein Booster ist eine Extra-Dosis nach einer vollständigen Grundimmunisierung, weil der Schutz nachlässt.

Das ist etwas anderes als:

- die zweite oder dritte Dosis einer Serie, etwa Hepatitis A oder Hepatitis B
- eine jährliche Impfung, etwa gegen Grippe, weil sich das Virus ändert
- Extra-Dosen nach einem Biss oder einer Wunde, etwa bei Tollwut oder Tetanus

### Diese Impfstoffe brauchen einen Booster

**DTP (Diphtherie, Tetanus, Polio).** Alle 10 Jahre nach einer vollständigen Serie. Nie geimpft? Zuerst drei Dosen. Das RVP sagt: nach der DTP-Impfung mit 14 Jahren bist du etwa 10 Jahre gegen Diphtherie und Tetanus geschützt, und wahrscheinlich lebenslang gegen Polio-Lähmung. Das LCR nennt dasselbe 10-Jahres-Intervall für Reisende.

**Keuchhusten.** Der azelluläre Impfstoff schützt laut LCI etwa 4 bis 6 Jahre. Kinder bekommen mit 5 Jahren einen Booster. Erwachsene haben kein festes Intervall. In der Schwangerschaft rät das RIVM ab Woche 22 zur Keuchhustenimpfung, in jeder Schwangerschaft neu.

**Typhus.** Eine Dosis schützt 3 Jahre, sagt das LCR. Der Schutz ist nicht vollständig. Alle 3 Jahre wiederholen, wenn weiter Risiko besteht.

**Japanische Enzephalitis.** Zwei Dosen innerhalb eines Monats geben 2 Jahre Schutz. Ab einem Jahr später ist eine dritte Dosis möglich. Danach 10 Jahre, laut LCR.

**FSME.** Drei Dosen (Monat 0, 1 und 6). Danach ein Booster nach 3 Jahren. Anschließend alle 5 Jahre unter 60, und alle 3 Jahre ab 60, laut LCI. Für Reisende ab 50 nennt das LCR manchmal eine Extra-Dosis in der Grundserie.

**Cholera.** Gewöhnliche Reisende in den Niederlanden bekommen das meist nicht. Laut LCR schützt eine Impfung 6 Monate bei Kindern von 2 bis 5 Jahren und 2 Jahre bei älteren Kindern und Erwachsenen.

**Meningokokken ACWY.** Eine Dosis schützt mindestens 10 Jahre, sagt das LCR. Für Mekka-Pilger gilt der Nachweis 5 Jahre.

**Gelbfieber.** Meist kein Booster. Eine Dosis schützt bei den meisten Menschen lebenslang. Das Zertifikat gilt seit 1. Juli 2016 lebenslang. Die LCI rät zu einer Wiederholung nach Impfung in der Schwangerschaft, bei HIV oder Laborarbeit mit dem Virus.

**Tollwut.** Zwei Dosen vor der Reise geben laut LCI lebenslangen Basisschutz. Kein Routine-Booster für Reisende. Nach Biss, Kratzer oder Lecken immer Extra-Dosen.

**Grippe.** Jährlich, für Menschen mit Extra-Risiko. Die LCI sagt: Antikörper bleiben etwa 24 Wochen auf Niveau. Der Impfstoff lässt nach einem halben Jahr nach, und das Virus ändert sich.

**COVID-19.** Eine Herbst-Runde für Risikogruppen. Das RIVM hält bevorzugt 6 Monate nach der letzten Dosis ein, mindestens 3 Monate.

**Pneumokokken.** Der ältere PPV23-Impfstoff wird alle 5 Jahre wiederholt. Nach dem neueren Konjugatimpfstoff PCV20 brauchen Erwachsene derzeit keine Wiederholung, sagt die LCI.

### Diese Impfstoffe brauchen nach der Serie keinen Booster

**Hepatitis A.** Zwei Dosen, mindestens 6 Monate Abstand. Nach der ersten Dosis: 1 Jahr. Nach der zweiten: 50 Jahre, laut öffentlicher LCR-Seite. Die LCI sagt: gesunde Menschen brauchen danach keine Extra-Dosis. Modelle deuten auf 50 Jahre, möglicherweise lebenslang.

**Hepatitis B.** Drei Dosen. Danach grundsätzlich lebenslang, wenn der Körper gut reagiert hat. Ein Booster ist dann nicht nötig. Manche Berufe machen trotzdem einen Bluttest.

**HPV.** Zwei Dosen im Abstand von 6 Monaten, im RVP um das 10. Lebensjahr. Die LCI sieht nach 12 Jahren noch guten Schutz. Ein späterer Booster ist derzeit nicht vorgesehen.

**MMR (Masern, Mumps, Röteln).** Zwei Dosen. Danach wahrscheinlich lebenslang gegen Masern und Röteln. Bei Mumps ist das weniger klar. Nach zwei Dosen sind 99 % geschützt, sagt das RVP.

**Gürtelrose.** Zwei Dosen Shingrix. Das RIVM sagt: der Schutz hält mindestens 6 bis 10 Jahre. Es gibt noch keine Standard-dritte Dosis.

**Dengue und Chikungunya.** Das LCR sagt, die Schutzdauer sei noch unbekannt.

### Kinder: Booster im staatlichen Impfplan

Kinder bekommen Wiederholungen schon im Plan. Laut rijksvaccinatieprogramma.nl unter anderem:

- 12 Monate: DKTP-Hib-HepB und Pneumokokken
- 14 Monate: MMR und MenACWY
- 3 Jahre: zweite MMR (manche Jahrgänge später, per Nachholaktion)
- 5 Jahre: DKT
- 10 Jahre: HPV, zweimal
- 14 Jahre: DTP und MenACWY (DTP mit 14 ab Geburtsjahr 2016)

Danach ist DTP nicht mehr im Kinderprogramm. Für Diphtherie und Tetanus bleibt alle 10 Jahre ein Booster sinnvoll, vor allem bei Reisen.

### Was das für deine Unterlagen bedeutet

Notiere Datum, Impfstoffname und ob die Serie fertig ist. Dann siehst du, ob du einen Booster brauchst oder nur die nächste Dosis derselben Serie.

Mit Vaxy liegt das auf einem Handy. So verpasst du seltener DTP nach 10 Jahren oder die zweite Hepatitis-A-Dosis.

Bei Unsicherheit frag deine Ärztin, den öffentlichen Gesundheitsdienst oder eine Reiseimpfstelle. Menschen mit schwächerer Abwehr, Schwangere und manche Berufe haben oft einen anderen Plan.

### Kurze Antworten

**Welche Impfstoffe brauchen einen Booster?**
DTP alle 10 Jahre, Typhus alle 3 Jahre, plus FSME, Japanische Enzephalitis und Cholera. Grippe und Corona für Risikogruppen jedes Jahr.

**Wie lange wirkt DTP?**
10 Jahre gegen Diphtherie und Tetanus. Polio nach dem Kinderplan wahrscheinlich lebenslang gegen Lähmung.

**Brauche ich Hepatitis A später noch einmal?**
Nicht nach zwei Dosen. Nach der zweiten Dosis 50 Jahre laut LCR.

**Gilt Gelbfieber lebenslang?**
Meistens ja. Manchmal ist eine Extra-Dosis nötig, etwa bei HIV.

### Quellen

- [Niederländisches Impfprogramm: Zeitplan](https://rijksvaccinatieprogramma.nl/vaccinaties/vaccinatieschema)
- [RVP: DTP-Impfung](https://rijksvaccinatieprogramma.nl/vaccinaties/dtp)
- [RVP: MMR-Impfung](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [LCR: Informationen zu Impfungen](https://lcr.nl/informatie-vaccinaties)
- [LCI: Tetanus](https://lci.rivm.nl/richtlijnen/tetanus)
- [LCI: Hepatitis-A-Impfung](https://lci.rivm.nl/factsheets/hepatitis-a-vaccinatie)
- [LCI: Hepatitis-B-Impfung](https://lci.rivm.nl/factsheets/hepatitis-b-vaccinatie)
- [LCI: Gelbfieber](https://lci.rivm.nl/richtlijnen/gele-koorts)
- [LCI: FSME-Impfung](https://lci.rivm.nl/factsheets/tbe-vaccinatie)
- [LCI: Keuchhustenimpfung](https://lci.rivm.nl/factsheets/kinkhoestvaccinatie)
- [LCI: HPV-Impfung](https://lci.rivm.nl/factsheets/hpv-vaccinatie)
- [LCI: Pneumokokkenimpfung](https://lci.rivm.nl/factsheets/pneumokokkenvaccinatie)
- [LCI: Influenzaimpfung](https://lci.rivm.nl/richtlijnen/influenzavaccinatie)
- [LCI: COVID-19-Impfung](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)
- [LCI: Tollwut](https://lci.rivm.nl/richtlijnen/rabies)
- [RIVM: Corona-Impfung](https://www.rivm.nl/corona/coronaprik)
- [RIVM: Gürtelroseimpfung](https://www.rivm.nl/gordelroos/vaccinatie)
- [RIVM: Impfungen in der Schwangerschaft](https://www.rivm.nl/zwangerschap-en-infectieziekten/vaccinaties-tijdens-zwangerschap)`,
  },
  {
    slug: "how-mrna-vaccines-work",
    date: "2026-08-19",
    category: "Gesundheitstipps",
    image: "/images/blog/how-mrna-vaccines-work.webp",
    author: "Vaxy",
    readTime: "7 Min. Lesezeit",
    title: "Was sind mRNA-Impfstoffe und wie wirken sie?",
    excerpt:
      "Ein mRNA-Impfstoff ist eine kurze Nachricht für deine Zellen, kein lebendes Virus. WHO, CDC, EMA und RIVM erklären, wie er wirkt und warum er deine DNA nicht verändert.",
    faq: [
      {
        q: "Was ist ein mRNA-Impfstoff?",
        a: "Ein mRNA-Impfstoff ist eine Spritze mit einer kurzen Nachricht für deine Zellen. Die Nachricht lehrt deinen Körper, ein harmloses Stück eines Virus zu erkennen. Es ist kein lebendes Virus darin.",
      },
      {
        q: "Wie wirkt ein mRNA-Impfstoff?",
        a: "Nach der Spritze lesen deine Zellen die mRNA und machen ein harmloses Proteinstück. Dein Immunsystem lernt, dieses Stück zu erkennen. Danach baut dein Körper die mRNA schnell ab.",
      },
      {
        q: "Verändert ein mRNA-Impfstoff deine DNA?",
        a: "Nein. CDC und EMA sagen, dass die mRNA nicht in den Zellkern kommt, wo deine DNA liegt. Sie kann deinen Erbgut-Code also nicht verändern.",
      },
      {
        q: "Kann ein mRNA-Impfstoff dich mit Corona anstecken?",
        a: "Nein. Die CDC sagt, dass kein lebendes Coronavirus darin ist. Du kannst davon keine COVID-19 bekommen.",
      },
      {
        q: "Wie lange bleibt die mRNA im Körper?",
        a: "Nur kurz. Die EMA sagt, dass dein Körper die mRNA schnell abbaut, wie deine eigene mRNA. In Studien sank sie innerhalb von Tagen.",
      },
    ],
    content: `## Was sind mRNA-Impfstoffe und wie wirken sie?

Ein mRNA-Impfstoff ist eine Spritze, die deinen Körper lehrt, eine Krankheit zu erkennen. Er enthält kein lebendes Virus. Er enthält eine kurze Nachricht: mRNA.

mRNA bedeutet messenger RNA, also Boten-RNA. Dein Körper macht den ganzen Tag selbst mRNA. Diese Nachricht sagt einer Zelle, welches Protein sie machen soll.

Beim mRNA-Impfstoff ist die Nachricht im Labor gemacht. Sie geht um ein harmloses Stück des Virus. Bei Corona ist das oft das Spike-Protein. Danach räumt dein Körper die Nachricht schnell auf.

Die Weltgesundheitsorganisation (WHO) nennt das einen Impfstoff mit einer Bauanleitung, statt dem Virus selbst.

### Wie wirkt ein mRNA-Impfstoff, Schritt für Schritt?

Laut der US-Behörde CDC läuft es so:

1. Du bekommst die Spritze in den Oberarm.
2. Die mRNA sitzt in kleinen Fettbläschen. Die helfen der Nachricht in die Zelle.
3. In der Zelle liest der Körper die Nachricht und macht ein harmloses Proteinstück.
4. Danach baut die Zelle die mRNA ab. Sie verlässt den Körper als Abfall.
5. Die Zelle zeigt das Proteinstück deinem Immunsystem.
6. Dein Immunsystem macht Antikörper und Gedächtniszellen.
7. Triffst du später das echte Virus, erkennt dein Körper es schneller.

Die CDC sagt: Es dauert meist ein paar Wochen, bis dieser Schutz bereit ist.

### Verändert ein mRNA-Impfstoff deine DNA?

Nein. Das sagen die CDC und die Europäische Arzneimittel-Agentur (EMA) beide.

Die mRNA kommt nicht in den Zellkern. Dort liegt deine DNA. Die Nachricht bleibt außerhalb des Kerns, wo dein Körper immer Proteine macht.

mRNA ist auch chemisch anders als DNA. Sie kann nicht einfach in deine DNA gehen.

Die EMA sagt auch: Ein mRNA-Impfstoff ist keine Gentherapie. Das Ziel ist nicht, deine Gene zu reparieren oder zu verändern.

Der Impfstoff kann dich nicht mit Corona krank machen. Es ist kein lebendes Coronavirus darin, sagt die CDC.

### Wie lange bleibt die mRNA im Körper?

Nur kurz. Die EMA sagt: Der Körper baut die mRNA schnell ab, wie deine eigene mRNA.

In Tierstudien zu Comirnaty (Pfizer/BioNTech) sank die mRNA in 6 bis 9 Tagen. Bei Spikevax (Moderna) fanden Forschende nach 1 bis 3 Tagen vor allem noch mRNA an der Einstichstelle, in Lymphknoten und in der Milz.

Bei der Zulassung erwarteten die Behörden keine Ansammlung in Organen.

### Warum sind Fettbläschen im Impfstoff?

mRNA ist empfindlich. Ohne Schutz zerfällt sie schnell.

Die EMA erklärt: Kleine Fettteilchen, Lipid-Nanopartikel, helfen der mRNA in die Zelle. Die Zellwand besteht auch aus Fett. Die Bläschen verschmelzen damit.

Solche Fettteilchen in Medikamenten sind nicht neu. Das erste Medikament mit diesen Bläschen wurde 2018 in der EU zugelassen.

### Sind mRNA-Impfstoffe neu?

Die Corona-Spritzen waren für viele Menschen neu. Die Technik nicht.

Die EMA sagt: Forschende arbeiteten schon etwa zwanzig Jahre mit mRNA, bei Infektionskrankheiten und bei Krebs. Die CDC nennt frühere Studien zu Grippe, Zika, Tollwut und CMV.

mRNA ist relativ klein und einfach herzustellen. Deshalb konnten Hersteller schneller mehr produzieren als bei klassischen Impfstoffen. Die Sicherheitstests wurden nicht übersprungen, sagt die CDC.

### Welche mRNA-Impfstoffe gibt es?

Die bekanntesten in Europa sind:

- Comirnaty von Pfizer/BioNTech
- Spikevax von Moderna

Sie sind von der EMA zugelassen. Das niederländische RIVM nutzt Corona-Impfstoffe im Herbstprogramm, vor allem für Menschen mit höherem Risiko. Im Jahr 2026 können unter anderem Menschen ab 70 Jahren und Menschen mit extra medizinischem Risiko eine Corona-Spritze bekommen.

Es gibt auch andere Arten von Corona-Impfstoffen, zum Beispiel Protein-Impfstoffe. Die enthalten schon ein Proteinstück. Das RIVM nennt das eine Option, wenn du aus medizinischen Gründen keinen mRNA-Impfstoff darfst oder willst.

Die RIVM-Leitlinie sagt es kurz: Comirnaty ist ein mRNA-Impfstoff. Er enthält kein lebendes Virus. Die mRNA kodiert für das Spike-Protein.

mRNA wird auch für andere Krankheiten und für manche Krebsarten erforscht. Das ist noch Forschung. Ein COVID-19-mRNA-Impfstoff ist nicht dasselbe wie ein persönlicher Krebsimpfstoff.

### Was sind die Nebenwirkungen?

Die WHO sagt: Leichte bis mittlere Beschwerden kommen vor. Das ist ein Zeichen, dass dein Körper Schutz aufbaut.

Häufig:

- Schmerz oder Rötung an der Einstichstelle
- Müdigkeit
- Kopfschmerz
- Muskelschmerz
- Schüttelfrost
- Fieber

Die meisten Beschwerden gehen innerhalb weniger Tage weg. Nicht jeder bekommt Nebenwirkungen.

Schwere Nebenwirkungen sind selten. Die EMA sagt: Die Sicherheit der zugelassenen COVID-19-Impfstoffe ist beruhigend, nach Hunderten Millionen Dosen.

Eine bekannte seltene Nebenwirkung von mRNA-Impfstoffen ist eine Entzündung des Herzmuskels oder des Herzbeutels. Ärztinnen und Ärzte nennen das Myokarditis oder Perikarditis. Das RIVM nennt das selten. Es tritt vor allem bei jungen Männern kurz nach der Spritze auf. Die meisten Menschen genesen. Geh zum Arzt bei Brustschmerz, Atemnot oder schnellem Herzschlag nach der Spritze.

### Schützen mRNA-Impfstoffe noch?

Ja, vor allem vor schwerer Krankheit und Tod. Kein Impfstoff wirkt zu 100%.

Die WHO sagt: Der Schutz ist in den ersten Monaten am höchsten. Nach etwa sechs Monaten nimmt er ab. Deshalb bekommen Menschen mit höherem Risiko manchmal eine neue Spritze.

Du kannst nach der Impfung trotzdem Corona bekommen. Die Chance, sehr krank zu werden, ist dann meist kleiner.

### Was bedeutet das für deinen Impfnachweis?

Notiere, welche Spritze du bekommen hast, das Datum und den Namen des Impfstoffs, zum Beispiel Comirnaty oder Spikevax. Das hilft bei einer späteren Dosis, auf Reisen oder wenn eine Ärztin oder ein Arzt fragt.

Mit Vaxy speicherst du diese Daten an einem Ort, für dich und deine Familie.

Dieser Artikel ist Information, keine medizinische Beratung. Bei Zweifeln frag deine Ärztin, deinen Arzt oder den öffentlichen Gesundheitsdienst.

### Kurze Antworten

**Was ist ein mRNA-Impfstoff?**
Eine Spritze mit einer kurzen Nachricht für deine Zellen. Kein lebendes Virus.

**Wie wirkt er?**
Deine Zellen machen ein harmloses Proteinstück. Deine Abwehr lernt es. Dann verschwindet die mRNA.

**Verändert er deine DNA?**
Nein. CDC und EMA sagen, dass die mRNA nicht zu deiner DNA im Zellkern kommt.

**Kannst du davon Corona bekommen?**
Nein. Es ist kein lebendes Coronavirus darin.

**Wie lange bleibt die mRNA im Körper?**
Nur kurz. Die EMA sagt, dass dein Körper sie schnell abbaut.

### Quellen

- [WHO: Wie Impfstoffe wirken](https://www.who.int/news-room/feature-stories/detail/how-do-vaccines-work)
- [WHO: Fragen und Antworten zu COVID-19-Impfstoffen, 15. Mai 2026](https://www.who.int/news-room/questions-and-answers/item/coronavirus-disease-(covid-19)-vaccines)
- [CDC: COVID-19 Vaccine Basics, wie mRNA-Impfstoffe wirken](https://www.cdc.gov/covid/vaccines/how-they-work.html)
- [EMA: Kernfakten zu COVID-19-Impfstoffen, inklusive mRNA](https://www.ema.europa.eu/en/human-regulatory-overview/public-health-threats/coronavirus-disease-covid-19/covid-19-medicines/covid-19-vaccines-key-facts)
- [EMA: Comirnaty (Pfizer/BioNTech)](https://www.ema.europa.eu/en/medicines/human/EPAR/comirnaty)
- [EMA: Spikevax (Moderna)](https://www.ema.europa.eu/en/medicines/human/EPAR/spikevax)
- [RIVM: Corona-Impfung](https://www.rivm.nl/covid-19-vaccinatie)
- [RIVM-LCI-Leitlinie COVID-19-Impfung](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)`,
  },
  {
    slug: "us-green-card-vaccination-records",
    date: "2026-08-19",
    category: "Reisegesundheit",
    image: "/images/blog/us-green-card-vaccination-records.webp",
    author: "Vaxy",
    readTime: "7 Min. Lesezeit",
    title: "Wie Vaxy bei den Impfanforderungen für eine US-Greencard hilft",
    excerpt:
      "Bei der medizinischen Untersuchung für eine US-Greencard werden Impfungen geprüft. USCIS sagt, du sollst schriftliche Nachweise mitbringen. So hilft ein digitales Register in Vaxy, mit Quellen.",
    faq: [
      {
        q: "Ist Vaxy eine Greencard?",
        a: "Nein. Eine Greencard ist eine Aufenthaltserlaubnis. Vaxy ist ein digitaler Impfnachweis. USCIS bittet dich, schriftliche Impfnachweise zur Einwanderungsuntersuchung mitzubringen.",
      },
      {
        q: "Brauchen Touristinnen und Touristen das für die Einreise in die USA?",
        a: "Nein. Diese Impfprüfung gilt für Menschen, die ein Einwanderungsvisum oder eine Statusanpassung zur dauerhaften Aufenthaltserlaubnis beantragen. Formular I-693 ist für einen normalen Urlaub nicht nötig.",
      },
      {
        q: "Kann Vaxy Formular I-693 ersetzen?",
        a: "Nein. Nur ein designated civil surgeon in den USA oder ein panel physician im Ausland darf die Impfbewertung auf Formular I-693 abschließen.",
      },
      {
        q: "Ist ein COVID-19-Impfstoff für eine Greencard noch Pflicht?",
        a: "Nein. Seit dem 20. Januar 2025 musst du für die Statusanpassung keine COVID-19-Impfung mehr nachweisen.",
      },
    ],
    content: `## Wie Vaxy bei den Impfanforderungen für eine US-Greencard hilft

Eine US-Greencard ist kein Impfheft. Sie ist eine Aufenthaltserlaubnis: lawful permanent residence.

Wer diesen Status beantragt, muss bestimmte Impfungen nachweisen können. Das US-Einwanderungsrecht sagt, dass du ohne diesen Nachweis unzulässig sein kannst. USCIS ist klar: nimm jede schriftliche Impfdokumentation zur Untersuchung mit.

Genau dabei hilft Vaxy. Vaxy ist keine Greencard und kein offizielles USCIS-Formular. Es ist dein Impfregister, immer auf dem Handy, bereit für die Untersuchung.

### Für wen das gilt

Das gilt, wenn du:

- im Ausland ein Einwanderungsvisum beantragst, oder
- in den Vereinigten Staaten deinen Status zur dauerhaften Aufenthaltserlaubnis anpassen willst, oft mit Formular I-485

Für einen normalen Urlaub oder kurzen Besuch ist diese Untersuchung nicht nötig. Die Regeln unten gelten für Greencard-Antragstellende.

### Warum Impfnachweise zählen

Nach Artikel 212(a)(1)(A)(ii) des Immigration and Nationality Act (INA) kann jemand, der die erforderlichen Impfungen nicht nachweisen kann, bei der Einreise oder der Statusanpassung abgelehnt werden.

In den USA macht ein designated civil surgeon die Untersuchung. Im Ausland macht das ein panel physician, den das US-Außenministerium benennt. Die Ergebnisse stehen auf Formular I-693, Report of Medical Examination and Vaccination Record.

USCIS sagt: du musst Impfungen nicht wiederholen, wenn du schon einen Nachweis hast. Nimm deine Unterlagen mit.

### Welche Impfstoffe Pflicht sind

Das Einwanderungsrecht nennt unter anderem:

- Mumps
- Masern
- Röteln
- Polio
- Tetanus und Diphtherie
- Pertussis (Keuchhusten)
- Haemophilus influenzae Typ B (Hib)
- Hepatitis B
- Weitere Impfstoffe, die ACIP empfiehlt und die CDC für die Einwanderung vorschreibt

CDC verlangt außerdem, wenn sie zu deinem Alter passen, Schutz gegen:

- Windpocken (Varizellen)
- Hepatitis A
- Meningokokken
- Pneumokokken
- Rotavirus, bei kleinen Säuglingen
- Saisonale Grippe, in der Grippesaison

Du brauchst nicht jeden Impfstoff auf der Liste. Nur die, die zu deinem Alter passen. Der civil surgeon folgt den Technical Instructions der CDC, inklusive einer Tabelle nach Alter.

**COVID-19:** Seit dem 20. Januar 2025 ist ein COVID-19-Impfstoff für die Statusanpassung nicht mehr Pflicht.

**Polio:** Seit dem 1. Mai 2024 soll jede Person ab 2 Monaten, die nicht auf dem aktuellen Stand ist, eine Dosis IPV bekommen. Das gilt auch für Erwachsene ohne Nachweis der Kinderserie.

**Grippe:** Für die Einwanderungsuntersuchung ist der Grippeimpfstoff vom 1. September bis 31. März Pflicht, wenn er verfügbar ist.

### Was du zur Untersuchung mitbringst

USCIS sagt, du sollst jede schriftliche Impfdokumentation mitnehmen. Wenn eine Ärztin oder ein Arzt Immunität getestet hat, nimm auch diesen Nachweis mit.

Fehlt etwas, kann der civil surgeon den Impfstoff geben. Du kannst fehlende Impfungen auch bei deiner eigenen Praxis holen und danach mit Nachweis zurückkommen. Nur ein civil surgeon darf den Impfteil von Formular I-693 ausfüllen.

Du brauchst in der Regel nur eine Dosis jedes erforderlichen Impfstoffs zum Zeitpunkt der Untersuchung. Du musst eine lange Serie nicht abschließen, bevor das Formular unterschrieben werden darf.

### Wie Vaxy hilft

Papierhefte gehen verloren. Impfungen liegen in verschiedenen Ländern, Sprachen und Akten.

Mit Vaxy kannst du:

- Jede Impfung an einem Ort speichern, mit Datum
- Familienmitglieder hinzufügen, die auch eine Greencard beantragen
- Dein Register auf dem Handy beim Termin öffnen
- Vorher sehen, was du schon hast, damit weniger Impfungen wiederholt werden

Zeig dem civil surgeon dein Vaxy-Register, zusammen mit Papiernachweisen, die du noch hast. Der civil surgeon entscheidet, was zählt.

### Was Vaxy nicht ist

Vaxy ist keine Greencard, kein Formular I-693 und kein Ersatz für die Einwanderungsuntersuchung. USCIS akzeptiert keine unvollständige Impftabelle. Nur ein designated civil surgeon oder panel physician darf das Formular beglaubigen.

Dieser Artikel ist Information, keine rechtliche oder medizinische Beratung. Regeln können sich ändern. Prüfe USCIS und CDC vor deinem Termin.

### Kurze Antworten

**Ist Vaxy eine Greencard?**
Nein. Es ist ein digitaler Impfnachweis, den du zur Untersuchung mitnimmst.

**Gilt das für Touristinnen und Touristen?**
Nein. Das gilt für Menschen, die eine Greencard beantragen.

**Kann Vaxy Formular I-693 ersetzen?**
Nein. Nur ein civil surgeon oder panel physician darf dieses Formular abschließen.

**Ist COVID-19 noch Pflicht?**
Nein, nicht seit dem 20. Januar 2025.

### Quellen

- [USCIS, Vaccination Requirements](https://www.uscis.gov/tools/designated-civil-surgeons/vaccination-requirements)
- [CDC, Vaccination Technical Instructions for Civil Surgeons](https://www.cdc.gov/immigrant-refugee-health/hcp/civil-surgeons/vaccination.html)
- [USCIS Policy Manual, Volume 8, Part B, Chapter 9](https://www.uscis.gov/policy-manual/volume-8-part-b-chapter-9)
- [Formular I-693, Report of Medical Examination and Vaccination Record](https://www.uscis.gov/i-693)
- [42 C.F.R. Section 34.3](https://www.ecfr.gov/current/title-42/chapter-I/subchapter-C/part-34/section-34.3)`,
  },
  {
    slug: "vaccines-do-not-cause-autism",
    date: "2026-08-19",
    category: "Gesundheitsnews",
    image: "/images/blog/vaccines-do-not-cause-autism.webp",
    author: "Vaxy",
    readTime: "6 Min. Lesezeit",
    title: "Impfungen verursachen kein Autismus: Was die Wissenschaft sagt",
    excerpt:
      "Impfungen verursachen kein Autismus. WHO, RIVM und große Studien finden keinen Beweis. So entstand der Mythos, und was Eltern wissen sollten.",
    faq: [
      {
        q: "Verursachen Impfungen Autismus?",
        a: "Nein. Die WHO, das niederländische RIVM und große Studien in vielen Ländern finden keinen Zusammenhang. Impfungen verursachen kein Autismus.",
      },
      {
        q: "Verursacht die MMR-Impfung Autismus?",
        a: "Nein. Die MMR-Impfung schützt vor Masern, Mumps und Röteln. Große Studien, darunter eine in Dänemark mit mehr als 650.000 Kindern, finden keinen Zusammenhang mit Autismus.",
      },
      {
        q: "Woher kommt die Behauptung, Impfungen würden Autismus verursachen?",
        a: "Aus einem Artikel von Andrew Wakefield aus dem Jahr 1998. Nur 12 Kinder nahmen teil. Der Artikel wurde später zurückgezogen. Die Daten stimmten nicht, und Wakefield durfte nicht mehr als Arzt arbeiten.",
      },
      {
        q: "Ist es sicherer, die MMR-Impfung in drei einzelne Impfungen aufzuteilen?",
        a: "Nein. Es gibt keinen Beweis, dass Aufteilen sicherer ist. Dein Kind braucht dann mehr Termine und bleibt länger ungeschützt gegen Masern.",
      },
      {
        q: "Was sagen WHO und RIVM zu Impfungen und Autismus?",
        a: "Beide sagen dasselbe: Impfungen verursachen kein Autismus. Die WHO hat das im Dezember 2025 erneut bestätigt. Das RIVM sagt es auf den Seiten zur MMR-Impfung und zu Zweifeln an Impfungen.",
      },
    ],
    content: `## Impfungen verursachen kein Autismus: Was die Wissenschaft sagt

Impfungen verursachen kein Autismus. Das ist die klare Schlussfolgerung der Weltgesundheitsorganisation (WHO), des niederländischen Impfprogramms beim RIVM und großer Studien in vielen Ländern.

Vaxy stimmt dem zu. Eltern verdienen klare Informationen, keine Angst.

### Was steht in den Nachrichten?

Am 11. August 2026 hat der US-Präsident Donald Trump eine Anordnung zu Kinderimpfungen unterschrieben. Er hat die alte Behauptung wiederholt, Impfungen würden Autismus verursachen. Er will auch, dass die MMR-Impfung gegen Masern, Mumps und Röteln in einzelne Impfungen aufgeteilt wird.

Ärztinnen, Ärzte und Gesundheitsorganisationen nennen diese Anordnung gefährlich. Das ist keine Wissenschaft. In den Vereinigten Staaten entscheiden die Bundesstaaten, welche Impfungen Schulen verlangen, nicht der Präsident. Die Anordnung ist also ein Rat, kein Gesetz. Sie kann Eltern trotzdem unsicher machen.

### Woher kommt dieser Mythos?

1998 hat der britische Arzt Andrew Wakefield einen Artikel veröffentlicht. Er schrieb, die MMR-Impfung könne Autismus verursachen. Nur 12 Kinder nahmen teil. Später wurde klar, dass die Daten nicht stimmten. Wakefield hatte auch Geld von Anwälten bekommen, die Impfungen verklagen wollten.

Die medizinische Fachzeitschrift The Lancet hat den Artikel zurückgezogen. Das bedeutet: Der Artikel darf nicht mehr als Beweis genutzt werden. Wakefield durfte danach nicht mehr als Arzt arbeiten.

Seit 1998 gab es sehr viele große, sorgfältige Studien. Sie finden keinen Zusammenhang zwischen Impfungen und Autismus.

### Was sagen große Studien?

Eine bekannte Studie aus Dänemark von 2019 hat 657.461 Kinder begleitet. Forschende vom Statens Serum Institut haben geprüft, ob die MMR-Impfung Autismus verursacht. Die Antwort war nein. Auch nicht bei Kindern mit einem höheren Risiko, zum Beispiel weil ein Geschwisterkind Autismus hat.

2025 kam eine neue dänische Studie zu Aluminium in Impfstoffen. Kleine Mengen Aluminiumsalze stecken in manchen Impfstoffen. Sie helfen dem Körper, besser auf die Impfung zu reagieren. Die Forschenden haben mehr als 1 Million Kinder begleitet. Sie fanden keinen Zusammenhang mit Autismus und auch nicht mit 49 anderen Gesundheitsproblemen.

Das RIVM sammelt solche Studien auf einer eigenen Seite. Die Schlussfolgerung ist immer dieselbe: Impfen verursacht kein Autismus.

### Was sagt die WHO?

Im Dezember 2025 hat eine WHO-Expertengruppe die Wissenschaft erneut geprüft. Die Gruppe heißt GACVS. Sie haben 31 Studien von 2010 bis 2025 aus vielen Ländern angesehen.

Ihre Schlussfolgerung: Impfungen verursachen kein Autismus. Auch nicht Impfungen mit Thiomersal, einem Konservierungsmittel, oder mit einem kleinen bisschen Aluminium.

Die WHO hat das schon 2002, 2004 und 2012 gesagt. Die neue Prüfung bestätigt das erneut.

Die WHO sagt auch: Kinderimpfungen haben in 50 Jahren mindestens 154 Millionen Leben gerettet.

### Was sagt das niederländische Impfprogramm?

Das niederländische Impfprogramm schützt Kinder vor schweren Krankheiten, zum Beispiel Masern, Mumps, Röteln, Keuchhusten und Polio.

Auf der RIVM-Seite zur MMR-Impfung steht klar: Du bekommst kein Autismus durch die MMR-Impfung. Auch keinen Diabetes und keine Darmkrankheit.

Kinder in den Niederlanden bekommen die MMR-Impfung zweimal: mit etwa 14 Monaten und mit etwa 3 Jahren. Nach der ersten Impfung sind etwa 95% der Kinder geschützt. Nach der zweiten Impfung sind es etwa 99%.

Das RIVM sagt auch: Zweifel sind verständlich. Stell Fragen in der Kinderberatung oder bei der Ärztin oder dem Arzt. Nutze verlässliche Quellen, nicht nur soziale Medien.

### Ist es sicherer, die MMR-Impfung aufzuteilen?

Nein. Die WHO, das RIVM und die US-CDC sagen, dass es keinen Beweis gibt, dass einzelne Impfungen sicherer sind.

Wenn du die Impfungen aufteilst, braucht dein Kind mehr Termine. Zwischen den Terminen ist dein Kind länger ungeschützt. Masern sind sehr ansteckend. Ein Kind kann krank werden, bevor alle Impfungen fertig sind.

Die MMR-Impfung ist eine kleine Dosis. Das sind ein paar Tropfen. Es ist keine große Menge Flüssigkeit im Körper.

### Warum bekommen Kinder heute mehr Impfungen als früher?

Kinder bekommen heute Schutz vor mehr Krankheiten. Das ist Fortschritt, keine Gefahr. Kombinationsimpfungen bedeuten weniger Stiche in den Arm, nicht mehr.

Der Impfplan folgt dem Alter, in dem das Immunsystem am besten reagiert, und in dem Kinder das höchste Risiko haben.

Impfungen auslassen oder verschieben senkt diesen Schutz. Masern kommen öfter zurück, wenn weniger Kinder geimpft sind.

### Was können Eltern tun?

1. Folge dem nationalen Impfprogramm in deinem Land.
2. Frag eine Ärztin, einen Arzt oder die Kinderberatung, wenn du Zweifel hast.
3. Nutze Quellen wie die WHO und das RIVM, nicht anonyme Beiträge.
4. Halte die Impfungen deines Kindes fest, damit nichts fehlt.

Mit Vaxy hältst du alle Impfungen der Familie an einem Ort. Du siehst, was schon gegeben wurde, und bekommst eine Erinnerung für die nächste Impfung.

Dieser Artikel ist Information, keine medizinische Beratung. Hast du Sorgen um die Gesundheit deines Kindes? Sprich mit einer Ärztin oder einem Arzt.

### Kurze Antworten

**Verursachen Impfungen Autismus?**
Nein. WHO, RIVM und große Studien finden keinen Zusammenhang.

**Verursacht die MMR-Impfung Autismus?**
Nein. Große Studien mit Hunderttausenden Kindern finden keinen Zusammenhang.

**Woher kommt der Mythos?**
Aus einem Artikel von 1998, der später zurückgezogen wurde. Die Daten stimmten nicht.

**Ist Aufteilen der MMR-Impfung sicherer?**
Nein. Dafür gibt es keinen Beweis. Dein Kind bleibt dann länger ungeschützt.

**Was sagen WHO und RIVM?**
Impfungen verursachen kein Autismus. Die WHO hat das 2025 erneut bestätigt.

### Quellen

- [WHO: Impfungen verursachen kein Autismus, 11. Dezember 2025](https://www.who.int/news/item/11-12-2025-who-expert-group-s-new-analysis-reaffirms-there-is-no-link-between-vaccines-and-autism)
- [WHO-GACVS-Erklärung zu Impfungen und Autismus](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [RIVM: Forschung zu Impfen und Autismus](https://rijksvaccinatieprogramma.nl/over-vaccineren/onderzoek-vaccins/onderzoek-verband-vaccineren-autisme)
- [RIVM: Impfen verursacht kein Autismus](https://rijksvaccinatieprogramma.nl/over-vaccineren/twijfels)
- [RIVM: MMR-Impfung](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [Hviid et al., MMR-Impfung und Autismus, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [Statens Serum Institut: kein Zusammenhang zwischen Aluminium in Impfstoffen und Autismus, 2025](https://en.ssi.dk/news/news/2025/large-danish-study-no-link-between-vaccines-and-autism-or-49-other-health-conditions)
- [The Lancet zieht den Wakefield-Artikel zurück, 2010](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(10)60175-4/fulltext)
- [CDC: Vaccines Do Not Cause Autism](https://www.cdc.gov/vaccine-safety/about/autism.html)
- [BBC: Trump unterschreibt Anordnung zu Kinderimpfungen, 11. August 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)`,
  },
  {
    slug: "childhood-vaccines-trump-order",
    date: "2026-08-19",
    category: "Gesundheitsnews",
    image: "/images/blog/childhood-vaccines-trump-order.webp",
    author: "Vaxy",
    readTime: "8 Min. Lesezeit",
    title: "Warum Kinderimpfungen nach Trumps Anordnung weiter wichtig sind",
    excerpt:
      "Trump will weniger Kinderimpfungen und die MMR-Impfung aufteilen. Hier erfährst du, warum es diese Impfungen gibt, was die Wissenschaft sagt, und was passieren kann, wenn weniger Kinder geschützt sind.",
    faq: [
      {
        q: "Ändert diese US-Anordnung den Impfplan in Europa?",
        a: "Nein. Es ist eine Empfehlung in den USA, kein Gesetz. In den Niederlanden gilt das Rijksvaccinatieprogramma. US-Bundesstaaten legen selbst fest, welche Impfungen Schulen verlangen.",
      },
      {
        q: "Warum bekommen Kinder so viele Impfungen?",
        a: "Weil es jetzt Schutz vor mehr schweren Krankheiten gibt. Der Plan folgt dem Alter, in dem das Immunsystem am besten reagiert und Kinder am meisten Risiko haben.",
      },
      {
        q: "Ist es sicherer, die MMR-Impfung aufzuteilen?",
        a: "Nein. Es gibt keinen Beweis, dass Aufteilen sicherer ist. Dein Kind braucht dann mehr Termine und bleibt länger ungeschützt gegen Masern, Mumps und Röteln.",
      },
      {
        q: "Was kann passieren, wenn weniger Kinder geimpft sind?",
        a: "Krankheiten wie Masern, Keuchhusten und Polio können zurückkommen. Masern sind sehr ansteckend. Ausbrüche wachsen schon in Ländern mit sinkender Impfquote.",
      },
    ],
    content: `## Warum Kinderimpfungen nach Trumps Anordnung weiter wichtig sind

Am 11. August 2026 hat der US-Präsident Donald Trump eine Anordnung zu Kinderimpfungen unterschrieben. Er will weniger Impfungen für alle Kinder, und er will die kombinierte MMR-Impfung gegen Masern, Mumps und Röteln in einzelne Spritzen aufteilen.

Ärztinnen und Ärzte nennen das gefährlich. Die Anordnung ist eine Empfehlung, kein Gesetz. Trotzdem kann sie Eltern verunsichern und Impfquoten weiter senken. Dieser Artikel erklärt, warum Kinderimpfungen existieren und was die Folgen sein können.

### Was steht in der Anordnung?

Laut der [BBC](https://www.bbc.com/news/articles/ce3q5vl581wo) will Trump die Zahl der Krankheiten, gegen die alle US-Kinder geimpft werden, von 18 auf 11 senken. Auf seiner Liste stehen: Masern, Mumps, Röteln, Diphtherie, Tetanus, Keuchhusten, Polio, Hib, Pneumokokken, HPV und Windpocken.

Andere Impfungen, etwa gegen Hepatitis B und Meningokokken, sollen nur noch für Kinder mit höherem Risiko bleiben. Grippe und COVID-19 sollen eine Entscheidung zwischen Eltern und Arzt werden.

Trump will auch, dass Impfungen nicht mehr im selben Termin gegeben werden, und dass die kombinierte MMR-Impfung aufgeteilt wird.

Schulimpfungen bleiben Sache der Bundesstaaten. Eltern in den USA können also nicht einfach drei einzelne MMR-Spritzen verlangen. Diese einzelnen Produkte sind in den USA derzeit nicht zugelassen.

### Warum es Kinderimpfungen gibt

Kinderimpfungen schützen vor Krankheiten, die früher viele Kinder schwer krank machten oder töteten. Masern können Lungenentzündung, Hirnentzündung und Taubheit verursachen. Keuchhusten ist vor allem für Babys gefährlich. Polio kann Lähmungen verursachen. Hib kann Hirnhautentzündung verursachen. Hepatitis B kann später Leberkrebs auslösen.

Die WHO sagt, Impfungen haben in 50 Jahren mehr als 150 Millionen Leben gerettet. Allein gegen Masern verhinderten Impfungen zwischen 2000 und 2024 schätzungsweise 59 Millionen Todesfälle.

Kinder bekommen Impfungen in einem festen Alter, nicht zufällig. Ärztinnen und Ärzte wählen den Zeitpunkt, an dem das Immunsystem am besten reagiert und das Kind am meisten Risiko hat. Die American Academy of Pediatrics (AAP) sagt: Es gibt keinen medizinischen Grund, empfohlene Impfungen zu verschieben oder auszulassen.

Kinder sind heute gegen mehr Krankheiten geschützt als früher. Das ist Fortschritt. Kombinationsimpfungen bedeuten weniger Nadeln, nicht mehr.

### Gemeinschaftsschutz ist kein Extra

Manche Kinder können nicht geimpft werden. Babys sind vielleicht zu jung. Andere Kinder haben ein schwaches Immunsystem. Sie hängen von den Menschen um sie herum ab.

Gegen Masern brauchen mindestens 95% der Kinder zwei Dosen, um einen Ausbruch zu stoppen. Fällt diese Zahl, findet das Virus schnell einen Weg. Masern sind oft die erste Krankheit, die zurückkommt, wenn die Impfquote sinkt.

Im Jahr 2023 gab es weltweit schätzungsweise 10,3 Millionen Masernfälle. WHO, UNICEF und Gavi warnen, dass Ausbrüche von Masern, Meningitis und Gelbfieber zunehmen. In den USA liegen die Masernzahlen laut aktuellen Berichten auf dem höchsten Stand seit 35 Jahren.

### Was kann diese Anordnung bewirken?

**Mehr verpasste Dosen.** Extra Termine klingen vorsichtig. In der Praxis kommen Familien seltener zurück. Eine verpasste Dosis bedeutet längeren Schutzverlust.

**Länger ungeschützt gegen Masern.** Zwischen einzelnen Spritzen liegt Zeit. In dieser Zeit kann ein Kind Masern, Mumps oder Röteln bekommen. Die CDC sagt, es gibt keinen wissenschaftlichen Beweis, dass das Aufteilen der kombinierten MMR-Impfung etwas bringt. Die kombinierte Impfung gibt es seit 1971 und wirkt genauso gut wie drei einzelne Spritzen.

**Krankheiten, die von der Liste für alle Kinder fallen.** Eine Hepatitis-B-Impfung bei der Geburt hilft, eine lebenslange Infektion zu verhindern. Rotavirus-Impfungen verhindern schwere Durchfälle bei Babys. Wenn diese Impfungen nicht mehr für alle Kinder empfohlen werden, erkranken mehr Kinder.

**Mehr Verwirrung, niedrigere Impfquote.** Trump wiederholte die Behauptung, Impfungen verursachten Autismus. Das ist widerlegt. Wenn Eltern Impfungen aus Unsicherheit verschieben, steigt das Ausbruchrisiko. Senator Bill Cassidy, selbst Arzt, nannte die Anordnung "falsch". Der Präsident der AAP nannte sie "gefährlich".

**Kein Gesetz, aber ein Signal.** Die Bundesstaaten machen weiter die Schulregeln. Bundesempfehlungen beeinflussen trotzdem Ärztinnen und Ärzte, Versicherungen und Eltern. Unklare Empfehlungen führen oft zu weniger Impfungen.

### Verursachen Impfungen Autismus?

Nein. Die WHO hat das im Dezember 2025 erneut bestätigt. Eine dänische Studie von 2019 hat 657.461 Kinder verfolgt und keinen Zusammenhang zwischen der MMR-Impfung und Autismus gefunden. Mehr zu diesem Mythos steht in [Impfungen verursachen kein Autismus](/de/blog/vaccines-do-not-cause-autism).

### Was bedeutet das, wenn du nicht in den USA lebst?

Das niederländische Rijksvaccinatieprogramma ändert sich dadurch nicht. Das gilt auch für Impfpläne in anderen europäischen Ländern.

Masern stoppen nicht an einer Grenze. Reisen in Gebiete mit Ausbrüchen, oder Kontakt mit Reisenden, bleiben ein Grund, im Plan zu bleiben. Prüfe deine Impfungen und die deiner Kinder vor der Abreise.

### Was du tun kannst

1. Folge dem nationalen Impfplan deines Landes, nicht einer politischen Empfehlung aus einem anderen Land.
2. Stell Fragen an eine Ärztin, einen Arzt oder die Beratungsstelle, wenn du unsicher bist.
3. Verschiebe Impfungen nicht "zur Sicherheit". Aufschub ist extra Risiko.
4. Halt fest, welche Impfungen dein Kind schon bekommen hat.

Mit Vaxy hast du alle Impfungen deiner Familie an einem Ort. Du siehst, was gesetzt wurde, und bekommst eine Erinnerung für die nächste Impfung.

Dieser Artikel ist Information, keine medizinische Beratung. Hast du Sorgen um die Gesundheit deines Kindes? Sprich mit einer Ärztin oder einem Arzt.

### Kurze Antworten

**Ändert das den Impfplan in Europa?**
Nein. Es ist eine US-Empfehlung, keine europäische Regel.

**Warum so viele Kinderimpfungen?**
Weil sie vor mehr schweren Krankheiten schützen, in dem Alter, in dem dieser Schutz am nötigsten ist.

**Ist das Aufteilen der MMR-Impfung sicherer?**
Nein. Dafür gibt es keinen Beweis. Dein Kind bleibt dann länger ungeschützt.

**Was, wenn weniger Kinder geimpft werden?**
Masern und andere Krankheiten kommen zurück. Ausbrüche wachsen schon.

### Quellen

- [BBC: Trump unterschreibt Anordnung zu Kinderimpfungen, 11. August 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)
- [CIDRAP: Trump unterschreibt Anordnung zur Aufteilung der MMR-Impfung](https://www.cidrap.umn.edu/trump-signs-executive-order-break-measles-mumps-rubella-vaccine)
- [WHO, UNICEF und Gavi: Ausbrüche impfpräventabler Krankheiten nehmen zu, 24. April 2025](https://www.who.int/news/item/24-04-2025-increases-in-vaccine-preventable-disease-outbreaks-threaten-years-of-progress--warn-who--unicef--gavi)
- [WHO: Masern, Faktenblatt](https://www.who.int/news-room/fact-sheets/detail/measles)
- [WHO: Masern-Todesfälle sinken, Fälle steigen, 28. November 2025](https://www.who.int/news/item/28-11-2025-measles-deaths-down-88--since-2000--but-cases-surge)
- [WHO GACVS: Impfungen verursachen kein Autismus](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [Hviid et al., MMR-Impfung und Autismus, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [RIVM: MMR-Impfung](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)`,
  },
  {
    slug: "moderna-mrna-melanoma-vaccine",
    date: "2026-08-19",
    category: "Gesundheitsnews",
    image: "/images/blog/moderna-mrna-melanoma-vaccine.webp",
    author: "Vaxy",
    readTime: "6 Min. Lesezeit",
    title: "Moderna-mRNA-Krebsimpfstoff besteht große Melanom-Studie",
    excerpt:
      "Moderna und Merck sagen, dass ein persönlicher mRNA-Impfstoff plus Keytruda hilft, zu verhindern, dass Melanom nach einer Operation zurückkommt. Hier erfährst du in einfacher Sprache, was die Phase-3-Studie bedeutet.",
    faq: [
      {
        q: "Verhindert dieser mRNA-Krebsimpfstoff Krebs?",
        a: "Nein. Es ist eine persönliche Behandlung für Menschen, die schon ein Melanom hatten. Ärztinnen und Ärzte geben ihn nach der Operation, damit der Krebs seltener zurückkommt.",
      },
      {
        q: "Ist der Moderna-Melanom-Impfstoff dasselbe wie der COVID-19-Impfstoff?",
        a: "Nein. Beide nutzen mRNA, aber dieser Krebsimpfstoff wird für den Tumor einer Person gemacht. Ein COVID-19-Impfstoff ist für viele Menschen gleich und zielt auf ein Virus.",
      },
      {
        q: "Kann man den Moderna-mRNA-Melanom-Impfstoff jetzt schon bekommen?",
        a: "Noch nicht. Er ist nicht für die allgemeine Nutzung zugelassen. Moderna und Merck wollen in den kommenden Monaten eine Zulassung beantragen.",
      },
      {
        q: "Wirkt der Impfstoff besser als Keytruda allein?",
        a: "Ja, in dieser Phase-3-Studie. Menschen mit Impfstoff plus Keytruda blieben länger krebsfrei, und der Krebs streute seltener. Die vollständigen Zahlen folgen auf einem medizinischen Kongress.",
      },
    ],
    content: `## Moderna-mRNA-Krebsimpfstoff besteht große Melanom-Studie

Am 19. August 2026 haben Moderna und Merck gute Nachrichten geteilt. Ihr persönlicher mRNA-Krebsimpfstoff, zusammen mit dem Krebsmedikament Keytruda, hat in einer großen Studie gewirkt. Der Impfstoff half Menschen mit Melanom, nach einer Operation länger krebsfrei zu bleiben.

Das ist die erste große Phase-3-Studie, die zeigt, dass ein mRNA-Krebsimpfstoff wirken kann. Es ist auch das erste Mal, dass dieser persönliche Impfstoff besser war als Keytruda allein, nach einer Melanom-Operation.

### Was ist der mRNA-Krebsimpfstoff von Moderna?

Die Behandlung heißt intismeran autogene. Du siehst auch die Namen V940 oder mRNA-4157.

Es ist kein Impfstoff, den gesunde Menschen bekommen, um Krebs zu verhindern. Es ist eine Behandlung für Menschen, die schon ein Melanom hatten, nachdem Ärztinnen und Ärzte den Tumor operativ entfernt haben.

Das nennt man adjuvante Behandlung. Das bedeutet extra Behandlung nach der Operation, damit der Krebs seltener zurückkommt.

### Wie wirkt ein persönlicher mRNA-Krebsimpfstoff?

Melanom ist eine ernste Form von Hautkrebs. Jeder Tumor hat eigene Veränderungen, Mutationen genannt. Du kannst das wie einen einzigartigen Fingerabdruck sehen.

Der Impfstoff wird für eine Person gemacht:

1. Ärztinnen und Ärzte nehmen eine Probe des Tumors.
2. Forschende lesen den Fingerabdruck dieses Tumors.
3. Sie machen einen mRNA-Impfstoff, der das Immunsystem lehrt, diese Krebsmerkmale zu finden.
4. Nach der Spritze liest der Körper die mRNA und macht kleine Proteinteile. Das Immunsystem lernt dann, Krebszellen mit denselben Merkmalen anzugreifen.

mRNA ist eine kurze Nachricht, die Zellen lesen können. COVID-19-Impfstoffe nutzen auch mRNA, aber dieser Krebsimpfstoff ist anders. Er wird für eine Patientin oder einen Patienten und einen Tumor gemacht.

Der Impfstoff wird zusammen mit Keytruda (Pembrolizumab) gegeben. Keytruda ist eine Immuntherapie. Sie hilft dem Immunsystem, Krebszellen zu erkennen und zu bekämpfen.

### Was hat die Phase-3-Melanom-Studie gefunden?

Die Studie heißt INTerpath-001. Eine Phase-3-Studie ist eine große, späte Studie. Unternehmen nutzen sie oft, bevor sie bei Behörden eine Zulassung beantragen.

Bei einer geplanten Zwischenprüfung hat die Studie zwei Hauptziele erreicht:

- Menschen blieben länger krebsfrei. Der Krebs kam seltener zurück.
- Der Krebs streute seltener in andere Teile des Körpers.

Die Unternehmen sagen, dass der Unterschied echt ist, also kein Zufall, und dass er für Patientinnen und Patienten zählt. Die Studie läuft noch. Forschende schauen auch, wie lange Menschen leben.

Die vollständigen Zahlen folgen auf einem medizinischen Kongress. Moderna und Merck wollen auch mit Behörden über eine Zulassung sprechen.

### Wer hat an der Studie teilgenommen?

An der Studie haben 1.137 Menschen mit hohem Risiko für ein Melanom teilgenommen. Ihr Krebs war Stadium 2B, 2C, 3 oder 4. Eine Operation hatte den Krebs schon entfernt.

Die Teilnehmenden wurden zufällig in zwei Gruppen geteilt:

- Etwa zwei von drei Menschen bekamen den persönlichen mRNA-Impfstoff plus Keytruda
- Etwa eine von drei Personen bekam nur Keytruda

Die Behandlung dauerte etwa ein Jahr. Menschen konnten bis zu neun Dosen des Impfstoffs bekommen.

### Ist der mRNA-Krebsimpfstoff sicher?

Bisher berichten die Unternehmen von keinen neuen Sicherheitsproblemen. In früheren Studien waren häufige Nebenwirkungen:

- Müdigkeit
- Schmerz an der Einstichstelle
- Schüttelfrost

Keytruda kann auch Immun-Nebenwirkungen verursachen. Menschen in einer Studie werden eng überwacht.

### Was haben frühere Studien gezeigt?

In einer kleineren Phase-2-Studie senkte dieselbe Kombination das Risiko, dass Krebs zurückkommt oder jemand stirbt, nach fünf Jahren um 49%, verglichen mit Keytruda allein. Das Risiko, dass Krebs weit weg streut oder jemand stirbt, sank um 59%.

Die neue Phase-3-Studie ist größer. Das ist die Art von Studie, die Behörden nutzen, wenn sie über eine Zulassung entscheiden.

### Kann man diesen Impfstoff jetzt schon bekommen?

Noch nicht. Diese Behandlung ist nicht für die allgemeine Nutzung zugelassen.

Moderna sagt, dass die Unternehmen in den nächsten Monaten eine Zulassung beantragen wollen. Es gibt noch kein genaues Datum.

Wenn er zugelassen wird, wäre er zuerst für Menschen mit hohem Risiko nach einer Melanom-Operation, nicht für alle.

### Kann das auch bei anderen Krebsarten helfen?

Merck und Moderna testen diese Idee auch bei anderen Krebsarten, zum Beispiel Lungenkrebs, Blasenkrebs und Nierenkrebs.

Diese Studien laufen noch. Gute Ergebnisse beim Melanom bedeuten nicht automatisch, dass es bei jeder Krebsart wirkt.

### Was bedeutet diese Nachricht für dich?

Wenn du kein Melanom hast, brauchst du diesen Impfstoff nicht. Die besten Wege, das Melanom-Risiko zu senken, bleiben einfach:

- Schütze deine Haut vor der Sonne
- Vermeide Solarien
- Prüfe deine Haut und geh zur Ärztin oder zum Arzt, wenn sich ein Leberfleck ändert

Wenn du oder jemand nah bei dir ein Melanom hatte, ist das eine hoffnungsvolle Nachricht. Sprich mit einer Krebsspezialistin oder einem Krebsspezialisten. Sie können die aktuelle Behandlung erklären und sagen, ob eine klinische Studie eine Option sein kann.

Dieser Artikel ist Information, keine medizinische Beratung.

### Kurze Antworten

**Verhindert dieser Impfstoff Krebs?**
Nein. Es ist eine persönliche Behandlung nach einer Operation wegen Melanom.

**Ist er dasselbe wie der COVID-19-Impfstoff?**
Nein. Beide nutzen mRNA, aber dieser Impfstoff wird für den Tumor einer Person gemacht.

**Ist er zugelassen?**
Noch nicht. Die Unternehmen wollen bald eine Zulassung beantragen.

**Wirkt er besser als Keytruda allein?**
Ja, in dieser Phase-3-Studie. Die vollständigen Details folgen auf einem medizinischen Kongress.

### Quellen

- [Merck-Pressemitteilung, 19. August 2026](https://www.merck.com/news/merck-and-moderna-announce-phase-3-interpath-001-trial-of-intismeran-autogene-plus-keytruda-met-endpoints-of-recurrence-free-survival-rfs-and-distant-metastasis-free-survival-dmfs-in-patient/)
- [CNN Health, 19. August 2026](https://edition.cnn.com/2026/08/19/health/moderna-merck-mrna-melanoma-vaccine)`,
  },
  {
    slug: "why-vaccination-records-matter",
    date: "2026-06-10",
    category: "Gesundheitstipps",
    image: "/images/blog/why-vaccination-records-matter.webp",
    author: "Vaxy",
    readTime: "4 Min. Lesezeit",
    title: "Warum deine Impfnachweise wichtiger sind denn je",
    excerpt:
      "In einer zunehmend vernetzten Welt kann schneller Zugriff auf deine Impfgeschichte den Unterschied zwischen einer entspannten Reise und Stress an der Grenze machen.",
    content: `## Warum deine Impfnachweise wichtiger sind denn je

In einer zunehmend vernetzten Welt kann schneller Zugriff auf deine Impfgeschichte den Unterschied zwischen einer entspannten Reise und Stress an der Grenze machen.

### Das Problem mit Papier

Der gelbe WHO-Impfpass war jahrzehntelang der Standard für Gesundheitsnachweise auf Reisen. Doch Papier geht verloren, verblasst oder bleibt genau dann zu Hause, wenn du es brauchst. Viele Reisende kennen den Stress, wenn an der Grenze ein Nachweis für Gelbfieber verlangt wird und der Impfpass noch auf dem Küchentisch liegt.

### Was ohne Nachweise passieren kann

- **Einreise verweigert**, wenn bestimmte Impfungen vorgeschrieben sind
- **Unnötige Wiederholungsimpfungen**, weil Ärzte deine Historie nicht sehen können
- **Verpasste Auffrischungen**, weil es keine Erinnerungen gibt
- **Notfälle**, in denen medizinisches Personal deinen Impfstatus nicht kennt

### Die digitale Alternative

Apps wie Vaxy geben dir einen sicheren, jederzeit verfügbaren digitalen Überblick über jede Impfung, für dich und deine ganze Familie. Kein Papier, kein Rätselraten.

### Reisebestimmungen ändern sich

Länder ändern ihre Impfanforderungen regelmäßig. Vorgaben zu Gelbfieber, Meningokokken und COVID-19 haben sich in den letzten Jahren stark bewegt. Mit einem aktuellen digitalen Nachweis siehst du schnell, was du hast und was du vor der nächsten Reise noch brauchst.

### So fängst du an

Der beste Zeitpunkt zum Digitalisieren deiner Impfnachweise ist, bevor du sie brauchst. Nimm dir heute zehn Minuten für Vaxy, dann musst du nie wieder hektisch nach einem Papierheft suchen.`,
  },
  {
    slug: "travel-vaccinations-guide",
    date: "2026-05-28",
    category: "Reisegesundheit",
    image: "/images/blog/travel-vaccinations-guide.webp",
    author: "Vaxy",
    readTime: "7 Min. Lesezeit",
    title: "Der komplette Leitfaden für Reiseimpfungen 2026",
    excerpt:
      "Du planst eine Auslandsreise? Hier erfährst du, welche Impfungen vorgeschrieben oder empfohlen sind und wann du sie einplanen solltest.",
    content: `## Der komplette Leitfaden für Reiseimpfungen 2026

Du planst eine Auslandsreise? Hier erfährst du, welche Impfungen vorgeschrieben oder empfohlen sind und wann du sie einplanen solltest.

### 6-8 Wochen vorher planen

Der häufigste Fehler ist, bis kurz vor Abreise zu warten. Manche Impfungen brauchen mehrere Dosen über mehrere Wochen, und dein Immunsystem benötigt Zeit, um Schutz aufzubauen. Vereinbare die reisemedizinische Beratung mindestens 6-8 Wochen vor der Abreise.

### Vorgeschrieben oder empfohlen

**Vorgeschriebene** Impfungen sind für die Einreise in bestimmte Länder gesetzlich erforderlich. Das bekannteste Beispiel ist Gelbfieber: Viele Länder in Afrika und Südamerika verlangen einen Nachweis, wenn du aus einem Risikogebiet einreist.

**Empfohlene** Impfungen sind nicht gesetzlich verpflichtend, werden aber je nach Ziel, Aktivitäten und Gesundheitszustand dringend geraten.

### Häufige Reiseimpfungen nach Region

**Afrika**
- Gelbfieber, in vielen Ländern vorgeschrieben
- Typhus
- Hepatitis A & B
- Malariaprophylaxe, kein Impfstoff, aber oft wichtig
- Meningokokken, besonders für Hadsch-Reisende

**Südostasien**
- Hepatitis A & B
- Typhus
- Japanische Enzephalitis in ländlichen Gebieten
- Tollwut bei längeren Aufenthalten oder Tierkontakt

**Südamerika**
- Gelbfieber
- Typhus
- Hepatitis A & B

**Europa & Nordamerika**
- Meist reichen aktuelle Routineimpfungen
- FSME für Waldgebiete in Mittel- und Osteuropa

### Routineimpfungen nicht vergessen

Prüfe zuerst deine Basisimpfungen:
- MMR
- Tetanus, Diphtherie und Pertussis
- Grippe
- COVID-19

### Vaxy für die Reiseplanung nutzen

Mit der Reisegesundheitsfunktion von Vaxy wählst du dein Ziel und siehst sofort, welche Impfungen vorgeschrieben oder empfohlen sind. Vaxy vergleicht das mit deinen vorhandenen Nachweisen und zeigt genau, was fehlt.`,
  },
  {
    slug: "vaccinating-your-children",
    date: "2026-05-14",
    category: "Familiengesundheit",
    image: "/images/blog/vaccinating-your-children.webp",
    author: "Vaxy",
    readTime: "5 Min. Lesezeit",
    title: "Den Impfplan deiner Kinder im Blick behalten",
    excerpt:
      "Kinder erhalten in den ersten Lebensjahren viele Impfungen. So behältst du den Überblick und verpasst keine wichtige Dosis.",
    content: `## Den Impfplan deiner Kinder im Blick behalten

Kinder erhalten in den ersten Lebensjahren viele Impfungen. So behältst du den Überblick und verpasst keine wichtige Dosis.

### Der Impfplan für Kinder

In den Niederlanden und in den meisten europäischen Ländern folgen Kinder einem nationalen Impfprogramm. In den Niederlanden ist das Rijksvaccinatieprogramma, das Schutz von der Geburt bis ins Jugendalter bietet. Der Plan umfasst unter anderem:

- DKTP
- Hib
- Hepatitis B
- Pneumokokken
- Meningokokken
- MMR
- HPV für Mädchen und Jungen um das 10. Lebensjahr

### Warum eine verpasste Dosis zählt

Impfstoffe wirken am besten, wenn sie im richtigen Alter gegeben werden. Der Plan schützt Kinder, bevor sie für bestimmte Krankheiten besonders anfällig sind. Eine verpasste Dosis betrifft nicht nur dein Kind, sondern kann auch den Gemeinschaftsschutz schwächen.

### Die Herausforderung für Eltern

Bei vielen Impfungen in verschiedenen Altersstufen verliert man leicht den Überblick, besonders mit mehreren Kindern. Jedes Kind hat seinen eigenen Plan, und Papierhefte bei verschiedenen Arztbesuchen machen es nicht einfacher.

### Wie Vaxy Familien hilft

Mit Vaxy kannst du alle Familienmitglieder in einem Konto hinzufügen. Jede Person hat einen eigenen vollständigen Impfverlauf, und du erhältst Erinnerungen, wenn kommende Impfungen fällig werden.

### Tipps, um im Plan zu bleiben

1. **Ab Geburt erfassen**: füge dein Neugeborenes so früh wie möglich zu Vaxy hinzu
2. **Mit Arzt oder Beratungsstelle abgleichen**: lass Einträge nach jedem Besuch bestätigen
3. **Erinnerungen aktivieren**: nutze Pushmeldungen für kommende Dosen
4. **Notizen hinzufügen**: dokumentiere Reaktionen oder besondere Umstände pro Impfung`,
  },
];

const fr: BlogPost[] = [
  {
    slug: "west-nile-virus",
    date: "2026-09-11",
    category: "Maladies infectieuses",
    image: "/images/blog/west-nile-virus-hero.svg",
    author: "Vaxy",
    readTime: "6 min de lecture",
    title:
      "Virus du Nil occidental : ce que c'est, les risques et ce qui peut se passer ensuite",
    excerpt:
      "Le virus du Nil occidental se transmet par les moustiques. La plupart des gens ne tombent pas malades, mais un petit groupe peut avoir une forme grave. Que peut-il se passer dans les prochaines années ?",
    faq: [
      {
        q: "Qu'est-ce que le virus du Nil occidental ?",
        a: "C'est un virus que l'on trouve surtout chez les oiseaux. Les moustiques peuvent le transmettre aux humains et aux chevaux.",
      },
      {
        q: "Est-ce dangereux ?",
        a: "La plupart des gens ne remarquent rien ou ont seulement des symptômes légers. Un petit groupe tombe gravement malade, surtout les personnes âgées et celles qui sont plus fragiles.",
      },
      {
        q: "Peut-on l'attraper d'une autre personne ?",
        a: "Non, pas par contact normal. Ce sont les moustiques qui le transmettent. Plus rarement, il peut passer par le sang ou une greffe.",
      },
      {
        q: "Va-t-il devenir plus fréquent dans les prochaines années ?",
        a: "L'OMS et l'ECDC disent que des étés plus chauds et des saisons de moustiques plus longues donnent plus de temps au virus. En Europe, il peut donc rester actif plus longtemps et apparaître dans plus de régions.",
      },
    ],
    content: `## Virus du Nil occidental

Le virus du Nil occidental se trouve surtout chez les oiseaux. Les moustiques peuvent le transmettre aux humains et aux chevaux. La plupart des gens ne remarquent rien ou n'ont que des symptômes légers.

### Quels sont les risques ?

Les symptômes ressemblent souvent à une grippe: fièvre, maux de tete, douleurs musculaires et fatigue. Un petit groupe tombe gravement malade. Cela arrive plus souvent chez les personnes agees et chez les personnes plus fragiles. Le virus peut alors toucher le cerveau ou les nerfs.

Il n'existe pas de vaccin pour les humains et aucun medicament ne retire le virus lui-meme. A l'hopital, les medecins peuvent toutefois soigner les symptomes et donner les soins necessaires.

### Que peut-il se passer dans les prochaines années ?

L'OMS et l'ECDC expliquent que des etes plus chauds et des saisons de moustiques plus longues donnent plus de temps au virus. En Europe, il peut donc rester actif plus longtemps et apparaître dans plus de regions.

Aux Pays-Bas, le RIVM surveille déjà le virus chez les oiseaux et les moustiques. De nouveaux cas humains restent donc possibles quand le temps et les moustiques s'y preteront.

### Que pouvez-vous faire ?

Protégez-vous contre les moustiques, surtout le soir et tôt le matin. Utilisez un répulsif avec DEET, portez des vêtements couvrants et dormez sous une moustiquaire si besoin.

Retirez l'eau stagnante autour de la maison et du jardin, car les moustiques peuvent s'y reproduire. Si vous avez une forte fièvre, une nuque raide ou de la confusion après une piqûre, contactez un médecin tout de suite.

### Réponses courtes

**Peut-on attraper le virus du Nil occidental d'une autre personne ?**
Non, pas par contact normal. Ce sont les moustiques qui le transmettent. Plus rarement, le sang ou une greffe peuvent aussi jouer un rôle.

**Qui a un risque plus élevé de forme grave ?**
Surtout les personnes âgées et les personnes plus fragiles. La plupart des autres personnes ne tombent pas malades ou seulement peu.

**Existe-t-il un vaccin ou un traitement ?**
Non, pas pour les humains. C'est pour cela que la prévention contre les moustiques est si importante.

**Quel est le meilleur conseil pour les prochaines années ?**
Éviter les piqûres de moustiques. C'est la meilleure protection, maintenant et plus tard.

### Sources

- [OMS Europe: West Nile virus, as cases rise across parts of Europe](https://www.who.int/europe/news/item/24-07-2026-west-nile-virus--as-cases-rise-across-parts-of-europe--here-is-what-you-need-to-know)
- [OMS: vector-borne diseases](https://www.who.int/news-room/fact-sheets/detail/vector-borne-diseases)
- [RIVM: West Nile fever](https://www.rivm.nl/en/west-nile-fever)
- [RIVM: Westnijlkoorts, actualités](https://www.rivm.nl/westnijlkoorts/actueel)
- [ECDC: surveillance and updates for West Nile virus infection](https://www.ecdc.europa.eu/en/topics-z/west-nile-virus-infection/surveillance-and-updates-west-nile-virus-infection)

Ceci est une information, pas un avis medical.`,
  },
  {
    slug: "future-of-vaccination",
    date: "2026-08-26",
    category: "Conseils santé",
    image: "/images/blog/future-of-vaccination.webp",
    author: "Vaxy",
    readTime: "8 min de lecture",
    title: "Les alternatives futures à la piqûre vaccinale",
    excerpt:
      "La plupart des vaccins passent encore par une aiguille. Gouttes, vaccin nasal et injection sans aiguille existent déjà. Les patchs de Micron, Vaxxas et d'autres restent de la recherche. OMS, Gavi et RIVM, en clair.",
    faq: [
      {
        q: "Quelles alternatives à l'aiguille existent déjà ?",
        a: "Vaccins oraux contre la polio, gouttes rotavirus et vaccin oral contre le choléra. Dans certains pays, un vaccin nasal contre la grippe. PharmaJet Tropis est un injecteur sans aiguille préqualifié par l'OMS, utilisé dans des campagnes polio. Le programme néerlandais reste une piqûre en standard.",
      },
      {
        q: "Qu'est-ce qu'un patch à micro-aiguilles ?",
        a: "Un petit patch cutané avec des pointes très courtes qui déposent le vaccin dans la peau superficielle. L'OMS cite deux candidats rougeole-rubéole : Micron Biomedical (dissolvable) et Vaxxas (enduit, avec applicateur). La phase 3 et l'approbation OMS restent à venir.",
      },
      {
        q: "Puis-je déjà être vacciné sans aiguille aux Pays-Bas ?",
        a: "Les bébés reçoivent déjà le rotavirus en gouttes. Sinon le RIVM vaccine encore avec aiguille et seringue dans le programme national. Les patchs cutanés ne sont pas un produit de routine.",
      },
      {
        q: "Pourquoi l'OMS cherche-t-elle des alternatives ?",
        a: "Parce que la couverture stagne. Seringues, reconstitution et chaîne du froid rendent la vaccination difficile, surtout là où les enfants n'ont reçu aucune dose. Un produit plus simple peut atteindre plus de monde.",
      },
      {
        q: "Est-ce un avis médical ?",
        a: "Non. C'est une explication basée sur l'OMS, Gavi, BARDA et le RIVM. Le vaccin dont vous avez besoin se décide avec votre médecin, le GGD ou un centre de vaccination voyage.",
      },
    ],
    content: `## Les alternatives futures à la piqûre vaccinale

La plupart des vaccins passent encore par une aiguille dans le haut du bras. Cela fonctionne. C'est aussi compliqué : seringues, parfois reconstitution d'une poudre, chaîne du froid, et des gens qui préfèrent éviter la piqure.

L'Organisation mondiale de la Santé (OMS) dit : ce mode d'administration est l'une des raisons pour lesquelles la couverture vaccinale a plafonné. En 2020, 23 millions d'enfants ont manqué leurs doses, 3,7 millions de plus qu'en 2019.

C'est pourquoi l'OMS, Gavi, l'UNICEF, PATH et la Fondation Bill & Melinda Gates cherchent d'autres façons de vacciner. Leur Vaccine Innovation Prioritisation Strategy (VIPS) a choisi en mai 2020 trois priorités : les patchs à micro-aiguilles, les vaccins thermostables, et les codes-barres sur l'emballage primaire.

Tout n'est pas du futur. Certaines alternatives à l'aiguille existent déjà. D'autres sont en essai. Ceci est une information, pas un avis médical. Dans le programme national néerlandais, la piqûre reste la norme.

### Qu'est-ce qui existe déjà sans aiguille ?

Depuis longtemps :

- vaccins oraux contre la polio
- gouttes rotavirus pour les bébés, y compris aux Pays-Bas
- vaccin oral contre le choléra
- dans certains pays, un vaccin nasal contre la grippe

Les vaccins nasaux ciblent les muqueuses, là où les virus respiratoires entrent. Des chercheurs travaillent aussi sur des vaccins nasaux contre d'autres infections respiratoires. La plupart des vaccins COVID-19 en Europe restent une injection dans le bras.

**PharmaJet Tropis** est autre chose : pas un patch, mais un fin jet de liquide dans la peau, sans aiguille. L'OMS a préqualifié l'appareil pour l'administration intradermique. Il a été utilisé dans des campagnes polio, notamment au Pakistan et au Nigeria. C'est le plus proche de « déjà sans aiguille sur le terrain ». Il ne remplace pas le DTP ou le ROR aux Pays-Bas.

### Patchs cutanés : Micron, Vaxxas et d'autres

Un microarray patch, ou MAP, est un petit patch cutané avec des pointes très courtes. Trop courtes pour une injection classique. Elles déposent le vaccin dans la peau superficielle, là où se trouvent beaucoup de cellules immunitaires.

L'OMS cite notamment ces avantages : pas de reconstitution ni de seringue, moins de douleur, éventuellement une meilleure stabilité hors du réfrigérateur, administration par un personnel moins spécialisé.

Pour la rougeole et la rubéole, l'OMS cite deux candidats cliniques :

**Micron Biomedical** fabrique un patch dissolvable. Il reste quelques minutes au poignet. Leur étude de phase 1/2 chez des adultes, des tout-petits et des nourrissons en Gambie a été publiée dans *The Lancet*. Sécurité et réponse immunitaire semblaient comparables à l'injection sous-cutanée habituelle. Chez les nourrissons, c'est le MAP le plus avancé.

**Vaxxas** fabrique un High-Density Microarray Patch : des milliers de pointes d'environ 0,25 millimètre, avec vaccin séché, et un applicateur à usage unique sur le bras ou la cuisse. Leur essai adulte rougeole-rubéole est terminé. Une phase pédiatrique était prévue ensuite.

La phase 3, l'autorisation et la préqualification OMS restent à venir. N'en attendez pas un à votre prochaine DTP ou ROR.

D'autres entreprises mettent de l'ARNm sur un patch. C'est plus tôt. Le Patch Forward Prize de la BARDA américaine a cité, outre Vaxxas et Micron, **LTS Lohmann** (Allemagne) avec **BioNet**, et **PopVax** (Inde) avec LTS. Objectif : un vaccin grippe ou COVID en ARN sur un patch à micro-aiguilles. Pas un produit de routine.

### Vaccins thermostables

La deuxième priorité VIPS : des vaccins qui peuvent rester un temps limité hors du réfrigérateur, en chaîne à température contrôlée. Moins de glace, moins de pertes, plus de portée. Cela peut aller avec un patch ou une seringue classique.

L'ARNm est un autre saut : fabriquer plus vite face à un nouveau variant. Ces vaccins passent encore par une aiguille, et demandent souvent un froid plus strict.

### Qu'est-ce que cela change pour votre carnet ?

Piqure, gouttes, jet ou plus tard un patch : la date, la maladie et le nom du produit restent nécessaires. Pour un voyage, un rappel ou une nouvelle épidémie, un médecin doit savoir ce que vous avez déjà.

Avec Vaxy, vous gardez cela en un seul endroit, pour vous et votre famille.

Un doute sur un vaccin ? Demandez à votre médecin, au GGD ou à un centre de vaccination voyage.

### Réponses courtes

**Quelles alternatives à l'aiguille existent déjà ?**
Gouttes (polio, rotavirus, choléra), dans certains pays un vaccin nasal contre la grippe, et l'injection sans aiguille PharmaJet dans certaines campagnes polio.

**Qu'est-ce qu'un patch à micro-aiguilles ?**
Un petit patch avec des pointes courtes. Micron et Vaxxas sont les deux candidats cités par l'OMS pour la rougeole et la rubéole.

**Puis-je déjà éviter l'aiguille aux Pays-Bas ?**
Le rotavirus déjà en gouttes. Sinon le RIVM vaccine encore avec aiguille et seringue. Les patchs sont de la recherche.

**Pourquoi l'OMS veut-elle cela ?**
Une administration plus simple peut atteindre plus de monde, surtout les enfants qui n'ont aujourd'hui aucune dose.

### Sources

- [OMS : administration innovante des vaccins, y compris les patchs à micro-aiguilles](https://www.who.int/teams/immunization-vaccines-and-biologicals/product-and-delivery-research/delivery-technologies)
- [OMS : groupe consultatif technique sur les patchs rougeole-rubéole](https://www.who.int/news-room/articles-detail/2023-November-132)
- [OMS : profil de produit cible pour le MAP rougeole-rubéole](https://www.who.int/publications/i/item/9789240000209)
- [The Lancet : patch Micron rougeole-rubéole, phase 1/2 en Gambie](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(24)00532-4/fulltext)
- [OMS PQS : PharmaJet Tropis, injecteur sans aiguille](https://extranet.who.int/prequal/immunization-devices/e008-071)
- [BARDA : Patch Forward Prize, lauréats du concept](https://www.patchforwardprize.com/)
- [Gavi : Vaccine Innovation Prioritisation Strategy (VIPS)](https://www.gavi.org/our-alliance/market-shaping/vaccine-innovation-prioritisation-strategy)
- [RIVM : programme national de vaccination](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "philanthropy-global-infectious-diseases",
    date: "2026-08-26",
    category: "Actualités santé",
    image: "/images/blog/philanthropy-global-infectious-diseases.webp",
    author: "Vaxy",
    readTime: "8 min de lecture",
    title:
      "Comment la philanthropie pense les maladies infectieuses : Gates, Gavi et partenaires",
    excerpt:
      "La Fondation Gates, Gavi, Wellcome et CEPI voient les vaccins comme l'un des outils les plus forts contre les maladies infectieuses dans le monde. L'OMS fixe le cap. Voici comment cette coopération fonctionne.",
    faq: [
      {
        q: "Que veut la Fondation Gates sur les maladies infectieuses ?",
        a: "Atteindre enfants, adolescents et adultes des pays à faible revenu avec les vaccins dont ils ont besoin, et empêcher la génération suivante de mourir de maladies infectieuses. La fondation appelle les vaccins l'un des outils de santé les plus rentables : 3,5 à 5 millions de vies par an.",
      },
      {
        q: "Qu'est-ce que Gavi ?",
        a: "Gavi, the Vaccine Alliance, est un partenariat public-privé. États, OMS, UNICEF, Banque mondiale, industrie du vaccin et Fondation Gates y travaillent. Gavi achète et distribue des vaccins pour les pays à faible revenu.",
      },
      {
        q: "Bill Gates fixe-t-il la politique vaccinale néerlandaise ?",
        a: "Non. Aux Pays-Bas, le RIVM, le Conseil de la santé et le ministère de la Santé fixent la politique. L'OMS publie des orientations mondiales. Les fonds philanthropiques financent recherche et achats. Ils n'écrivent pas le calendrier national.",
      },
      {
        q: "Quels autres fonds font ce travail ?",
        a: "Wellcome Trust et CEPI (vaccins d'épidémie), Rotary International (polio) et Gavi elle-même, entre autres. Ils travaillent en général avec l'OMS et les États, pas à leur place.",
      },
      {
        q: "Est-ce un avis médical ?",
        a: "Non. Ceci explique une stratégie mondiale. Pour vos vaccins : médecin, GGD ou centre de vaccination voyage.",
      },
    ],
    content: `## Comment la philanthropie pense les maladies infectieuses : Gates, Gavi et partenaires

Les maladies infectieuses ne s'arrêtent pas à une frontière. Les fonds philanthropiques raisonnent ainsi : si un vaccin manque à Lagos ou à Lahore, la même maladie peut apparaître plus tard en Europe. Les vaccins sont pour eux l'outil le plus net et le moins cher.

La Fondation Bill & Melinda Gates est le plus grand acteur privé. Elle ne travaille pas seule. L'Organisation mondiale de la Santé (OMS) fixe le cap. Gavi achète et distribue. Wellcome et CEPI financent la recherche sur les nouvelles épidémies.

Ceci explique cette stratégie, ce n'est pas un avis médical. Les vaccins néerlandais suivent le programme national, pas une fondation à Seattle.

### Comment pense la Fondation Gates ?

La fondation le dit brièvement : les vaccins sauvent 3,5 à 5 millions de vies par an. Depuis que l'OMS a lancé le Programme élargi de vaccination en 1974, environ 154 millions de vies ont été sauvées, surtout des enfants.

Son objectif dans les pays à faible revenu : atteindre enfants, adolescents et adultes avec les doses dont ils ont besoin. Et un monde sans maladies que l'on peut prévenir par un vaccin.

Trois lignes reviennent toujours :

1. **Acheter et distribuer ce qui existe déjà.** Gavi est leur plus grand bénéficiaire. De 2000 à 2023, Gavi a vacciné plus de 1,1 milliard d'enfants et évité plus de 18,8 millions de décès futurs, selon la fondation.
2. **Rendre les nouveaux vaccins abordables.** Pneumocoque, rotavirus, HPV, choléra, méningite A.
3. **Renforcer les soins ordinaires.** Un programme de vaccination est pour eux la colonne vertébrale des soins primaires.

En juin 2025, la fondation a promis 1,6 milliard de dollars à Gavi sur cinq ans. Depuis 2000, plus de 30,6 milliards de dollars sont allés aux vaccins, dont 7,7 milliards à Gavi.

Cela s'aligne sur l'Immunization Agenda 2030 de l'OMS : chacun, partout, à tout âge. Et sur Gavi 6.0 (2026–2030) : vacciner plus de 500 millions d'enfants et d'adolescents supplémentaires, sauver 8 à 9 millions de vies de plus.

### Où voient-ils le problème ?

Les progrès stagnent. En 2023, 14,5 millions d'enfants n'ont reçu aucun vaccin, plus que l'année précédente. Les épidémies de rougeole ont augmenté. La fondation y voit un risque pour des décennies de gains.

Leur réponse n'est pas un nouveau vaccin, mais la portée : introductions du HPV, campagnes rougeole, et un effort extra dans les zones à plus faible couverture, y compris en conflit.

La polio reste une priorité. Depuis 2007, la fondation soutient l'Initiative mondiale pour l'éradication de la poliomyélite, avec l'OMS, Rotary International, CDC, UNICEF et Gavi. La polio sauvage existe encore au Pakistan et en Afghanistan. Depuis 1988, les cas ont chuté de plus de 99 %.

### Wellcome, CEPI et Rotary

D'autres fonds pensent dans la même direction, avec un autre rôle.

**Wellcome Trust** finance la science et a cofondé CEPI en 2017.

**CEPI**, la Coalition for Epidemic Preparedness Innovations, existe pour avoir des vaccins prêts avant la prochaine épidémie. États, Gates et Wellcome l'ont créée après Ebola. L'objectif : un vaccin plus rapide face à une maladie X inconnue, dans leur vision en 100 jours. CEPI n'écrit pas les calendriers. Cela reste à l'OMS et aux pays.

**Rotary International** collecte depuis des décennies pour la polio, comme partenaire du GPEI.

L'alliance VIPS (Gavi, OMS, Fondation Gates, UNICEF, PATH) montre comment ces clubs choisissent ensemble des innovations, comme les patchs cutanés. La philanthropie paie souvent les premiers pas. Les États et l'OMS décident si un produit entre dans un programme.

### Qu'est-ce que cela change pour les Pays-Bas ?

Rien dans votre carnet n'est fixé par une fondation. Le RIVM, le Conseil de la santé et le ministère de la Santé s'en chargent.

Cela vous touche tout de même. Les achats mondiaux maintiennent rougeole, polio et choléra plus petits. Vaccins voyage, préparation aux pandémies et nouvelles formes d'administration viennent du même réseau.

Notez la date et le nom de chaque dose. Avec Vaxy, vous gardez cet historique, y compris si vous voyagez plus tard dans un pays où Gavi fait les achats.

Cet article est une information, pas un avis médical.

### Réponses courtes

**Que veut la Fondation Gates ?**
Faire parvenir les vaccins aux personnes des pays à faible revenu, et prévenir les maladies infectieuses pour la génération suivante.

**Qu'est-ce que Gavi ?**
Une alliance d'États, OMS, UNICEF, Banque mondiale, industrie et Fondation Gates qui achète des vaccins pour les pays plus pauvres.

**La philanthropie fixe-t-elle la politique néerlandaise ?**
Non. RIVM, Conseil de la santé et ministère s'en chargent. L'OMS donne des orientations mondiales.

**Qui d'autre y travaille ?**
Wellcome, CEPI, Rotary, UNICEF et des donateurs nationaux, entre autres.

### Sources

- [Fondation Gates : Immunization](https://www.gatesfoundation.org/our-work/programs/global-development/immunization)
- [Fondation Gates : promesse de 1,6 milliard de dollars à Gavi, juin 2025](https://www.gatesfoundation.org/ideas/media-center/press-releases/2025/06/renewed-gavi-commitment-lifesaving-vaccine-access)
- [Fondation Gates : polio](https://www.gatesfoundation.org/our-work/programs/global-development/polio)
- [OMS : Immunization Agenda 2030](https://www.who.int/teams/immunization-vaccines-and-biologicals/strategies/ia2030)
- [OMS : couverture vaccinale, fiche](https://www.who.int/news-room/fact-sheets/detail/immunization-coverage)
- [Gavi 6.0, stratégie 2026–2030](https://www.gavi.org/our-work/strategy/phase-6-2026-2030)
- [CEPI](https://cepi.net/)
- [RIVM : programme national de vaccination](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "climate-diseases-netherlands",
    date: "2026-08-26",
    category: "Conseils santé",
    image: "/images/blog/climate-diseases-netherlands.webp",
    author: "Vaxy",
    readTime: "8 min de lecture",
    title:
      "Nouvelles maladies infectieuses aux Pays-Bas à cause du changement climatique",
    excerpt:
      "La dengue reste une maladie d'importation dans les Pays-Bas européens. RIVM, Conseil de la santé et ECDC disent : temps plus chaud, tiques et moustiques changent le risque. Qu'est-ce qui arrive, qu'est-ce qui est déjà là ?",
    faq: [
      {
        q: "La dengue est-elle déjà aux Pays-Bas ?",
        a: "Dans les Pays-Bas européens, seulement chez les voyageurs. Le RIVM n'a pas confirmé d'infection locale. Dans les Pays-Bas caribéens, la dengue existe, avec de plus grandes épidémies en 2023–2024. L'Europe du Sud a déjà des cas locaux, notamment en France, en Italie et en Espagne.",
      },
      {
        q: "Le moustique tigre va-t-il s'installer ici ?",
        a: "Pas encore. La NVWA combat les introductions. Le RIVM dit : plus de moustiques tigres arrivent surtout par le commerce et les voyages, pas par le climat. Le Conseil de la santé attend une installation sur certains sites d'ici 2 à 5 ans. Alors, des étés plus chauds pourraient permettre une dengue locale après introduction par un voyageur.",
      },
      {
        q: "Quelles maladies ont déjà changé ici ?",
        a: "Encéphalite à tiques dans les tiques néerlandaises depuis 2016, avec 8 signalements d'infection acquise aux Pays-Bas en 2024. Virus du Nil occidental chez 8 personnes en 2020. La maladie de Lyme via les tiques est là depuis longtemps. Une eau plus chaude augmente le risque de vibrions et de légionelles.",
      },
      {
        q: "Dois-je me faire vacciner contre la dengue ou l'encéphalite à tiques aux Pays-Bas ?",
        a: "Le RIVM dit : pas de vaccin TBE nécessaire pour vivre aux Pays-Bas. Pour voyager dans des pays où TBE ou dengue sont fréquents : demandez au LCR, à votre médecin ou à un centre voyage. Des vaccins existent pour les deux.",
      },
      {
        q: "Est-ce un avis médical ?",
        a: "Non. C'est une explication basée sur le RIVM, le Conseil de la santé, l'ECDC et l'OMS. Après une piqûre de moustique ou de tique avec des symptômes : médecin.",
      },
    ],
    content: `## Nouvelles maladies infectieuses aux Pays-Bas à cause du changement climatique

La dengue sonne tropicale. Dans les Pays-Bas européens, elle l'est encore : une maladie que les voyageurs ramènent. Le Conseil de la santé a pourtant prévenu en mai 2026 : le changement climatique rend les conditions ici plus favorables aux maladies transmises par moustiques, tiques et eau.

Le RIVM est plus prudent. Le climat est un facteur. Commerce, voyages et mode de vie pèsent souvent davantage. Un nouveau moustique aux Pays-Bas n'est pas automatiquement la faute d'un été plus chaud.

Ceci est une information, pas un avis médical.

### Dengue : importée maintenant, peut-être locale plus tard

La dengue se transmet par le moustique de la fièvre jaune et le moustique tigre asiatique. L'OMS estime des centaines de millions d'infections par an dans le monde. Fièvre, douleurs musculaires, parfois des hémorragies graves.

Selon la directive LCI du RIVM, la dengue dans les Pays-Bas européens est une maladie d'importation. Aucune infection locale n'a été confirmée. Les tests positifs chez les voyageurs de retour ont augmenté : 136 par an en moyenne en 2014–2019, 367 en 2023 et 544 en 2024.

Dans les Pays-Bas caribéens, la dengue existe. Le RIVM cite des dizaines de cas par an et de plus grandes épidémies en 2023–2024. Le Conseil de la santé donne la priorité à ces îles, car les risques y sont plus graves et plus divers.

L'Europe du Sud a déjà connu une dengue locale. L'ECDC suit des cas en France, en Italie, en Espagne et en Croatie, entre autres. En 2026, jusqu'au début d'août, seule la France avait signalé un cas de dengue acquis localement sur le continent européen.

Des vaccins contre la dengue existent. Aux Pays-Bas, ils sont destinés aux voyageurs, pas à toute la population. Demandez au LCR ou à un centre de vaccination voyage.

### Le moustique tigre : la lutte gagne du temps

Le moustique tigre asiatique (*Aedes albopictus*) peut transmettre dengue, chikungunya et zika. Il est bien installé autour de la Méditerranée. Pas encore aux Pays-Bas, grâce à la lutte du ministère de la Santé et de la NVWA.

Le RIVM est clair : plus de découvertes aux Pays-Bas viennent surtout du commerce et des voyages, pas du changement climatique. Si le moustique s'installe, le climat compte alors. À des températures estivales plus élevées, les virus se multiplient plus vite chez le moustique.

Le Conseil de la santé attend une installation sur certains sites d'ici 2 à 5 ans. Ensuite, des foyers locaux de dengue peuvent suivre si un voyageur virémique apporte le virus.

En août 2026, la NVWA a publié une étude ADN : le moustique arrive par plusieurs introductions, notamment via pneus usagés et aéroports. Sur certains sites, il a survécu à l'hiver. La lutte peut quand même éliminer une population.

### Qu'est-ce qui est déjà là ?

Tout n'est pas du futur.

**Encéphalite à tiques (TBE).** Depuis 2016, on sait que des tiques néerlandaises peuvent porter le virus, notamment sur la Sallandse et l'Utrechtse Heuvelrug. Entre 2016 et juin 2024, 23 patients ont contracté l'infection aux Pays-Bas. En 2024, 8 signalements. Depuis le 1er mars 2025, la TBE avec symptômes neurologiques est à déclaration obligatoire. Environ 1 tique sur 1 500 dans les zones connues porte le virus. Pour Lyme, environ 1 sur 5. Le RIVM dit : pas de vaccin TBE nécessaire pour vivre aux Pays-Bas. Oui pour voyager dans des pays où la TBE est fréquente.

**Fièvre du Nil occidental.** En 2020, 8 personnes aux Pays-Bas ont contracté le virus, surtout autour d'Utrecht, via le moustique domestique commun. Le virus circule chez les oiseaux. Le Conseil de la santé dit que des étés plus chauds favorisent la transmission. Le risque pour la plupart des gens reste faible.

**Lyme.** Déjà la maladie à tiques la plus fréquente ici. Plus de tiques par temps plus doux augmentent le contact, note aussi le Conseil de la santé.

**Eau.** Les eaux côtières néerlandaises sont devenues plus favorables aux *Vibrio*. Une eau de surface plus chaude aide aussi légionelles et cyanobactéries.

Chikungunya et zika suivent la même histoire de moustiques que la dengue : surtout un risque voyage maintenant, éventuellement local plus tard si le moustique tigre reste.

### Que pouvez-vous faire ?

- Voyage vers des zones de dengue ou de TBE : demandez à temps si une vaccination a un sens.
- Aux Pays-Bas : chercher des tiques après un passage dans la nature, limiter les piqûres de moustiques le soir. Fièvre après un voyage tropical : médecin, et dites où vous étiez.

Notez vaccins et doses de voyage. Avec Vaxy, vous voyez avant le départ si dengue ou TBE figure déjà dans votre dossier.

Cet article est une information, pas un avis médical.

### Réponses courtes

**La dengue est-elle déjà là ?**
Dans les Pays-Bas européens, seulement chez les voyageurs. Oui dans les Pays-Bas caribéens. La transmission locale existe déjà en Europe du Sud.

**Le moustique tigre va-t-il s'installer ?**
Pas encore, grâce à la lutte. Le Conseil de la santé l'attend sur certains sites d'ici 2 à 5 ans.

**Quelles maladies sont déjà là ?**
TBE depuis 2016, Nil occidental chez l'humain en 2020, Lyme depuis plus longtemps. Eau plus chaude : vibrions et légionelles.

**Dois-je me vacciner ici contre dengue ou TBE ?**
En général non pour vivre dans les Pays-Bas européens. Parfois oui pour certains voyages. Demandez au LCR, à votre médecin ou au GGD.

### Sources

- [RIVM : changement climatique et maladies infectieuses](https://www.rivm.nl/klimaat-en-gezondheid/infectieziekten)
- [RIVM : dengue](https://www.rivm.nl/dengue)
- [Directive LCI dengue](https://lci.rivm.nl/richtlijnen/dengue)
- [RIVM : fièvre du Nil occidental](https://www.rivm.nl/westnijlkoorts)
- [RIVM : encéphalite à tiques](https://www.rivm.nl/tekenencefalitis)
- [Conseil de la santé : climat et santé, 21 mai 2026](https://www.gezondheidsraad.nl/actueel/nieuws/2026/05/21/bescherm-de-bevolking-tegen-de-toenemende-gezondheidsrisicos-van-klimaatverandering)
- [ECDC : risque dengue UE/EEE](https://www.ecdc.europa.eu/en/dengue/surveillance-and-updates/risk-assessment)
- [OMS : dengue](https://www.who.int/news-room/fact-sheets/detail/dengue-and-severe-dengue)
- [NVWA : étude ADN du moustique tigre asiatique, 20 août 2026](https://www.nvwa.nl/actueel/nieuws/2026/08/20/dna-onderzoek-biedt-nvwa-nieuw-inzicht-in-verspreiding-en-bestrijding-van-de-aziatische-tijgermug)
- [LCR : informations sur les vaccinations](https://lcr.nl/informatie-vaccinaties)`,
  },
  {
    slug: "vaccine-boosters",
    date: "2026-08-22",
    category: "Conseils santé",
    image: "/images/blog/vaccine-boosters.webp",
    author: "Vaxy",
    readTime: "8 min de lecture",
    title:
      "Quels vaccins ont un rappel, et combien de temps êtes-vous protégé ?",
    excerpt:
      "Tous les vaccins n'ont pas besoin d'une nouvelle dose. Le RIVM, le LCI et le LCR indiquent quand un rappel est dû, et combien de temps dure ensuite la protection.",
    faq: [
      {
        q: "Quels vaccins ont un rappel ?",
        a: "Surtout le DTP (tous les 10 ans), la typhoïde (tous les 3 ans), l'encéphalite à tiques, l'encéphalite japonaise et le choléra. La grippe et le COVID pour les groupes à risque sont des vaccins annuels, pas des rappels classiques.",
      },
      {
        q: "À quelle fréquence répéter le DTP ?",
        a: "Tous les 10 ans après une série complète. Le programme néerlandais et le LCR disent: 10 ans de protection contre la diphtérie et le tétanos. Après le calendrier enfant, vous êtes probablement protégé à vie contre la paralysie due à la polio.",
      },
      {
        q: "Faut-il refaire l'hépatite A plus tard ?",
        a: "Non si vous avez eu deux doses. Le LCR dit: 1 an après la première, 50 ans après la deuxième. Le LCI dit qu'une dose supplémentaire n'est pas nécessaire chez les personnes en bonne santé.",
      },
      {
        q: "La fièvre jaune est-elle valable à vie ?",
        a: "Le plus souvent oui. Une dose protège à vie chez la plupart des gens. Le certificat est valable à vie depuis 2016. Certains groupes, comme les personnes vivant avec le VIH, ont tout de même besoin d'un rappel.",
      },
      {
        q: "Est-ce un avis médical ?",
        a: "Non. C'est une explication d'après le RIVM, le LCI et le LCR. En cas de doute, demandez à votre médecin, au service de santé publique ou à une clinique du voyageur.",
      },
    ],
    content: `## Quels vaccins ont un rappel, et combien de temps êtes-vous protégé ?

Tous les vaccins n'ont pas besoin d'une nouvelle dose. Certaines séries protègent longtemps, ou à vie, une fois terminées. D'autres vaccins doivent être refaits plus tard.

Cet aperçu concerne les personnes en bonne santé aux Pays-Bas, d'après le programme national (RVP), les recommandations LCI du RIVM et le centre néerlandais de santé des voyageurs (LCR). C'est une information, pas un avis médical.

### Qu'est-ce qu'un rappel ?

Un rappel est une dose supplémentaire après une série primaire complète, parce que la protection diminue.

Ce n'est pas la même chose que:

- la deuxième ou troisième dose d'une série, comme l'hépatite A ou B
- un vaccin annuel, comme la grippe, parce que le virus change
- des doses extra après une morsure ou une plaie, comme la rage ou le tétanos

### Vaccins qui ont besoin d'un rappel

**DTP (diphtérie, tétanos, polio).** Tous les 10 ans après une série complète. Jamais vacciné ? Commencez par trois doses. Le RVP dit: après la dose DTP à 14 ans, vous êtes protégé environ 10 ans contre la diphtérie et le tétanos, et probablement à vie contre la paralysie due à la polio. Le LCR donne le même intervalle de 10 ans aux voyageurs.

**Coqueluche.** Le vaccin acellulaire protège environ 4 à 6 ans selon le LCI. Les enfants ont un rappel à 5 ans. Les adultes n'ont pas d'intervalle fixe. Pendant la grossesse, le RIVM conseille le vaccin coqueluche à partir de 22 semaines, à chaque grossesse.

**Typhoïde.** Une dose protège 3 ans, dit le LCR. La protection n'est pas complète. Répétez tous les 3 ans si le risque continue.

**Encéphalite japonaise.** Deux doses en un mois donnent 2 ans de protection. À partir d'un an plus tard, une troisième dose est possible. Ensuite 10 ans, selon le LCR.

**Encéphalite à tiques.** Trois doses (mois 0, 1 et 6). Puis un rappel après 3 ans. Ensuite tous les 5 ans avant 60 ans, et tous les 3 ans à partir de 60 ans, selon le LCI. Pour les voyageurs de 50 ans et plus, le LCR utilise parfois une dose extra dans la série primaire.

**Choléra.** Les voyageurs ordinaires aux Pays-Bas ne le reçoivent en général pas. Selon le LCR, une vaccination protège 6 mois chez les enfants de 2 à 5 ans, et 2 ans chez les plus âgés et les adultes.

**Méningocoque ACWY.** Une dose protège au moins 10 ans, dit le LCR. Pour les pèlerins de La Mecque, le certificat vaut 5 ans.

**Fièvre jaune.** En général pas de rappel. Une dose protège à vie chez la plupart des gens. Le certificat est valable à vie depuis le 1er juillet 2016. Le LCI conseille tout de même un rappel après vaccination pendant la grossesse, le VIH, ou un travail de laboratoire avec le virus.

**Rage.** Deux doses avant le voyage donnent une protection de base à vie, dit le LCI. Pas de rappel de routine pour les voyageurs. Après une morsure, une griffure ou un léchage, des doses extra sont toujours nécessaires.

**Grippe.** Chaque année, pour les personnes à risque. Le LCI dit: les anticorps restent à niveau environ 24 semaines. Le vaccin s'essouffle après six mois, et le virus change.

**COVID-19.** Une campagne d'automne pour les groupes à risque. Le RIVM préfère 6 mois après la dose précédente, minimum 3 mois.

**Pneumocoque.** L'ancien vaccin PPV23 se répète tous les 5 ans. Après le vaccin conjugué plus récent PCV20, les adultes n'ont pour l'instant pas besoin d'une nouvelle dose, dit le LCI.

### Vaccins sans rappel après la série

**Hépatite A.** Deux doses, au moins 6 mois d'écart. Après la première: 1 an. Après la deuxième: 50 ans, selon la page publique du LCR. Le LCI dit qu'une dose extra n'est pas nécessaire chez les personnes en bonne santé. Les modèles indiquent 50 ans, peut-être à vie.

**Hépatite B.** Trois doses. Ensuite à vie en principe, si le corps a bien répondu. Un rappel n'est alors pas nécessaire. Certains métiers font tout de même une prise de sang.

**HPV.** Deux doses à 6 mois d'intervalle, dans le RVP vers 10 ans. Le LCI voit encore une bonne protection après 12 ans. Un rappel plus tard n'est pas indiqué pour l'instant.

**ROR (rougeole, oreillons, rubéole).** Deux doses. Ensuite probablement à vie contre la rougeole et la rubéole. Pour les oreillons, c'est moins clair. Après deux doses, 99 % sont protégés, dit le RVP.

**Zona.** Deux doses de Shingrix. Le RIVM dit: la protection dure au moins 6 à 10 ans. Il n'y a pas encore de troisième dose standard.

**Dengue et chikungunya.** Le LCR dit que la durée de protection est encore inconnue.

### Enfants: rappels dans le programme national

Les enfants reçoivent déjà des doses de rappel dans le calendrier. Selon rijksvaccinatieprogramma.nl notamment:

- 12 mois: DKTP-Hib-HepB et pneumocoque
- 14 mois: ROR et MenACWY
- 3 ans: deuxième ROR (certains millésimes plus tard, en rattrapage)
- 5 ans: DKT
- 10 ans: HPV, deux fois
- 14 ans: DTP et MenACWY (DTP à 14 ans à partir de la génération 2016)

Ensuite, le DTP n'est plus dans le programme enfant. Pour la diphtérie et le tétanos, un rappel tous les 10 ans reste prudent, surtout en voyage.

### Que cela change pour vos dossiers

Notez la date, le nom du vaccin et si la série est terminée. Vous verrez alors s'il faut un rappel, ou seulement la dose suivante de la même série.

Avec Vaxy, tout est sur un téléphone. Vous ratez moins souvent le DTP après 10 ans, ou la deuxième dose d'hépatite A.

En cas de doute, demandez à votre médecin, au service de santé publique ou à une clinique du voyageur. Les personnes immunodéprimées, les femmes enceintes et certains métiers ont souvent un autre calendrier.

### Réponses courtes

**Quels vaccins ont un rappel ?**
DTP tous les 10 ans, typhoïde tous les 3 ans, plus encéphalite à tiques, encéphalite japonaise et choléra. Grippe et COVID pour les groupes à risque chaque année.

**Combien de temps dure le DTP ?**
10 ans contre la diphtérie et le tétanos. La polio après le calendrier enfant, probablement à vie contre la paralysie.

**Faut-il refaire l'hépatite A plus tard ?**
Pas après deux doses. Après la deuxième, 50 ans selon le LCR.

**La fièvre jaune est-elle à vie ?**
Le plus souvent oui. Parfois une dose extra est nécessaire, par exemple avec le VIH.

### Sources

- [Programme néerlandais: calendrier](https://rijksvaccinatieprogramma.nl/vaccinaties/vaccinatieschema)
- [RVP: vaccin DTP](https://rijksvaccinatieprogramma.nl/vaccinaties/dtp)
- [RVP: vaccin ROR](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [LCR: informations sur les vaccinations](https://lcr.nl/informatie-vaccinaties)
- [LCI: tétanos](https://lci.rivm.nl/richtlijnen/tetanus)
- [LCI: vaccination hépatite A](https://lci.rivm.nl/factsheets/hepatitis-a-vaccinatie)
- [LCI: vaccination hépatite B](https://lci.rivm.nl/factsheets/hepatitis-b-vaccinatie)
- [LCI: fièvre jaune](https://lci.rivm.nl/richtlijnen/gele-koorts)
- [LCI: vaccination TBE](https://lci.rivm.nl/factsheets/tbe-vaccinatie)
- [LCI: vaccination coqueluche](https://lci.rivm.nl/factsheets/kinkhoestvaccinatie)
- [LCI: vaccination HPV](https://lci.rivm.nl/factsheets/hpv-vaccinatie)
- [LCI: vaccination pneumocoque](https://lci.rivm.nl/factsheets/pneumokokkenvaccinatie)
- [LCI: vaccination grippe](https://lci.rivm.nl/richtlijnen/influenzavaccinatie)
- [LCI: vaccination COVID-19](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)
- [LCI: rage](https://lci.rivm.nl/richtlijnen/rabies)
- [RIVM: vaccination corona](https://www.rivm.nl/corona/coronaprik)
- [RIVM: vaccination zona](https://www.rivm.nl/gordelroos/vaccinatie)
- [RIVM: vaccinations pendant la grossesse](https://www.rivm.nl/zwangerschap-en-infectieziekten/vaccinaties-tijdens-zwangerschap)`,
  },
  {
    slug: "how-mrna-vaccines-work",
    date: "2026-08-19",
    category: "Conseils santé",
    image: "/images/blog/how-mrna-vaccines-work.webp",
    author: "Vaxy",
    readTime: "7 min de lecture",
    title: "Qu'est-ce qu'un vaccin à ARN messager et comment ça marche ?",
    excerpt:
      "Un vaccin à ARN messager est un court message pour vos cellules, pas un virus vivant. L'OMS, le CDC, l'EMA et le RIVM expliquent comment il agit et pourquoi il ne change pas votre ADN.",
    faq: [
      {
        q: "Qu'est-ce qu'un vaccin à ARN messager ?",
        a: "C'est une piqûre avec un court message pour vos cellules. Ce message apprend à votre corps à reconnaître un morceau inoffensif d'un virus. Il n'y a pas de virus vivant dedans.",
      },
      {
        q: "Comment fonctionne un vaccin à ARN messager ?",
        a: "Après la piqûre, vos cellules lisent l'ARN messager et fabriquent un morceau de protéine inoffensif. Votre système immunitaire apprend à le reconnaître. Ensuite, votre corps dégrade vite l'ARN messager.",
      },
      {
        q: "Un vaccin à ARN messager change-t-il votre ADN ?",
        a: "Non. Le CDC et l'EMA disent que l'ARN messager n'entre pas dans le noyau de la cellule, où se trouve votre ADN. Il ne peut donc pas changer votre code génétique.",
      },
      {
        q: "Un vaccin à ARN messager peut-il vous donner la COVID-19 ?",
        a: "Non. Le CDC dit qu'il n'y a pas de coronavirus vivant dans le vaccin. Vous ne pouvez pas attraper la COVID-19 à cause de lui.",
      },
      {
        q: "Combien de temps l'ARN messager reste-t-il dans le corps ?",
        a: "Peu de temps. L'EMA dit que votre corps dégrade l'ARN messager rapidement, comme votre propre ARN messager. Dans les études, il baissait en quelques jours.",
      },
    ],
    content: `## Qu'est-ce qu'un vaccin à ARN messager et comment ça marche ?

Un vaccin à ARN messager est une piqûre qui apprend à votre corps à reconnaître une maladie. Il ne contient pas de virus vivant. Il contient un court message : l'ARN messager, aussi appelé mRNA.

ARN messager veut dire messenger RNA. Votre corps fabrique de l'ARN messager toute la journée. Ce message dit à une cellule quelle protéine elle doit faire.

Dans un vaccin à ARN messager, le message est fabriqué en laboratoire. Il parle d'un morceau inoffensif du virus. Pour la COVID-19, c'est souvent la protéine spike. Ensuite, votre corps élimine vite le message.

L'Organisation mondiale de la Santé (OMS) appelle cela un vaccin avec un plan de construction, au lieu du virus lui-même.

### Comment fonctionne un vaccin à ARN messager, étape par étape ?

Selon le CDC américain, cela se passe ainsi :

1. Vous recevez la piqûre dans le haut du bras.
2. L'ARN messager est dans de petites bulles de graisse. Elles aident le message à entrer dans la cellule.
3. Dans la cellule, le corps lit le message et fabrique un morceau de protéine inoffensif.
4. Ensuite, la cellule dégrade l'ARN messager. Il quitte le corps comme un déchet.
5. La cellule montre le morceau de protéine à votre système immunitaire.
6. Votre système immunitaire fabrique des anticorps et des cellules mémoire.
7. Si vous rencontrez plus tard le vrai virus, votre corps le reconnaît plus vite.

Le CDC dit : il faut en général quelques semaines pour que cette protection soit prête.

### Un vaccin à ARN messager change-t-il votre ADN ?

Non. Le CDC et l'Agence européenne des médicaments (EMA) le disent tous les deux.

L'ARN messager n'entre pas dans le noyau de la cellule. C'est là que se trouve votre ADN. Le message reste hors du noyau, là où votre corps fabrique toujours des protéines.

L'ARN messager est aussi chimiquement différent de l'ADN. Il ne peut pas s'installer dans votre ADN.

L'EMA dit aussi : un vaccin à ARN messager n'est pas une thérapie génique. Le but n'est pas de réparer ou de changer vos gènes.

Le vaccin ne peut pas vous donner la COVID-19. Il n'y a pas de coronavirus vivant dedans, dit le CDC.

### Combien de temps l'ARN messager reste-t-il dans le corps ?

Peu de temps. L'EMA dit : le corps dégrade l'ARN messager rapidement, comme votre propre ARN messager.

Dans des études animales sur Comirnaty (Pfizer/BioNTech), l'ARN messager baissait en 6 à 9 jours. Pour Spikevax (Moderna), les chercheurs trouvaient après 1 à 3 jours surtout encore de l'ARN messager au point d'injection, dans les ganglions et dans la rate.

Au moment de l'autorisation, les autorités n'attendaient pas d'accumulation dans les organes.

### Pourquoi y a-t-il des bulles de graisse dans le vaccin ?

L'ARN messager est fragile. Sans protection, il se casse vite.

L'EMA explique : de petites particules de graisse, les nanoparticules lipidiques, aident l'ARN messager à entrer dans la cellule. La paroi de la cellule est aussi faite de graisse. Les bulles fusionnent avec elle.

Ce type de particules de graisse dans les médicaments n'est pas nouveau. Le premier médicament avec ces bulles a été autorisé dans l'UE en 2018.

### Les vaccins à ARN messager sont-ils nouveaux ?

Les vaccins contre la COVID-19 étaient nouveaux pour beaucoup de gens. La technique ne l'était pas.

L'EMA dit : des chercheurs travaillaient déjà depuis une vingtaine d'années avec l'ARN messager, pour des maladies infectieuses et pour le cancer. Le CDC cite des études plus anciennes sur la grippe, Zika, la rage et le CMV.

L'ARN messager est relativement petit et simple à fabriquer. C'est pourquoi les fabricants ont pu produire plus vite qu'avec des vaccins classiques. Les tests de sécurité n'ont pas été sautés, dit le CDC.

### Quels vaccins à ARN messager existent ?

Les plus connus en Europe sont :

- Comirnaty de Pfizer/BioNTech
- Spikevax de Moderna

Ils sont autorisés par l'EMA. L'institut néerlandais RIVM utilise des vaccins COVID-19 dans son programme d'automne, surtout pour les personnes à plus haut risque. En 2026, les personnes de 70 ans et plus, et les personnes avec un risque médical extra, peuvent recevoir une piqûre COVID-19.

Il existe aussi d'autres types de vaccins COVID-19, comme les vaccins protéiques. Ceux-là contiennent déjà un morceau de protéine. Le RIVM dit que cela peut être une option si vous ne pouvez pas ou ne voulez pas un vaccin à ARN messager pour raison médicale.

La directive du RIVM le dit simplement : Comirnaty est un vaccin à ARN messager. Il ne contient pas de virus vivant. L'ARN messager code pour la protéine spike.

L'ARN messager est aussi étudié pour d'autres maladies, et pour certains cancers. C'est encore de la recherche. Un vaccin COVID-19 à ARN messager n'est pas la même chose qu'un vaccin personnalisé contre le cancer.

### Quels sont les effets secondaires ?

L'OMS dit : des symptômes légers à modérés peuvent arriver. C'est un signe que votre corps construit une protection.

Souvent :

- douleur ou rougeur au point d'injection
- fatigue
- mal de tête
- courbatures
- frissons
- fièvre

La plupart des symptômes passent en quelques jours. Tout le monde n'a pas d'effets secondaires.

Les effets secondaires graves sont rares. L'EMA dit : la sécurité des vaccins COVID-19 autorisés est rassurante, après des centaines de millions de doses.

Un effet secondaire rare connu des vaccins à ARN messager est une inflammation du muscle cardiaque ou de l'enveloppe autour du cœur. Les médecins appellent cela myocardite ou péricardite. Le RIVM dit que c'est rare. Cela arrive surtout chez de jeunes hommes, peu après la piqûre. La plupart des personnes guérissent. Voyez un médecin en cas de douleur dans la poitrine, de souffle court ou de rythme cardiaque rapide après la piqûre.

### Les vaccins à ARN messager protègent-ils encore ?

Oui, surtout contre la maladie grave et le décès. Aucun vaccin ne marche à 100 %.

L'OMS dit : la protection est la plus haute dans les premiers mois. Après environ six mois, elle baisse. C'est pourquoi les personnes à plus haut risque reçoivent parfois une nouvelle piqûre.

Vous pouvez encore attraper la COVID-19 après la vaccination. Le risque de tomber très malade est alors en général plus bas.

### Que signifie cela pour votre dossier de vaccination ?

Notez quelle piqûre vous avez eue, la date, et le nom du vaccin, comme Comirnaty ou Spikevax. Cela aide pour une dose plus tard, pour voyager, ou si un médecin demande.

Avec Vaxy, vous gardez ces informations au même endroit, pour vous et votre famille.

Cet article est une information, pas un avis médical. En cas de doute, demandez à votre médecin ou au service de santé publique.

### Réponses courtes

**Qu'est-ce qu'un vaccin à ARN messager ?**
Une piqûre avec un court message pour vos cellules. Pas de virus vivant.

**Comment ça marche ?**
Vos cellules fabriquent un morceau de protéine inoffensif. Votre défense l'apprend. Ensuite l'ARN messager disparaît.

**Change-t-il votre ADN ?**
Non. Le CDC et l'EMA disent que l'ARN messager n'atteint pas votre ADN dans le noyau.

**Peut-on attraper la COVID-19 à cause de lui ?**
Non. Il n'y a pas de coronavirus vivant dedans.

**Combien de temps reste-t-il dans le corps ?**
Peu de temps. L'EMA dit que votre corps le dégrade vite.

### Sources

- [OMS : comment fonctionnent les vaccins](https://www.who.int/news-room/feature-stories/detail/how-do-vaccines-work)
- [OMS : questions-réponses sur les vaccins COVID-19, 15 mai 2026](https://www.who.int/news-room/questions-and-answers/item/coronavirus-disease-(covid-19)-vaccines)
- [CDC : bases des vaccins COVID-19, comment fonctionnent les vaccins à ARN messager](https://www.cdc.gov/covid/vaccines/how-they-work.html)
- [EMA : faits clés sur les vaccins COVID-19, y compris l'ARN messager](https://www.ema.europa.eu/en/human-regulatory-overview/public-health-threats/coronavirus-disease-covid-19/covid-19-medicines/covid-19-vaccines-key-facts)
- [EMA : Comirnaty (Pfizer/BioNTech)](https://www.ema.europa.eu/en/medicines/human/EPAR/comirnaty)
- [EMA : Spikevax (Moderna)](https://www.ema.europa.eu/en/medicines/human/EPAR/spikevax)
- [RIVM : vaccination COVID-19](https://www.rivm.nl/covid-19-vaccinatie)
- [Directive RIVM LCI sur la vaccination COVID-19](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)`,
  },
  {
    slug: "us-green-card-vaccination-records",
    date: "2026-08-19",
    category: "Santé voyage",
    image: "/images/blog/us-green-card-vaccination-records.webp",
    author: "Vaxy",
    readTime: "7 min de lecture",
    title:
      "Comment Vaxy aide pour les vaccins exigés pour une green card américaine",
    excerpt:
      "L'examen médical pour une green card américaine comprend un contrôle des vaccins. USCIS demande d'apporter une preuve écrite. Voici comment un dossier numérique dans Vaxy peut aider, avec des sources.",
    faq: [
      {
        q: "Vaxy est-il une green card ?",
        a: "Non. Une green card est un titre de séjour. Vaxy est un dossier vaccinal numérique. USCIS demande d'apporter une preuve écrite de vos vaccins à l'examen médical d'immigration.",
      },
      {
        q: "Les touristes ont-ils besoin de cela pour entrer aux États-Unis ?",
        a: "Non. Ce contrôle vaccinal concerne les personnes qui demandent un visa d'immigrant ou un ajustement de statut vers résident permanent. Le formulaire I-693 n'est pas nécessaire pour des vacances ordinaires.",
      },
      {
        q: "Vaxy peut-il remplacer le formulaire I-693 ?",
        a: "Non. Seul un civil surgeon désigné aux États-Unis, ou un panel physician à l'étranger, peut terminer l'évaluation vaccinale sur le formulaire I-693.",
      },
      {
        q: "Un vaccin COVID-19 est-il encore obligatoire pour une green card ?",
        a: "Non. Depuis le 20 janvier 2025, vous n'avez plus à montrer une vaccination COVID-19 pour un ajustement de statut.",
      },
    ],
    content: `## Comment Vaxy aide pour les vaccins exigés pour une green card américaine

Une green card américaine n'est pas un carnet de vaccination. C'est un titre de séjour : lawful permanent residence.

Si vous demandez ce statut, vous devez pouvoir montrer certains vaccins. La loi d'immigration américaine dit que vous pouvez être jugé inadmissible sans cette preuve. USCIS est clair : apportez toute documentation vaccinale écrite au civil surgeon.

C'est là que Vaxy aide. Vaxy n'est pas une green card et n'est pas un formulaire officiel USCIS. C'est votre dossier vaccinal, toujours sur votre téléphone, prêt pour l'examen.

### À qui cela s'applique

Cela s'applique si vous :

- demandez un visa d'immigrant depuis l'étranger, ou
- demandez un ajustement de statut vers résident permanent aux États-Unis, souvent avec le formulaire I-485

Des vacances ordinaires ou un court séjour n'ont pas besoin de cet examen. Les règles ci-dessous concernent les personnes qui demandent une green card.

### Pourquoi la preuve des vaccins compte

Selon l'article 212(a)(1)(A)(ii) de l'Immigration and Nationality Act (INA), une personne qui ne peut pas prouver les vaccins exigés peut se voir refuser l'admission ou l'ajustement de statut.

Aux États-Unis, un civil surgeon désigné fait l'examen. À l'étranger, c'est un panel physician désigné par le Département d'État américain. Les résultats vont sur le formulaire I-693, Report of Medical Examination and Vaccination Record.

USCIS indique que vous n'avez pas à refaire les vaccins si vous avez déjà une preuve. Apportez vos dossiers.

### Quels vaccins sont exigés

La loi d'immigration cite notamment :

- Les oreillons
- La rougeole
- La rubéole
- La polio
- Le tétanos et la diphtérie
- La coqueluche
- Haemophilus influenzae type B (Hib)
- L'hépatite B
- D'autres vaccins recommandés par l'ACIP et exigés par le CDC pour l'immigration

Le CDC exige aussi, selon votre âge, une protection contre :

- La varicelle
- L'hépatite A
- Les méningocoques
- Les pneumocoques
- Le rotavirus, pour les très jeunes nourrissons
- La grippe saisonnière, pendant la saison grippale

Vous n'avez pas besoin de tous les vaccins de la liste. Seulement ceux adaptés à votre âge. Le civil surgeon suit les Technical Instructions du CDC, y compris un tableau par âge.

**COVID-19 :** Depuis le 20 janvier 2025, un vaccin COVID-19 n'est plus exigé pour l'ajustement de statut.

**Polio :** Depuis le 1er mai 2024, toute personne de 2 mois ou plus qui n'est pas à jour doit recevoir une dose d'IPV. Cela inclut les adultes sans preuve de la série de l'enfance.

**Grippe :** Pour l'examen d'immigration, le vaccin contre la grippe est exigé du 1er septembre au 31 mars, s'il est disponible.

### Que faut-il apporter à l'examen

USCIS dit qu'il est important d'apporter toute documentation vaccinale écrite. Si un médecin a testé votre immunité, apportez aussi cette preuve.

S'il manque quelque chose, le civil surgeon peut administrer le vaccin. Vous pouvez aussi faire les vaccins manquants chez votre médecin, puis revenir avec une preuve. Seul un civil surgeon peut remplir la partie vaccination du formulaire I-693.

Vous n'avez en général besoin que d'une dose de chaque vaccin exigé au moment de l'examen. Vous n'avez pas à terminer une longue série avant que le formulaire puisse être signé.

### Comment Vaxy aide

Les carnets papier se perdent. Les vaccins sont dans différents pays, langues et dossiers.

Avec Vaxy, vous pouvez :

- Garder chaque vaccin au même endroit, avec les dates
- Ajouter des membres de la famille qui demandent aussi une green card
- Ouvrir votre dossier sur votre téléphone au rendez-vous
- Voir ce que vous avez déjà avant de réserver, pour moins de vaccins répétés

Montrez au civil surgeon votre dossier Vaxy, avec les preuves papier que vous avez encore. Le civil surgeon décide ce qui compte.

### Ce que Vaxy n'est pas

Vaxy n'est pas une green card, ni le formulaire I-693, ni un substitut à l'examen médical d'immigration. USCIS n'accepte pas un tableau vaccinal incomplet. Seul un civil surgeon désigné ou un panel physician peut certifier le formulaire.

Cet article est une information, pas un conseil juridique ou médical. Les règles peuvent changer. Vérifiez USCIS et le CDC avant votre rendez-vous.

### Réponses rapides

**Vaxy est-il une green card ?**
Non. C'est un dossier vaccinal numérique à apporter à l'examen.

**Cela s'applique-t-il aux touristes ?**
Non. Cela s'applique aux personnes qui demandent une green card.

**Vaxy peut-il remplacer le formulaire I-693 ?**
Non. Seul un civil surgeon ou un panel physician peut terminer ce formulaire.

**La COVID-19 est-elle encore exigée ?**
Non, plus depuis le 20 janvier 2025.

### Sources

- [USCIS, Vaccination Requirements](https://www.uscis.gov/tools/designated-civil-surgeons/vaccination-requirements)
- [CDC, Vaccination Technical Instructions for Civil Surgeons](https://www.cdc.gov/immigrant-refugee-health/hcp/civil-surgeons/vaccination.html)
- [USCIS Policy Manual, Volume 8, Part B, Chapter 9](https://www.uscis.gov/policy-manual/volume-8-part-b-chapter-9)
- [Formulaire I-693, Report of Medical Examination and Vaccination Record](https://www.uscis.gov/i-693)
- [42 C.F.R. Section 34.3](https://www.ecfr.gov/current/title-42/chapter-I/subchapter-C/part-34/section-34.3)`,
  },
  {
    slug: "vaccines-do-not-cause-autism",
    date: "2026-08-19",
    category: "Actualités santé",
    image: "/images/blog/vaccines-do-not-cause-autism.webp",
    author: "Vaxy",
    readTime: "6 min de lecture",
    title: "Les vaccins ne causent pas l'autisme : ce que dit la science",
    excerpt:
      "Les vaccins ne causent pas l'autisme. L'OMS, le RIVM et de grandes études ne trouvent aucune preuve. Voici d'où vient le mythe, et ce que les parents doivent savoir.",
    faq: [
      {
        q: "Les vaccins causent-ils l'autisme ?",
        a: "Non. L'OMS, le RIVM néerlandais et de grandes études dans de nombreux pays ne trouvent aucun lien. Les vaccins ne causent pas l'autisme.",
      },
      {
        q: "Le vaccin ROR cause-t-il l'autisme ?",
        a: "Non. Le vaccin ROR protège contre la rougeole, les oreillons et la rubéole. De grandes études, dont une au Danemark avec plus de 650 000 enfants, ne trouvent aucun lien avec l'autisme.",
      },
      {
        q: "D'où vient l'idée que les vaccins causent l'autisme ?",
        a: "D'un article d'Andrew Wakefield en 1998. Seulement 12 enfants y ont participé. L'article a ensuite été retiré. Les données n'étaient pas justes, et Wakefield n'a plus eu le droit d'exercer comme médecin.",
      },
      {
        q: "Est-il plus sûr de séparer le vaccin ROR en trois piqûres ?",
        a: "Non. Il n'y a aucune preuve que séparer soit plus sûr. Votre enfant a alors besoin de plus de rendez-vous et reste plus longtemps sans protection contre la rougeole.",
      },
      {
        q: "Que disent l'OMS et le RIVM sur les vaccins et l'autisme ?",
        a: "Ils disent la même chose : les vaccins ne causent pas l'autisme. L'OMS l'a confirmé à nouveau en décembre 2025. Le RIVM le dit sur sa page ROR et sur sa page sur les doutes à propos de la vaccination.",
      },
    ],
    content: `## Les vaccins ne causent pas l'autisme : ce que dit la science

Les vaccins ne causent pas l'autisme. C'est la conclusion claire de l'Organisation mondiale de la Santé (OMS), du programme national de vaccination néerlandais au RIVM, et de grandes études dans de nombreux pays.

Vaxy est d'accord. Les parents méritent une information claire, pas de la peur.

### Que dit l'actualité ?

Le 11 août 2026, le président américain Donald Trump a signé un décret sur les vaccins infantiles. Il a répété l'ancienne idée que les vaccins causent l'autisme. Il veut aussi que le vaccin ROR contre la rougeole, les oreillons et la rubéole soit séparé en plusieurs piqûres.

Les médecins et les organisations de santé disent que ce décret est dangereux. Ce n'est pas de la science. Aux États-Unis, ce sont les États qui décident quels vaccins les écoles demandent, pas le président. Le décret est donc un conseil, pas une loi. Il peut quand même inquiéter les parents.

### D'où vient ce mythe ?

En 1998, le médecin britannique Andrew Wakefield a publié un article. Il écrivait que le vaccin ROR pouvait causer l'autisme. Seulement 12 enfants y ont participé. Plus tard, on a vu que les données n'étaient pas justes. Wakefield avait aussi reçu de l'argent d'avocats qui voulaient attaquer les vaccins en justice.

La revue médicale The Lancet a retiré l'article. Cela veut dire : on ne doit plus l'utiliser comme preuve. Ensuite, Wakefield n'a plus eu le droit d'exercer comme médecin.

Depuis 1998, beaucoup de grandes études soigneuses ont été faites. Elles ne trouvent aucun lien entre les vaccins et l'autisme.

### Que disent les grandes études ?

Une étude connue du Danemark, en 2019, a suivi 657 461 enfants. Des chercheurs du Statens Serum Institut ont demandé si le vaccin ROR causait l'autisme. La réponse était non. C'était aussi vrai pour les enfants avec un risque plus élevé, par exemple parce qu'un frère ou une sœur a de l'autisme.

En 2025, une nouvelle étude danoise a regardé l'aluminium dans les vaccins. De petites quantités de sels d'aluminium se trouvent dans certains vaccins. Elles aident le corps à mieux réagir à la piqûre. Les chercheurs ont suivi plus d'1 million d'enfants. Ils n'ont trouvé aucun lien avec l'autisme, ni avec 49 autres problèmes de santé.

Le RIVM rassemble ce type de recherche sur une page dédiée. La conclusion est toujours la même : vacciner ne cause pas l'autisme.

### Que dit l'OMS ?

En décembre 2025, un groupe d'experts de l'OMS a regardé la science à nouveau. Ce groupe s'appelle GACVS. Ils ont étudié 31 études de 2010 à 2025, venant de nombreux pays.

Leur conclusion : les vaccins ne causent pas l'autisme. Cela vaut aussi pour les vaccins avec du thiomersal, un conservateur, ou avec un petit peu d'aluminium.

L'OMS l'avait déjà dit en 2002, 2004 et 2012. La nouvelle vérification le confirme encore.

L'OMS dit aussi : les vaccins infantiles ont sauvé au moins 154 millions de vies en 50 ans.

### Que dit le programme de vaccination néerlandais ?

Le programme national néerlandais protège les enfants contre des maladies graves, comme la rougeole, les oreillons, la rubéole, la coqueluche et la polio.

La page du RIVM sur le vaccin ROR est claire : on n'attrape pas l'autisme avec le vaccin ROR. On n'attrape pas non plus le diabète ou une maladie de l'intestin.

Aux Pays-Bas, les enfants reçoivent le vaccin ROR deux fois : vers 14 mois et vers 3 ans. Après la première piqûre, environ 95% des enfants sont protégés. Après la deuxième, environ 99% sont protégés.

Le RIVM dit aussi : le doute est compréhensible. Posez vos questions au centre de santé infantile ou à votre médecin. Utilisez des sources fiables, pas seulement les réseaux sociaux.

### Est-il plus sûr de séparer le vaccin ROR ?

Non. L'OMS, le RIVM et le CDC américain disent qu'il n'y a aucune preuve que des piqûres séparées soient plus sûres.

Si vous séparez les piqûres, votre enfant a besoin de plus de rendez-vous. Entre ces rendez-vous, votre enfant n'est pas protégé plus longtemps. La rougeole se transmet très facilement. Un enfant peut tomber malade avant que toutes les piqûres soient faites.

Le vaccin ROR est une petite dose. C'est quelques gouttes. Ce n'est pas une grande quantité de liquide dans le corps.

### Pourquoi les enfants reçoivent-ils plus de vaccins aujourd'hui qu'avant ?

Les enfants sont maintenant protégés contre plus de maladies. C'est un progrès, pas un danger. Les vaccins combinés veulent dire moins de piqûres dans le bras, pas plus.

Le calendrier suit l'âge où le système immunitaire réagit le mieux, et où les enfants ont le plus de risque.

Sauter ou retarder des vaccins baisse cette protection. La rougeole revient plus souvent quand moins d'enfants sont vaccinés.

### Que peuvent faire les parents ?

1. Suivez le programme national de vaccination de votre pays.
2. Posez vos questions à un médecin ou à un centre de santé infantile si vous avez un doute.
3. Utilisez des sources comme l'OMS et le RIVM, pas des messages anonymes.
4. Gardez un suivi des vaccins de votre enfant, pour n'en manquer aucun.

Avec Vaxy, vous gardez tous les vaccins de la famille au même endroit. Vous voyez ce qui a déjà été fait, et vous recevez un rappel pour la prochaine piqûre.

Cet article est une information, pas un avis médical. Vous vous inquiétez pour la santé de votre enfant ? Parlez-en à un médecin.

### Réponses courtes

**Les vaccins causent-ils l'autisme ?**
Non. L'OMS, le RIVM et de grandes études ne trouvent aucun lien.

**Le vaccin ROR cause-t-il l'autisme ?**
Non. De grandes études sur des centaines de milliers d'enfants ne trouvent aucun lien.

**D'où vient le mythe ?**
D'un article de 1998 qui a ensuite été retiré. Les données n'étaient pas justes.

**Séparer le vaccin ROR est-il plus sûr ?**
Non. Il n'y a aucune preuve. Votre enfant reste alors plus longtemps sans protection.

**Que disent l'OMS et le RIVM ?**
Les vaccins ne causent pas l'autisme. L'OMS l'a confirmé à nouveau en 2025.

### Sources

- [OMS : les vaccins ne causent pas l'autisme, 11 décembre 2025](https://www.who.int/news/item/11-12-2025-who-expert-group-s-new-analysis-reaffirms-there-is-no-link-between-vaccines-and-autism)
- [Déclaration GACVS de l'OMS sur les vaccins et l'autisme](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [RIVM : recherche sur la vaccination et l'autisme](https://rijksvaccinatieprogramma.nl/over-vaccineren/onderzoek-vaccins/onderzoek-verband-vaccineren-autisme)
- [RIVM : vacciner ne cause pas l'autisme](https://rijksvaccinatieprogramma.nl/over-vaccineren/twijfels)
- [RIVM : vaccination ROR](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [Hviid et al., vaccination ROR et autisme, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [Statens Serum Institut : aucun lien entre l'aluminium des vaccins et l'autisme, 2025](https://en.ssi.dk/news/news/2025/large-danish-study-no-link-between-vaccines-and-autism-or-49-other-health-conditions)
- [The Lancet retire l'article de Wakefield, 2010](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(10)60175-4/fulltext)
- [CDC: Vaccines Do Not Cause Autism](https://www.cdc.gov/vaccine-safety/about/autism.html)
- [BBC : Trump signe un décret sur les vaccins infantiles, 11 août 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)`,
  },
  {
    slug: "childhood-vaccines-trump-order",
    date: "2026-08-19",
    category: "Actualités santé",
    image: "/images/blog/childhood-vaccines-trump-order.webp",
    author: "Vaxy",
    readTime: "8 min de lecture",
    title:
      "Pourquoi les vaccins infantiles restent essentiels après la décision de Trump",
    excerpt:
      "Trump veut moins de vaccins infantiles et séparer le vaccin ROR. Voici pourquoi ces vaccins existent, ce que dit la science, et ce qui peut arriver si moins d'enfants sont protégés.",
    faq: [
      {
        q: "Cette décision américaine change-t-elle le calendrier vaccinal en Europe ?",
        a: "Non. C'est une recommandation aux États-Unis, pas une loi. Aux Pays-Bas, c'est le Rijksvaccinatieprogramma qui s'applique. Les États américains fixent eux-mêmes les vaccins exigés par les écoles.",
      },
      {
        q: "Pourquoi les enfants reçoivent-ils autant de vaccins ?",
        a: "Parce qu'ils peuvent maintenant être protégés contre plus de maladies graves. Le calendrier suit l'âge où le système immunitaire réagit le mieux, et où les enfants sont le plus à risque.",
      },
      {
        q: "Est-il plus sûr de séparer le vaccin ROR en trois injections ?",
        a: "Non. Rien ne prouve que séparer soit plus sûr. Votre enfant aurait alors plus de rendez-vous et resterait plus longtemps sans protection contre la rougeole, les oreillons et la rubéole.",
      },
      {
        q: "Que peut-il arriver si moins d'enfants sont vaccinés ?",
        a: "Des maladies comme la rougeole, la coqueluche et la polio peuvent revenir. La rougeole se transmet très facilement. Les épidémies augmentent déjà là où la couverture vaccinale baisse.",
      },
    ],
    content: `## Pourquoi les vaccins infantiles restent essentiels après la décision de Trump

Le 11 août 2026, le président américain Donald Trump a signé un décret sur les vaccins infantiles. Il veut moins d'injections pour tous les enfants, et il veut séparer le vaccin combiné rougeole-oreillons-rubéole (ROR) en injections distinctes.

Les médecins jugent cela dangereux. Le décret est une recommandation, pas une loi. Il peut pourtant inquiéter les parents et faire baisser encore la vaccination. Cet article explique pourquoi les vaccins infantiles existent, et ce qui peut suivre.

### Que dit le décret ?

Selon la [BBC](https://www.bbc.com/news/articles/ce3q5vl581wo), Trump veut faire passer de 18 à 11 le nombre de maladies contre lesquelles tous les enfants américains sont vaccinés. Sa liste de 11 maladies : rougeole, oreillons, rubéole, diphtérie, tétanos, coqueluche, polio, Hib, pneumocoques, HPV et varicelle.

D'autres vaccins, comme l'hépatite B et le méningocoque, ne resteraient que pour les enfants à plus haut risque. La grippe et la COVID-19 deviendraient un choix entre parent et médecin.

Trump veut aussi que les vaccins ne soient plus donnés lors de la même visite, et que le ROR combiné soit séparé.

Les règles scolaires restent du ressort des États. Les parents aux États-Unis ne peuvent donc pas demander tout de suite trois injections ROR séparées. Ces produits séparés ne sont pas actuellement autorisés aux États-Unis.

### Pourquoi les vaccins infantiles existent

Les vaccins infantiles protègent contre des maladies qui rendaient autrefois beaucoup d'enfants très malades, ou les tuaient. La rougeole peut causer une pneumonie, une inflammation du cerveau et une surdité. La coqueluche est surtout dangereuse pour les bébés. La polio peut provoquer une paralysie. L'Hib peut provoquer une méningite. L'hépatite B peut plus tard causer un cancer du foie.

L'OMS indique que les vaccins ont sauvé plus de 150 millions de vies en 50 ans. La vaccination contre la rougeole seule a évité environ 59 millions de décès entre 2000 et 2024.

Les enfants reçoivent les vaccins à des âges précis, pas au hasard. Les médecins choisissent le moment où le système immunitaire réagit le mieux, et où l'enfant est le plus exposé. L'American Academy of Pediatrics (AAP) affirme qu'il n'y a aucune raison médicale de retarder ou de sauter les vaccins recommandés.

Les enfants sont aujourd'hui protégés contre plus de maladies qu'avant. C'est un progrès. Les vaccins combinés signifient moins d'aiguilles, pas plus.

### La protection collective n'est pas un luxe

Certains enfants ne peuvent pas être vaccinés. Les bébés peuvent être trop jeunes. D'autres ont un système immunitaire faible. Ils dépendent des personnes autour d'eux.

Pour la rougeole, au moins 95 % des enfants doivent recevoir deux doses pour arrêter une épidémie. Si ce chiffre baisse, le virus trouve vite un chemin. La rougeole est souvent la première maladie à revenir quand la vaccination baisse.

En 2023, on estimait à 10,3 millions le nombre de cas de rougeole dans le monde. L'OMS, l'UNICEF et Gavi alertent : les épidémies de rougeole, de méningite et de fièvre jaune augmentent. Aux États-Unis, les cas de rougeole ont atteint un plus haut depuis 35 ans, selon des reportages récents.

### Que peut provoquer ce décret ?

**Plus de doses manquées.** Des rendez-vous en plus semblent prudents. En pratique, les familles reviennent moins souvent. Une dose manquée, c'est une plus longue période sans protection.

**Plus longtemps sans protection contre la rougeole.** Entre des injections séparées, il y a un délai. Pendant ce délai, un enfant peut attraper la rougeole, les oreillons ou la rubéole. Les CDC indiquent qu'aucune preuve scientifique publiée ne montre un bénéfice à séparer le vaccin ROR combiné. Ce vaccin combiné existe depuis 1971 et fonctionne aussi bien que trois injections séparées.

**Des maladies retirées de la liste « pour tous les enfants ».** Un vaccin contre l'hépatite B à la naissance aide à éviter une infection à vie. Les vaccins contre le rotavirus évitent les diarrhées graves chez les bébés. Si ces vaccins ne sont plus conseillés pour tous les enfants, plus d'enfants tomberont malades.

**Plus de confusion, moins de vaccination.** Trump a répété l'idée que les vaccins causent l'autisme. C'est faux. Si les parents retardent les vaccins par doute, les épidémies deviennent plus probables. Le sénateur Bill Cassidy, lui-même médecin, a jugé le décret « faux ». Le président de l'AAP l'a qualifié de « dangereux ».

**Pas une loi, mais un signal.** Les États fixent encore les règles scolaires. Les recommandations fédérales influencent pourtant les médecins, les assureurs et les parents. Des conseils moins clairs mènent souvent à moins de vaccinations.

### Les vaccins causent-ils l'autisme ?

Non. L'OMS l'a confirmé à nouveau en décembre 2025. Une étude danoise de 2019 a suivi 657 461 enfants et n'a trouvé aucun lien entre le vaccin ROR et l'autisme. Nous expliquons ce mythe dans [Les vaccins ne causent pas l'autisme](/fr/blog/vaccines-do-not-cause-autism).

### Que cela change-t-il si vous ne vivez pas aux États-Unis ?

Le Rijksvaccinatieprogramma néerlandais ne change pas. Les calendriers des autres pays européens non plus.

La rougeole ne s'arrête pas à une frontière. Voyager dans une zone d'épidémie, ou croiser des voyageurs, reste une raison de rester à jour. Vérifiez vos vaccins, et ceux de vos enfants, avant de partir.

### Que pouvez-vous faire ?

1. Suivez le calendrier national de votre pays, pas une décision politique d'un autre pays.
2. Posez vos questions à un médecin si vous avez un doute.
3. Ne retardez pas les vaccins « par prudence ». Retarder, c'est un risque en plus.
4. Gardez une trace des vaccins déjà reçus par votre enfant.

Avec Vaxy, vous rassemblez toutes les vaccinations de la famille. Vous voyez ce qui a été fait, et vous recevez un rappel pour la prochaine injection.

Cet article est une information, pas un conseil médical. Si vous êtes inquiet pour la santé de votre enfant, parlez-en à un médecin.

### Réponses rapides

**Cela change-t-il les vaccins en Europe ?**
Non. C'est une recommandation américaine, pas une règle européenne.

**Pourquoi autant de vaccins infantiles ?**
Parce qu'ils protègent contre plus de maladies graves, aux âges où cette protection est le plus nécessaire.

**Séparer le ROR est-il plus sûr ?**
Non. Aucune preuve. Votre enfant resterait plus longtemps sans protection.

**Et si moins d'enfants sont vaccinés ?**
La rougeole et d'autres maladies reviennent. Les épidémies augmentent déjà.

### Sources

- [BBC : Trump signe un décret sur les vaccins infantiles, 11 août 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)
- [CIDRAP : Trump signe un décret pour séparer le vaccin ROR](https://www.cidrap.umn.edu/trump-signs-executive-order-break-measles-mumps-rubella-vaccine)
- [OMS, UNICEF et Gavi : les épidémies de maladies évitables par la vaccination augmentent, 24 avril 2025](https://www.who.int/news/item/24-04-2025-increases-in-vaccine-preventable-disease-outbreaks-threaten-years-of-progress--warn-who--unicef--gavi)
- [OMS : fiche rougeole](https://www.who.int/news-room/fact-sheets/detail/measles)
- [OMS : moins de décès par rougeole, plus de cas, 28 novembre 2025](https://www.who.int/news/item/28-11-2025-measles-deaths-down-88--since-2000--but-cases-surge)
- [OMS GACVS : les vaccins ne causent pas l'autisme](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [Hviid et al., vaccination ROR et autisme, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [RIVM : vaccination ROR](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)`,
  },
  {
    slug: "moderna-mrna-melanoma-vaccine",
    date: "2026-08-19",
    category: "Actualités santé",
    image: "/images/blog/moderna-mrna-melanoma-vaccine.webp",
    author: "Vaxy",
    readTime: "6 min de lecture",
    title:
      "Le vaccin ARNm contre le cancer de Moderna réussit un grand essai sur le mélanome",
    excerpt:
      "Moderna et Merck indiquent qu'un vaccin ARNm personnel plus Keytruda a aidé à empêcher le mélanome de revenir après une opération. Voici ce que l'essai de phase 3 signifie, en langage simple.",
    faq: [
      {
        q: "Ce vaccin ARNm contre le cancer empêche-t-il le cancer ?",
        a: "Non. C'est un traitement personnel pour les personnes qui ont déjà eu un mélanome. Les médecins le donnent après l'opération pour réduire le risque que le cancer revienne.",
      },
      {
        q: "Le vaccin Moderna contre le mélanome est-il le même que le vaccin COVID-19 ?",
        a: "Non. Les deux utilisent l'ARNm, mais ce vaccin contre le cancer est fabriqué pour la tumeur d'une seule personne. Un vaccin COVID-19 est le même pour beaucoup de personnes et vise un virus.",
      },
      {
        q: "Peut-on déjà recevoir le vaccin ARNm Moderna contre le mélanome ?",
        a: "Pas encore. Il n'est pas autorisé pour un usage général. Moderna et Merck prévoient de demander une autorisation aux autorités de santé dans les mois à venir.",
      },
      {
        q: "Le vaccin fonctionne-t-il mieux que Keytruda seul ?",
        a: "Oui, dans cet essai de phase 3. Les personnes qui ont reçu le vaccin plus Keytruda sont restées plus longtemps sans cancer, et le cancer s'est moins souvent répandu. Les chiffres complets seront présentés lors d'un congrès médical.",
      },
    ],
    content: `## Le vaccin ARNm contre le cancer de Moderna réussit un grand essai sur le mélanome

Le 19 août 2026, Moderna et Merck ont annoncé une bonne nouvelle. Leur vaccin ARNm personnel contre le cancer, utilisé avec le médicament Keytruda, a fonctionné dans une grande étude. Le vaccin a aidé les personnes atteintes de mélanome à rester plus longtemps sans cancer après une opération.

C'est le premier grand essai de phase 3 qui montre qu'un vaccin ARNm contre le cancer peut fonctionner. C'est aussi la première fois que ce type de vaccin personnel fait mieux que Keytruda seul après une opération pour un mélanome.

### Qu'est-ce que le vaccin ARNm contre le cancer de Moderna ?

Le traitement s'appelle intismeran autogene. On voit aussi les noms V940 ou mRNA-4157.

Ce n'est pas un vaccin que les personnes en bonne santé reçoivent pour empêcher le cancer. C'est un traitement pour les personnes qui ont déjà eu un mélanome, après que les médecins ont enlevé la tumeur par une opération.

Les médecins appellent cela un traitement adjuvant. Cela signifie un traitement en plus après l'opération, pour réduire le risque que le cancer revienne.

### Comment fonctionne un vaccin ARNm personnel contre le cancer ?

Le mélanome est une forme grave de cancer de la peau. Chaque tumeur a ses propres changements, appelés mutations. On peut le voir comme une empreinte unique.

Le vaccin est fabriqué pour une personne :

1. Les médecins prennent un échantillon de la tumeur.
2. Les scientifiques lisent l'empreinte de cette tumeur.
3. Ils fabriquent un vaccin ARNm qui apprend au système immunitaire à trouver ces marques du cancer.
4. Après l'injection, le corps lit l'ARNm et fabrique de petits morceaux de protéines. Le système immunitaire apprend ensuite à attaquer les cellules cancéreuses qui ont les mêmes marques.

L'ARNm est un court message que les cellules peuvent lire. Les vaccins COVID-19 utilisent aussi l'ARNm, mais ce vaccin contre le cancer est différent. Il est fabriqué pour une personne et une tumeur.

Le vaccin est donné avec Keytruda (pembrolizumab). Keytruda est une immunothérapie. Il aide le système immunitaire à remarquer et à combattre les cellules cancéreuses.

### Qu'a trouvé l'essai de phase 3 sur le mélanome ?

L'étude s'appelle INTerpath-001. Un essai de phase 3 est une grande étude tardive. Les entreprises l'utilisent souvent avant de demander une autorisation aux autorités de santé.

Lors d'un contrôle prévu des données, l'étude a atteint deux objectifs principaux :

- Les personnes sont restées plus longtemps sans cancer. Le cancer revenait moins souvent.
- Le cancer se répandait moins souvent dans d'autres parties du corps.

Les entreprises disent que la différence est réelle, pas due au hasard, et qu'elle compte pour les patients. L'étude continue. Les chercheurs regarderont aussi combien de temps les personnes vivent.

Les chiffres complets seront présentés lors d'un congrès médical. Moderna et Merck prévoient aussi de parler avec les autorités d'une possible autorisation.

### Qui a participé à l'étude ?

L'essai a inclus 1 137 personnes avec un mélanome à haut risque. Leur cancer était au stade 2B, 2C, 3 ou 4. Une opération avait déjà enlevé le cancer.

Les personnes ont été réparties au hasard en deux groupes :

- Environ deux personnes sur trois ont reçu le vaccin ARNm personnel plus Keytruda
- Environ une personne sur trois a reçu Keytruda seul

Le traitement a duré environ un an. Les personnes pouvaient recevoir jusqu'à neuf doses du vaccin.

### Le vaccin ARNm contre le cancer est-il sûr ?

Pour l'instant, les entreprises ne signalent pas de nouveaux problèmes de sécurité. Dans les études précédentes, les effets indésirables fréquents étaient :

- La fatigue
- La douleur à l'endroit de l'injection
- Les frissons

Keytruda peut aussi causer des effets indésirables immunitaires. Les personnes dans un essai sont suivies de près.

### Que montraient les études précédentes ?

Dans une étude de phase 2 plus petite, la même combinaison a réduit de 49 % le risque que le cancer revienne ou que la personne meure, après cinq ans, par rapport à Keytruda seul. Le risque que le cancer se répande loin, ou que la personne meure, a baissé de 59 %.

La nouvelle étude de phase 3 est plus grande. C'est le type d'étude que les autorités utilisent pour décider si un traitement peut être autorisé.

### Peut-on déjà recevoir ce vaccin ?

Pas encore. Ce traitement n'est pas autorisé pour un usage général.

Moderna indique que les entreprises veulent demander une autorisation dans les mois à venir. Il n'y a pas encore de date exacte.

S'il est autorisé, il serait d'abord pour les personnes à haut risque après une opération pour un mélanome, pas pour tout le monde.

### Cela pourrait-il aider d'autres cancers ?

Merck et Moderna testent aussi cette idée dans d'autres cancers, comme le cancer du poumon, de la vessie et du rein.

Ces études sont encore en cours. De bons résultats dans le mélanome ne veulent pas dire que cela marchera pour chaque cancer.

### Que signifie cette nouvelle pour vous ?

Si vous n'avez pas de mélanome, vous n'avez pas besoin de ce vaccin. Les meilleurs moyens de réduire le risque de mélanome restent simples :

- Protéger votre peau du soleil
- Éviter les cabines de bronzage
- Surveiller votre peau et voir un médecin si un grain de beauté change

Si vous ou une personne proche avez eu un mélanome, cette nouvelle est porteuse d'espoir. Parlez-en avec un oncologue. Il ou elle peut expliquer le traitement actuel et dire si un essai clinique peut être une option.

Cet article est une information, pas un conseil médical.

### Réponses rapides

**Ce vaccin empêche-t-il le cancer ?**
Non. C'est un traitement personnel après une opération pour un mélanome.

**Est-ce le même vaccin que le vaccin COVID-19 ?**
Non. Les deux utilisent l'ARNm, mais ce vaccin est fabriqué pour la tumeur d'une seule personne.

**Est-il autorisé ?**
Pas encore. Les entreprises prévoient de demander une autorisation bientôt.

**A-t-il mieux fonctionné que Keytruda seul ?**
Oui, dans cet essai de phase 3. Les détails complets seront présentés lors d'un congrès médical.

### Sources

- [Communiqué de Merck, 19 août 2026](https://www.merck.com/news/merck-and-moderna-announce-phase-3-interpath-001-trial-of-intismeran-autogene-plus-keytruda-met-endpoints-of-recurrence-free-survival-rfs-and-distant-metastasis-free-survival-dmfs-in-patient/)
- [CNN Health, 19 août 2026](https://edition.cnn.com/2026/08/19/health/moderna-merck-mrna-melanoma-vaccine)`,
  },
  {
    slug: "why-vaccination-records-matter",
    date: "2026-06-10",
    category: "Conseils santé",
    image: "/images/blog/why-vaccination-records-matter.webp",
    author: "Vaxy",
    readTime: "4 min de lecture",
    title: "Pourquoi vos dossiers de vaccination comptent plus que jamais",
    excerpt:
      "Dans un monde toujours plus connecté, accéder immédiatement à votre historique vaccinal peut éviter bien du stress à la frontière.",
    content: `## Pourquoi vos dossiers de vaccination comptent plus que jamais

Dans un monde toujours plus connecté, accéder immédiatement à votre historique vaccinal peut éviter bien du stress à la frontière.

### Le problème du papier

Pendant des décennies, le carnet jaune de vaccination de l'OMS a été la référence pour les documents de santé en voyage. Mais le papier se perd, s'abîme ou reste à la maison au moment précis où vous en avez besoin. Beaucoup de voyageurs connaissent l'angoisse d'un contrôle demandant une preuve de vaccination contre la fièvre jaune alors que le carnet est resté sur la table de la cuisine.

### Ce qui peut arriver sans dossier

- **Entrée refusée** aux frontières qui exigent certains vaccins
- **Vaccins répétés inutilement** parce que les médecins n'ont pas accès à votre historique
- **Rappels manqués** faute de système de rappel
- **Situations d'urgence** où le personnel médical ne connaît pas votre statut vaccinal

### L'alternative numérique

Des apps comme Vaxy offrent un dossier numérique sécurisé et toujours disponible pour chaque vaccin, pour vous et toute votre famille. Plus de papier, plus d'incertitude.

### Les règles de voyage changent

Les pays mettent souvent à jour leurs exigences vaccinales. Les règles concernant la fièvre jaune, les méningocoques et la COVID-19 ont beaucoup évolué ces dernières années. Un dossier numérique à jour permet de voir rapidement ce que vous avez et ce qu'il vous manque avant de réserver.

### Bien commencer

Le meilleur moment pour numériser vos vaccins, c'est avant d'en avoir besoin. Prenez dix minutes aujourd'hui pour les enregistrer dans Vaxy et vous ne chercherez plus jamais un carnet papier dans l'urgence.`,
  },
  {
    slug: "travel-vaccinations-guide",
    date: "2026-05-28",
    category: "Santé voyage",
    image: "/images/blog/travel-vaccinations-guide.webp",
    author: "Vaxy",
    readTime: "7 min de lecture",
    title: "Le guide complet des vaccins de voyage en 2026",
    excerpt:
      "Vous partez à l'étranger ? Voici ce qu'il faut savoir sur les vaccins obligatoires, recommandés et le bon moment pour les faire.",
    content: `## Le guide complet des vaccins de voyage en 2026

Vous partez à l'étranger ? Voici ce qu'il faut savoir sur les vaccins obligatoires, recommandés et le bon moment pour les faire.

### Prévoir 6 à 8 semaines avant le départ

La plus grande erreur consiste à attendre la dernière minute. Certains vaccins nécessitent plusieurs doses espacées de plusieurs semaines, et le système immunitaire a besoin de temps pour construire sa protection. Prenez rendez-vous pour un conseil santé voyage au moins 6 à 8 semaines avant le départ.

### Obligatoire ou recommandé

Les vaccins **obligatoires** sont exigés légalement pour entrer dans certains pays. L'exemple le plus courant est la fièvre jaune : de nombreux pays d'Afrique et d'Amérique du Sud demandent une preuve si vous arrivez d'une zone à risque.

Les vaccins **recommandés** ne sont pas légalement obligatoires, mais fortement conseillés selon votre destination, vos activités et votre état de santé.

### Vaccins fréquents par région

**Afrique**
- Fièvre jaune, obligatoire dans de nombreux pays
- Typhoïde
- Hépatite A & B
- Prophylaxie antipaludique, pas un vaccin mais souvent essentielle
- Méningocoques, surtout pour les pèlerins du Hadj

**Asie du Sud-Est**
- Hépatite A & B
- Typhoïde
- Encéphalite japonaise en zones rurales
- Rage en cas de long séjour ou de contact avec des animaux

**Amérique du Sud**
- Fièvre jaune
- Typhoïde
- Hépatite A & B

**Europe & Amérique du Nord**
- Les vaccins de routine suffisent le plus souvent
- Encéphalite à tiques pour certaines zones boisées d'Europe centrale et orientale

### Ne pas oublier les vaccins de routine

Avant les vaccins de voyage plus spécifiques, vérifiez vos vaccins de base :
- ROR
- Tétanos, diphtérie et coqueluche
- Grippe
- COVID-19

### Utiliser Vaxy pour préparer le voyage

La fonction santé voyage de Vaxy vous permet de choisir votre destination et de voir immédiatement quels vaccins sont obligatoires ou recommandés. Elle compare ces conseils à vos dossiers existants et indique précisément ce qui manque.`,
  },
  {
    slug: "vaccinating-your-children",
    date: "2026-05-14",
    category: "Santé familiale",
    image: "/images/blog/vaccinating-your-children.webp",
    author: "Vaxy",
    readTime: "5 min de lecture",
    title: "Suivre le calendrier vaccinal de vos enfants",
    excerpt:
      "Les enfants reçoivent de nombreux vaccins dans leurs premières années. Voici comment rester organisé et ne manquer aucune dose importante.",
    content: `## Suivre le calendrier vaccinal de vos enfants

Les enfants reçoivent de nombreux vaccins dans leurs premières années. Voici comment rester organisé et ne manquer aucune dose importante.

### Le calendrier vaccinal des enfants

Aux Pays-Bas et dans la plupart des pays européens, les enfants suivent un programme national de vaccination. Aux Pays-Bas, le Rijksvaccinatieprogramma couvre les vaccins de la naissance à l'adolescence. Il protège notamment contre :

- DKTP
- Hib
- Hépatite B
- Pneumocoques
- Méningocoques
- ROR
- HPV pour les filles et les garçons vers 10 ans

### Pourquoi une dose manquée compte

Les vaccins fonctionnent mieux lorsqu'ils sont administrés au bon âge. Le calendrier est conçu pour protéger les enfants avant les périodes où ils sont les plus vulnérables. Une dose manquée ne concerne pas seulement votre enfant, elle peut aussi réduire l'immunité collective.

### Le défi pour les parents

Avec autant de vaccins à des âges différents, il est facile de perdre le fil, surtout avec plusieurs enfants. Chaque enfant a son propre calendrier, et les carnets papier dispersés entre les rendez-vous compliquent l'organisation.

### Comment Vaxy aide les familles

Avec Vaxy, vous pouvez ajouter tous les membres de la famille sous un seul compte. Chacun dispose de son dossier vaccinal complet, et vous recevez des rappels lorsque des vaccins sont à prévoir.

### Conseils pour rester à jour

1. **Inscrire dès la naissance** : ajoutez votre nouveau-né dans Vaxy dès que possible
2. **Vérifier avec votre médecin** : faites confirmer les données après chaque visite
3. **Activer les rappels** : utilisez les notifications push pour les prochaines doses
4. **Ajouter des notes** : notez les réactions ou circonstances particulières pour chaque vaccin`,
  },
];

const es: BlogPost[] = [
  {
    slug: "west-nile-virus",
    date: "2026-09-11",
    category: "Enfermedades infecciosas",
    image: "/images/blog/west-nile-virus-hero.svg",
    author: "Vaxy",
    readTime: "6 min de lectura",
    title:
      "Virus del Nilo occidental: qué es, cuáles son los riesgos y qué puede pasar después",
    excerpt:
      "El virus del Nilo occidental se transmite por mosquitos. La mayoría de las personas no se enferma, pero un grupo pequeño puede enfermar de forma grave. ¿Qué puede pasar en los próximos años?",
    faq: [
      {
        q: "¿Qué es el virus del Nilo occidental?",
        a: "Es un virus que vive sobre todo en aves. Los mosquitos pueden pasarlo a las personas y a los caballos.",
      },
      {
        q: "¿Es peligroso?",
        a: "La mayoría de las personas no nota nada o tiene síntomas leves. Un grupo pequeño puede enfermar de forma grave, sobre todo personas mayores y personas con menos defensas.",
      },
      {
        q: "¿Se puede contagiar de persona a persona?",
        a: "No, no por contacto normal. Lo transmiten los mosquitos. De forma rara, puede pasar por sangre o un trasplante.",
      },
      {
        q: "¿Será más frecuente en los próximos años?",
        a: "La OMS y el ECDC dicen que los veranos más cálidos y las temporadas de mosquitos más largas dan más tiempo al virus. En Europa puede seguir activo durante más tiempo y aparecer en más zonas.",
      },
    ],
    content: `## Virus del Nilo occidental

El virus del Nilo occidental vive sobre todo en aves. Los mosquitos pueden pasarlo a las personas y a los caballos. La mayoría de las personas no nota nada o solo tiene síntomas leves.

### ¿Cuáles son los riesgos?

Los síntomas suelen parecer una gripe: fiebre, dolor de cabeza, dolor muscular y cansancio. Un grupo pequeño enferma de forma grave. Eso pasa más a menudo en personas mayores y en personas con menos defensas. Entonces el virus puede afectar al cerebro o a los nervios.

No existe una vacuna para personas ni un medicamento que quite el virus. En el hospital, los médicos pueden ayudar con los síntomas y con los cuidados necesarios.

### ¿Qué puede pasar en los próximos años?

La OMS y el ECDC dicen que los veranos más cálidos y las temporadas de mosquitos más largas dan más tiempo al virus. Por eso puede seguir activo durante más tiempo en Europa y aparecer en más zonas.

En los Países Bajos, el RIVM ya vigila el virus en aves y mosquitos. Eso significa que todavía pueden aparecer nuevos casos humanos cuando el clima y los mosquitos lo permitan.

### ¿Qué puedes hacer?

Protégente de las picaduras de mosquito, sobre todo por la tarde y a primera hora de la mañana. Usa repelente con DEET, ropa que cubra la piel y, si hace falta, mosquitera.

Quita el agua estancada alrededor de casa y del jardín, porque allí pueden criarse los mosquitos. Si tienes fiebre alta, rigidez en el cuello o confusión después de una picadura, llama a un médico enseguida.

### Respuestas cortas

**¿Se puede contagiar el virus del Nilo occidental de una persona a otra?**
No, no por contacto normal. Lo transmiten los mosquitos. De forma rara, puede pasar por sangre o trasplante.

**¿Quién tiene más riesgo de enfermedad grave?**
Sobre todo personas mayores y personas con menos defensas. La mayoría de las demás personas no se enferma o tiene solo una forma leve.

**¿Hay vacuna o tratamiento?**
No, no para personas. Por eso la prevención contra los mosquitos es tan importante.

**¿Cuál es el mejor consejo para los próximos años?**
Evitar las picaduras de mosquito. Esa es la mejor protección ahora y después.

### Fuentes

- [OMS Europa: West Nile virus, as cases rise across parts of Europe](https://www.who.int/europe/news/item/24-07-2026-west-nile-virus--as-cases-rise-across-parts-of-europe--here-is-what-you-need-to-know)
- [OMS: vector-borne diseases](https://www.who.int/news-room/fact-sheets/detail/vector-borne-diseases)
- [RIVM: West Nile fever](https://www.rivm.nl/en/west-nile-fever)
- [RIVM: Westnijlkoorts, actualizaciones](https://www.rivm.nl/westnijlkoorts/actueel)
- [ECDC: surveillance and updates for West Nile virus infection](https://www.ecdc.europa.eu/en/topics-z/west-nile-virus-infection/surveillance-and-updates-west-nile-virus-infection)

Esto es informacion, no consejo medico.`,
  },
  {
    slug: "future-of-vaccination",
    date: "2026-08-26",
    category: "Consejos de salud",
    image: "/images/blog/future-of-vaccination.webp",
    author: "Vaxy",
    readTime: "8 min de lectura",
    title: "Alternativas futuras a la vacuna con aguja",
    excerpt:
      "La mayoría de las vacunas siguen yendo con aguja. Ya hay gotas, una vacuna nasal y un chorro sin aguja. Los parches de Micron, Vaxxas y otros siguen en investigación. OMS, Gavi y RIVM, en claro.",
    faq: [
      {
        q: "¿Qué alternativas a la aguja hay ya?",
        a: "Vacunas orales contra la polio, gotas de rotavirus y vacuna oral contra el cólera. En algunos países, una vacuna nasal contra la gripe. PharmaJet Tropis es un inyector sin aguja precalificado por la OMS, usado en campañas de polio. El programa neerlandés sigue usando una inyección como estándar.",
      },
      {
        q: "¿Qué es un parche de microagujas?",
        a: "Un parche cutáneo pequeño con puntas muy cortas que depositan la vacuna en la piel superficial. La OMS cita dos candidatos de sarampión y rubéola: Micron Biomedical (disoluble) y Vaxxas (recubierto, con aplicador). Faltan la fase 3 y la aprobación de la OMS.",
      },
      {
        q: "¿Puedo vacunarme ya sin aguja en los Países Bajos?",
        a: "Los bebés ya reciben rotavirus en gotas. Por lo demás, el RIVM sigue usando aguja y jeringa en el programa nacional. Los parches cutáneos no son un producto rutinario.",
      },
      {
        q: "¿Por qué busca la OMS alternativas?",
        a: "Porque la cobertura se ha estancado. Jeringas, reconstitución y cadena de frío dificultan vacunar, sobre todo donde los niños no han recibido ninguna dosis. Un producto más simple puede llegar a más gente.",
      },
      {
        q: "¿Es esto un consejo médico?",
        a: "No. Es una explicación basada en OMS, Gavi, BARDA y RIVM. Qué vacuna necesitas lo decides con tu médico, el GGD o un centro de vacunación del viajero.",
      },
    ],
    content: `## Alternativas futuras a la vacuna con aguja

La mayoría de las vacunas siguen yendo al brazo con una aguja. Funciona. También es engorroso: jeringas, a veces reconstituir un polvo, cadena de frío y gente que prefiere saltarse el pinchazo.

La Organización Mundial de la Salud (OMS) dice: esa forma de administrar es una de las razones por las que la cobertura se ha estancado. En 2020, 23 millones de niños se perdieron sus dosis, 3,7 millones más que en 2019.

Por eso OMS, Gavi, UNICEF, PATH y la Fundación Bill y Melinda Gates buscan otras formas de vacunar. Su Vaccine Innovation Prioritisation Strategy (VIPS) eligió en mayo de 2020 tres prioridades: parches de microagujas, vacunas termoestables y códigos de barras en el envase primario.

No todo es futuro. Algunas alternativas a la aguja ya existen. Otras están en ensayos. Esto es información, no un consejo médico. En el programa nacional neerlandés, la inyección sigue siendo la norma.

### ¿Qué hay ya sin aguja?

Desde hace años:

- vacunas orales contra la polio
- gotas de rotavirus para bebés, también en los Países Bajos
- vacuna oral contra el cólera
- en algunos países, una vacuna nasal contra la gripe

Las vacunas nasales llegan a la mucosa, por donde entran los virus respiratorios. También se investigan vacunas nasales contra otras infecciones respiratorias. La mayoría de las vacunas COVID-19 en Europa siguen siendo un pinchazo en el brazo.

**PharmaJet Tropis** es otra cosa: no un parche, sino un chorro fino de líquido en la piel, sin aguja. La OMS ha precalificado el aparato para administración intradérmica. Se ha usado en campañas de polio, entre ellas en Pakistán y Nigeria. Es lo más cercano a «ya sin aguja sobre el terreno». No sustituye la DTP ni la triple vírica en los Países Bajos.

### Parches cutáneos: Micron, Vaxxas y otros

Un microarray patch, o MAP, es un parche cutáneo pequeño con puntas muy cortas. Demasiado cortas para una inyección normal. Depositan la vacuna en la piel superficial, donde hay muchas células inmunitarias.

La OMS cita, entre otras, estas ventajas: sin reconstitución ni jeringa, menos dolor, posiblemente mejor estabilidad fuera de la nevera, administración por personal menos especializado.

Para sarampión y rubéola, la OMS cita dos candidatos clínicos:

**Micron Biomedical** hace un parche disoluble. Permanece unos minutos en la muñeca. Su ensayo de fase 1/2 en adultos, niños pequeños y lactantes en Gambia se publicó en *The Lancet*. Seguridad y respuesta inmunitaria parecieron comparables a la inyección subcutánea habitual. En lactantes, este es el MAP más avanzado.

**Vaxxas** hace un High-Density Microarray Patch: miles de puntas de unos 0,25 milímetros, con vacuna seca, y un aplicador de un solo uso en el brazo o el muslo. Su ensayo en adultos de sarampión y rubéola está cerrado. Después estaba prevista una fase pediátrica.

Faltan la fase 3, el registro y la precalificación de la OMS. No esperes uno en tu próxima DTP o triple vírica.

Otras empresas ponen ARNm en un parche. Eso está más atrás. El Patch Forward Prize de BARDA (EE. UU.) citó, además de Vaxxas y Micron, a **LTS Lohmann** (Alemania) con **BioNet**, y a **PopVax** (India) con LTS. Objetivo: una vacuna de gripe o COVID en ARN sobre un parche de microagujas. No es un producto rutinario.

### Vacunas termoestables

La segunda prioridad de VIPS son vacunas que pueden estar un tiempo limitado fuera de la nevera, en una cadena de temperatura controlada. Menos hielo, menos pérdidas, más alcance. Puede ir con un parche o con una jeringa clásica.

El ARNm es otro salto: fabricar más rápido ante una nueva variante. Esas vacunas siguen yendo con aguja y a menudo piden un frío extra.

### ¿Qué implica para tu registro?

Pinchazo, gotas, chorro o más adelante un parche: la fecha, la enfermedad y el nombre del producto siguen haciendo falta. En un viaje, un recuerdo o un brote nuevo, un médico necesita saber qué tienes ya.

Con Vaxy lo guardas en un solo sitio, para ti y tu familia.

¿Dudas sobre una vacuna? Pregunta a tu médico, al GGD o a un centro de vacunación del viajero.

### Respuestas cortas

**¿Qué alternativas a la aguja hay ya?**
Gotas (polio, rotavirus, cólera), en algunos países una vacuna nasal contra la gripe, y el chorro sin aguja de PharmaJet en algunas campañas de polio.

**¿Qué es un parche de microagujas?**
Un parche pequeño con puntas cortas. Micron y Vaxxas son los dos candidatos que cita la OMS para sarampión y rubéola.

**¿Puedo ir ya sin aguja en los Países Bajos?**
Rotavirus ya en gotas. Por lo demás, el RIVM sigue usando aguja y jeringa. Los parches son investigación.

**¿Por qué lo quiere la OMS?**
Una administración más simple puede llegar a más gente, sobre todo a niños que hoy no reciben ninguna dosis.

### Fuentes

- [OMS: administración innovadora de vacunas, incluidos parches de microagujas](https://www.who.int/teams/immunization-vaccines-and-biologicals/product-and-delivery-research/delivery-technologies)
- [OMS: grupo consultivo técnico sobre parches de sarampión y rubéola](https://www.who.int/news-room/articles-detail/2023-November-132)
- [OMS: perfil de producto objetivo del MAP de sarampión y rubéola](https://www.who.int/publications/i/item/9789240000209)
- [The Lancet: parche Micron de sarampión y rubéola, fase 1/2 en Gambia](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(24)00532-4/fulltext)
- [OMS PQS: PharmaJet Tropis, inyector sin aguja](https://extranet.who.int/prequal/immunization-devices/e008-071)
- [BARDA: Patch Forward Prize, ganadores de concepto](https://www.patchforwardprize.com/)
- [Gavi: Vaccine Innovation Prioritisation Strategy (VIPS)](https://www.gavi.org/our-alliance/market-shaping/vaccine-innovation-prioritisation-strategy)
- [RIVM: programa nacional de vacunación](https://rijksvaccinatieprograma.nl/)`,
  },
  {
    slug: "philanthropy-global-infectious-diseases",
    date: "2026-08-26",
    category: "Noticias de salud",
    image: "/images/blog/philanthropy-global-infectious-diseases.webp",
    author: "Vaxy",
    readTime: "8 min de lectura",
    title:
      "Cómo piensa la filantropía las enfermedades infecciosas: Gates, Gavi y socios",
    excerpt:
      "La Fundación Gates, Gavi, Wellcome y CEPI ven las vacunas como una de las herramientas más fuertes contra las enfermedades infecciosas en el mundo. La OMS marca el rumbo. Así funciona esa cooperación.",
    faq: [
      {
        q: "¿Qué quiere la Fundación Gates con las enfermedades infecciosas?",
        a: "Llegar a niños, adolescentes y adultos de países de renta baja con las vacunas que necesitan, y evitar que la siguiente generación muera de enfermedades infecciosas. La fundación llama a las vacunas una de las herramientas sanitarias más rentables: de 3,5 a 5 millones de vidas al año.",
      },
      {
        q: "¿Qué es Gavi?",
        a: "Gavi, the Vaccine Alliance, es una alianza público-privada. Gobiernos, OMS, UNICEF, Banco Mundial, industria de vacunas y la Fundación Gates trabajan en ella. Gavi compra y reparte vacunas para países de renta baja.",
      },
      {
        q: "¿Decide Bill Gates la política de vacunación neerlandesa?",
        a: "No. En los Países Bajos, el RIVM, el Consejo de Salud y el Ministerio de Sanidad fijan la política. La OMS emite orientaciones mundiales. Los fondos filantrópicos financian investigación y compras. No redactan el calendario nacional.",
      },
      {
        q: "¿Qué otros fondos hacen este trabajo?",
        a: "Wellcome Trust y CEPI (vacunas de brotes), Rotary International (polio) y la propia Gavi, entre otros. Suelen trabajar con la OMS y los gobiernos, no en su lugar.",
      },
      {
        q: "¿Es esto un consejo médico?",
        a: "No. Explica una estrategia mundial. Para tus vacunas: médico, GGD o centro de vacunación del viajero.",
      },
    ],
    content: `## Cómo piensa la filantropía las enfermedades infecciosas: Gates, Gavi y socios

Las enfermedades infecciosas no se detienen en una frontera. Los fondos filantrópicos razonan así: si falta una vacuna en Lagos o en Lahore, la misma enfermedad puede aparecer después en Europa. Las vacunas son, para ellos, la herramienta más afilada y barata.

La Fundación Bill y Melinda Gates es el mayor actor privado. No trabaja sola. La Organización Mundial de la Salud (OMS) marca el rumbo. Gavi compra y reparte. Wellcome y CEPI financian investigación sobre nuevos brotes.

Esto explica esa estrategia; no es un consejo médico. Las vacunas neerlandesas siguen el programa nacional, no una fundación en Seattle.

### ¿Cómo piensa la Fundación Gates?

La fundación lo dice en breve: las vacunas salvan de 3,5 a 5 millones de vidas al año. Desde que la OMS lanzó el Programa Ampliado de Inmunización en 1974, se han salvado unos 154 millones de vidas, sobre todo de niños.

Su objetivo en países de renta baja: llegar a niños, adolescentes y adultos con las dosis que necesitan. Y un mundo sin enfermedades que se puedan prevenir con una vacuna.

Tres líneas vuelven una y otra vez:

1. **Comprar y repartir lo que ya existe.** Gavi es su mayor beneficiario. De 2000 a 2023, Gavi inmunizó a más de 1.100 millones de niños y evitó más de 18,8 millones de muertes futuras, según la fundación.
2. **Hacer asequibles las vacunas nuevas.** Neumococo, rotavirus, VPH, cólera, meningitis A.
3. **Reforzar la atención ordinaria.** Un programa de inmunización es, para ellos, la columna de la atención primaria.

En junio de 2025 la fundación comprometió 1.600 millones de dólares a Gavi en cinco años. Desde 2000 ha destinado más de 30.600 millones de dólares a vacunas, de ellos 7.700 millones a Gavi.

Eso encaja con la Immunization Agenda 2030 de la OMS: todas las personas, en todas partes, a cualquier edad. Y con Gavi 6.0 (2026–2030): vacunar a más de 500 millones de niños y adolescentes adicionales, salvar de 8 a 9 millones de vidas extra.

### ¿Dónde ven el problema?

El avance se ha estancado. En 2023, 14,5 millones de niños no recibieron ni una sola vacuna, más que el año anterior. Los brotes de sarampión subieron. La fundación lo llama un riesgo para décadas de logros.

Su respuesta no es una vacuna nueva, sino el alcance: introducciones de VPH, campañas de sarampión y un esfuerzo extra en las zonas con menor cobertura, también en conflictos.

La polio sigue siendo un estandarte. Desde 2007 la fundación apoya la Iniciativa de Erradicación Mundial de la Poliomielitis, con OMS, Rotary International, CDC, UNICEF y Gavi. La polio salvaje sigue en Pakistán y Afganistán. Desde 1988 los casos han caído más del 99 %.

### Wellcome, CEPI y Rotary

Otros fondos piensan en la misma dirección, con otra tarea.

**Wellcome Trust** financia ciencia y cofundó CEPI en 2017.

**CEPI**, la Coalition for Epidemic Preparedness Innovations, existe para tener vacunas listas antes de la siguiente epidemia. Gobiernos, Gates y Wellcome la crearon tras el ébola. El objetivo: una vacuna más rápida ante una enfermedad X desconocida, en su visión en 100 días. CEPI no escribe calendarios. Eso sigue en la OMS y los países.

**Rotary International** recauda desde hace décadas para la polio, como socio de la GPEI.

La alianza VIPS (Gavi, OMS, Fundación Gates, UNICEF, PATH) muestra cómo estos clubes eligen juntas innovaciones, como los parches cutáneos. La filantropía suele pagar los primeros pasos. Gobiernos y OMS deciden si un producto entra en un programa.

### ¿Qué implica para los Países Bajos?

Nada en tu cartilla lo decide una fundación. Lo hacen el RIVM, el Consejo de Salud y el Ministerio de Sanidad.

Aun así te toca. La compra mundial mantiene más pequeños el sarampión, la polio y el cólera. Vacunas de viaje, preparación pandémica y nuevas formas de administrar salen de la misma red.

Anota la fecha y el nombre de cada dosis. Con Vaxy conservas ese registro, también si más adelante viajas a un país donde Gavi hace las compras.

Este artículo es información, no un consejo médico.

### Respuestas cortas

**¿Qué quiere la Fundación Gates?**
Llevar vacunas a personas de países de renta baja y prevenir enfermedades infecciosas en la siguiente generación.

**¿Qué es Gavi?**
Una alianza de gobiernos, OMS, UNICEF, Banco Mundial, industria y la Fundación Gates que compra vacunas para países más pobres.

**¿Decide la filantropía la política neerlandesa?**
No. Lo hacen RIVM, Consejo de Salud y ministerio. La OMS da orientaciones mundiales.

**¿Quién más trabaja en esto?**
Wellcome, CEPI, Rotary, UNICEF y donantes nacionales, entre otros.

### Fuentes

- [Fundación Gates: Immunization](https://www.gatesfoundation.org/our-work/programs/global-development/immunization)
- [Fundación Gates: compromiso de 1.600 millones de dólares a Gavi, junio de 2025](https://www.gatesfoundation.org/ideas/media-center/press-releases/2025/06/renewed-gavi-commitment-lifesaving-vaccine-access)
- [Fundación Gates: polio](https://www.gatesfoundation.org/our-work/programs/global-development/polio)
- [OMS: Immunization Agenda 2030](https://www.who.int/teams/immunization-vaccines-and-biologicals/strategies/ia2030)
- [OMS: cobertura de inmunización, ficha](https://www.who.int/news-room/fact-sheets/detail/immunization-coverage)
- [Gavi 6.0, estrategia 2026–2030](https://www.gavi.org/our-work/strategy/phase-6-2026-2030)
- [CEPI](https://cepi.net/)
- [RIVM: programa nacional de vacunación](https://rijksvaccinatieprogramma.nl/)`,
  },
  {
    slug: "climate-diseases-netherlands",
    date: "2026-08-26",
    category: "Consejos de salud",
    image: "/images/blog/climate-diseases-netherlands.webp",
    author: "Vaxy",
    readTime: "8 min de lectura",
    title:
      "Nuevas enfermedades infecciosas en los Países Bajos por el cambio climático",
    excerpt:
      "El dengue sigue siendo una enfermedad importada en los Países Bajos europeos. RIVM, Consejo de Salud y ECDC dicen: el calor, las garrapatas y los mosquitos cambian el riesgo. Qué se acerca y qué ya está aquí.",
    faq: [
      {
        q: "¿Hay ya dengue en los Países Bajos?",
        a: "En los Países Bajos europeos, solo en viajeros. El RIVM no ha confirmado infección local. En el Caribe neerlandés sí hay dengue, con brotes mayores en 2023–2024. El sur de Europa ya tiene casos locales, entre ellos Francia, Italia y España.",
      },
      {
        q: "¿Se va a asentar aquí el mosquito tigre?",
        a: "Todavía no. La NVWA combate las introducciones. El RIVM dice: más mosquitos tigre llegan sobre todo por comercio y viajes, no por el clima. El Consejo de Salud espera asentamiento en algunos sitios en 2 a 5 años. Entonces, veranos más cálidos podrían permitir dengue local tras la introducción por un viajero.",
      },
      {
        q: "¿Qué enfermedades ya han cambiado aquí?",
        a: "Encefalitis por garrapatas en garrapatas neerlandesas desde 2016, con 8 notificaciones de infección adquirida en los Países Bajos en 2024. Virus del Nilo Occidental en 8 personas en 2020. Lyme por garrapatas lleva tiempo. El agua más cálida aumenta el riesgo de vibrio y legionela.",
      },
      {
        q: "¿Debo vacunarme en los Países Bajos contra el dengue o la TBE?",
        a: "El RIVM dice: no hace falta vacuna TBE para vivir en los Países Bajos. Para viajar a países donde TBE o dengue son frecuentes: pregunta al LCR, a tu médico o a un centro de viajeros. Existen vacunas para ambas.",
      },
      {
        q: "¿Es esto un consejo médico?",
        a: "No. Es una explicación basada en RIVM, Consejo de Salud, ECDC y OMS. Tras una picadura de mosquito o garrapata con síntomas: médico.",
      },
    ],
    content: `## Nuevas enfermedades infecciosas en los Países Bajos por el cambio climático

El dengue suena tropical. En los Países Bajos europeos aún lo es: una enfermedad que traen los viajeros. Aun así, el Consejo de Salud advirtió en mayo de 2026: el cambio climático hace aquí más favorables las condiciones para enfermedades por mosquitos, garrapatas y agua.

El RIVM es más cauto. El clima es un factor. Comercio, viajes y cómo vivimos suelen pesar más. Un mosquito nuevo en los Países Bajos no es automáticamente culpa de un verano más cálido.

Esto es información, no un consejo médico.

### Dengue: ahora importado, quizá local más adelante

El dengue se transmite por el mosquito de la fiebre amarilla y el mosquito tigre asiático. La OMS estima cientos de millones de infecciones al año en el mundo. Fiebre, dolor muscular, a veces hemorragias graves.

Según la guía LCI del RIVM, el dengue en los Países Bajos europeos es una enfermedad importada. Hasta ahora no se ha confirmado infección local. Las pruebas positivas en viajeros que vuelven sí subieron: de media 136 al año en 2014–2019, 367 en 2023 y 544 en 2024.

En el Caribe neerlandés sí hay dengue. El RIVM cita decenas de casos al año y brotes mayores en 2023–2024. El Consejo de Salud da prioridad a esas islas, porque allí los riesgos son mayores y más diversos.

El sur de Europa ya ha tenido dengue local. El ECDC sigue casos en Francia, Italia, España y Croacia, entre otros. En 2026, hasta principios de agosto, solo Francia había notificado un caso de dengue adquirido localmente en el continente europeo.

Existen vacunas contra el dengue. En los Países Bajos son para viajeros, no para toda la población. Pregunta al LCR o a un centro de vacunación del viajero.

### El mosquito tigre: el control gana tiempo

El mosquito tigre asiático (*Aedes albopictus*) puede transmitir dengue, chikungunya y zika. Está bien asentado en el Mediterráneo. Aún no en los Países Bajos, gracias al control del Ministerio de Sanidad y la NVWA.

El RIVM es claro: más hallazgos en los Países Bajos vienen sobre todo del comercio y los viajes, no del cambio climático. Si el mosquito se asienta, entonces el clima sí cuenta. Con veranos más calurosos, los virus se multiplican más rápido en el mosquito.

El Consejo de Salud espera asentamiento en algunos lugares en 2 a 5 años. Después pueden seguir brotes locales de dengue si un viajero virémico trae el virus.

En agosto de 2026 la NVWA publicó un estudio de ADN: el mosquito llega por varias introducciones, entre ellas neumáticos usados y aeropuertos. En algunos sitios sobrevivió al invierno. El control aún puede eliminar una población.

### ¿Qué hay ya aquí?

No todo es futuro.

**Encefalitis por garrapatas (TBE).** Desde 2016 sabemos que las garrapatas neerlandesas pueden llevar el virus, entre otros en Sallandse y Utrechtse Heuvelrug. Entre 2016 y junio de 2024, 23 pacientes contrajeron la infección en los Países Bajos. En 2024 hubo 8 notificaciones. Desde el 1 de marzo de 2025, la TBE con síntomas neurológicos es de declaración obligatoria. Unas 1 de cada 1.500 garrapatas en zonas conocidas lleva el virus. En Lyme, unas 1 de cada 5. El RIVM dice: no hace falta vacuna TBE para vivir en los Países Bajos. Sí al viajar a países donde la TBE es frecuente.

**Fiebre del Nilo Occidental.** En 2020, 8 personas en los Países Bajos contrajeron el virus, sobre todo alrededor de Utrecht, a través del mosquito doméstico común. El virus está en las aves. El Consejo de Salud dice que los veranos más cálidos favorecen la transmisión. El riesgo para la mayoría de la gente sigue siendo pequeño.

**Lyme.** Sigue siendo la enfermedad por garrapatas más frecuente aquí. Más garrapatas con tiempo más suave aumentan el contacto, también según el Consejo de Salud.

**Agua.** Las aguas costeras neerlandesas se han vuelto más adecuadas para *Vibrio*. El agua superficial más cálida también ayuda a la legionela y a las algas azul-verdosas.

Chikungunya y zika siguen la misma historia de mosquitos que el dengue: ahora sobre todo un riesgo de viaje, quizá local más adelante si el mosquito tigre se queda.

### ¿Qué puedes hacer?

- Viaje a zonas con dengue o TBE: pregunta a tiempo si tiene sentido vacunarte.
- En los Países Bajos: revisa garrapatas tras estar en zonas verdes, reduce picaduras de mosquito por la tarde. Fiebre tras un viaje tropical: médico, y di dónde estuviste.

Anota vacunas y dosis de viaje. Con Vaxy ves antes de salir si dengue o TBE ya están en tu registro.

Este artículo es información, no un consejo médico.

### Respuestas cortas

**¿Hay ya dengue aquí?**
En los Países Bajos europeos, solo en viajeros. Sí en el Caribe neerlandés. La transmisión local ya existe en el sur de Europa.

**¿Se asentará el mosquito tigre?**
Todavía no, por el control. El Consejo de Salud lo espera en algunos sitios en 2 a 5 años.

**¿Qué enfermedades hay ya?**
TBE desde 2016, Nilo Occidental en personas en 2020, Lyme desde antes. Agua más cálida: vibrio y legionela.

**¿Debo vacunarme aquí contra dengue o TBE?**
Por lo general no para vivir en los Países Bajos europeos. A veces sí para ciertos viajes. Pregunta al LCR, a tu médico o al GGD.

### Fuentes

- [RIVM: cambio climático y enfermedades infecciosas](https://www.rivm.nl/klimaat-en-gezondheid/infectieziekten)
- [RIVM: dengue](https://www.rivm.nl/dengue)
- [Guía LCI sobre dengue](https://lci.rivm.nl/richtlijnen/dengue)
- [RIVM: fiebre del Nilo Occidental](https://www.rivm.nl/westnijlkoorts)
- [RIVM: encefalitis por garrapatas](https://www.rivm.nl/tekenencefalitis)
- [Consejo de Salud: clima y salud, 21 de mayo de 2026](https://www.gezondheidsraad.nl/actueel/nieuws/2026/05/21/bescherm-de-bevolking-tegen-de-toenemende-gezondheidsrisicos-van-klimaatverandering)
- [ECDC: riesgo de dengue UE/EEE](https://www.ecdc.europa.eu/en/dengue/surveillance-and-updates/risk-assessment)
- [OMS: dengue](https://www.who.int/news-room/fact-sheets/detail/dengue-and-severe-dengue)
- [NVWA: estudio de ADN del mosquito tigre asiático, 20 de agosto de 2026](https://www.nvwa.nl/actueel/nieuws/2026/08/20/dna-onderzoek-biedt-nvwa-nieuw-inzicht-in-verspreiding-en-bestrijding-van-de-aziatische-tijgermug)
- [LCR: información sobre vacunas](https://lcr.nl/informatie-vaccinaties)`,
  },
  {
    slug: "vaccine-boosters",
    date: "2026-08-22",
    category: "Consejos de salud",
    image: "/images/blog/vaccine-boosters.webp",
    author: "Vaxy",
    readTime: "8 min de lectura",
    title:
      "¿Qué vacunas necesitan una dosis de recuerdo y cuánto dura la protección?",
    excerpt:
      "No todas las vacunas necesitan una dosis extra. El RIVM, el LCI y el LCR explican cuándo toca un recuerdo y cuánto tiempo sigues protegido después.",
    faq: [
      {
        q: "¿Qué vacunas necesitan un recuerdo?",
        a: "Sobre todo DTP (cada 10 años), fiebre tifoidea (cada 3 años), encefalitis por garrapatas, encefalitis japonesa y cólera. La gripe y la COVID para grupos de riesgo son vacunas anuales, no recuerdos clásicos.",
      },
      {
        q: "¿Cada cuánto debo repetir el DTP?",
        a: "Cada 10 años tras una serie completa. El programa neerlandés y el LCR dicen que entonces estás protegido 10 años frente a difteria y tétanos. Tras el calendario infantil, probablemente estás protegido de por vida frente a la parálisis por polio.",
      },
      {
        q: "¿Hay que repetir la hepatitis A más adelante?",
        a: "No si ya tienes dos dosis. El LCR dice: 1 año tras la primera, 50 años tras la segunda. El LCI dice que las personas sanas no necesitan otra dosis.",
      },
      {
        q: "¿La fiebre amarilla vale para toda la vida?",
        a: "Casi siempre sí. Una dosis protege de por vida en la mayoría de las personas. El certificado es válido de por vida desde 2016. Algunos grupos, como las personas con VIH, sí necesitan una repetición.",
      },
      {
        q: "¿Esto es consejo médico?",
        a: "No. Es una explicación basada en RIVM, LCI y LCR. Si dudas, pregunta a tu médico, al servicio de salud pública o a una clínica del viajero.",
      },
    ],
    content: `## ¿Qué vacunas necesitan una dosis de recuerdo y cuánto dura la protección?

No todas las vacunas necesitan una dosis extra. Algunas series duran mucho, o de por vida, cuando están completas. Otras sí hay que repetirlas más adelante.

Este resumen es para personas sanas en los Países Bajos, según el programa nacional (RVP), las guías LCI del RIVM y el centro neerlandés de salud del viajero (LCR). Es información, no consejo médico.

### ¿Qué es un recuerdo?

Un recuerdo es una dosis extra después de una serie primaria completa, porque la protección baja.

Eso no es lo mismo que:

- la segunda o tercera dosis de una serie, como hepatitis A o B
- una vacuna anual, como la de la gripe, porque el virus cambia
- dosis extra tras una mordedura o una herida, como rabia o tétanos

### Vacunas que sí necesitan recuerdo

**DTP (difteria, tétanos, polio).** Cada 10 años tras una serie completa. ¿Nunca vacunado? Empieza con tres dosis. El RVP dice: tras la DTP a los 14 años estás protegido unos 10 años frente a difteria y tétanos, y probablemente de por vida frente a la parálisis por polio. El LCR da el mismo intervalo de 10 años a los viajeros.

**Tos ferina.** La vacuna acelular protege unos 4 a 6 años, según el LCI. Los niños reciben un recuerdo a los 5 años. Los adultos no tienen un intervalo fijo. Si estás embarazada, el RIVM aconseja la vacuna de tos ferina a partir de la semana 22, en cada embarazo.

**Fiebre tifoidea.** Una dosis protege 3 años, dice el LCR. La protección no es completa. Repite cada 3 años si sigues en riesgo.

**Encefalitis japonesa.** Dos dosis en un mes dan 2 años de protección. A partir de un año después puedes poner una tercera. Luego 10 años, según el LCR.

**Encefalitis por garrapatas.** Tres dosis (meses 0, 1 y 6). Luego un recuerdo a los 3 años. Después cada 5 años si tienes menos de 60, y cada 3 años a partir de 60, según el LCI. Para viajeros de 50 años o más, el LCR a veces usa una dosis extra en la serie primaria.

**Cólera.** Los viajeros normales en los Países Bajos casi no la reciben. Según el LCR, una vacunación protege 6 meses en niños de 2 a 5 años, y 2 años en niños mayores y adultos.

**Meningococo ACWY.** Una dosis protege al menos 10 años, dice el LCR. Para peregrinos a La Meca el certificado vale 5 años.

**Fiebre amarilla.** Casi nunca hay recuerdo. Una dosis protege de por vida en la mayoría. El certificado es válido de por vida desde el 1 de julio de 2016. El LCI sí aconseja una repetición tras vacunación en el embarazo, VIH o trabajo de laboratorio con el virus.

**Rabia.** Dos dosis antes del viaje dan protección básica de por vida, dice el LCI. No hay recuerdo rutinario para viajeros. Tras una mordedura, un arañazo o un lametazo siempre hacen falta dosis extra.

**Gripe.** Cada año, para personas con más riesgo. El LCI dice: los anticuerpos se mantienen unos 24 semanas. La vacuna pierde efecto a los seis meses, y el virus cambia.

**COVID-19.** Una ronda de otoño para grupos de riesgo. El RIVM prefiere 6 meses tras la dosis anterior, mínimo 3 meses.

**Neumococo.** La vacuna antigua PPV23 se repite cada 5 años. Tras la conjugada más nueva PCV20, los adultos no necesitan por ahora otra dosis, dice el LCI.

### Vacunas que no necesitan recuerdo tras la serie

**Hepatitis A.** Dos dosis, al menos 6 meses de intervalo. Tras la primera: 1 año. Tras la segunda: 50 años, según la página pública del LCR. El LCI dice que las personas sanas no necesitan otra dosis. Los modelos apuntan a 50 años, quizá de por vida.

**Hepatitis B.** Tres dosis. Luego de por vida en principio, si el cuerpo respondió bien. Entonces no hace falta recuerdo. Algunos trabajos sí hacen un análisis de sangre.

**VPH.** Dos dosis con 6 meses de intervalo, en el RVP hacia los 10 años. El LCI sigue viendo buena protección a los 12 años. Un recuerdo posterior no está indicado por ahora.

**Triple vírica (sarampión, paperas, rubéola).** Dos dosis. Luego probablemente de por vida frente a sarampión y rubéola. En paperas está menos claro. Tras dos dosis, el 99 % está protegido, dice el RVP.

**Culebrilla.** Dos dosis de Shingrix. El RIVM dice: la protección dura al menos 6 a 10 años. Aún no hay una tercera dosis estándar.

**Dengue y chikungunya.** El LCR dice que aún no se sabe cuánto dura la protección.

### Niños: recuerdos en el programa nacional

Los niños ya reciben dosis de repetición en el calendario. Según rijksvaccinatieprogramma.nl, entre otras:

- 12 meses: DKTP-Hib-HepB y neumococo
- 14 meses: triple vírica y MenACWY
- 3 años: segunda triple vírica (algunos años de nacimiento más tarde, en recuperación)
- 5 años: DKT
- 10 años: VPH, dos veces
- 14 años: DTP y MenACWY (DTP a los 14 desde el año de nacimiento 2016)

Después, el DTP ya no está en el programa infantil. Para difteria y tétanos sigue siendo prudente un recuerdo cada 10 años, sobre todo si viajas.

### Qué implica esto para tu registro

Anota la fecha, el nombre de la vacuna y si la serie está completa. Así ves si te falta un recuerdo, o solo la siguiente dosis de la misma serie.

Con Vaxy lo tienes en un teléfono. Es menos fácil olvidar el DTP a los 10 años, o la segunda dosis de hepatitis A.

Si dudas, pregunta a tu médico, al servicio de salud pública o a una clínica del viajero. Las personas con las defensas bajas, las embarazadas y algunos trabajos suelen tener otro calendario.

### Respuestas cortas

**¿Qué vacunas necesitan un recuerdo?**
DTP cada 10 años, tifoidea cada 3 años, más encefalitis por garrapatas, encefalitis japonesa y cólera. Gripe y COVID para grupos de riesgo cada año.

**¿Cuánto dura el DTP?**
10 años frente a difteria y tétanos. La polio tras el calendario infantil, probablemente de por vida frente a la parálisis.

**¿Hay que repetir la hepatitis A más adelante?**
No tras dos dosis. Tras la segunda, 50 años según el LCR.

**¿La fiebre amarilla es de por vida?**
Casi siempre sí. A veces hace falta una dosis extra, por ejemplo con VIH.

### Fuentes

- [Programa neerlandés: calendario](https://rijksvaccinatieprogramma.nl/vaccinaties/vaccinatieschema)
- [RVP: vacuna DTP](https://rijksvaccinatieprogramma.nl/vaccinaties/dtp)
- [RVP: vacuna triple vírica](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [LCR: información sobre vacunaciones](https://lcr.nl/informatie-vaccinaties)
- [LCI: tétanos](https://lci.rivm.nl/richtlijnen/tetanus)
- [LCI: vacunación hepatitis A](https://lci.rivm.nl/factsheets/hepatitis-a-vaccinatie)
- [LCI: vacunación hepatitis B](https://lci.rivm.nl/factsheets/hepatitis-b-vaccinatie)
- [LCI: fiebre amarilla](https://lci.rivm.nl/richtlijnen/gele-koorts)
- [LCI: vacunación TBE](https://lci.rivm.nl/factsheets/tbe-vaccinatie)
- [LCI: vacunación tos ferina](https://lci.rivm.nl/factsheets/kinkhoestvaccinatie)
- [LCI: vacunación VPH](https://lci.rivm.nl/factsheets/hpv-vaccinatie)
- [LCI: vacunación neumococo](https://lci.rivm.nl/factsheets/pneumokokkenvaccinatie)
- [LCI: vacunación gripe](https://lci.rivm.nl/richtlijnen/influenzavaccinatie)
- [LCI: vacunación COVID-19](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)
- [LCI: rabia](https://lci.rivm.nl/richtlijnen/rabies)
- [RIVM: vacunación corona](https://www.rivm.nl/corona/coronaprik)
- [RIVM: vacunación culebrilla](https://www.rivm.nl/gordelroos/vaccinatie)
- [RIVM: vacunaciones en el embarazo](https://www.rivm.nl/zwangerschap-en-infectieziekten/vaccinaties-tijdens-zwangerschap)`,
  },
  {
    slug: "how-mrna-vaccines-work",
    date: "2026-08-19",
    category: "Consejos de salud",
    image: "/images/blog/how-mrna-vaccines-work.webp",
    author: "Vaxy",
    readTime: "7 min de lectura",
    title: "Qué son las vacunas de ARNm y cómo funcionan",
    excerpt:
      "Una vacuna de ARNm es un mensaje corto para tus células, no un virus vivo. La OMS, el CDC, la EMA y el RIVM explican cómo funciona y por qué no cambia tu ADN.",
    faq: [
      {
        q: "¿Qué es una vacuna de ARNm?",
        a: "Es una inyección con un mensaje corto para tus células. Ese mensaje enseña a tu cuerpo a reconocer un trozo inofensivo de un virus. No hay virus vivo dentro.",
      },
      {
        q: "¿Cómo funciona una vacuna de ARNm?",
        a: "Después de la inyección, tus células leen el ARNm y fabrican un trozo de proteína inofensivo. Tu sistema inmunitario aprende a reconocerlo. Luego tu cuerpo degrada el ARNm con rapidez.",
      },
      {
        q: "¿Una vacuna de ARNm cambia tu ADN?",
        a: "No. El CDC y la EMA dicen que el ARNm no entra en el núcleo de la célula, donde está tu ADN. Por eso no puede cambiar tu código genético.",
      },
      {
        q: "¿Puede una vacuna de ARNm darte COVID-19?",
        a: "No. El CDC dice que no hay coronavirus vivo en la vacuna. No puedes coger COVID-19 por ella.",
      },
      {
        q: "¿Cuánto tiempo permanece el ARNm en el cuerpo?",
        a: "Poco. La EMA dice que tu cuerpo degrada el ARNm rápido, igual que tu propio ARNm. En los estudios bajaba en pocos días.",
      },
    ],
    content: `## Qué son las vacunas de ARNm y cómo funcionan

Una vacuna de ARNm es una inyección que enseña a tu cuerpo a reconocer una enfermedad. No contiene un virus vivo. Contiene un mensaje corto: ARNm.

ARNm significa ARN mensajero. Tu cuerpo fabrica ARNm todo el día. Ese mensaje dice a una célula qué proteína debe hacer.

En una vacuna de ARNm, el mensaje se fabrica en un laboratorio. Habla de un trozo inofensivo del virus. En la COVID-19 suele ser la proteína de la espícula, o spike. Luego tu cuerpo elimina el mensaje con rapidez.

La Organización Mundial de la Salud (OMS) llama a esto una vacuna con un plano de construcción, en lugar del virus mismo.

### Cómo funciona una vacuna de ARNm, paso a paso

Según el CDC de Estados Unidos, funciona así:

1. Recibes la inyección en la parte alta del brazo.
2. El ARNm va en pequeñas burbujas de grasa. Ayudan al mensaje a entrar en la célula.
3. Dentro de la célula, el cuerpo lee el mensaje y fabrica un trozo de proteína inofensivo.
4. Luego la célula degrada el ARNm. Sale del cuerpo como residuo.
5. La célula muestra el trozo de proteína a tu sistema inmunitario.
6. Tu sistema inmunitario fabrica anticuerpos y células de memoria.
7. Si más tarde encuentras el virus de verdad, tu cuerpo lo reconoce antes.

El CDC dice: suele tardar unas semanas hasta que esa protección está lista.

### ¿Una vacuna de ARNm cambia tu ADN?

No. Lo dicen el CDC y la Agencia Europea de Medicamentos (EMA).

El ARNm no entra en el núcleo de la célula. Ahí está tu ADN. El mensaje se queda fuera del núcleo, donde tu cuerpo siempre fabrica proteínas.

El ARNm también es químicamente distinto del ADN. No puede meterse así en tu ADN.

La EMA dice además: una vacuna de ARNm no es una terapia génica. El objetivo no es reparar ni cambiar tus genes.

La vacuna no puede darte COVID-19. No hay coronavirus vivo dentro, dice el CDC.

### ¿Cuánto tiempo permanece el ARNm en el cuerpo?

Poco. La EMA dice: el cuerpo degrada el ARNm rápido, igual que tu propio ARNm.

En estudios con animales de Comirnaty (Pfizer/BioNTech), el ARNm bajaba en 6 a 9 días. En Spikevax (Moderna), los investigadores hallaban al cabo de 1 a 3 días ARNm sobre todo en el punto de inyección, en los ganglios y en el bazo.

En la autorización, las autoridades no esperaban acumulación en los órganos.

### ¿Por qué hay burbujas de grasa en la vacuna?

El ARNm es frágil. Sin protección se rompe rápido.

La EMA explica: pequeñas partículas de grasa, nanopartículas lipídicas, ayudan al ARNm a entrar en la célula. La pared de la célula también es de grasa. Las burbujas se fusionan con ella.

Este tipo de partículas de grasa en medicamentos no es nuevo. El primer medicamento con estas burbujas se autorizó en la UE en 2018.

### ¿Son nuevas las vacunas de ARNm?

Las vacunas contra la COVID-19 eran nuevas para mucha gente. La técnica no lo era.

La EMA dice: los investigadores ya llevaban unos veinte años trabajando con ARNm, para enfermedades infecciosas y para el cáncer. El CDC menciona estudios anteriores sobre gripe, zika, rabia y CMV.

El ARNm es relativamente pequeño y sencillo de fabricar. Por eso los fabricantes pudieron producir más rápido que con vacunas clásicas. Las pruebas de seguridad no se saltaron, dice el CDC.

### Qué vacunas de ARNm existen

Las más conocidas en Europa son:

- Comirnaty de Pfizer/BioNTech
- Spikevax de Moderna

Están autorizadas por la EMA. El instituto neerlandés RIVM usa vacunas COVID-19 en su programa de otoño, sobre todo para personas con más riesgo. En 2026 pueden vacunarse, entre otras, las personas de 70 años o más y las personas con riesgo médico extra.

También hay otros tipos de vacunas COVID-19, como las vacunas de proteína. Esas ya contienen un trozo de proteína. El RIVM dice que puede ser una opción si no puedes o no quieres una vacuna de ARNm por motivo médico.

La guía del RIVM lo dice breve: Comirnaty es una vacuna de ARNm. No contiene virus vivo. El ARNm codifica la proteína de la espícula.

El ARNm también se investiga para otras enfermedades y para algunos cánceres. Eso sigue siendo investigación. Una vacuna COVID-19 de ARNm no es lo mismo que una vacuna personal contra el cáncer.

### Cuáles son los efectos secundarios

La OMS dice: pueden aparecer síntomas leves o moderados. Es una señal de que tu cuerpo está creando protección.

A menudo:

- dolor o enrojecimiento en el punto de inyección
- cansancio
- dolor de cabeza
- dolor muscular
- escalofríos
- fiebre

La mayoría de los síntomas pasan en unos días. No todo el mundo tiene efectos secundarios.

Los efectos secundarios graves son raros. La EMA dice: la seguridad de las vacunas COVID-19 autorizadas es tranquilizadora, después de cientos de millones de dosis.

Un efecto secundario raro conocido de las vacunas de ARNm es la inflamación del músculo del corazón o de la capa que lo rodea. Los médicos lo llaman miocarditis o pericarditis. El RIVM lo llama raro. Ocurre sobre todo en hombres jóvenes, poco después de la inyección. La mayoría se recupera. Ve al médico si tienes dolor en el pecho, falta de aire o un latido rápido después de la inyección.

### ¿Siguen protegiendo las vacunas de ARNm?

Sí, sobre todo contra la enfermedad grave y la muerte. Ninguna vacuna funciona al 100%.

La OMS dice: la protección es más alta en los primeros meses. Al cabo de unos seis meses baja. Por eso las personas con más riesgo a veces reciben otra dosis.

Puedes coger COVID-19 después de vacunarte. La probabilidad de enfermar muy grave suele ser entonces más baja.

### Qué significa esto para tu registro de vacunación

Anota qué inyección recibiste, la fecha y el nombre de la vacuna, como Comirnaty o Spikevax. Eso ayuda para una dosis posterior, para viajar o si un médico lo pregunta.

Con Vaxy guardas esos datos en un solo sitio, para ti y tu familia.

Este artículo es información, no consejo médico. Si tienes dudas, pregunta a tu médico o al servicio de salud pública.

### Respuestas cortas

**¿Qué es una vacuna de ARNm?**
Una inyección con un mensaje corto para tus células. Sin virus vivo.

**¿Cómo funciona?**
Tus células fabrican un trozo de proteína inofensivo. Tu defensa lo aprende. Luego desaparece el ARNm.

**¿Cambia tu ADN?**
No. El CDC y la EMA dicen que el ARNm no llega a tu ADN en el núcleo.

**¿Puedes coger COVID-19 por ella?**
No. No hay coronavirus vivo dentro.

**¿Cuánto tiempo permanece el ARNm en el cuerpo?**
Poco. La EMA dice que tu cuerpo lo degrada rápido.

### Fuentes

- [OMS: cómo funcionan las vacunas](https://www.who.int/news-room/feature-stories/detail/how-do-vaccines-work)
- [OMS: preguntas y respuestas sobre vacunas COVID-19, 15 de mayo de 2026](https://www.who.int/news-room/questions-and-answers/item/coronavirus-disease-(covid-19)-vaccines)
- [CDC: conceptos básicos de las vacunas COVID-19, cómo funcionan las de ARNm](https://www.cdc.gov/covid/vaccines/how-they-work.html)
- [EMA: datos clave sobre vacunas COVID-19, incluido el ARNm](https://www.ema.europa.eu/en/human-regulatory-overview/public-health-threats/coronavirus-disease-covid-19/covid-19-medicines/covid-19-vaccines-key-facts)
- [EMA: Comirnaty (Pfizer/BioNTech)](https://www.ema.europa.eu/en/medicines/human/EPAR/comirnaty)
- [EMA: Spikevax (Moderna)](https://www.ema.europa.eu/en/medicines/human/EPAR/spikevax)
- [RIVM: vacunación COVID-19](https://www.rivm.nl/covid-19-vaccinatie)
- [Guía RIVM LCI sobre vacunación COVID-19](https://lci.rivm.nl/richtlijnen/covid-19-vaccinatie)`,
  },
  {
    slug: "us-green-card-vaccination-records",
    date: "2026-08-19",
    category: "Salud en viajes",
    image: "/images/blog/us-green-card-vaccination-records.webp",
    author: "Vaxy",
    readTime: "7 min de lectura",
    title:
      "Cómo Vaxy ayuda con las vacunas exigidas para una green card de EE. UU.",
    excerpt:
      "El examen médico para una green card de EE. UU. incluye un control de vacunas. USCIS pide llevar prueba escrita. Así ayuda un registro digital en Vaxy, con fuentes.",
    faq: [
      {
        q: "¿Vaxy es una green card?",
        a: "No. Una green card es un permiso de residencia. Vaxy es un registro digital de vacunación. USCIS pide llevar prueba escrita de tus vacunas al examen médico de inmigración.",
      },
      {
        q: "¿Los turistas necesitan esto para entrar en Estados Unidos?",
        a: "No. Este control de vacunas es para quienes solicitan un visado de inmigrante o un ajuste de estatus a residente permanente. El Formulario I-693 no hace falta para unas vacaciones normales.",
      },
      {
        q: "¿Puede Vaxy sustituir el Formulario I-693?",
        a: "No. Solo un civil surgeon designado en Estados Unidos, o un panel physician en el extranjero, puede completar la evaluación de vacunas en el Formulario I-693.",
      },
      {
        q: "¿Sigue siendo obligatoria la vacuna contra la COVID-19 para una green card?",
        a: "No. Desde el 20 de enero de 2025 ya no hay que mostrar una vacunación contra la COVID-19 para el ajuste de estatus.",
      },
    ],
    content: `## Cómo Vaxy ayuda con las vacunas exigidas para una green card de EE. UU.

Una green card de EE. UU. no es una cartilla de vacunación. Es un permiso de residencia: lawful permanent residence.

Si solicitas ese estatus, debes poder demostrar ciertas vacunas. La ley de inmigración de EE. UU. dice que puedes ser considerado inadmisible sin esa prueba. USCIS es claro: lleva cualquier documentación escrita de vacunas al civil surgeon.

Ahí es donde ayuda Vaxy. Vaxy no es una green card ni un formulario oficial de USCIS. Es tu registro de vacunación, siempre en el teléfono, listo para el examen.

### A quién aplica

Esto aplica si tú:

- solicitas un visado de inmigrante desde el extranjero, o
- solicitas ajustar el estatus a residente permanente en Estados Unidos, a menudo con el Formulario I-485

Unas vacaciones normales o una visita corta no necesitan este examen. Las reglas de abajo son para quienes solicitan una green card.

### Por qué importa la prueba de vacunas

Según el artículo 212(a)(1)(A)(ii) de la Immigration and Nationality Act (INA), una persona que no pueda demostrar las vacunas exigidas puede ser rechazada para la admisión o el ajuste de estatus.

En Estados Unidos, un civil surgeon designado hace el examen. En el extranjero lo hace un panel physician designado por el Departamento de Estado de EE. UU. Los resultados van en el Formulario I-693, Report of Medical Examination and Vaccination Record.

USCIS dice que no tienes que repetir vacunas si ya tienes prueba. Lleva tus registros.

### Qué vacunas son obligatorias

La ley de inmigración cita, entre otras:

- Paperas
- Sarampión
- Rubéola
- Polio
- Tétanos y difteria
- Tosferina
- Haemophilus influenzae tipo B (Hib)
- Hepatitis B
- Otras vacunas recomendadas por el ACIP y exigidas por los CDC para inmigración

Los CDC también exigen, cuando corresponden a tu edad, protección frente a:

- Varicela
- Hepatitis A
- Meningococo
- Neumococo
- Rotavirus, en bebés muy pequeños
- Gripe estacional, durante la temporada de gripe

No necesitas todas las vacunas de la lista. Solo las adecuadas para tu edad. El civil surgeon sigue las Technical Instructions de los CDC, incluida una tabla por edad.

**COVID-19:** Desde el 20 de enero de 2025, la vacuna contra la COVID-19 ya no es obligatoria para el ajuste de estatus.

**Polio:** Desde el 1 de mayo de 2024, cualquier persona de 2 meses o más que no esté al día debe recibir una dosis de IPV. Eso incluye a adultos sin prueba de la serie infantil.

**Gripe:** Para el examen de inmigración, la vacuna de la gripe es obligatoria del 1 de septiembre al 31 de marzo, si está disponible.

### Qué llevar al examen

USCIS dice que es importante llevar cualquier documentación escrita de vacunas. Si un médico te ha hecho pruebas de inmunidad, lleva también esa prueba.

Si falta algo, el civil surgeon puede poner la vacuna. También puedes ponerte las dosis que falten con tu propio médico y volver con la prueba. Solo un civil surgeon puede completar la parte de vacunas del Formulario I-693.

Suele bastar una dosis de cada vacuna exigida en el momento del examen. No tienes que terminar una serie larga antes de que se pueda firmar el formulario.

### Cómo ayuda Vaxy

Las cartillas de papel se pierden. Las vacunas están en distintos países, idiomas y archivos.

Con Vaxy puedes:

- Guardar cada vacuna en un solo sitio, con fechas
- Añadir a familiares que también soliciten una green card
- Abrir tu registro en el teléfono en la cita
- Ver lo que ya tienes antes de reservar, para repetir menos vacunas

Muestra al civil surgeon tu registro de Vaxy, junto con cualquier prueba en papel que aún tengas. El civil surgeon decide qué cuenta.

### Lo que Vaxy no es

Vaxy no es una green card, ni el Formulario I-693, ni un sustituto del examen médico de inmigración. USCIS no acepta una tabla de vacunación incompleta. Solo un civil surgeon designado o un panel physician puede certificar el formulario.

Este artículo es información, no consejo legal ni médico. Las normas pueden cambiar. Revisa USCIS y los CDC antes de tu cita.

### Respuestas rápidas

**¿Vaxy es una green card?**
No. Es un registro digital de vacunación que llevas al examen.

**¿Esto aplica a turistas?**
No. Aplica a quienes solicitan una green card.

**¿Puede Vaxy sustituir el Formulario I-693?**
No. Solo un civil surgeon o un panel physician puede completar ese formulario.

**¿Sigue siendo obligatoria la COVID-19?**
No, no desde el 20 de enero de 2025.

### Fuentes

- [USCIS, Vaccination Requirements](https://www.uscis.gov/tools/designated-civil-surgeons/vaccination-requirements)
- [CDC, Vaccination Technical Instructions for Civil Surgeons](https://www.cdc.gov/immigrant-refugee-health/hcp/civil-surgeons/vaccination.html)
- [USCIS Policy Manual, Volume 8, Part B, Chapter 9](https://www.uscis.gov/policy-manual/volume-8-part-b-chapter-9)
- [Formulario I-693, Report of Medical Examination and Vaccination Record](https://www.uscis.gov/i-693)
- [42 C.F.R. Section 34.3](https://www.ecfr.gov/current/title-42/chapter-I/subchapter-C/part-34/section-34.3)`,
  },
  {
    slug: "vaccines-do-not-cause-autism",
    date: "2026-08-19",
    category: "Noticias de salud",
    image: "/images/blog/vaccines-do-not-cause-autism.webp",
    author: "Vaxy",
    readTime: "6 min de lectura",
    title: "Las vacunas no causan autismo: lo que dice la ciencia",
    excerpt:
      "Las vacunas no causan autismo. La OMS, el RIVM y grandes estudios no encuentran pruebas. Así nació el mito, y lo que los padres deben saber.",
    faq: [
      {
        q: "¿Las vacunas causan autismo?",
        a: "No. La OMS, el RIVM neerlandés y grandes estudios en muchos países no encuentran ninguna relación. Las vacunas no causan autismo.",
      },
      {
        q: "¿La vacuna triple vírica causa autismo?",
        a: "No. La triple vírica protege contra el sarampión, las paperas y la rubéola. Grandes estudios, entre ellos uno en Dinamarca con más de 650.000 niños, no encuentran relación con el autismo.",
      },
      {
        q: "¿De dónde sale la idea de que las vacunas causan autismo?",
        a: "De un artículo de Andrew Wakefield de 1998. Solo participaron 12 niños. El artículo se retiró después. Los datos no eran correctos, y Wakefield ya no pudo ejercer como médico.",
      },
      {
        q: "¿Es más seguro separar la triple vírica en tres pinchazos?",
        a: "No. No hay pruebas de que separar sea más seguro. Entonces tu hijo necesita más citas y permanece más tiempo sin protección contra el sarampión.",
      },
      {
        q: "¿Qué dicen la OMS y el RIVM sobre vacunas y autismo?",
        a: "Dicen lo mismo: las vacunas no causan autismo. La OMS lo confirmó de nuevo en diciembre de 2025. El RIVM lo dice en su página sobre la triple vírica y en su página sobre dudas de vacunación.",
      },
    ],
    content: `## Las vacunas no causan autismo: lo que dice la ciencia

Las vacunas no causan autismo. Esa es la conclusión clara de la Organización Mundial de la Salud (OMS), el programa nacional de vacunación neerlandés del RIVM y grandes estudios en muchos países.

Vaxy está de acuerdo. Los padres merecen información clara, no miedo.

### ¿Qué hay en las noticias?

El 11 de agosto de 2026, el presidente de Estados Unidos, Donald Trump, firmó una orden sobre las vacunas infantiles. Repitió la vieja idea de que las vacunas causan autismo. También quiere que la triple vírica, contra el sarampión, las paperas y la rubéola, se separe en pinchazos distintos.

Médicos y organizaciones de salud dicen que esta orden es peligrosa. No es ciencia. En Estados Unidos, los estados deciden qué vacunas piden los colegios, no el presidente. Así que la orden es un consejo, no una ley. Aun así puede hacer que los padres se sientan inseguros.

### ¿De dónde sale este mito?

En 1998, el médico británico Andrew Wakefield publicó un artículo. Escribía que la triple vírica podía causar autismo. Solo participaron 12 niños. Más tarde se vio que los datos no eran correctos. Wakefield también había recibido dinero de abogados que querían demandar a las vacunas.

La revista médica The Lancet retiró el artículo. Eso significa: ya no se debe usar como prueba. Después, Wakefield ya no pudo ejercer como médico.

Desde 1998 se han hecho muchos estudios grandes y cuidadosos. No encuentran relación entre las vacunas y el autismo.

### ¿Qué dicen los grandes estudios?

Un estudio conocido de Dinamarca, de 2019, siguió a 657.461 niños. Investigadores del Statens Serum Institut preguntaron si la triple vírica causaba autismo. La respuesta fue no. Tampoco en niños con más riesgo, por ejemplo porque un hermano o una hermana tiene autismo.

En 2025, un nuevo estudio danés miró el aluminio en las vacunas. Pequeñas cantidades de sales de aluminio están en algunas vacunas. Ayudan al cuerpo a reaccionar mejor al pinchazo. Los investigadores siguieron a más de 1 millón de niños. No encontraron relación con el autismo, ni con otros 49 problemas de salud.

El RIVM reúne este tipo de investigación en una página propia. La conclusión es siempre la misma: vacunar no causa autismo.

### ¿Qué dice la OMS?

En diciembre de 2025, un grupo de expertos de la OMS volvió a revisar la ciencia. El grupo se llama GACVS. Revisaron 31 estudios de 2010 a 2025, de muchos países.

Su conclusión: las vacunas no causan autismo. Tampoco las vacunas con tiomersal, un conservante, o con un poco de aluminio.

La OMS ya lo dijo en 2002, 2004 y 2012. La nueva revisión lo confirma otra vez.

La OMS también dice: las vacunas infantiles han salvado al menos 154 millones de vidas en 50 años.

### ¿Qué dice el programa de vacunación neerlandés?

El programa nacional neerlandés protege a los niños contra enfermedades graves, como el sarampión, las paperas, la rubéola, la tosferina y la polio.

La página del RIVM sobre la triple vírica es clara: no se coge autismo con esta vacuna. Tampoco diabetes ni una enfermedad intestinal.

En los Países Bajos, los niños reciben la triple vírica dos veces: hacia los 14 meses y hacia los 3 años. Tras la primera dosis, alrededor del 95% de los niños está protegido. Tras la segunda, alrededor del 99%.

El RIVM también dice: la duda es comprensible. Pregunta en el centro de salud infantil o a tu médico. Usa fuentes fiables, no solo las redes sociales.

### ¿Es más seguro separar la triple vírica?

No. La OMS, el RIVM y el CDC de Estados Unidos dicen que no hay pruebas de que pinchazos separados sean más seguros.

Si separas las dosis, tu hijo necesita más citas. Entre esas citas, tu hijo está más tiempo sin protección. El sarampión se contagia muy fácil. Un niño puede enfermar antes de terminar todas las dosis.

La triple vírica es una dosis pequeña. Son unas gotas. No es una gran cantidad de líquido en el cuerpo.

### ¿Por qué los niños reciben más vacunas ahora que antes?

Ahora los niños están protegidos contra más enfermedades. Eso es progreso, no peligro. Las vacunas combinadas significan menos pinchazos en el brazo, no más.

El calendario sigue la edad en la que el sistema inmunitario responde mejor, y en la que los niños tienen más riesgo.

Saltar o retrasar vacunas baja esa protección. El sarampión vuelve más a menudo cuando menos niños están vacunados.

### ¿Qué pueden hacer los padres?

1. Sigue el programa nacional de vacunación de tu país.
2. Pregunta a un médico o al centro de salud infantil si tienes dudas.
3. Usa fuentes como la OMS y el RIVM, no mensajes anónimos.
4. Lleva un registro de las vacunas de tu hijo, para no perder ninguna.

Con Vaxy guardas todas las vacunas de la familia en un solo sitio. Ves lo que ya se ha puesto y recibes un recordatorio para la siguiente dosis.

Este artículo es información, no consejo médico. ¿Te preocupa la salud de tu hijo? Habla con un médico.

### Respuestas cortas

**¿Las vacunas causan autismo?**
No. La OMS, el RIVM y grandes estudios no encuentran relación.

**¿La triple vírica causa autismo?**
No. Grandes estudios con cientos de miles de niños no encuentran relación.

**¿De dónde sale el mito?**
De un artículo de 1998 que luego se retiró. Los datos no eran correctos.

**¿Separar la triple vírica es más seguro?**
No. No hay pruebas. Entonces tu hijo permanece más tiempo sin protección.

**¿Qué dicen la OMS y el RIVM?**
Las vacunas no causan autismo. La OMS lo confirmó de nuevo en 2025.

### Fuentes

- [OMS: las vacunas no causan autismo, 11 de diciembre de 2025](https://www.who.int/news/item/11-12-2025-who-expert-group-s-new-analysis-reaffirms-there-is-no-link-between-vaccines-and-autism)
- [Declaración GACVS de la OMS sobre vacunas y autismo](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [RIVM: investigación sobre vacunación y autismo](https://rijksvaccinatieprogramma.nl/over-vaccineren/onderzoek-vaccins/onderzoek-verband-vaccineren-autisme)
- [RIVM: vacunar no causa autismo](https://rijksvaccinatieprogramma.nl/over-vaccineren/twijfels)
- [RIVM: vacunación triple vírica](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)
- [Hviid et al., triple vírica y autismo, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [Statens Serum Institut: no hay relación entre el aluminio de las vacunas y el autismo, 2025](https://en.ssi.dk/news/news/2025/large-danish-study-no-link-between-vaccines-and-autism-or-49-other-health-conditions)
- [The Lancet retira el artículo de Wakefield, 2010](https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(10)60175-4/fulltext)
- [CDC: Vaccines Do Not Cause Autism](https://www.cdc.gov/vaccine-safety/about/autism.html)
- [BBC: Trump firma una orden sobre vacunas infantiles, 11 de agosto de 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)`,
  },
  {
    slug: "childhood-vaccines-trump-order",
    date: "2026-08-19",
    category: "Noticias de salud",
    image: "/images/blog/childhood-vaccines-trump-order.webp",
    author: "Vaxy",
    readTime: "8 min de lectura",
    title:
      "Por qué las vacunas infantiles siguen importando tras la orden de Trump",
    excerpt:
      "Trump quiere menos vacunas infantiles y separar la triple vírica. Aquí se explica por qué existen esas vacunas, qué dice la ciencia y qué puede pasar si menos niños están protegidos.",
    faq: [
      {
        q: "¿Esta orden de EE. UU. cambia el calendario de vacunación en Europa?",
        a: "No. Es una recomendación en Estados Unidos, no una ley. En los Países Bajos rige el Rijksvaccinatieprogramma. Los estados de EE. UU. deciden qué vacunas piden las escuelas.",
      },
      {
        q: "¿Por qué los niños reciben tantas vacunas?",
        a: "Porque ahora hay protección frente a más enfermedades graves. El calendario sigue la edad en la que el sistema inmunitario responde mejor y en la que los niños tienen más riesgo.",
      },
      {
        q: "¿Es más seguro separar la triple vírica en tres inyecciones?",
        a: "No. No hay pruebas de que separar sea más seguro. Tu hijo necesitaría más citas y permanecería más tiempo sin protección frente al sarampión, las paperas y la rubéola.",
      },
      {
        q: "¿Qué puede pasar si se vacunan menos niños?",
        a: "Enfermedades como el sarampión, la tosferina y la polio pueden volver. El sarampión se contagia con mucha facilidad. Los brotes ya crecen donde baja la vacunación.",
      },
    ],
    content: `## Por qué las vacunas infantiles siguen importando tras la orden de Trump

El 11 de agosto de 2026, el presidente de EE. UU. Donald Trump firmó una orden sobre vacunas infantiles. Quiere menos vacunas para todos los niños y quiere separar la vacuna combinada contra el sarampión, las paperas y la rubéola (triple vírica) en inyecciones distintas.

Los médicos lo llaman peligroso. La orden es una recomendación, no una ley. Aun así puede inquietar a los padres y hacer bajar aún más la vacunación. Este artículo explica por qué existen las vacunas infantiles y qué puede ocurrir.

### ¿Qué dice la orden?

Según la [BBC](https://www.bbc.com/news/articles/ce3q5vl581wo), Trump quiere bajar de 18 a 11 el número de enfermedades para las que todos los niños de EE. UU. reciben una vacuna. En su lista de 11 están: sarampión, paperas, rubéola, difteria, tétanos, tosferina, polio, Hib, neumococo, VPH y varicela.

Otras vacunas, como hepatitis B y meningococo, quedarían solo para niños con más riesgo. Gripe y COVID-19 pasarían a ser una decisión entre padre o madre y médico.

Trump también quiere que las vacunas no se den en la misma visita y que se separe la triple vírica combinada.

Las normas escolares siguen siendo de cada estado. Los padres en EE. UU. no pueden pedir ya tres inyecciones separadas de triple vírica. Esos productos separados no están autorizados ahora en Estados Unidos.

### Por qué existen las vacunas infantiles

Las vacunas infantiles protegen frente a enfermedades que antes enfermaban gravemente a muchos niños, o los mataban. El sarampión puede causar neumonía, inflamación del cerebro y sordera. La tosferina es especialmente peligrosa para los bebés. La polio puede causar parálisis. Hib puede causar meningitis. La hepatitis B puede causar después cáncer de hígado.

La OMS dice que las vacunas han salvado más de 150 millones de vidas en 50 años. Solo la vacunación contra el sarampión evitó unos 59 millones de muertes entre 2000 y 2024.

Los niños reciben las vacunas a edades fijas, no al azar. Los médicos eligen el momento en que el sistema inmunitario responde mejor y en que el niño corre más riesgo. La American Academy of Pediatrics (AAP) dice que no hay razón médica para retrasar o saltarse las vacunas recomendadas.

Hoy los niños están protegidos frente a más enfermedades que antes. Eso es progreso. Las vacunas combinadas significan menos agujas, no más.

### La protección comunitaria no es un extra

Algunos niños no pueden vacunarse. Los bebés pueden ser demasiado pequeños. Otros tienen un sistema inmunitario débil. Dependen de las personas a su alrededor.

Para el sarampión hace falta que al menos el 95% de los niños reciba dos dosis para detener un brote. Si ese número baja, el virus encuentra camino rápido. El sarampión suele ser la primera enfermedad que vuelve cuando baja la vacunación.

En 2023 hubo unos 10,3 millones de casos de sarampión en el mundo. La OMS, UNICEF y Gavi advierten de que aumentan los brotes de sarampión, meningitis y fiebre amarilla. En EE. UU., los casos de sarampión están en el nivel más alto en 35 años, según informaciones recientes.

### ¿Qué puede provocar esta orden?

**Más dosis perdidas.** Más citas suena prudente. En la práctica, las familias vuelven menos. Una dosis perdida es más tiempo sin protección.

**Más tiempo sin protección frente al sarampión.** Entre inyecciones separadas hay un hueco. En ese hueco un niño puede contagiarse de sarampión, paperas o rubéola. Los CDC dicen que no hay evidencia científica publicada de que separar la triple vírica combinada aporte nada. La vacuna combinada se usa desde 1971 y funciona igual de bien que tres inyecciones separadas.

**Enfermedades que salen de la lista «para todos los niños».** Una vacuna de hepatitis B al nacer ayuda a evitar una infección de por vida. Las vacunas frente a rotavirus evitan diarreas graves en bebés. Si esas vacunas dejan de recomendarse para todos los niños, más niños se enfermarán.

**Más confusión, menos vacunación.** Trump repitió la idea de que las vacunas causan autismo. Eso está desmentido. Si los padres retrasan las vacunas por duda, los brotes son más probables. El senador Bill Cassidy, que es médico, llamó a la orden «equivocada». El presidente de la AAP la llamó «peligrosa».

**No es una ley, pero es una señal.** Los estados siguen fijando las normas escolares. Los consejos federales influyen aun así en médicos, aseguradoras y padres. Recomendaciones menos claras suelen significar menos vacunaciones.

### ¿Las vacunas causan autismo?

No. La OMS lo confirmó otra vez en diciembre de 2025. Un estudio danés de 2019 siguió a 657.461 niños y no encontró relación entre la triple vírica y el autismo. Explicamos ese mito en [Las vacunas no causan autismo](/es/blog/vaccines-do-not-cause-autism).

### Qué significa esto si no vives en EE. UU.

El Rijksvaccinatieprogramma de los Países Bajos no cambia por esto. Tampoco los calendarios de otros países europeos.

El sarampión no se detiene en una frontera. Viajar a zonas con brotes, o el contacto con viajeros, sigue siendo motivo para estar al día. Revisa tus vacunas y las de tus hijos antes de salir.

### Qué puedes hacer

1. Sigue el calendario nacional de tu país, no una orden política de otro país.
2. Pregunta a un médico si tienes dudas.
3. No retrases las vacunas «por si acaso». Retrasar es más riesgo.
4. Guarda un registro de las vacunas que ya ha recibido tu hijo.

Con Vaxy tienes todas las vacunas de la familia en un solo sitio. Ves lo que ya está puesto y recibes un recordatorio para la siguiente.

Este artículo es información, no consejo médico. Si te preocupa la salud de tu hijo, habla con un médico.

### Respuestas rápidas

**¿Esto cambia las vacunas en Europa?**
No. Es una recomendación de EE. UU., no una norma europea.

**¿Por qué tantas vacunas infantiles?**
Porque protegen frente a más enfermedades graves, en las edades en que esa protección más se necesita.

**¿Separar la triple vírica es más seguro?**
No. No hay pruebas. Tu hijo estaría más tiempo sin protección.

**¿Y si se vacunan menos niños?**
El sarampión y otras enfermedades vuelven. Los brotes ya están creciendo.

### Fuentes

- [BBC: Trump firma una orden sobre vacunas infantiles, 11 de agosto de 2026](https://www.bbc.com/news/articles/ce3q5vl581wo)
- [CIDRAP: Trump firma una orden para separar la triple vírica](https://www.cidrap.umn.edu/trump-signs-executive-order-break-measles-mumps-rubella-vaccine)
- [OMS, UNICEF y Gavi: aumentan los brotes de enfermedades prevenibles con vacunas, 24 de abril de 2025](https://www.who.int/news/item/24-04-2025-increases-in-vaccine-preventable-disease-outbreaks-threaten-years-of-progress--warn-who--unicef--gavi)
- [OMS: ficha del sarampión](https://www.who.int/news-room/fact-sheets/detail/measles)
- [OMS: bajan las muertes por sarampión, suben los casos, 28 de noviembre de 2025](https://www.who.int/news/item/28-11-2025-measles-deaths-down-88--since-2000--but-cases-surge)
- [OMS GACVS: las vacunas no causan autismo](https://www.who.int/news/item/11-12-2025-statement-gacvs-vaccines-autism)
- [Hviid et al., triple vírica y autismo, Annals of Internal Medicine 2019](https://www.acpjournals.org/doi/10.7326/M18-2101)
- [RIVM: vacunación triple vírica](https://rijksvaccinatieprogramma.nl/vaccinaties/bmr)`,
  },
  {
    slug: "moderna-mrna-melanoma-vaccine",
    date: "2026-08-19",
    category: "Noticias de salud",
    image: "/images/blog/moderna-mrna-melanoma-vaccine.webp",
    author: "Vaxy",
    readTime: "6 min de lectura",
    title:
      "La vacuna de ARNm contra el cáncer de Moderna funciona en un gran ensayo de melanoma",
    excerpt:
      "Moderna y Merck dicen que una vacuna de ARNm personal más Keytruda ayudó a impedir que el melanoma volviera después de una operación. Esto es lo que significa el estudio de fase 3, en lenguaje sencillo.",
    faq: [
      {
        q: "¿Esta vacuna de ARNm contra el cáncer previene el cáncer?",
        a: "No. Es un tratamiento personal para personas que ya tuvieron melanoma. Los médicos la dan después de la operación para reducir la posibilidad de que el cáncer vuelva.",
      },
      {
        q: "¿La vacuna de Moderna contra el melanoma es la misma que la vacuna contra la COVID-19?",
        a: "No. Ambas usan ARNm, pero esta vacuna contra el cáncer se fabrica para el tumor de una persona. Una vacuna contra la COVID-19 es igual para mucha gente y se dirige a un virus.",
      },
      {
        q: "¿Ya se puede recibir la vacuna de ARNm de Moderna contra el melanoma?",
        a: "Todavía no. No está aprobada para uso general. Moderna y Merck planean pedir la aprobación a las autoridades sanitarias en los próximos meses.",
      },
      {
        q: "¿La vacuna funciona mejor que Keytruda sola?",
        a: "Sí, en este estudio de fase 3. Las personas que recibieron la vacuna más Keytruda permanecieron más tiempo sin cáncer, y el cáncer se extendió menos. Los números completos se presentarán en un congreso médico.",
      },
    ],
    content: `## La vacuna de ARNm contra el cáncer de Moderna funciona en un gran ensayo de melanoma

El 19 de agosto de 2026, Moderna y Merck compartieron una buena noticia. Su vacuna de ARNm personal contra el cáncer, usada junto con el medicamento Keytruda, funcionó en un estudio grande. La vacuna ayudó a las personas con melanoma a permanecer más tiempo sin cáncer después de una operación.

Este es el primer estudio grande de fase 3 que muestra que una vacuna de ARNm contra el cáncer puede funcionar. También es la primera vez que este tipo de vacuna personal va mejor que Keytruda sola después de una operación de melanoma.

### ¿Qué es la vacuna de ARNm contra el cáncer de Moderna?

El tratamiento se llama intismeran autogene. También puedes ver los nombres V940 o mRNA-4157.

No es una vacuna que las personas sanas reciben para prevenir el cáncer. Es un tratamiento para personas que ya tuvieron melanoma, después de que los médicos quitaron el tumor con una operación.

Los médicos llaman a esto tratamiento adyuvante. Significa un tratamiento extra después de la operación, para reducir la posibilidad de que el cáncer vuelva.

### ¿Cómo funciona una vacuna de ARNm personal contra el cáncer?

El melanoma es una forma grave de cáncer de piel. Cada tumor tiene sus propios cambios, llamados mutaciones. Puedes verlo como una huella única.

La vacuna se fabrica para una persona:

1. Los médicos toman una muestra del tumor.
2. Los científicos leen la huella de ese tumor.
3. Fabrican una vacuna de ARNm que enseña al sistema inmunitario a encontrar esas marcas del cáncer.
4. Después de la inyección, el cuerpo lee el ARNm y fabrica pequeños trozos de proteína. El sistema inmunitario aprende entonces a atacar las células cancerosas con las mismas marcas.

El ARNm es un mensaje corto que las células pueden leer. Las vacunas contra la COVID-19 también usan ARNm, pero esta vacuna contra el cáncer es diferente. Se fabrica para una persona y un tumor.

La vacuna se da junto con Keytruda (pembrolizumab). Keytruda es una inmunoterapia. Ayuda al sistema inmunitario a notar y combatir las células cancerosas.

### ¿Qué encontró el ensayo de fase 3 de melanoma?

El estudio se llama INTerpath-001. Un ensayo de fase 3 es un estudio grande y tardío. Las empresas lo usan a menudo antes de pedir a las autoridades sanitarias que aprueben un tratamiento.

En una revisión prevista de los datos, el estudio cumplió dos objetivos principales:

- Las personas permanecieron más tiempo sin cáncer. El cáncer volvió menos a menudo.
- El cáncer se extendió menos a otras partes del cuerpo.

Las empresas dicen que la diferencia es real, no casualidad, y que importa para los pacientes. El estudio sigue en marcha. Los investigadores también mirarán cuánto tiempo viven las personas.

Los números completos se presentarán en un congreso médico. Moderna y Merck también planean hablar con las autoridades sobre la aprobación.

### ¿Quién participó en el estudio?

El ensayo incluyó a 1.137 personas con melanoma de alto riesgo. Su cáncer era de estadio 2B, 2C, 3 o 4. Una operación ya había quitado el cáncer.

Las personas se dividieron al azar en dos grupos:

- Unas dos de cada tres personas recibieron la vacuna de ARNm personal más Keytruda
- Una de cada tres personas recibió solo Keytruda

El tratamiento duró aproximadamente un año. Las personas podían recibir hasta nueve dosis de la vacuna.

### ¿Es segura la vacuna de ARNm contra el cáncer?

Hasta ahora, las empresas no informan de nuevos problemas de seguridad. En estudios anteriores, los efectos secundarios frecuentes fueron:

- Cansancio
- Dolor en el lugar de la inyección
- Escalofríos

Keytruda también puede causar efectos secundarios inmunitarios. Las personas en un ensayo se vigilan de cerca.

### ¿Qué mostraron los estudios anteriores?

En un estudio de fase 2 más pequeño, la misma combinación redujo un 49% el riesgo de que el cáncer volviera o de muerte a los cinco años, frente a Keytruda sola. También redujo un 59% el riesgo de que el cáncer se extendiera lejos, o de muerte.

El nuevo estudio de fase 3 es más grande. Este es el tipo de estudio que usan las autoridades cuando deciden si un tratamiento puede aprobarse.

### ¿Ya se puede recibir esta vacuna?

Todavía no. Este tratamiento no está aprobado para uso general.

Moderna dice que las empresas quieren pedir la aprobación en los próximos meses. Aún no hay una fecha exacta.

Si se aprueba, primero sería para personas con melanoma de alto riesgo después de una operación, no para todo el mundo.

### ¿Podría ayudar en otros cánceres?

Merck y Moderna también están probando esta idea en otros cánceres, como el de pulmón, el de vejiga y el de riñón.

Esos estudios siguen en marcha. Buenos resultados en melanoma no significan automáticamente que funcione en todos los cánceres.

### Qué significa esta noticia para ti

Si no tienes melanoma, no necesitas esta vacuna. Las mejores formas de reducir el riesgo de melanoma siguen siendo sencillas:

- Protege tu piel del sol
- Evita las camas solares
- Revisa tu piel y ve al médico si un lunar cambia

Si tú o alguien cercano ha tenido melanoma, esta noticia da esperanza. Habla con un oncólogo. Puede explicar el tratamiento actual y si un ensayo clínico puede ser una opción.

Este artículo es información, no consejo médico.

### Respuestas rápidas

**¿Esta vacuna previene el cáncer?**
No. Es un tratamiento personal después de una operación por melanoma.

**¿Es la misma que la vacuna contra la COVID-19?**
No. Ambas usan ARNm, pero esta vacuna se fabrica para el tumor de una persona.

**¿Está aprobada?**
Todavía no. Las empresas planean pedir la aprobación pronto.

**¿Funcionó mejor que Keytruda sola?**
Sí, en este estudio de fase 3. Los detalles completos se presentarán en un congreso médico.

### Fuentes

- [Comunicado de Merck, 19 de agosto de 2026](https://www.merck.com/news/merck-and-moderna-announce-phase-3-interpath-001-trial-of-intismeran-autogene-plus-keytruda-met-endpoints-of-recurrence-free-survival-rfs-and-distant-metastasis-free-survival-dmfs-in-patient/)
- [CNN Health, 19 de agosto de 2026](https://edition.cnn.com/2026/08/19/health/moderna-merck-mrna-melanoma-vaccine)`,
  },
  {
    slug: "why-vaccination-records-matter",
    date: "2026-06-10",
    category: "Consejos de salud",
    image: "/images/blog/why-vaccination-records-matter.webp",
    author: "Vaxy",
    readTime: "4 min de lectura",
    title: "Por qué tus registros de vacunación importan más que nunca",
    excerpt:
      "En un mundo cada vez más conectado, tener acceso inmediato a tu historial de vacunación puede evitar mucho estrés en la frontera.",
    content: `## Por qué tus registros de vacunación importan más que nunca

En un mundo cada vez más conectado, tener acceso inmediato a tu historial de vacunación puede evitar mucho estrés en la frontera.

### El problema del papel

Durante décadas, la cartilla amarilla de vacunación de la OMS fue el estándar para documentar la salud en viajes. Pero el papel se pierde, se deteriora o se queda en casa justo cuando más lo necesitas. Muchos viajeros conocen la ansiedad de que les pidan una prueba de vacunación contra la fiebre amarilla en aduanas y darse cuenta de que la cartilla está en la cocina.

### Qué puede salir mal sin registros

- **Entrada denegada** en fronteras que exigen vacunas concretas
- **Vacunas repetidas** porque los médicos no pueden consultar tu historial
- **Refuerzos olvidados** por no tener recordatorios
- **Urgencias** en las que el personal médico no conoce tu estado vacunal

### La alternativa digital

Apps como Vaxy lo solucionan con un registro digital seguro y siempre disponible de cada vacuna, para ti y para toda tu familia. Sin papel y sin dudas.

### Los requisitos de viaje cambian

Los países actualizan con frecuencia sus requisitos de vacunación. Las normas sobre fiebre amarilla, meningococo y COVID-19 han cambiado mucho en los últimos años. Con un registro digital actualizado puedes comprobar rápidamente qué tienes y qué necesitas antes de reservar.

### Cómo empezar

El mejor momento para digitalizar tus vacunas es antes de necesitarlas. Dedica diez minutos hoy a registrarlas en Vaxy y no volverás a buscar una cartilla en el último momento.`,
  },
  {
    slug: "travel-vaccinations-guide",
    date: "2026-05-28",
    category: "Salud en viajes",
    image: "/images/blog/travel-vaccinations-guide.webp",
    author: "Vaxy",
    readTime: "7 min de lectura",
    title: "La guía completa de vacunas para viajar en 2026",
    excerpt:
      "¿Planeas viajar al extranjero? Esto es lo que debes saber sobre vacunas obligatorias, recomendadas y cuándo ponértelas.",
    content: `## La guía completa de vacunas para viajar en 2026

¿Planeas viajar al extranjero? Esto es lo que debes saber sobre vacunas obligatorias, recomendadas y cuándo ponértelas.

### Empieza 6-8 semanas antes

El mayor error de los viajeros es esperar al último momento. Algunas vacunas requieren varias dosis repartidas durante semanas, y el sistema inmunitario necesita tiempo para generar protección. Reserva una consulta de salud del viajero al menos 6-8 semanas antes de salir.

### Obligatorias o recomendadas

Las vacunas **obligatorias** son necesarias por ley para entrar en ciertos países. El ejemplo más común es la fiebre amarilla: muchos países de África y Sudamérica exigen prueba si viajas desde una zona endémica.

Las vacunas **recomendadas** no son obligatorias por ley, pero se aconsejan según tu destino, actividades y estado de salud.

### Vacunas frecuentes por región

**África**
- Fiebre amarilla, obligatoria en muchos países
- Tifoidea
- Hepatitis A y B
- Profilaxis contra la malaria, no es vacuna pero suele ser esencial
- Meningococo, especialmente para peregrinos del Hajj

**Sudeste asiático**
- Hepatitis A y B
- Tifoidea
- Encefalitis japonesa en zonas rurales
- Rabia en estancias largas o con contacto animal

**Sudamérica**
- Fiebre amarilla
- Tifoidea
- Hepatitis A y B

**Europa y Norteamérica**
- Normalmente bastan las vacunas rutinarias
- Encefalitis transmitida por garrapatas en zonas boscosas de Europa central y oriental

### No olvides las vacunas rutinarias

Antes de pensar en vacunas de viaje más específicas, revisa las básicas:
- Triple vírica
- Tétanos, difteria y tosferina
- Gripe
- COVID-19

### Usar Vaxy para planificar

La función de salud para viajes de Vaxy permite elegir destino y ver al instante qué vacunas son obligatorias o recomendadas. Compara esos consejos con tus registros y muestra exactamente qué te falta.`,
  },
  {
    slug: "vaccinating-your-children",
    date: "2026-05-14",
    category: "Salud familiar",
    image: "/images/blog/vaccinating-your-children.webp",
    author: "Vaxy",
    readTime: "5 min de lectura",
    title: "Cómo seguir el calendario de vacunación de tus hijos",
    excerpt:
      "Los niños reciben muchas vacunas en sus primeros años. Así puedes organizarte y no perder ninguna dosis importante.",
    content: `## Cómo seguir el calendario de vacunación de tus hijos

Los niños reciben muchas vacunas en sus primeros años. Así puedes organizarte y no perder ninguna dosis importante.

### El calendario infantil

En los Países Bajos y en la mayor parte de Europa, los niños siguen un programa nacional de vacunación. En los Países Bajos, el Rijksvaccinatieprogramma cubre desde el nacimiento hasta la adolescencia. Incluye protección frente a:

- DKTP
- Hib
- Hepatitis B
- Neumococo
- Meningococo
- Triple vírica
- VPH para niñas y niños alrededor de los 10 años

### Por qué importa una dosis perdida

Las vacunas funcionan mejor cuando se administran a la edad adecuada. El calendario está diseñado para proteger antes de las etapas de mayor vulnerabilidad. Una dosis perdida no solo afecta a tu hijo, también puede reducir la inmunidad comunitaria.

### El reto para los padres

Con tantas vacunas a edades distintas es fácil perder la pista, sobre todo con varios hijos. Cada niño tiene su propio calendario, y organizar cartillas en papel entre distintas visitas médicas no es sencillo.

### Cómo ayuda Vaxy a las familias

Con Vaxy puedes añadir a todos los miembros de la familia en una cuenta. Cada persona tiene su registro completo y recibes recordatorios cuando se acercan próximas vacunas.

### Consejos para mantener el calendario

1. **Registra desde el nacimiento**: añade a tu bebé a Vaxy cuanto antes
2. **Confirma con tu médico**: pide que revisen los datos tras cada visita
3. **Activa recordatorios**: usa notificaciones para próximas dosis
4. **Añade notas**: registra reacciones o circunstancias especiales de cada vacuna`,
  },
];

export const blog: Record<Locale, BlogPost[]> = copy(nl, en, de, fr, es);
